import React, { useState, useEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Shield, FileText, MapPin, Book, Car, AlertTriangle, Camera, Share2, Upload, Search, ChevronRight, ChevronLeft, X, Menu, Home, User, Users, Info, Phone, Scale, Clock, DollarSign, CheckCircle, Navigation, Flag, Star, ExternalLink, Bell, Calendar, Volume2, VolumeX, Globe, WifiOff, Plus, Trash2, Play, Pause, Radio, CloudRain, Sun, Eye, Timer, Receipt, RefreshCw, Settings, Gavel, AlertCircle, Calculator, MessageCircle, HelpCircle, Filter, Zap, GraduationCap, BookOpen, Building2, Send, Briefcase, Image, Award, Target, ChevronDown, ChevronUp, Clipboard, UserCheck, BadgeCheck, Wrench, LogIn, LogOut, UserCog, Lock, Box, Tag, BarChart3, Truck, Store, CreditCard, Smartphone, Mail, Loader2 } from 'lucide-react';
import Fuse from 'fuse.js';
import regulationData from './ja-road-traffic-data.json';
import enhancedOffencesData from './data/ja-offences-enhanced.json';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage, AdminDashboard, AdminSubmissionsPage, AdminUsersPage, AdminDirectoryPage, AdminAllListingsPage, AdminHotspotsPage, AdminCamerasPage, AdminSettingsPage, AdminContactSubmissions as AdminContactSubmissionsPage } from './components/Admin';
import { AdminSubmissionsPageEnhanced, AdminServiceProvidersPage, AdminSubscriptionsPage, AdminPaymentsPage, AdminEmailTemplatesPage, AdminPaymentRequestsPage } from './components/AdminServiceProviders';
import { AdminAnalyticsPage, AdminSettingsPageEnhanced } from './components/AdminAnalytics';
import { AdminBannerAdsPage } from './components/AdminBannerAds';
import { AdminHeroCarouselPage } from './components/AdminHeroCarousel';
import { HeroCarousel } from './components/HeroCarousel';
import { AdvertiserPortal } from './components/AdvertiserPortal';
import VendorPortalPage from './components/VendorPortal';
import { ServiceProvidersPage } from './components/ServiceProvidersPage';
import { GetListedPageEnhanced } from './components/GetListedPageEnhanced';
import { SEO, WebsiteSchema, OrganizationSchema, PAGE_SEO } from './components/SEO';
import { TopBanner, BottomBanner, InlineBanner1, InlineBanner2, InlineBanner3 } from './components/BannerAd';
import { initGA4, trackPageView } from './lib/analytics';
import { appSettings } from './lib/supabase';
import OffencesPageEnhanced from './components/OffencesPage';
import { DataCorrectionReport } from './components/DataCorrectionReport';
import { AdminDataCorrectionsPage } from './components/AdminDataCorrections';
import { AdminNotificationsPage, NotificationBell } from './components/AdminNotifications';
import { UserDashboard } from './components/UserDashboard';
import { TicketTrackerPage } from './components/TicketTracker';
import RoadFatalityTracker from './components/RoadFatalityTracker';
import AdminRoadFatalities from './components/AdminRoadFatalities';
import GlobalSearch from './components/GlobalSearch';
import trafficService, { getWeather, getHotspots, createHotspot, confirmHotspot, flagHotspot, deleteHotspot, getSpeedCameras, createSpeedCamera, confirmSpeedCamera, flagSpeedCamera, deleteSpeedCamera, getCurrentLocation, formatDistance, sortByDistance, getTimeAgo, getTimeUntil, getReputationLevel, PARISH_COORDS } from './lib/trafficService';

// ==================== CUSTOM HOOK ====================
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch { return initialValue; }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
      return { success: true };
    } catch (e) {
      console.error('localStorage error:', e);
      return { success: false, error: e.name === 'QuotaExceededError' ? 'Storage quota exceeded. Try removing some photos or old reports.' : 'Failed to save. Please try again.' };
    }
  };
  return [storedValue, setValue];
};

// ==================== BACK BUTTON COMPONENT ====================
const BackButton = ({ setPage, goBack, label = "Back" }) => (
  <button onClick={() => goBack ? goBack() : setPage('home')}
    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// ==================== AUTH GATE COMPONENT ====================
// Protects sensitive pages that require authentication
const AuthGate = ({ children, setPage, goBack, pageName, pageIcon: PageIcon }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50 p-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50 p-4">
        <div className="max-w-lg mx-auto pt-8">
          <BackButton setPage={setPage} goBack={goBack} />
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Sign In Required</h2>
            <p className="text-gray-600 mb-6">
              {pageName} contains your personal data. Please sign in or create an account to access this feature securely.
            </p>
            <div className="bg-emerald-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-3 text-emerald-700">
                {PageIcon && <PageIcon className="w-6 h-6" />}
                <span className="font-medium">{pageName}</span>
              </div>
              <p className="text-sm text-emerald-600 mt-2">Your data is stored securely and only visible to you</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => setPage('login')}
                className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Sign In / Register
              </button>
              <button
                onClick={() => setPage('home')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all"
              >
                Back to Home
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-6">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// ==================== TRANSLATIONS ====================
const T = {
  en: {
    // App
    appName: "Jamaica Driver's Guide",
    tagline: "Regulations, Rights & Resources",

    // Navigation
    home: "Home",
    policeStopGuide: "Police Stop Guide",
    offencesFines: "Offences & Fines",
    myDocuments: "My Documents",
    hotspots: "Hotspots",
    resources: "Resources",
    demeritPoints: "Demerit Points",
    ticketTracker: "Ticket Tracker",
    accidentReport: "Accident Report",
    speedCameras: "Speed Cameras",
    lawyers: "Lawyers",
    traffic: "Traffic",
    settings: "Settings",

    // Hero
    stoppedByPolice: "Stopped by Police?",
    lookUpOffence: "Look Up Offence",
    knowYourRights: "Know Your Rights",
    onEveryRoad: "& So Much More",
    heroDesc: "Road Traffic Regulations, 2022 — plus fees, how-to guides & service directories.",

    // Common
    disclaimer: "This app provides general information, not legal advice.",
    yourRights: "Your Rights",
    tips: "Tips",
    courtRemedy: "Court Remedy",
    fineRange: "Fine Range",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    previous: "Previous",
    learnMore: "Learn more",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    close: "Close",
    back: "Back",
    submit: "Submit",
    delete: "Delete",
    edit: "Edit",
    view: "View",

    // Homepage sections
    quickReference: "Quick Reference",
    yourToolsServices: "Your Tools & Services",
    trafficLocations: "Traffic & Locations",
    serviceProviders: "Service Providers",

    // Quick Reference items
    askBot: "Ask Bot",
    fineCalculator: "Fine Calculator",
    roadCode: "Road Code",
    policeStop: "Police Stop",
    feesSchedule: "Fees Schedule",
    howToGuides: "How-To Guides",
    learnerGuide: "Learner Guide",

    // Service Providers
    findServiceProviders: "Find Service Providers",
    serviceProvidersDesc: "Lawyers, Insurance, Mechanics, Wreckers & more",
    getYourBusinessListed: "Get Your Business Listed",
    getListedDesc: "Join our directory of trusted service providers",

    // eMVRC Banner
    emvrcTitle: "New: Electronic Registration (eMVRC)",
    emvrcDesc: "Since July 2025, physical registration stickers are no longer issued. All certificates are now electronic. A valid email address is required for all TAJ transactions.",

    // Disclaimer
    importantDisclaimer: "Important Disclaimer",

    // Offline
    youreOffline: "You're offline - App still works!",

    // Footer
    generalInfoOnly: "General information only, not legal advice.",

    // Police Stop
    whatToDo: "What to do",
    doNot: "Don't",
    remember: "Remember",

    // Offences
    searchOffences: "Search offences...",
    allCategories: "All Categories",
    fixedPenalty: "Fixed Penalty",
    courtOnly: "Court Only",
    points: "Points",

    // Documents
    addDocument: "Add Document",
    noDocuments: "No documents yet",

    // Hotspots
    reportHotspot: "Report Hotspot",
    noHotspots: "No hotspots reported yet",

    // Speed Cameras
    reportCamera: "Report Camera",
    noCameras: "No cameras reported yet",

    // Forms
    required: "Required",
    optional: "Optional",
    selectParish: "Select Parish",
    enterDetails: "Enter details",

    // Ticket Tracker
    addTicket: "Add Ticket",
    noTickets: "No tickets tracked",
    paid: "Paid",
    unpaid: "Unpaid",

    // Accident Report
    startReport: "Start Report",

    // Settings
    language: "Language",
    english: "English",
    patois: "Patois"
  },
  patois: {
    // App
    appName: "Jamaica Driver's Guide",
    tagline: "Regulations, Rights & Resources",

    // Navigation
    home: "Home",
    policeStopGuide: "Police Stop Guide",
    offencesFines: "Offences & Fines",
    myDocuments: "Mi Documents",
    hotspots: "Hotspots",
    resources: "Resources",
    demeritPoints: "Demerit Points",
    ticketTracker: "Ticket Tracker",
    accidentReport: "Accident Report",
    speedCameras: "Speed Cameras",
    lawyers: "Lawyers",
    traffic: "Traffic",
    settings: "Settings",

    // Hero
    stoppedByPolice: "Police Stop Yuh?",
    lookUpOffence: "Look Up Offence",
    knowYourRights: "Know Yuh Rights",
    onEveryRoad: "& So Much More",
    heroDesc: "Road Traffic Regulations, 2022 — plus fees, how-to guides & service directories.",

    // Common
    disclaimer: "Dis app give general info, not legal advice.",
    yourRights: "Yuh Rights",
    tips: "Tips",
    courtRemedy: "Court Remedy",
    fineRange: "Fine Range",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    previous: "Back",
    learnMore: "Learn more",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    close: "Close",
    back: "Back",
    submit: "Submit",
    delete: "Delete",
    edit: "Edit",
    view: "View",

    // Homepage sections
    quickReference: "Quick Reference",
    yourToolsServices: "Yuh Tools & Services",
    trafficLocations: "Traffic & Locations",
    serviceProviders: "Service Providers",

    // Quick Reference items
    askBot: "Ask Bot",
    fineCalculator: "Fine Calculator",
    roadCode: "Road Code",
    policeStop: "Police Stop Yuh",
    feesSchedule: "Fees Schedule",
    howToGuides: "How-To Guides",
    learnerGuide: "Learner Guide",

    // Service Providers
    findServiceProviders: "Find Service Providers",
    serviceProvidersDesc: "Lawyers, Insurance, Mechanics, Wreckers & more",
    getYourBusinessListed: "Get Yuh Business Listed",
    getListedDesc: "Join wi directory of trusted service providers",

    // eMVRC Banner
    emvrcTitle: "New: Electronic Registration (eMVRC)",
    emvrcDesc: "From July 2025, dem nuh gi out physical registration sticker again. All certificate dem electronic now. Yuh need valid email fi all TAJ transaction.",

    // Disclaimer
    importantDisclaimer: "Warning",

    // Offline
    youreOffline: "Yuh offline - App still work!",

    // Footer
    generalInfoOnly: "Dis nuh legal advice.",

    // Police Stop
    whatToDo: "Wha fi do",
    doNot: "Nuh do dis",
    remember: "Remember",

    // Offences
    searchOffences: "Search offences...",
    allCategories: "All Categories",
    fixedPenalty: "Fixed Penalty",
    courtOnly: "Court Only",
    points: "Points",

    // Documents
    addDocument: "Add Document",
    noDocuments: "No documents yet",

    // Hotspots
    reportHotspot: "Report Hotspot",
    noHotspots: "No hotspots reported yet",

    // Speed Cameras
    reportCamera: "Report Camera",
    noCameras: "No cameras reported yet",

    // Forms
    required: "Required",
    optional: "Optional",
    selectParish: "Select Parish",
    enterDetails: "Enter details",

    // Ticket Tracker
    addTicket: "Add Ticket",
    noTickets: "No tickets tracked",
    paid: "Paid",
    unpaid: "Unpaid",

    // Accident Report
    startReport: "Start Report",

    // Settings
    language: "Language",
    english: "English",
    patois: "Patois"
  }
};

// ==================== DATA ====================
// Transform JSON offences data for app use
const transformOffences = () => {
  const offences = {};
  Object.entries(regulationData.offences).forEach(([key, off]) => {
    const fineStr = off.fines?.fixed_penalty
      ? (off.fines.court ? `${off.fines.fixed_penalty} / ${off.fines.court}` : off.fines.fixed_penalty)
      : (off.fines?.court ? `Court Only: ${off.fines.court}` : (off.fines?.first_offence || 'Court Only'));

    const pointsVal = typeof off.demerit_points === 'object'
      ? `${off.demerit_points.minor}-${off.demerit_points.major}`
      : (off.demerit_points || 'No Points');

    offences[key] = {
      title: off.title,
      reg: off.regulation?.replace('Regulations ', '').replace('Regulation ', '').replace('Road Traffic Act ', ''),
      fine: fineStr,
      points: String(pointsVal),
      category: off.category || 'General',
      details: off.description || off.note ||
        (off.includes ? (Array.isArray(off.includes) ? off.includes.join('. ') : String(off.includes)) :
        (off.prohibited ? (Array.isArray(off.prohibited) ? off.prohibited.join('. ') : Object.values(off.prohibited).flat().join('. ')) :
        (off.requirements ? (Array.isArray(off.requirements) ? off.requirements.join('. ') : Object.values(off.requirements).flat().join('. ')) : off.title))),
      rights: off.rights || ['Contest in Parish Court'],
      defenses: off.defenses || [],
      fullData: off
    };
  });
  return offences;
};

const OFFENCES = transformOffences();
const OFFENCE_CATEGORIES = [...new Set(Object.values(OFFENCES).map(o => o.category))].sort();

// Transform enhanced offences for Ask Bot search
const ENHANCED_OFFENCES = enhancedOffencesData.offences.reduce((acc, off) => {
  acc[off.code] = {
    code: off.code,
    title: off.title,
    shortTitle: off.shortTitle,
    category: off.category,
    reg: off.regulation,
    fine: off.fixedPenalty ? `$${off.fixedPenalty.toLocaleString()}` : `Court: $${off.maxFine.toLocaleString()}`,
    maxFine: off.maxFine,
    fixedPenalty: off.fixedPenalty,
    points: off.demeritPoints || 0,
    imprisonment: off.imprisonment,
    courtRequired: off.courtRequired,
    details: off.description,
    keywords: off.keywords || [],
    severity: off.severity,
    tip: off.tip,
    rights: off.rights || [],
    defenses: off.defenses || []
  };
  return acc;
}, {});

// Build keyword mappings from enhanced offences
const ENHANCED_KEYWORD_MAPPINGS = {};
enhancedOffencesData.offences.forEach(off => {
  // Add shortTitle words as keywords
  const titleWords = off.shortTitle.toLowerCase().split(/\s+/);
  titleWords.forEach(word => {
    if (word.length > 3) {
      if (!ENHANCED_KEYWORD_MAPPINGS[word]) ENHANCED_KEYWORD_MAPPINGS[word] = [];
      if (!ENHANCED_KEYWORD_MAPPINGS[word].includes(off.code)) {
        ENHANCED_KEYWORD_MAPPINGS[word].push(off.code);
      }
    }
  });
  // Add explicit keywords
  (off.keywords || []).forEach(kw => {
    const key = kw.toLowerCase();
    if (!ENHANCED_KEYWORD_MAPPINGS[key]) ENHANCED_KEYWORD_MAPPINGS[key] = [];
    if (!ENHANCED_KEYWORD_MAPPINGS[key].includes(off.code)) {
      ENHANCED_KEYWORD_MAPPINGS[key].push(off.code);
    }
  });
});

// ==================== ASK BOT FUZZY SEARCH ====================
// Fuzzy index over the enhanced offences so the Ask Bot can still surface
// relevant results for misspelled, partial, or novel phrasing that the
// hand-written pattern rules don't explicitly cover.
const OFFENCE_SEARCH_INDEX = enhancedOffencesData.offences.map((off) => ({
  code: off.code,
  title: off.title,
  shortTitle: off.shortTitle,
  category: off.category,
  reg: off.regulation,
  fine: off.fixedPenalty
    ? `$${off.fixedPenalty.toLocaleString()}`
    : `Court: $${(off.maxFine || 0).toLocaleString()}`,
  description: off.description || '',
  keywords: (off.keywords || []).join(' '),
}));

const offenceFuse = new Fuse(OFFENCE_SEARCH_INDEX, {
  keys: [
    { name: 'shortTitle', weight: 0.4 },
    { name: 'title', weight: 0.2 },
    { name: 'keywords', weight: 0.4 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 3,
  includeScore: true,
});

// Per-token fuzzy search: fuzzy-match each meaningful word separately and
// aggregate relevance across tokens. This tolerates misspellings word-by-word
// (e.g. "sppeeding" -> Speeding) and rewards offences that match several of
// the query's words, which plain whole-string fuzzy matching handles poorly.
const fuzzyMatchOffences = (tokens) => {
  const scores = {};
  tokens.forEach((token) => {
    offenceFuse.search(token).slice(0, 6).forEach((r) => {
      if (r.score === undefined) return;
      scores[r.item.code] = (scores[r.item.code] || 0) + (1 - r.score);
    });
  });
  return Object.entries(scores)
    .map(([code, score]) => ({ item: OFFENCE_SEARCH_INDEX.find((o) => o.code === code), score }))
    .filter((r) => r.item && r.score >= 0.5)
    .sort((a, b) => b.score - a.score);
};

// Common English stop-words stripped before fuzzy matching so a phrase like
// "what is the fine for" doesn't dilute the meaningful terms.
const BOT_STOP_WORDS = new Set([
  'what', 'whats', 'is', 'the', 'a', 'an', 'for', 'of', 'to', 'do', 'i', 'my',
  'can', 'if', 'when', 'how', 'much', 'are', 'me', 'you', 'get', 'got', 'am',
  'was', 'in', 'on', 'and', 'or', 'it', 'this', 'that', 'about', 'tell', 'fine',
  'penalty', 'cost', 'happens', 'caught', 'law', 'legal', 'jamaica',
]);

// Query mappings for natural language search
const QUERY_MAPPINGS = {
  "speed": ["speeding", "school_zone_speeding", "minimum_speed_violation"],
  "licence": ["no_licence", "learner_unaccompanied", "driving_wrong_class", "driving_while_disqualified"],
  "license": ["no_licence", "learner_unaccompanied", "driving_wrong_class", "driving_while_disqualified"],
  "insurance": ["no_insurance"],
  "fitness": ["no_fitness", "driving_unfit_vehicle"],
  "registration": ["no_registration", "no_number_plates"],
  "seatbelt": ["seatbelt", "child_restraint_violation"],
  "seat belt": ["seatbelt", "child_restraint_violation"],
  "phone": ["mobile_phone"],
  "cell": ["mobile_phone"],
  "drunk": ["dui", "refusing_breath_test"],
  "alcohol": ["dui", "refusing_breath_test"],
  "dui": ["dui", "refusing_breath_test", "drug_driving"],
  "tint": ["tinted_windows"],
  "noise": ["excessive_noise", "improper_horn_use"],
  "red light": ["traffic_light"],
  "signal": ["traffic_light"],
  "helmet": ["helmet_violation", "motorcycle_protective_gear"],
  "motorcycle": ["helmet_violation", "motorcycle_pillion_violation", "motorcycle_protective_gear", "motorcycle_lane_splitting"],
  "parking": ["parking_violations", "bus_stop_parking"],
  "overtaking": ["improper_overtaking", "dangerous_driving"],
  "lane": ["improper_lane_change", "bus_lane_violation", "motorcycle_lane_splitting"],
  "tailgate": ["following_too_close"],
  "follow": ["following_too_close", "following_emergency_vehicle"],
  "learner": ["learner_without_l_plates", "learner_unaccompanied", "learner_night_driving", "learner_on_highway"],
  "l plate": ["learner_without_l_plates"],
  "child": ["child_restraint_violation", "smoking_in_vehicle_with_child"],
  "brake": ["defective_brakes"],
  "tyre": ["defective_tyres"],
  "tire": ["defective_tyres"],
  "light": ["no_sidelights"],
  "emergency": ["emergency_vehicle_obstruction", "following_emergency_vehicle"],
  "taxi": ["no_ppv_licence", "taxi_overcharging", "refusing_fare", "pirating"],
  "dangerous": ["dangerous_driving", "dangerous_goods_violation"],
  "reckless": ["dangerous_driving", "racing_on_road"],
  "accident": ["failing_to_report_accident", "hit_and_run", "careless_driving"],
  "hit and run": ["hit_and_run"],
  "u-turn": ["u_turn_violation"],
  "reverse": ["reversing_unsafely"],
  "pedestrian": ["pedestrian_crossing_violation"],
  "crosswalk": ["pedestrian_crossing_violation"],
  "overload": ["overloading_passengers", "overloading_goods"],
  "weight": ["overloading_goods"]
};

const POLICE_STEPS = [
  { id: 1, title: "Initial Stop", titleJA: "First Stop",
    inst: "Pull over safely to the left. Turn on hazard lights, turn off engine, keep hands visible.",
    rights: ["Police must identify themselves", "Right to know why stopped", "Not required to exit unless asked"],
    audio: "When police signal you to stop, safely pull over. Keep hands visible on steering wheel. Stay calm." },
  { id: 2, title: "Document Presentation", titleJA: "Show Documents",
    inst: "Present Driver's Licence, Insurance, Registration when requested.",
    rights: ["7 days to produce at station (Reg 11)", "Digital copies may be accepted"],
    audio: "Present your documents when asked. You have 7 days to produce at a station if not with you." },
  { id: 3, title: "Understand the Allegation", titleJA: "Wah Dem Seh",
    inst: "Ask clearly what offence you're accused of. Get badge number, note time and location.",
    rights: ["Right to know specific offence", "Right to remain silent", "Do not admit guilt"],
    audio: "Ask for the specific offence and regulation. Note officer badge number, time and location." },
  { id: 4, title: "Fixed Penalty or Court", titleJA: "Pay Or Court",
    inst: "Fixed penalties can be paid to avoid court. You can always contest in court.",
    rights: ["Can always choose court", "28 days to pay or contest", "Get receipt for payment"],
    audio: "You can pay the fixed penalty or contest in court. You have 28 days to decide." },
  { id: 5, title: "Documentation", titleJA: "Write It Down",
    inst: "Document everything. Note names, badge numbers, take photos of location.",
    rights: ["May record interactions respectfully", "Right to copy of ticket", "Request to see evidence"],
    audio: "Document everything about the stop. Take photos. Write down conversation details." }
];

const SPEED_CAMERAS = [
  { id: 1, location: "Mandela Highway (Near Ferry)", parish: "St. Catherine", type: "Fixed", limit: 80 },
  { id: 2, location: "Portmore Toll Plaza", parish: "St. Catherine", type: "Fixed", limit: 50 },
  { id: 3, location: "Highway 2000 - Spanish Town Bypass", parish: "St. Catherine", type: "Fixed", limit: 110 },
  { id: 4, location: "Constant Spring Road", parish: "St. Andrew", type: "Mobile", limit: 50 },
  { id: 5, location: "Washington Boulevard", parish: "St. Andrew", type: "Fixed", limit: 50 },
  { id: 6, location: "Hagley Park Road", parish: "Kingston", type: "Mobile", limit: 50 },
  { id: 7, location: "Marcus Garvey Drive", parish: "Kingston", type: "Fixed", limit: 50 },
  { id: 8, location: "Montego Bay Bypass", parish: "St. James", type: "Fixed", limit: 80 },
  { id: 9, location: "Ocho Rios Bypass", parish: "St. Ann", type: "Fixed", limit: 80 },
  { id: 10, location: "Half Way Tree Road", parish: "St. Andrew", type: "Mobile", limit: 50 }
];

const LAWYERS = [
  // Service providers are loaded from database - no seeded data
];

// Insurance Companies Directory
const INSURANCE_COMPANIES = [
  // Service providers are loaded from database - no seeded data
];

// Vehicle Evaluators & Inspection Centers
const VEHICLE_EVALUATORS = [
  // Service providers are loaded from database - no seeded data
];

// Mechanics & Garages Directory
const MECHANICS = [
  // Service providers are loaded from database - no seeded data
];

// Auto Parts Stores Directory
const PARTS_STORES = [
  // Service providers are loaded from database - no seeded data
];

// Road Signs Data for Road Code
const ROAD_SIGNS = {
  regulatory: [
    { id: 1, name: "Stop Sign", shape: "Octagon", color: "Red/White", meaning: "Come to a complete stop, yield to traffic and pedestrians", icon: "🛑" },
    { id: 2, name: "Give Way/Yield", shape: "Triangle (inverted)", color: "Red/White", meaning: "Slow down, prepare to stop, yield to traffic with right of way", icon: "⚠️" },
    { id: 3, name: "No Entry", shape: "Circle", color: "Red/White", meaning: "Do not enter this road or lane", icon: "⛔" },
    { id: 4, name: "Speed Limit", shape: "Circle", color: "Red border/White", meaning: "Maximum speed allowed on this road", icon: "🚫" },
    { id: 5, name: "No Overtaking", shape: "Circle", color: "Red/White", meaning: "Do not pass other vehicles in this zone", icon: "🚷" },
    { id: 6, name: "No Parking", shape: "Circle", color: "Red/Blue", meaning: "Parking is prohibited in this area", icon: "🅿️" },
    { id: 7, name: "No U-Turn", shape: "Circle", color: "Red/White", meaning: "U-turns are not permitted", icon: "↩️" },
    { id: 8, name: "No Left Turn", shape: "Circle", color: "Red/White", meaning: "Left turns are prohibited", icon: "⬅️" },
    { id: 9, name: "No Right Turn", shape: "Circle", color: "Red/White", meaning: "Right turns are prohibited", icon: "➡️" },
    { id: 10, name: "One Way", shape: "Rectangle", color: "Blue/White", meaning: "Traffic flows in one direction only", icon: "➡️" },
    { id: 11, name: "Keep Left", shape: "Circle", color: "Blue/White", meaning: "Pass obstacle or island on left side", icon: "⬅️" },
    { id: 12, name: "Keep Right", shape: "Circle", color: "Blue/White", meaning: "Pass obstacle or island on right side", icon: "➡️" }
  ],
  warning: [
    { id: 1, name: "Curve Ahead", shape: "Diamond", color: "Yellow/Black", meaning: "Road curves ahead - reduce speed", icon: "↪️" },
    { id: 2, name: "Intersection Ahead", shape: "Diamond", color: "Yellow/Black", meaning: "Road junction or crossing ahead", icon: "✚" },
    { id: 3, name: "Pedestrian Crossing", shape: "Diamond", color: "Yellow/Black", meaning: "Watch for pedestrians crossing", icon: "🚶" },
    { id: 4, name: "School Zone", shape: "Pentagon", color: "Yellow/Black", meaning: "School nearby - watch for children, 30km/h limit", icon: "🏫" },
    { id: 5, name: "Traffic Signal Ahead", shape: "Diamond", color: "Yellow/Black", meaning: "Traffic lights ahead - prepare to stop", icon: "🚦" },
    { id: 6, name: "Roundabout Ahead", shape: "Diamond", color: "Yellow/Black", meaning: "Circular intersection ahead - yield to traffic on right", icon: "🔄" },
    { id: 7, name: "Slippery Road", shape: "Diamond", color: "Yellow/Black", meaning: "Road may be slippery when wet - reduce speed", icon: "⚠️" },
    { id: 8, name: "Steep Hill", shape: "Diamond", color: "Yellow/Black", meaning: "Steep grade ahead - use lower gear", icon: "⛰️" },
    { id: 9, name: "Narrow Bridge", shape: "Diamond", color: "Yellow/Black", meaning: "Bridge narrower than road ahead", icon: "🌉" },
    { id: 10, name: "Road Works", shape: "Diamond", color: "Orange/Black", meaning: "Construction zone - slow down, expect delays", icon: "🚧" },
    { id: 11, name: "Cattle Crossing", shape: "Diamond", color: "Yellow/Black", meaning: "Watch for animals crossing road", icon: "🐄" },
    { id: 12, name: "Falling Rocks", shape: "Diamond", color: "Yellow/Black", meaning: "Risk of rocks or debris on road", icon: "🪨" }
  ],
  informational: [
    { id: 1, name: "Hospital", shape: "Rectangle", color: "Blue/White", meaning: "Hospital or medical facility nearby", icon: "🏥" },
    { id: 2, name: "Petrol Station", shape: "Rectangle", color: "Blue/White", meaning: "Fuel station ahead or nearby", icon: "⛽" },
    { id: 3, name: "Parking Area", shape: "Rectangle", color: "Blue/White", meaning: "Parking available in this area", icon: "🅿️" },
    { id: 4, name: "Bus Stop", shape: "Rectangle", color: "Blue/White", meaning: "Designated bus stop location", icon: "🚌" },
    { id: 5, name: "Rest Area", shape: "Rectangle", color: "Blue/White", meaning: "Rest stop or facility ahead", icon: "🛋️" },
    { id: 6, name: "Airport", shape: "Rectangle", color: "Blue/White", meaning: "Airport ahead or direction to airport", icon: "✈️" },
    { id: 7, name: "Tourist Information", shape: "Rectangle", color: "Brown/White", meaning: "Tourist information center nearby", icon: "ℹ️" },
    { id: 8, name: "Highway Exit", shape: "Rectangle", color: "Green/White", meaning: "Exit point from highway", icon: "🛣️" }
  ],
  pavement: [
    { id: 1, name: "Solid White Line", meaning: "Do not cross - lane boundary or road edge" },
    { id: 2, name: "Broken White Line", meaning: "May cross when safe to change lanes" },
    { id: 3, name: "Solid Yellow Line", meaning: "No overtaking zone - do not cross" },
    { id: 4, name: "Double Yellow Lines", meaning: "No overtaking in either direction" },
    { id: 5, name: "Yellow Hatching", meaning: "Do not enter - keep clear of this area" },
    { id: 6, name: "Zebra Crossing", meaning: "Pedestrian crossing - yield to pedestrians" },
    { id: 7, name: "Stop Line", meaning: "Stop here at traffic light or stop sign" },
    { id: 8, name: "Bus Lane Markings", meaning: "Reserved for buses during specified hours" }
  ]
};

// Learner Driver Requirements
const LEARNER_GUIDE = {
  requirements: {
    age: { minimum: 17, description: "Must be at least 17 years old to apply for a Learner's Permit" },
    documents: [
      "Valid government-issued ID (Passport, National ID)",
      "Proof of address (utility bill, bank statement)",
      "Two passport-sized photographs",
      "Application fee payment",
      "Parental consent if under 18"
    ],
    process: [
      { step: 1, title: "Study the Road Code", description: "Learn traffic signs, rules of the road, and safe driving practices" },
      { step: 2, title: "Apply at TAJ", description: "Visit Tax Administration Jamaica to apply for Provisional Licence" },
      { step: 3, title: "Written Test", description: "Pass the Road Code written examination (minimum 80%)" },
      { step: 4, title: "Eye Test", description: "Pass basic vision test at the testing center" },
      { step: 5, title: "Receive Learner's Permit", description: "Valid for 1 year - must be renewed if driving test not passed" },
      { step: 6, title: "Practice Driving", description: "Drive only with licensed supervisor, display L plates" },
      { step: 7, title: "Book Driving Test", description: "Schedule practical driving test after adequate practice" },
      { step: 8, title: "Practical Test", description: "Demonstrate safe driving skills to examiner" },
      { step: 9, title: "Receive Full Licence", description: "Upon passing, receive your Driver's Licence" }
    ]
  },
  restrictions: [
    { rule: "L Plates", description: "Must display 'L' plates front and rear at all times", regulation: "Reg 155" },
    { rule: "Supervisor Required", description: "Must be accompanied by licensed driver with 3+ years experience", regulation: "Reg 155" },
    { rule: "No Night Driving", description: "Cannot drive between 10pm and 5am", regulation: "Reg 155" },
    { rule: "No Highway/Toll Roads", description: "Learners prohibited from toll roads and highways", regulation: "Reg 155" },
    { rule: "No Passengers", description: "Only instructor/supervisor allowed, no other passengers", regulation: "Reg 155" },
    { rule: "Speed Limit", description: "Maximum 50 km/h even on roads with higher limits", regulation: "Reg 155" }
  ],
  practiceChecklist: [
    { item: "Vehicle controls", skills: ["Steering", "Accelerator", "Brake", "Clutch (manual)", "Indicators", "Lights", "Wipers", "Horn", "Mirrors"] },
    { item: "Basic maneuvers", skills: ["Starting on flat ground", "Starting on a hill", "Stopping smoothly", "Reversing in a straight line", "Parking (parallel, bay)", "Three-point turn", "Emergency stop"] },
    { item: "Road skills", skills: ["Lane positioning", "Changing lanes safely", "Overtaking safely", "Using roundabouts", "Traffic light procedures", "Give way situations", "Pedestrian crossings"] },
    { item: "Defensive driving", skills: ["Mirror checks", "Blind spot awareness", "Safe following distance", "Anticipating hazards", "Night driving awareness", "Wet weather driving"] }
  ],
  testComponents: {
    written: {
      questions: 40,
      passScore: 32,
      time: "45 mins",
      topics: ["Traffic signs", "Road markings", "Rules of the road", "Safe driving practices", "Vehicle regulations", "Demerit points system"]
    },
    practical: {
      duration: "20-30 minutes",
      components: ["Vehicle checks", "Hill start", "Reversing", "Parking", "Road driving", "Emergency stop"],
      passCriteria: "No serious faults, maximum 9 minor faults"
    }
  },
  licenceClasses: [
    { class: "A", vehicles: "Motorcycles", minAge: 17 },
    { class: "B", vehicles: "Private motor cars up to 3,500kg", minAge: 17 },
    { class: "C", vehicles: "Goods vehicles over 3,500kg", minAge: 21 },
    { class: "D", vehicles: "Public Passenger Vehicles (PPV)", minAge: 25 },
    { class: "E", vehicles: "Articulated vehicles", minAge: 21 },
    { class: "F", vehicles: "Agricultural tractors", minAge: 18 }
  ]
};

const DEMERIT = {
  points: [
    { off: "Speeding (minor)", pts: 2 }, { off: "Speeding (major)", pts: 6 },
    { off: "No insurance", pts: 4 }, { off: "No fitness", pts: 3 },
    { off: "Seat belt", pts: 2 }, { off: "Cell phone", pts: 3 },
    { off: "Dangerous driving", pts: "6-10" }, { off: "DUI", pts: 10 }
  ],
  consequences: [
    { range: "10-13 pts (speeding)", action: "Complete speeding dangers training (Reg 164(3))" },
    { range: "14-19 pts (speeding)", action: "Training + Road Code test + driving test (Reg 164(4))" },
    { range: "20+ pts", action: "Licence suspension" },
    { range: "DUI conviction", action: "Impaired driving training + tests (Reg 164(5))" }
  ],
  expiry: "Points expire after 15 months if licence not suspended (Reg 164(1))"
};

const RESOURCES = {
  taj: [
    { name: "TAJ Head Office", address: "PCJ Building, 36 Trafalgar Road, Kingston 10", phone: "888-829-4070", website: "jamaicatax.gov.jm", hours: "Mon-Fri 8:30am-4:00pm", services: "Vehicle registration, licensing, tax payments" },
    { name: "TAJ Constant Spring", address: "Sovereign Centre, 106 Constant Spring Road", phone: "876-929-6265", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services, driver's licence" },
    { name: "TAJ Half Way Tree", address: "2 Ruthven Road, Kingston 10", phone: "876-929-8990", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Cross Roads", address: "18 South Camp Road, Kingston", phone: "876-922-8763", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Spanish Town", address: "2 Oxford Road, Spanish Town", phone: "876-984-2354", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services, driver's licence" },
    { name: "TAJ Portmore", address: "Lot 2, Cookson Pen, Portmore", phone: "876-989-2257", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ May Pen", address: "45 Main Street, May Pen", phone: "876-986-2285", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services, driver's licence" },
    { name: "TAJ Mandeville", address: "5A Caledonia Road, Mandeville", phone: "876-962-2235", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Montego Bay", address: "28 Church Street, Montego Bay", phone: "876-952-2382", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services, driver's licence" },
    { name: "TAJ Ocho Rios", address: "Shop 7, Ocean Village, Ocho Rios", phone: "876-974-2554", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Savanna-la-Mar", address: "Great George Street, Savanna-la-Mar", phone: "876-955-2539", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Black River", address: "2 North Street, Black River", phone: "876-965-2243", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Port Antonio", address: "5 Harbour Street, Port Antonio", phone: "876-993-2644", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Lucea", address: "Parris Street, Lucea", phone: "876-956-2255", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" },
    { name: "TAJ Falmouth", address: "Water Square, Falmouth", phone: "876-954-3224", hours: "Mon-Fri 8:30am-4:00pm", services: "Motor vehicle services" }
  ],
  ita: [
    { name: "ITA Head Office - Kingston", address: "117 Maxfield Avenue, Kingston", phone: "876-926-8011", website: "ita.gov.jm", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, commercial licensing, vehicle inspection" },
    { name: "ITA Half Way Tree", address: "134 Maxfield Avenue Extension", phone: "876-968-3021", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Portmore", address: "Portmore Town Centre, St. Catherine", phone: "876-989-2274", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Spanish Town", address: "Burke Road, Spanish Town", phone: "876-984-2351", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA May Pen", address: "Main Street, May Pen", phone: "876-986-2278", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Mandeville", address: "Caledonia Road, Mandeville", phone: "876-962-2242", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Montego Bay", address: "Albion Road, Montego Bay", phone: "876-952-3094", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Ocho Rios", address: "DaCosta Drive, Ocho Rios", phone: "876-974-2583", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Savanna-la-Mar", address: "Great George Street, Savanna-la-Mar", phone: "876-955-2344", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Lucea", address: "Parris Street, Lucea", phone: "876-956-2366", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Port Antonio", address: "West Street, Port Antonio", phone: "876-993-2545", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Black River", address: "High Street, Black River", phone: "876-965-2357", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" },
    { name: "ITA Falmouth", address: "Market Street, Falmouth", phone: "876-954-3210", hours: "Mon-Fri 8:30am-4:00pm", services: "Fitness testing, vehicle inspection" }
  ],
  police: [
    { name: "Police Emergency", phone: "119", services: "Emergency response", isEmergency: true },
    { name: "Police Non-Emergency / Information", phone: "311", services: "General inquiries, non-urgent matters" },
    { name: "Crime Stop", phone: "311", services: "Anonymous crime reporting" },
    { name: "JCF Headquarters", address: "103 Old Hope Road, Kingston 6", phone: "876-967-1539", website: "jcf.gov.jm", hours: "24 hours", services: "Main headquarters" },
    { name: "Traffic Division HQ", address: "79 Duke Street, Kingston", phone: "876-922-0030", hours: "24 hours", services: "Traffic enforcement, ticket inquiries" },
    { name: "INDECOM", address: "1A Dumfries Road, Kingston 10", phone: "876-968-8875", website: "indecom.gov.jm", hours: "Mon-Fri 8:30am-5:00pm", services: "Police oversight, complaints against security forces" },
    { name: "Public Safety & Traffic Enforcement (PSTEB)", phone: "876-920-0183", services: "Traffic management, school zones" }
  ],
  transport: [
    { name: "Transport Authority of Jamaica", address: "119 Maxfield Avenue, Kingston 10", phone: "876-948-4440", website: "ta.org.jm", hours: "Mon-Fri 8:30am-4:30pm", services: "PPV licensing, route franchises, public transport regulation" },
    { name: "Transport Authority - Montego Bay", address: "Alice Eldemire Drive, Montego Bay", phone: "876-952-3092", hours: "Mon-Fri 8:30am-4:30pm", services: "PPV licensing, route franchises" },
    { name: "Transport Authority - Mandeville", address: "Caledonia Road, Mandeville", phone: "876-962-2240", hours: "Mon-Fri 8:30am-4:30pm", services: "PPV licensing, route franchises" },
    { name: "Transport Authority - May Pen", address: "Main Street, May Pen", phone: "876-986-2150", hours: "Mon-Fri 8:30am-4:30pm", services: "PPV licensing, route franchises" },
    { name: "National Road Operating & Constructing Co. (NROCC)", phone: "876-618-2222", website: "nrocc.com.jm", services: "Highway 2000 toll information, road conditions" },
    { name: "Jamaica Urban Transit Company (JUTC)", address: "6 Retirement Crescent, Kingston", phone: "876-948-5882", website: "jutc.gov.jm", hours: "Mon-Fri 8:00am-5:00pm", services: "Public bus information, lost property" }
  ],
  legal: [
    { name: "Legal Aid Council", address: "61-63 Harbour Street, Kingston", phone: "876-967-1305", website: "legalaidjamaica.org.jm", hours: "Mon-Fri 8:30am-4:00pm", services: "Free legal assistance for eligible persons" },
    { name: "Norman Manley Law School Legal Aid Clinic", address: "UWI, Mona, Kingston 7", phone: "876-970-0055", hours: "Mon-Fri 9:00am-4:00pm", services: "Legal advice, representation for vulnerable persons" },
    { name: "Traffic Court - Kingston", address: "14 East Street, Kingston", phone: "876-922-8200", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "Kingston" },
    { name: "Traffic Court - Spanish Town", address: "Court House, Spanish Town", phone: "876-984-2307", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "St. Catherine" },
    { name: "Traffic Court - Montego Bay", address: "Court House, Church Street, Montego Bay", phone: "876-952-2287", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "St. James" },
    { name: "Traffic Court - May Pen", address: "Court House, Main Street, May Pen", phone: "876-986-2276", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "Clarendon" },
    { name: "Traffic Court - Mandeville", address: "Court House, 1 Ward Avenue, Mandeville", phone: "876-962-2207", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "Manchester" },
    { name: "Traffic Court - Savanna-la-Mar", address: "Court House, Great George Street", phone: "876-955-2527", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "Westmoreland" },
    { name: "Traffic Court - Ocho Rios", address: "Court House, Main Street, Ocho Rios", phone: "876-974-2571", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "St. Ann" },
    { name: "Traffic Court - Port Antonio", address: "Court House, Harbour Street", phone: "876-993-2624", hours: "Mon-Fri 9:00am-4:00pm", services: "Traffic ticket payments, court appearances", parish: "Portland" },
    { name: "Road Safety Unit (RSU)", address: "119 Maxfield Avenue, Kingston", phone: "876-908-7680", website: "rsu.gov.jm", hours: "Mon-Fri 8:30am-4:30pm", services: "Road safety education, awareness campaigns" }
  ],
  customs: [
    { name: "Jamaica Customs Agency - Head Office", address: "Newport East, Kingston", phone: "876-922-5140", website: "jacustoms.gov.jm", hours: "Mon-Fri 8:30am-4:30pm", services: "Vehicle importation, customs clearance" },
    { name: "Jamaica Customs - Montego Bay", address: "Freeport, Montego Bay", phone: "876-952-5701", hours: "Mon-Fri 8:30am-4:30pm", services: "Vehicle importation, customs clearance" },
    { name: "Trade Board Limited", address: "107 Constant Spring Road, Kingston 10", phone: "876-920-4608", website: "tradeboard.gov.jm", hours: "Mon-Fri 8:30am-4:30pm", services: "Import permits, vehicle age verification" }
  ]
};

const PARISHES = ['Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'];

// ==================== FEES SCHEDULE ====================
// All fees marked with * are as at the date indicated. Verify current fees at jamaicatax.gov.jm
const FEES_SCHEDULE = {
  asAtDate: "January 2025",
  source: "Tax Administration Jamaica (TAJ)",
  sourceUrl: "https://www.jamaicatax.gov.jm/rates-and-fees",
  vehicleLicence: {
    title: "Motor Vehicle Licence Fees (Annual)*",
    note: "Based on engine capacity (cc). Can be paid for 6, 12, or 24 months.",
    categories: [
      { type: "Motor Car", ranges: [
        { range: "Up to 1199cc", fee: "$7,688" },
        { range: "1200cc - 2999cc", fee: "$12,600" },
        { range: "3000cc - 3999cc", fee: "$25,200" },
        { range: "4000cc and above", fee: "$43,650" },
        { range: "Electric Vehicle", fee: "$9,240" }
      ]},
      { type: "Motorcycle", ranges: [
        { range: "Up to 125cc", fee: "$3,690" },
        { range: "126cc - 250cc", fee: "$5,250" },
        { range: "251cc and above", fee: "$8,550" }
      ]},
      { type: "Trucks/Goods Vehicles", ranges: [
        { range: "Up to 3,500kg", fee: "$12,600" },
        { range: "3,501kg - 7,500kg", fee: "$16,800" },
        { range: "Over 7,500kg", fee: "$21,600" }
      ]}
    ]
  },
  driversLicence: {
    title: "Driver's Licence Fees*",
    items: [
      { item: "Learner's Permit", fee: "$1,800", validity: "1 year" },
      { item: "Road Code Test", fee: "$1,500", note: "Written examination" },
      { item: "Practical Driving Test", fee: "$2,500", note: "Per attempt" },
      { item: "Driver's Licence (First Issue)", fee: "$4,500", validity: "5 years" },
      { item: "Driver's Licence Renewal", fee: "$4,500", validity: "5 years" },
      { item: "Duplicate Licence (Lost/Stolen)", fee: "$2,500", note: "Requires police report" },
      { item: "International Driving Permit", fee: "$3,500", validity: "1 year" }
    ]
  },
  fitness: {
    title: "Certificate of Fitness Fees*",
    note: "Fee is for inspection service regardless of validity period issued.",
    items: [
      { type: "Private Vehicle (Car, Truck, Motorcycle)", fee: "$4,500" },
      { type: "PPV/Commercial/Trailer/Tractor", fee: "$5,400" }
    ],
    validityPeriods: [
      { condition: "Brand new private vehicle (first registration)", period: "5 years" },
      { condition: "Private vehicle 10 years or younger", period: "3 years" },
      { condition: "Private vehicle over 10 years", period: "1 year" },
      { condition: "New commercial vehicle (first registration)", period: "3 years" },
      { condition: "Commercial vehicle under 10 years", period: "1 year" },
      { condition: "Commercial vehicle over 10 years", period: "1 year" }
    ]
  },
  otherFees: {
    title: "Other Motor Vehicle Fees*",
    items: [
      { item: "Transfer of Ownership", fee: "$2,500", note: "Plus stamp duty 5%" },
      { item: "Replacement Registration Plates", fee: "$3,500", note: "Per pair" },
      { item: "Certificate of Title (New)", fee: "$2,000", note: "Processing ~3 months" },
      { item: "Duplicate Certificate of Title", fee: "$2,500", note: "Requires police report" },
      { item: "Police Letter (for lost items)", fee: "$1,500", note: "From Tax Office" },
      { item: "Change of Particulars", fee: "$1,500", note: "Name, address changes" },
      { item: "Notation of Lien", fee: "$2,000", note: "For financed vehicles" },
      { item: "Cancellation of Lien", fee: "$1,500", note: "When loan paid off" }
    ]
  }
};

// ==================== HOW-TO GUIDES ====================
const HOW_TO_GUIDES = {
  asAtDate: "January 2025",
  importantUpdate: {
    title: "Electronic MVRC (eMVRC) System",
    effectiveDate: "July 21, 2025",
    description: "Physical registration stickers and certificates are NO LONGER issued. All Motor Vehicle Registration Certificates are now electronic (eMVRC).",
    keyChanges: [
      "No more physical stickers or decals",
      "Registration certificates delivered electronically via email",
      "Valid email address REQUIRED for all transactions",
      "Printed copies are acceptable to show police",
      "Access certificates via CGDS portal at cgds.gov.jm",
      "New 24-month licensing option available (in addition to 6 and 12 months)"
    ],
    source: "Jamaica Gleaner, Jamaica Observer - July 2025"
  },
  guides: [
    {
      id: "first-registration",
      title: "First-Time Vehicle Registration",
      subtitle: "Registering a newly purchased or imported vehicle",
      icon: "Car",
      category: "Registration",
      regulation: "Regulations 7-9",
      estimatedTime: "1-2 business days",
      where: "Tax Administration Jamaica (TAJ) Office",
      canDoOnline: false,
      onlineNote: "First-time registration must be done in person",
      steps: [
        { step: 1, title: "Get Certificate of Fitness", description: "Have vehicle inspected at an Island Traffic Authority (ITA) depot. Pay fitness fee ($4,500* for private vehicles) at TAJ first.", documents: ["Valid ID", "Proof of ownership", "Fitness fee receipt"] },
        { step: 2, title: "Get Motor Vehicle Insurance", description: "Obtain at minimum third-party insurance from an authorized insurer.", documents: ["Insurance certificate or cover note"] },
        { step: 3, title: "Gather Required Documents", description: "Collect all necessary paperwork for registration.", documents: ["Certificate of Fitness", "Insurance Certificate", "Valid ID (Passport/Driver's Licence)", "Proof of ownership (Invoice/Bill of Sale)", "Import documents (if imported)", "TRN Card"] },
        { step: 4, title: "Complete MV01 Form", description: "Fill out the Application for Motor Vehicle Transaction form. Available at TAJ or download from jamaicatax.gov.jm", documents: ["Completed MV01 form"] },
        { step: 5, title: "Visit TAJ Office", description: "Submit all documents at the registration section. Officer will verify documents.", tip: "Arrive early to avoid long queues" },
        { step: 6, title: "Pay Registration Fees", description: "Pay the vehicle licence fee based on engine size (see fees schedule). Ensure you have a valid email address for eMVRC.", tip: "You can choose 6, 12, or 24-month licensing" },
        { step: 7, title: "Receive Registration", description: "You will receive: Registration plates, eMVRC sent to your email. Access your certificate at cgds.gov.jm", tip: "Print a copy to keep in your vehicle" }
      ],
      fees: [
        { item: "Fitness Examination", amount: "$4,500*", note: "Private vehicle" },
        { item: "Vehicle Licence", amount: "Varies*", note: "Based on engine size" },
        { item: "Insurance", amount: "Varies", note: "Shop around for best rates" }
      ],
      tips: [
        "Ensure all documents are original, not photocopies",
        "Company vehicles need authorization letter with company stamp",
        "Imported vehicles need import licence and customs clearance",
        "Processing usually completed same day if documents are in order"
      ],
      commonMistakes: [
        "Not having valid fitness certificate",
        "Insurance cover note expired",
        "Missing proof of ownership",
        "No valid email address for eMVRC"
      ]
    },
    {
      id: "licence-renewal",
      title: "Vehicle Licence Renewal",
      subtitle: "Renewing your motor vehicle registration",
      icon: "RefreshCw",
      category: "Registration",
      regulation: "Regulation 10",
      estimatedTime: "15-30 minutes (online) / 1-2 hours (in-person)",
      where: "Online at jamaicatax.gov.jm or TAJ Office",
      canDoOnline: true,
      onlineUrl: "https://www.jamaicatax.gov.jm/online-registration",
      steps: [
        { step: 1, title: "Ensure Fitness is Valid", description: "Your Certificate of Fitness must be current to renew registration.", tip: "Check fitness expiry before attempting renewal" },
        { step: 2, title: "Ensure Insurance is Valid", description: "Motor vehicle insurance must be current.", tip: "Renew insurance before registration if expiring soon" },
        { step: 3, title: "Choose Renewal Method", description: "You can renew online (private vehicles only) or visit a TAJ office in person.", tip: "Online is faster - no queues!" },
        { step: 4, title: "Online Renewal Process", description: "Log in to mytaxes.ads.taj.gov.jm, select Motor Vehicle Registration, enter plate number, verify details, pay with credit/debit card.", tip: "Have your email ready for eMVRC delivery" },
        { step: 5, title: "In-Person Process", description: "Bring ID, current registration info, and valid email. Pay at cashier and receive confirmation.", tip: "Some offices offer express service" },
        { step: 6, title: "Receive eMVRC", description: "Electronic certificate sent to your email. Access at cgds.gov.jm to download and print.", tip: "Print a copy to keep in vehicle" }
      ],
      fees: [
        { item: "Vehicle Licence", amount: "Varies*", note: "Based on engine size and period (6/12/24 months)" }
      ],
      tips: [
        "Renew before expiry to avoid penalties",
        "Online renewal available 24/7",
        "Keep printed copy of eMVRC in vehicle",
        "24-month option saves trips to TAJ"
      ],
      commonMistakes: [
        "Trying to renew with expired fitness",
        "No valid email address",
        "Not having debit/credit card for online payment"
      ]
    },
    {
      id: "transfer-ownership",
      title: "Transfer of Vehicle Ownership",
      subtitle: "Buying or selling a used vehicle",
      icon: "ArrowLeftRight",
      category: "Registration",
      regulation: "Regulation 22",
      estimatedTime: "2-3 hours at Tax Office",
      where: "Tax Administration Jamaica (TAJ) Office",
      canDoOnline: false,
      onlineNote: "Must be done in person at TAJ",
      steps: [
        { step: 1, title: "Seller Prepares Documents", description: "Seller must have Certificate of Title ready and properly endorsed.", documents: ["Certificate of Title (original)", "Valid ID", "TRN Card"] },
        { step: 2, title: "Buyer Prepares Documents", description: "Buyer gathers required documentation for transfer.", documents: ["Valid ID (Passport/Driver's Licence)", "TRN Card", "Proof of address", "Valid email address"] },
        { step: 3, title: "Get Current Fitness", description: "Vehicle should have valid fitness. If not, buyer will need to get new fitness.", tip: "Negotiate who pays for fitness in sale agreement" },
        { step: 4, title: "Obtain Sale Invoice", description: "Create a proper bill of sale showing vehicle details, price, and both parties' information.", documents: ["Bill of Sale/Invoice"] },
        { step: 5, title: "Complete MV01 Form", description: "Both seller (transferor) and buyer (transferee) complete relevant sections.", documents: ["Completed MV01 form"] },
        { step: 6, title: "Visit TAJ Together (or Separately)", description: "Both parties must visit the same TAJ office (not necessarily at the same time) to complete transfer.", tip: "Going together is faster" },
        { step: 7, title: "Pay Transfer Fees", description: "Pay the transfer fee plus stamp duty (5% of sale value or assessed value, whichever is higher).", tip: "Bring sufficient funds" },
        { step: 8, title: "Receive New Title", description: "New Certificate of Title issued to buyer. Processing takes approximately 3 months.", tip: "You'll get a receipt to prove ownership meanwhile" }
      ],
      fees: [
        { item: "Transfer Fee", amount: "$2,500*", note: "Fixed fee" },
        { item: "Stamp Duty", amount: "5%*", note: "Of sale price or assessed value" },
        { item: "New Certificate of Title", amount: "$2,000*", note: "If applicable" }
      ],
      sellerChecklist: [
        "Original Certificate of Title",
        "Valid government ID",
        "TRN card",
        "Bill of sale with your signature"
      ],
      buyerChecklist: [
        "Valid government ID",
        "TRN card",
        "Proof of address",
        "Purchase funds/proof of payment",
        "Valid email for eMVRC"
      ],
      tips: [
        "Verify the title matches the vehicle VIN/chassis number",
        "Check for outstanding liens on the vehicle",
        "Get vehicle inspected before purchase",
        "Don't pay full amount until transfer is initiated at TAJ",
        "Keep copies of all documents"
      ],
      commonMistakes: [
        "Seller not present or not providing authorization",
        "Mismatch between title and vehicle",
        "Outstanding liens not cleared",
        "Insufficient funds for stamp duty"
      ]
    },
    {
      id: "lost-plates",
      title: "Replace Lost/Stolen Registration Plates",
      subtitle: "Getting new plates when plates are lost, stolen, or damaged",
      icon: "AlertTriangle",
      category: "Replacement",
      regulation: "Regulation 19",
      estimatedTime: "1-2 business days",
      where: "Police Station then TAJ Office",
      canDoOnline: false,
      steps: [
        { step: 1, title: "Pay for Police Letter", description: "Visit TAJ office to pay for police letter fee ($1,500*).", tip: "Keep receipt for next step" },
        { step: 2, title: "Report to Police", description: "Go to nearest police station with your TAJ receipt. File a report for lost/stolen plates.", documents: ["TAJ receipt", "Valid ID", "Vehicle information"] },
        { step: 3, title: "Obtain Police Report", description: "Get the official police report/letter documenting the loss.", tip: "Ensure all vehicle details are correct on report" },
        { step: 4, title: "Return to TAJ", description: "Bring police report and ID to TAJ office where plates were originally issued.", documents: ["Police report", "Valid ID", "Proof of ownership"] },
        { step: 5, title: "Pay Replacement Fee", description: "Pay for new registration plates ($3,500* per pair).", tip: "May need to pay for new licence if expired" },
        { step: 6, title: "Receive New Plates", description: "New plates issued. You may receive same number or new number depending on availability.", tip: "Update insurance with new plate number if changed" }
      ],
      fees: [
        { item: "Police Letter Fee", amount: "$1,500*", note: "Paid at TAJ first" },
        { item: "Replacement Plates", amount: "$3,500*", note: "Per pair" }
      ],
      tips: [
        "Report theft immediately to police",
        "Keep a photo/record of your plate numbers",
        "One plate lost still requires reporting",
        "Process must be done at same TAJ office that issued original plates"
      ],
      commonMistakes: [
        "Going to police before paying TAJ fee",
        "Going to wrong TAJ office",
        "Not bringing sufficient documentation"
      ]
    },
    {
      id: "lost-documents",
      title: "Replace Lost Vehicle Documents",
      subtitle: "Replacing lost Certificate of Title, Licence Certificate, or Decal",
      icon: "FileX",
      category: "Replacement",
      regulation: "Regulation 20",
      estimatedTime: "1-3 business days (eMVRC is immediate)",
      where: "TAJ Office",
      canDoOnline: false,
      steps: [
        { step: 1, title: "Pay for Police Letter", description: "Visit TAJ office to pay police letter fee ($1,500*).", tip: "Required for most lost document replacements" },
        { step: 2, title: "File Police Report", description: "Report the lost document(s) at nearest police station.", documents: ["TAJ receipt", "Valid ID"] },
        { step: 3, title: "Complete Application", description: "Fill out MV01 form indicating replacement request.", documents: ["Completed MV01 form", "Police report", "Valid ID", "TRN"] },
        { step: 4, title: "Pay Replacement Fee", description: "Pay applicable fee for the document being replaced.", tip: "Fees vary by document type" },
        { step: 5, title: "Receive Replacement", description: "eMVRC sent to email immediately. Certificate of Title takes approximately 3 months.", tip: "Keep all receipts as proof" }
      ],
      documentTypes: [
        { type: "eMVRC/Licence Certificate", fee: "$2,500*", processingTime: "Immediate (via email)" },
        { type: "Certificate of Title", fee: "$2,500*", processingTime: "~3 months" },
        { type: "Decal/Sticker", fee: "N/A", note: "No longer issued - all electronic now" }
      ],
      tips: [
        "eMVRC can be reprinted anytime from cgds.gov.jm",
        "Physical stickers no longer issued since July 2025",
        "Keep digital copies of all documents",
        "Certificate of Title is most important to safeguard"
      ],
      commonMistakes: [
        "Not realizing eMVRC can be reprinted online",
        "Going to wrong TAJ office for title replacement"
      ]
    },
    {
      id: "learner-permit",
      title: "Getting a Learner's Permit",
      subtitle: "First step to becoming a licensed driver",
      icon: "GraduationCap",
      category: "Driver's Licence",
      regulation: "Regulation 155",
      estimatedTime: "1-2 hours",
      where: "Tax Administration Jamaica (TAJ) Office",
      canDoOnline: false,
      onlineNote: "Test must be taken in person",
      requirements: [
        "Must be at least 17 years old",
        "Valid government-issued ID",
        "Parental consent if under 18",
        "TRN (Tax Registration Number)",
        "Passport-sized photographs (2)"
      ],
      steps: [
        { step: 1, title: "Study the Road Code", description: "Learn traffic signs, rules of the road, and safe driving practices. Study materials available from Island Traffic Authority.", tip: "Take practice tests online" },
        { step: 2, title: "Gather Documents", description: "Collect required ID, photos, and parental consent if under 18.", documents: ["Valid ID", "TRN", "2 passport photos", "Birth certificate (if under 18)", "Parental consent letter (if under 18)"] },
        { step: 3, title: "Visit TAJ Office", description: "Go to any TAJ office that offers driver's licence services.", tip: "Check which offices offer testing services" },
        { step: 4, title: "Pay Fees", description: "Pay learner's permit fee ($1,800*) and road code test fee ($1,500*).", tip: "Total approximately $3,300" },
        { step: 5, title: "Take Written Test", description: "40 multiple choice questions on traffic signs, road rules, and safe driving. Need 32/40 (80%) to pass.", tip: "Take your time and read questions carefully" },
        { step: 6, title: "Eye Test", description: "Basic vision test to ensure you can see adequately for driving.", tip: "Bring glasses if you wear them" },
        { step: 7, title: "Receive Learner's Permit", description: "If you pass, your learner's permit is issued immediately. Valid for 1 year.", tip: "You can begin supervised practice driving" }
      ],
      fees: [
        { item: "Learner's Permit", amount: "$1,800*", note: "Valid 1 year" },
        { item: "Road Code Test", amount: "$1,500*", note: "Per attempt" },
        { item: "Eye Test", amount: "Included", note: "In permit fee" }
      ],
      restrictions: [
        "Must display 'L' plates front and rear",
        "Must be accompanied by licensed driver with 3+ years experience",
        "No driving between 10pm and 5am",
        "No highway or toll road driving",
        "No passengers except supervisor",
        "Maximum speed 50 km/h"
      ],
      tips: [
        "Study the official Road Code thoroughly",
        "Practice with sample tests before the exam",
        "Arrive early on test day",
        "Can retake test if failed (additional fee applies)"
      ],
      commonMistakes: [
        "Not studying road signs thoroughly",
        "Forgetting required documents",
        "Not bringing glasses for eye test"
      ]
    },
    {
      id: "drivers-licence",
      title: "Getting Your Driver's Licence",
      subtitle: "Converting learner's permit to full licence",
      icon: "Award",
      category: "Driver's Licence",
      regulation: "Regulations 155-163",
      estimatedTime: "1-2 hours (test day)",
      where: "Island Traffic Authority (ITA) Depot",
      canDoOnline: false,
      prerequisites: [
        "Valid learner's permit held for at least 6 months",
        "Completed supervised driving practice",
        "Must be at least 17 years old"
      ],
      steps: [
        { step: 1, title: "Practice Driving", description: "Get plenty of supervised driving practice. Minimum 6 months with learner's permit required.", tip: "Consider professional driving lessons" },
        { step: 2, title: "Book Driving Test", description: "Schedule your practical test at an ITA depot. You can apply after 6 months with learner's permit.", tip: "Book early - slots fill up quickly" },
        { step: 3, title: "Prepare for Test", description: "Practice all test maneuvers: hill start, reversing, parking, emergency stop, road driving.", tip: "Know the test route if possible" },
        { step: 4, title: "Test Day Documents", description: "Bring all required documents to the test.", documents: ["Valid learner's permit", "Valid ID", "Receipt for test fee"] },
        { step: 5, title: "Pay Test Fee", description: "Pay practical driving test fee ($2,500*) if not already paid.", tip: "Can pay at TAJ beforehand" },
        { step: 6, title: "Take Practical Test", description: "Demonstrate safe driving skills to examiner. Test lasts 20-30 minutes.", tip: "Stay calm and drive normally" },
        { step: 7, title: "Results & Licence Issue", description: "If passed, go to TAJ to pay for and receive your driver's licence ($4,500*). Valid for 5 years.", tip: "Same day processing available" }
      ],
      fees: [
        { item: "Practical Driving Test", amount: "$2,500*", note: "Per attempt" },
        { item: "Driver's Licence", amount: "$4,500*", note: "Valid 5 years" }
      ],
      testComponents: [
        { component: "Pre-drive Checks", description: "Checking mirrors, adjusting seat, fastening seatbelt" },
        { component: "Hill Start", description: "Starting smoothly on an incline without rolling back" },
        { component: "Reversing", description: "Reversing in a straight line and around corners" },
        { component: "Parallel Parking", description: "Parking between two vehicles" },
        { component: "Three-Point Turn", description: "Turning the vehicle around in a confined space" },
        { component: "Emergency Stop", description: "Stopping quickly and safely when signaled" },
        { component: "Road Driving", description: "Driving in traffic, obeying signs and signals" }
      ],
      tips: [
        "Get professional lessons before test",
        "Practice in the area around the test center",
        "Stay calm and focused during test",
        "If you fail, you can retake after additional practice"
      ],
      commonMistakes: [
        "Not checking mirrors frequently",
        "Rolling back on hill start",
        "Hitting the curb while parking",
        "Driving too slowly or hesitantly"
      ]
    },
    {
      id: "licence-renewal-driver",
      title: "Renew Driver's Licence",
      subtitle: "Renewing your expired or expiring driver's licence",
      icon: "RefreshCw",
      category: "Driver's Licence",
      regulation: "Regulation 155",
      estimatedTime: "15-30 minutes (online) / 1-2 hours (in-person)",
      where: "Online at jamaicatax.gov.jm or TAJ Office",
      canDoOnline: true,
      onlineUrl: "https://www.jamaicatax.gov.jm",
      steps: [
        { step: 1, title: "Check Expiry Date", description: "Find your licence expiry date. Renew before it expires to avoid penalties.", tip: "Can renew up to 6 months before expiry" },
        { step: 2, title: "Choose Renewal Method", description: "Renew online (fastest) or visit TAJ office in person.", tip: "Online works for standard renewals" },
        { step: 3, title: "Online Process", description: "Log into TAJ portal, select Driver's Licence Renewal, enter licence details, pay with card.", tip: "Have licence number and TRN ready" },
        { step: 4, title: "In-Person Process", description: "Bring current licence, ID, and TRN to TAJ office.", documents: ["Current licence", "Valid ID", "TRN card"] },
        { step: 5, title: "Pay Renewal Fee", description: "Pay $4,500* for 5-year renewal.", tip: "Receipt serves as temporary proof until new licence arrives" },
        { step: 6, title: "Receive New Licence", description: "New licence issued or sent to you. Online renewals may require pickup.", tip: "Keep receipt as proof of renewal" }
      ],
      fees: [
        { item: "Driver's Licence Renewal", amount: "$4,500*", note: "Valid 5 years" }
      ],
      tips: [
        "Renew before expiry - driving with expired licence is an offence",
        "Online renewal available 24/7",
        "Keep receipt as proof while waiting for new licence"
      ],
      commonMistakes: [
        "Waiting until after expiry to renew",
        "Not having current licence number for online renewal"
      ]
    },
    {
      id: "fitness-inspection",
      title: "Certificate of Fitness Inspection",
      subtitle: "Getting your vehicle inspected for roadworthiness",
      icon: "CheckCircle",
      category: "Fitness",
      regulation: "Regulations 4-6",
      estimatedTime: "1-3 hours",
      where: "Island Traffic Authority (ITA) Depot",
      canDoOnline: false,
      onlineNote: "Payment can be done online, inspection must be in person",
      steps: [
        { step: 1, title: "Pre-Inspection Check", description: "Check your vehicle meets basic requirements before going to depot.", tip: "See checklist below" },
        { step: 2, title: "Pay Fitness Fee", description: "Pay at TAJ office or online. $4,500* for private vehicles, $5,400* for commercial.", tip: "Keep receipt - required for inspection" },
        { step: 3, title: "Provide Valid Email", description: "Write your email clearly on the receipt - fitness certificate sent electronically.", tip: "Double-check email spelling" },
        { step: 4, title: "Take Vehicle to ITA Depot", description: "Bring vehicle and payment receipt to Island Traffic Authority depot.", documents: ["TAJ receipt", "Valid email on receipt"] },
        { step: 5, title: "Vehicle Inspection", description: "Examiner will check all safety components (see checklist).", tip: "Stay nearby during inspection" },
        { step: 6, title: "Results", description: "If passed, e-Fitness sent to your email. If failed, you'll receive a defect notice.", tip: "Can retest after fixing defects" }
      ],
      fees: [
        { item: "Private Vehicle", amount: "$4,500*", note: "Car, truck, motorcycle" },
        { item: "Commercial/PPV", amount: "$5,400*", note: "Taxi, bus, commercial truck" }
      ],
      inspectionChecklist: [
        { item: "Brakes", checks: ["Brake pads condition", "Brake fluid level", "Handbrake works", "No leaks"] },
        { item: "Lights", checks: ["Headlights (high/low)", "Tail lights", "Brake lights", "Indicators", "Reverse lights", "Number plate lights"] },
        { item: "Tyres", checks: ["Tread depth (min 1.6mm)", "No bulges or cracks", "Proper inflation", "Matching tyres on axle"] },
        { item: "Steering", checks: ["No excessive play", "Steering fluid", "Power steering works"] },
        { item: "Suspension", checks: ["Shock absorbers", "Springs not broken", "No excessive bounce"] },
        { item: "Exhaust", checks: ["No holes or leaks", "Silencer fitted", "Emissions acceptable"] },
        { item: "Body/Structure", checks: ["No excessive rust", "Doors close properly", "Seatbelts work", "Windscreen clear"] },
        { item: "Windows", checks: ["Windscreen not cracked (in driver's view)", "Wipers work", "Window tint within legal limit"] },
        { item: "Other", checks: ["Horn works", "Mirrors intact", "Number plates secure and legible", "Speedometer works"] }
      ],
      validityPeriods: [
        { condition: "Brand new private vehicle", period: "5 years" },
        { condition: "Private vehicle ≤10 years old", period: "3 years" },
        { condition: "Private vehicle >10 years old", period: "1 year" },
        { condition: "New commercial vehicle", period: "3 years" },
        { condition: "Commercial vehicle (any age)", period: "1 year" }
      ],
      tips: [
        "Fix obvious issues before inspection to avoid wasted trip",
        "Clean your vehicle - examiners appreciate it",
        "Check all lights the day before",
        "Top up all fluids",
        "Fee is for service, not validity period - same fee regardless of years issued"
      ],
      commonFailReasons: [
        "Worn brake pads",
        "Burnt out lights",
        "Bald or damaged tyres",
        "Window tint too dark",
        "Excessive exhaust smoke",
        "Windscreen cracks in driver's view",
        "Worn wiper blades"
      ]
    },
    {
      id: "notation-of-lien",
      title: "Notation of Lien (Financed Vehicles)",
      subtitle: "Recording a lien when vehicle is purchased on loan",
      icon: "Lock",
      category: "Registration",
      regulation: "Regulation 21",
      estimatedTime: "1-2 hours",
      where: "Tax Administration Jamaica (TAJ) Office",
      canDoOnline: false,
      description: "When you finance a vehicle (car loan), the lending institution places a lien on the Certificate of Title. This protects the lender's interest until the loan is paid off.",
      steps: [
        { step: 1, title: "Financing Institution Prepares Documents", description: "Your bank or financing company prepares the lien notation documents.", documents: ["Loan agreement", "Vehicle details", "Title information"] },
        { step: 2, title: "Complete MV01 Form", description: "Fill out Application for Motor Vehicle Transaction indicating lien notation.", documents: ["Completed MV01 form"] },
        { step: 3, title: "Visit TAJ Office", description: "Financing company representative or owner with authorization visits TAJ.", documents: ["MV01 form", "Certificate of Title", "Company authorization", "Loan documents"] },
        { step: 4, title: "Pay Notation Fee", description: "Pay the lien notation fee ($2,000*).", tip: "Usually paid by buyer as part of loan process" },
        { step: 5, title: "Lien Recorded", description: "Lien is noted on the Certificate of Title. Title usually held by financing institution.", tip: "You cannot sell vehicle without clearing lien" }
      ],
      fees: [
        { item: "Notation of Lien", amount: "$2,000*", note: "Registration of financing" },
        { item: "Cancellation of Lien", amount: "$1,500*", note: "When loan is paid off" }
      ],
      tips: [
        "Lien is usually arranged by the financing institution",
        "Keep records of all loan payments",
        "When loan is paid off, ensure lien is cancelled",
        "Cannot transfer ownership with active lien"
      ],
      commonMistakes: [
        "Trying to sell vehicle before clearing lien",
        "Not getting lien cancellation letter when loan paid"
      ]
    },
    {
      id: "exported-vehicle",
      title: "Exporting a Vehicle",
      subtitle: "Taking your vehicle out of Jamaica permanently",
      icon: "Ship",
      category: "De-registration",
      regulation: "Regulation 24",
      estimatedTime: "2-3 days (documentation)",
      where: "TAJ, Jamaica Customs Agency",
      canDoOnline: false,
      steps: [
        { step: 1, title: "Clear All Obligations", description: "Ensure all fees, tickets, and liens are cleared.", tip: "Check for outstanding tickets" },
        { step: 2, title: "Obtain Export Documents", description: "Get letter from TAJ confirming registration status.", documents: ["Certificate of Title", "Current registration", "ID"] },
        { step: 3, title: "Clear Customs", description: "Complete export procedures with Jamaica Customs Agency.", tip: "Work with shipping agent for process" },
        { step: 4, title: "Surrender Plates", description: "Return registration plates to TAJ.", tip: "Get receipt for surrendered plates" },
        { step: 5, title: "Ship Vehicle", description: "Complete shipping arrangements through approved shipper.", tip: "Ensure proper insurance coverage" }
      ],
      tips: [
        "Work with an experienced shipping agent",
        "Allow adequate time for paperwork",
        "Check import requirements of destination country"
      ]
    },
    {
      id: "deregister-vehicle",
      title: "De-Register a Vehicle",
      subtitle: "Removing a dead, destroyed, or retired vehicle from the register",
      icon: "Trash2",
      category: "De-registration",
      regulation: "Regulations 15-16",
      estimatedTime: "1-2 hours",
      where: "Tax Administration Jamaica (TAJ) Office",
      canDoOnline: false,
      reasons: [
        "Vehicle destroyed (fire, flood, accident)",
        "Vehicle scrapped/dismantled",
        "Vehicle no longer operational",
        "Vehicle exported"
      ],
      steps: [
        { step: 1, title: "Gather Documents", description: "Collect Certificate of Title and proof of vehicle disposition.", documents: ["Certificate of Title", "Valid ID", "Proof of destruction (if applicable)"] },
        { step: 2, title: "Complete MV01 Form", description: "Fill out form indicating cancellation request and reason.", documents: ["Completed MV01 form"] },
        { step: 3, title: "Surrender Plates", description: "Return registration plates to TAJ.", tip: "Both plates required" },
        { step: 4, title: "Submit Application", description: "Submit all documents to TAJ for cancellation processing.", tip: "Keep receipt for records" },
        { step: 5, title: "Confirmation", description: "Registration cancelled and removed from National Vehicle Register.", tip: "No further licensing fees owed" }
      ],
      fees: [
        { item: "Cancellation/Surrender", amount: "No fee", note: "Free service" }
      ],
      tips: [
        "De-register promptly to stop licensing fee obligations",
        "Keep confirmation receipt",
        "For destroyed vehicles, insurance may require proof of de-registration"
      ]
    }
  ]
};

// ==================== ASK BOT KNOWLEDGE BASE ====================
// Unified fuzzy-searchable index over every data domain in the app — fees,
// how-to guides, glossary terms, licence classes, emergency contacts, demerit
// rules, step-by-step situations, motorcycle rules, and dated news updates —
// so the Quick Answer Bot can answer questions well beyond offences, in
// natural language.

const kbTitleCase = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const kbFlatten = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(kbFlatten).filter(Boolean).join('; ');
  return Object.entries(v).map(([k, x]) => `${kbTitleCase(k)}: ${kbFlatten(x)}`).join('; ');
};

// Dated, sourced news updates relevant to motorists. Curated — update as
// headlines change. Verified 2026-07-10 from JIS, Jamaica Gleaner and
// Jamaica Observer reports.
const KB_NEWS_UPDATES = [
  {
    domain: 'news',
    title: 'Demerit Point System Starts October 1, 2026',
    keywords: 'demerit points start october 2026 zero tolerance new system news latest update when begin operational vaz',
    text: 'The demerit point system under the Road Traffic Act 2018 and Road Traffic Regulations 2022 takes effect October 1 2026 with zero-tolerance enforcement',
    answer: { type: 'demerit', title: 'Demerit Point System Starts October 1, 2026',
      answer: 'The Government has announced that the demerit point system under the Road Traffic Act, 2018 and Road Traffic Regulations, 2022 takes effect on October 1, 2026, with a zero-tolerance approach to traffic violations from that date. Every driver starts at zero points, and points are added for each offence committed.',
      tips: [
        'Settle outstanding tickets by September 30, 2026 — compliant motorists will not be penalised when the system starts',
        '10–13 points (speeding-related): mandatory speeding-dangers training',
        '14–19 points (speeding-related): training plus written and driving re-tests',
        '20+ points: licence suspension (Road Traffic Act Section 32)',
        'Points that caused a suspension are removed once the suspension ends',
        'Check outstanding tickets at trafficticketlookup.gov.jm'
      ],
      regulation: 'Announced June–July 2026 by the Ministry of Transport (JIS, Jamaica Gleaner, Jamaica Observer)' }
  },
  {
    domain: 'news',
    title: 'Pay Outstanding Tickets by September 30, 2026',
    keywords: 'outstanding tickets deadline september 30 2026 pay arrears clear settle unpaid ticket compliance grace period',
    text: 'Motorists owing outstanding traffic fines have until September 30 2026 to clear arrears before the demerit point system starts',
    answer: { type: 'warning', title: 'Deadline: Pay Outstanding Tickets by September 30, 2026',
      answer: 'Motorists with outstanding traffic tickets have until September 30, 2026 to clear their arrears. Those who settle and become compliant on or before that date will not be adversely affected when the demerit point system commences on October 1, 2026. Roughly 1.1 million outstanding tickets are on the books.',
      tips: [
        'Look up your tickets at trafficticketlookup.gov.jm',
        'Pay at any TAJ Tax Office, or online via TAJ eServices',
        'From October 1, 2026 enforcement is zero-tolerance',
        'Unpaid tickets can lead to court summons and warrants'
      ],
      regulation: 'Government announcement, June 2026 (JIS / Jamaica Observer)',
      suggestion: 'tickets' }
  },
  {
    domain: 'news',
    title: 'Traffic Ticket Amnesty — What Is Actually Happening',
    keywords: 'amnesty traffic ticket day forgiveness waive court backlog kingston st andrew july 2026 no more amnesty',
    text: 'There is no new traffic ticket amnesty. Courts held Traffic Ticket Days in July 2026 to clear backlog and the Prime Minister warned there will be no more amnesties',
    answer: { type: 'info', title: 'Traffic Ticket Amnesty — What Is Actually Happening',
      answer: 'There is NO new traffic ticket amnesty, and the Prime Minister has warned there will be no more amnesties. Instead, the courts held "Traffic Ticket Days" (July 8–9, 2026 for Kingston & St Andrew) to clear a backlog of more than 43,000 outstanding tickets, and the Government set a September 30, 2026 deadline to settle outstanding tickets before the demerit point system starts on October 1, 2026.',
      tips: [
        'Traffic Ticket Days let violators be heard by a judge — it is a backlog-clearing exercise, not forgiveness',
        'One motorist settled over $400,000 in tickets at the July 2026 Ticket Day',
        'Settle by September 30, 2026 to start the demerit system with a clean slate',
        'Check what you owe at trafficticketlookup.gov.jm'
      ],
      regulation: 'Court Administration Division / JIS / Jamaica Observer, June–July 2026',
      suggestion: 'tickets' }
  }
];

// Compact versions of the updates for the homepage "Latest Updates" banner
// (newest / most urgent first). Keep in sync with KB_NEWS_UPDATES above.
const HOME_NEWS_UPDATES = [
  {
    tag: 'Deadline',
    title: 'Pay Outstanding Tickets by September 30, 2026',
    text: 'Motorists who settle outstanding traffic tickets by September 30, 2026 will not be penalised when the demerit point system starts. Look up what you owe at trafficticketlookup.gov.jm.',
    page: 'tickets',
    urgent: true,
    sourceUrl: 'https://jamaica-gleaner.com/article/news/20260625/demerit-point-system-take-effect-october-1-drivers-urged-pay-outstanding',
    sourceName: 'Jamaica Gleaner'
  },
  {
    tag: 'New',
    title: 'Demerit Point System Starts October 1, 2026',
    text: 'The demerit point system under the Road Traffic Act takes effect October 1, 2026 with zero-tolerance enforcement. Every driver starts at zero points; 20+ points means suspension.',
    page: 'demerit',
    urgent: false,
    sourceUrl: 'https://jis.gov.jm/demerit-point-system-takes-effect-october-1/',
    sourceName: 'JIS'
  },
  {
    tag: 'Reminder',
    title: 'Electronic Registration (eMVRC)',
    text: 'Since July 2025, physical registration stickers are no longer issued — all certificates are electronic. A valid email address is required for all TAJ transactions.',
    page: 'howto',
    urgent: false,
    sourceUrl: 'https://cgds.gov.jm',
    sourceName: 'CGDS Portal'
  }
];

const KB_FEE_SYNONYMS = {
  "Learner's Permit": 'learner provisional permit new driver',
  'Road Code Test': 'written exam theory test road code',
  'Practical Driving Test': 'road test driving exam practical',
  "Driver's Licence (First Issue)": 'first new get obtain licence license',
  "Driver's Licence Renewal": 'renew renewal expired licence license',
  'Duplicate Licence (Lost/Stolen)': 'lost stolen replace duplicate licence license',
  'International Driving Permit': 'idp drive abroad overseas foreign international',
  'Transfer of Ownership': 'sell buy transfer ownership vehicle stamp duty',
  'Replacement Registration Plates': 'lost replace number plates registration',
  'Certificate of Title (New)': 'title ownership document certificate',
  'Duplicate Certificate of Title': 'lost title replace duplicate',
  'Police Letter (for lost items)': 'police report letter lost documents',
  'Change of Particulars': 'change name address update details particulars',
  'Notation of Lien': 'lien financed loan bank notation',
  'Cancellation of Lien': 'lien remove cancel loan paid'
};

const buildKnowledgeIndex = () => {
  const entries = [...KB_NEWS_UPDATES];
  const feeFooter = `Fees as at ${FEES_SCHEDULE.asAtDate} (${FEES_SCHEDULE.source}).`;

  // ---- Fees: driver's licence items ----
  (FEES_SCHEDULE.driversLicence?.items || []).forEach((f) => {
    entries.push({
      domain: 'fees', title: `${f.item} — Fee`,
      keywords: `fee cost price how much ${KB_FEE_SYNONYMS[f.item] || ''} ${f.item.toLowerCase()}`,
      text: `${f.item} costs ${f.fee}`,
      answer: { type: 'info', title: `${f.item} — Fee`,
        answer: `A ${f.item.toLowerCase()} costs ${f.fee}${f.validity ? ` and is valid for ${f.validity}` : ''}.${f.note ? ` ${f.note}.` : ''} You can pay at any TAJ Tax Office; many services are also available online via TAJ eServices.`,
        tips: [feeFooter, 'See the Fees page for the complete schedule'],
        regulation: 'TAJ Fee Schedule', suggestion: 'fees' }
    });
  });

  // ---- Fees: other motor vehicle items ----
  (FEES_SCHEDULE.otherFees?.items || []).forEach((f) => {
    entries.push({
      domain: 'fees', title: `${f.item} — Fee`,
      keywords: `fee cost price how much ${KB_FEE_SYNONYMS[f.item] || ''} ${f.item.toLowerCase()}`,
      text: `${f.item} costs ${f.fee}`,
      answer: { type: 'info', title: `${f.item} — Fee`,
        answer: `${f.item} costs ${f.fee}.${f.note ? ` Note: ${f.note}.` : ''} This is handled at any TAJ Tax Office.`,
        tips: [feeFooter, 'See the Fees page for the complete schedule'],
        regulation: 'TAJ Fee Schedule', suggestion: 'fees' }
    });
  });

  // ---- Fees: vehicle licence (road tax) aggregate ----
  if (FEES_SCHEDULE.vehicleLicence) {
    const vl = FEES_SCHEDULE.vehicleLicence;
    entries.push({
      domain: 'fees', title: 'Vehicle Licence / Road Tax (Annual)',
      keywords: 'road tax vehicle licence registration renewal annual engine cc motorcycle truck car electric how much cost fee',
      text: 'Annual vehicle licence fees based on engine capacity',
      answer: { type: 'info', title: 'Vehicle Licence / Road Tax (Annual)',
        answer: `Your annual vehicle licence (road tax) depends on engine size. ${vl.note || ''} For example, a motor car up to 1199cc costs ${vl.categories?.[0]?.ranges?.[0]?.fee || '$7,688'} per year.`,
        tips: (vl.categories || []).flatMap((c) => c.ranges.map((r) => `${c.type} — ${r.range}: ${r.fee}`)).concat([feeFooter]),
        regulation: 'TAJ Fee Schedule', suggestion: 'fees' }
    });
  }

  // ---- Fees: fitness certificate aggregate ----
  if (FEES_SCHEDULE.fitness) {
    const fit = FEES_SCHEDULE.fitness;
    entries.push({
      domain: 'fees', title: 'Certificate of Fitness — Fees & Validity',
      keywords: 'fitness certificate inspection examination fee cost validity how long ppv commercial private',
      text: 'Certificate of fitness inspection fees and validity periods',
      answer: { type: 'info', title: 'Certificate of Fitness — Fees & Validity',
        answer: `A fitness inspection costs ${fit.items?.[0]?.fee || '$4,500'} for private vehicles and ${fit.items?.[1]?.fee || '$5,400'} for PPV/commercial vehicles. ${fit.note || ''}`,
        tips: (fit.validityPeriods || []).map((v) => `${v.condition}: valid ${v.period}`).concat([feeFooter]),
        regulation: 'TAJ Fee Schedule', suggestion: 'fees' }
    });
  }

  // ---- How-to guides ----
  (HOW_TO_GUIDES.guides || []).forEach((g) => {
    entries.push({
      domain: 'guide', title: g.title,
      keywords: `how to steps procedure ${g.category?.toLowerCase() || ''} ${g.title.toLowerCase()} ${(g.subtitle || '').toLowerCase()}`,
      text: g.subtitle || g.title,
      answer: { type: 'procedure', title: g.title,
        answer: `${g.subtitle || g.title}. Where: ${g.where || 'see guide'}. Estimated time: ${g.estimatedTime || 'varies'}.${g.canDoOnline ? ' This can be done online.' : (g.onlineNote ? ` ${g.onlineNote}.` : '')}`,
        steps: (g.steps || []).map((s) => `${s.title} — ${s.description}`),
        tips: (g.tips || []).slice(0, 4),
        regulation: g.regulation, suggestion: 'howto' }
    });
  });

  // ---- Glossary definitions ----
  Object.entries(regulationData.glossary || {}).forEach(([term, def]) => {
    const nice = kbTitleCase(term);
    entries.push({
      domain: 'glossary', title: nice,
      keywords: `what is define meaning ${term.replace(/_/g, ' ')}`,
      text: def,
      answer: { type: 'info', title: `What is "${nice}"?`,
        answer: `"${nice}" means: ${def}`,
        tips: ['From the official glossary of the Road Traffic Regulations, 2022'],
        regulation: 'Road Traffic Regulations, 2022 — Glossary' }
    });
  });

  // ---- Licence classes ----
  const classes = regulationData.documents?.drivers_licence?.classes || {};
  const classList = Object.entries(classes).map(([c, d]) => `Class ${c}: ${d}`);
  Object.entries(classes).forEach(([cls, desc]) => {
    entries.push({
      domain: 'document', title: `Class ${cls} Licence`,
      keywords: `class ${cls.toLowerCase()} licence license what is drive ${desc.toLowerCase()}`,
      text: desc,
      answer: { type: 'info', title: `Class ${cls} Driver's Licence`,
        answer: `A Class ${cls} licence covers: ${desc}.`,
        tips: classList,
        regulation: regulationData.documents?.drivers_licence?.regulation || 'Regulations 155-163',
        suggestion: 'learner' }
    });
  });

  // ---- Emergency & agency contacts ----
  const ec = regulationData.emergencyContacts || {};
  entries.push({
    domain: 'contact', title: 'Emergency & Agency Contact Numbers',
    keywords: 'phone number call contact police ambulance fire emergency indecom legal aid ita island traffic authority taj tax hotline report',
    text: 'Police ambulance fire INDECOM legal aid ITA TAJ contact numbers',
    answer: { type: 'info', title: 'Emergency & Agency Contact Numbers',
      answer: `Police emergency: ${ec.police?.emergency || '119'}. Ambulance and fire: ${kbFlatten(ec.ambulance) || '110'}. For police complaints, contact INDECOM at ${ec.indecom?.phone || '876-968-8875'}.`,
      tips: [
        `Police non-emergency: ${ec.police?.non_emergency || '876-922-0030'}`,
        `INDECOM (${ec.indecom?.purpose || 'police complaints'}): ${ec.indecom?.phone || '876-968-8875'}`,
        `Legal Aid Council (${ec.legal_aid?.purpose || 'free legal help'}): ${ec.legal_aid?.phone || '876-926-4081'}`,
        `Island Traffic Authority: ${kbFlatten(ec.island_traffic_authority?.phone) || '876-754-1900'}`,
        `Tax Administration Jamaica: ${kbFlatten(ec.tax_administration?.phone) || '888-829-4357 (888-TAX-HELP)'}`
      ],
      regulation: 'Official contact directory' }
  });

  // ---- Demerit points (canonical tiers + per-offence points) ----
  const dp = regulationData.demeritPoints || {};
  entries.push({
    domain: 'points', title: 'Demerit Points — How Many Per Offence',
    keywords: 'demerit points how many offence speeding insurance suspension threshold expire reset system',
    text: 'Demerit points per offence and consequences',
    answer: { type: 'demerit', title: 'Demerit Points — How Many Per Offence',
      answer: `Points are recorded against your licence for each offence and expire after ${dp.system?.expiry?.period || '15 months'} if your licence is not suspended. The system takes full effect on October 1, 2026. Consequences: 10–13 points (speeding-related) require training; 14–19 require training plus re-tests; 20+ points means licence suspension.`,
      tips: (dp.points_by_offence || []).map((p) => `${p.offence}: ${p.points} points`),
      regulation: dp.regulation || 'Regulation 164', suggestion: 'demerit' }
  });

  // ---- Situations (step-by-step) ----
  const situationPages = { police_stop: 'walkthrough', accident: 'accident', buying_vehicle: 'howto', renewing_documents: 'online-services' };
  Object.entries(regulationData.situations || {}).forEach(([key, s]) => {
    entries.push({
      domain: 'situation', title: s.label || kbTitleCase(key),
      keywords: `what to do ${(s.label || key).toLowerCase()} steps procedure ${key.replace(/_/g, ' ')}`,
      text: (s.steps || []).map((st) => st.title).join('; '),
      answer: { type: 'procedure', title: s.label || kbTitleCase(key),
        answer: `Here is what to do, step by step:`,
        steps: (s.steps || []).map((st) => `${st.title} — ${st.instruction}`),
        tips: (s.steps || []).flatMap((st) => st.rights || []).slice(0, 5),
        regulation: 'Road Traffic Act & Regulations', suggestion: situationPages[key] }
    });
  });

  // ---- Motorcycle rules ----
  const mc = regulationData.motorcycleRules || {};
  entries.push({
    domain: 'motorcycle', title: 'Motorcycle Rules — Helmets, Passengers & Loads',
    keywords: 'motorcycle motorbike bike rules helmet pillion passenger load carry sidecar children',
    text: kbFlatten(mc).slice(0, 400),
    answer: { type: 'info', title: 'Motorcycle Rules — Helmets, Passengers & Loads',
      answer: `Helmets are ${mc.helmet?.requirement?.toLowerCase() || 'mandatory for driver and pillion'} and must meet approved safety standards.`,
      tips: [
        ...(mc.passengers?.requirements || []),
        mc.load_limits ? `Load limits: ${kbFlatten(mc.load_limits.projections)}` : null,
        mc.prohibited ? `Prohibited: ${kbFlatten(mc.prohibited)}` : null
      ].filter(Boolean),
      regulation: `${mc.helmet?.regulation || 'Regulations 221, 274'}; ${mc.passengers?.regulation || 'Regulation 261'}` }
  });

  return entries;
};

const KNOWLEDGE_INDEX = buildKnowledgeIndex();

const knowledgeFuse = new Fuse(KNOWLEDGE_INDEX, {
  keys: [
    { name: 'title', weight: 0.45 },
    { name: 'keywords', weight: 0.4 },
    { name: 'text', weight: 0.15 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 3,
  includeScore: true,
});

// Per-token aggregated fuzzy search over the knowledge base, optionally
// restricted to certain domains. Same scoring approach as the offence search:
// tolerate misspellings word-by-word and reward multi-word overlap.
const fuzzyMatchKnowledge = (tokens, domains = null) => {
  const scores = {};
  tokens.forEach((token) => {
    knowledgeFuse.search(token).slice(0, 8).forEach((r) => {
      if (r.score === undefined) return;
      if (domains && !domains.includes(r.item.domain)) return;
      const key = r.item.title;
      scores[key] = (scores[key] || 0) + (1 - r.score);
    });
  });
  return Object.entries(scores)
    .map(([title, score]) => ({ item: KNOWLEDGE_INDEX.find((e) => e.title === title), score }))
    .filter((r) => r.item && r.score >= 0.5)
    .sort((a, b) => b.score - a.score);
};

// ==================== BACK TO TOP BUTTON ====================
const BackToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 p-3 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      aria-label="Back to top"
    >
      <ChevronUp className="w-6 h-6" />
    </button>
  );
};

// ==================== HEADER ====================
const Header = ({ page, setPage, menuOpen, setMenuOpen, lang, setLang, setSelectedVendor, fatalityTrackerPublic, navigateToOffence, offences, howToGuides, serviceProviders, speedCameras }) => {
  const t = T[lang];
  const { isAuthenticated, isAdmin, profile, profileLoading, signOut, deleteAccount, linkedProviders, hasLinkedBusiness } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close user menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (userMenuOpen) {
        setUserMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [userMenuOpen]);

  const allNavItems = [
    { id: 'home', icon: Home, label: t.home },
    { id: 'askbot', icon: MessageCircle, label: 'Ask Bot' },
    { id: 'offences', icon: Scale, label: t.offencesFines },
    { id: 'calculator', icon: Calculator, label: 'Fine Calculator' },
    { id: 'roadcode', icon: BookOpen, label: 'Road Code' },
    { id: 'online-services', icon: Car, label: 'Registration & Fitness' },
    { id: 'walkthrough', icon: Shield, label: t.policeStopGuide },
    { id: 'howto', icon: Clipboard, label: 'How-To Guides' },
    { id: 'fees', icon: DollarSign, label: 'Fees' },
    { id: 'learner', icon: GraduationCap, label: 'Learner Guide' },
    { id: 'tickets', icon: Receipt, label: t.ticketTracker },
    { id: 'documents', icon: FileText, label: t.myDocuments },
    { id: 'accident', icon: AlertCircle, label: t.accidentReport },
    { id: 'fatality-tracker', icon: AlertTriangle, label: 'Road Safety Awareness' },
    { id: 'hotspots', icon: MapPin, label: t.hotspots },
    { id: 'cameras', icon: Camera, label: t.speedCameras },
    { id: 'traffic', icon: Radio, label: t.traffic },
    { id: 'insurance-lookup', icon: Shield, label: 'Insurance Lookup' },
    { id: 'services', icon: Building2, label: 'Service Providers' },
    { id: 'getlisted', icon: Send, label: 'Get Listed' },
    { id: 'resources', icon: Book, label: t.resources },
    { id: 'demerit', icon: AlertTriangle, label: t.demeritPoints }
  ];

  // Filter nav items - hide fatality tracker from non-admins when not public
  const navItems = allNavItems.filter(item => {
    if (item.id === 'fatality-tracker' && !isAdmin && !fatalityTrackerPublic) {
      return false;
    }
    return true;
  });

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    setPage('home');
  };

  const handleDeleteAccount = async () => {
    if (confirm('Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.')) {
      if (confirm('This is your final warning. Click OK to permanently delete your account and all associated data.')) {
        await deleteAccount();
        setUserMenuOpen(false);
        setPage('home');
        alert('Your account has been deleted. Thank you for using Jamaica Driver\'s Guide.');
      }
    }
  };

  return (
    <header role="banner" className="sticky top-0 z-50 bg-slate-800 backdrop-blur-md text-white shadow-lg border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <a href="#" onClick={(e) => { e.preventDefault(); setPage('home'); }} className="flex items-center gap-2 cursor-pointer" aria-label="Jamaica Driver's Guide - Go to home page">
            <div className="relative flex-shrink-0">
              <Shield className="w-8 h-8 md:w-10 md:h-10 text-yellow-400" aria-hidden="true" />
              <Car className="w-4 h-4 md:w-5 md:h-5 absolute -bottom-1 -right-1 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-xl font-black truncate">{t.appName}</h1>
              <p className="text-xs text-slate-300 hidden sm:block">{t.tagline}</p>
            </div>
          </a>
          <div className="flex items-center gap-2" role="navigation" aria-label="User actions">
            {/* Global Search */}
            <GlobalSearch
              setPage={setPage}
              offences={ENHANCED_OFFENCES}
              howToGuides={HOW_TO_GUIDES.guides}
              serviceProviders={{ lawyers: LAWYERS, insurance: INSURANCE_COMPANIES, mechanics: MECHANICS, evaluators: VEHICLE_EVALUATORS }}
              speedCameras={SPEED_CAMERAS}
              navigateToOffence={navigateToOffence}
              lang={lang}
            />
            {/* User Menu */}
            <div className="relative">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 min-h-[44px]"
                    aria-label="User menu"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    <User className="w-5 h-5" aria-hidden="true" />
                    <span className="hidden md:inline text-sm">{profile?.full_name?.split(' ')[0] || 'Account'}</span>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl py-2 min-w-[200px] text-gray-700 z-50" role="menu" aria-label="User menu options">
                      <div className="px-4 py-2 border-b">
                        <p className="font-bold">{profile?.full_name || profile?.email?.split('@')[0] || 'Account'}</p>
                        <p className="text-xs text-gray-500">{profile?.email || 'Loading...'}</p>
                      </div>
                      <button onClick={() => { setPage('dashboard'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 min-h-[44px]" role="menuitem">
                        <User className="w-4 h-4" aria-hidden="true" />My Dashboard
                      </button>
                      {(profile?.user_type === 'service_provider' || isAdmin) && (
                        <button onClick={() => { setPage('my-analytics'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 min-h-[44px]" role="menuitem">
                          <BarChart3 className="w-4 h-4" aria-hidden="true" />My Analytics
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => { setPage('admin'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 min-h-[44px]" role="menuitem">
                          <UserCog className="w-4 h-4" aria-hidden="true" />Admin Dashboard
                        </button>
                      )}
                      <button onClick={handleSignOut} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-gray-600 min-h-[44px]" role="menuitem">
                        <LogOut className="w-4 h-4" aria-hidden="true" />Sign Out
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button onClick={() => setPage('login')} className="flex items-center gap-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 min-h-[44px]" aria-label="Login to your account">
                  <LogIn className="w-5 h-5" aria-hidden="true" />
                  <span className="hidden md:inline text-sm">Login</span>
                </button>
              )}
            </div>
            {/* Language toggle hidden from the header bar — language can still
                be changed on the Settings page. Restore by uncommenting.
            <button onClick={() => setLang(lang === 'en' ? 'patois' : 'en')} className="hidden md:block p-2 rounded-lg bg-white/10 hover:bg-white/20 min-h-[44px]" aria-label={`Switch to ${lang === 'en' ? 'Patois' : 'English'}`}>
              <Globe className="w-5 h-5" aria-hidden="true" />
            </button> */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 min-h-[44px]"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls="main-navigation"
            >
              {menuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>
      {menuOpen && (
        <nav id="main-navigation" className="bg-slate-800 backdrop-blur-sm border-t border-slate-700 px-4 py-3" aria-label="Main navigation">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" role="menu">
            {navItems.map(item => (
              item.external ? (
                <a key={item.id} href={item.external} target="_blank" rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm hover:bg-slate-700 min-h-[44px]"
                  role="menuitem">
                  <item.icon className="w-4 h-4" aria-hidden="true" /><span className="truncate">{item.label}</span>
                  <ExternalLink className="w-3 h-3 opacity-50" aria-hidden="true" />
                  <span className="sr-only">(opens in new tab)</span>
                </a>
              ) : (
                <button key={item.id} onClick={() => { setPage(item.id); setMenuOpen(false); }}
                  className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm min-h-[44px] ${page === item.id ? 'bg-yellow-500 text-slate-900 font-semibold' : 'hover:bg-slate-700'}`}
                  role="menuitem"
                  aria-current={page === item.id ? 'page' : undefined}>
                  <item.icon className="w-4 h-4" aria-hidden="true" /><span className="truncate">{item.label}</span>
                </button>
              )
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

// ==================== HOME PAGE ====================
const HomePage = ({ setPage, goBack, lang }) => {
  const t = T[lang] || T.en;
  const { isAdmin } = useAuth();
  const [fatalityTrackerVisible, setFatalityTrackerVisible] = useState(null);

  // Rotating "Latest Updates" banner: one item at a time, auto-advances,
  // pauses while the user hovers over it.
  const [newsIdx, setNewsIdx] = useState(0);
  const [newsPaused, setNewsPaused] = useState(false);
  useEffect(() => {
    if (newsPaused || HOME_NEWS_UPDATES.length < 2) return undefined;
    const id = setInterval(() => {
      setNewsIdx((i) => (i + 1) % HOME_NEWS_UPDATES.length);
    }, 8000);
    return () => clearInterval(id);
  }, [newsPaused]);

  // Check if fatality tracker is visible to public
  useEffect(() => {
    const checkVisibility = async () => {
      const { data } = await appSettings.get('fatality_tracker_public');
      // Default to false (hidden) if not set
      setFatalityTrackerVisible(data === 'true' || data === true);
    };
    checkVisibility();
  }, []);

  // Show fatality tracker if: admin OR (setting is true and loaded)
  const showFatalityTracker = isAdmin || fatalityTrackerVisible === true;

  return (
    <div className="space-y-8">
      {/* Hero Carousel */}
      <HeroCarousel setPage={setPage} lang={lang} />

      {/* Top Banner Ad */}
      <TopBanner page="home" />

      {/* Latest Updates Banner: rotates one news item at a time */}
      <section
        className="bg-blue-50/80 backdrop-blur-sm border border-blue-200 rounded-2xl p-5 shadow-sm"
        onMouseEnter={() => setNewsPaused(true)}
        onMouseLeave={() => setNewsPaused(false)}
        aria-live="polite"
      >
        <style>{`@keyframes newsFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
        <div className="flex gap-4 items-start">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-white" />
          </div>
          {(() => {
            const n = HOME_NEWS_UPDATES[newsIdx] || HOME_NEWS_UPDATES[0];
            return (
              <div key={newsIdx} className="flex-1" style={{ animation: 'newsFadeIn 0.4s ease' }}>
                <h4 className="font-bold text-blue-900 text-lg flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${n.urgent ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{n.tag}</span>
                  {n.title}
                </h4>
                <p className="text-blue-800 mt-1 text-sm">{n.text}</p>
                <div className="mt-2 flex items-center gap-4 flex-wrap">
                  <button onClick={() => setPage(n.page)} className="text-blue-600 hover:text-blue-800 font-semibold text-sm flex items-center gap-1">
                    {t.learnMore} <ChevronRight className="w-4 h-4" />
                  </button>
                  {n.sourceUrl && (
                    <a href={n.sourceUrl} target="_blank" rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1">
                      Source: {n.sourceName} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="flex flex-col gap-1.5 pt-1 flex-shrink-0" role="tablist" aria-label="News updates">
            {HOME_NEWS_UPDATES.map((_, i) => (
              <button key={i} onClick={() => setNewsIdx(i)} role="tab" aria-selected={i === newsIdx}
                aria-label={`Update ${i + 1} of ${HOME_NEWS_UPDATES.length}`}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === newsIdx ? 'bg-blue-600' : 'bg-blue-200 hover:bg-blue-400'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reference Section */}
      <section className="bg-slate-800 rounded-3xl p-6 shadow-lg">
        <h3 className="text-base md:text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />{t.quickReference}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: MessageCircle, title: t.askBot, page: 'askbot' },
            { icon: Scale, title: t.offencesFines, page: 'offences' },
            { icon: Car, title: 'Registration & Fitness', page: 'online-services' },
            { icon: Shield, title: 'Insurance Lookup', page: 'insurance-lookup' },
            { icon: Clipboard, title: t.howToGuides, page: 'howto' },
            { icon: GraduationCap, title: t.learnerGuide, page: 'learner' },
            { icon: Book, title: t.resources, page: 'resources' },
            { icon: DollarSign, title: t.feesSchedule, page: 'fees' }
          ].map((item, i) => (
            item.external ? (
              <a key={i} href={item.external} target="_blank" rel="noopener noreferrer"
                className="group p-4 rounded-2xl bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 transition-all hover:-translate-y-1 flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-600/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="font-semibold text-sm md:text-base leading-tight text-white">{item.title}</span>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            ) : (
              <button key={i} onClick={() => setPage(item.page)}
                className="group p-4 rounded-2xl bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 transition-all hover:-translate-y-1 flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-600/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="font-semibold text-sm md:text-base leading-tight text-white">{item.title}</span>
              </button>
            )
          ))}
        </div>
      </section>

      {/* Inline Ad Slot 1 */}
      <InlineBanner1 page="home" />

      {/* Road Safety Awareness Section - Only visible to admins or when enabled */}
      {showFatalityTracker && (
        <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/80">
          <h3 className="text-base md:text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />Road Safety Awareness
            {isAdmin && !fatalityTrackerVisible && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">Admin Only</span>
            )}
          </h3>
          <button onClick={() => setPage('fatality-tracker')}
            className="w-full group p-6 rounded-2xl bg-gradient-to-br from-red-800 to-red-900 hover:from-red-700 hover:to-red-800 border border-red-700 transition-all hover:-translate-y-1 relative overflow-hidden shadow-lg">
            <div className="relative flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 mx-auto md:mx-0">
                <svg className="w-8 h-8 text-white transition-transform duration-300 group-hover:scale-110" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  <circle cx="12" cy="12" r="3" fill="currentColor"/>
                </svg>
              </div>
              <div className="text-center md:text-left flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-1">
                  <span className="font-bold text-base md:text-lg text-white">Road Fatality Map</span>
                  <span className="px-2 py-0.5 bg-yellow-400 text-red-900 text-xs font-bold rounded-full">NEW</span>
                </div>
                <p className="text-white/90 text-xs md:text-sm">Every life lost on our roads is a call to action. This map honours those we've lost while highlighting dangerous areas.</p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3 text-xs">
                  <span className="flex items-center gap-1 bg-white/20 text-white px-2 py-1 rounded-lg">
                    <MapPin className="w-3 h-3" />Interactive Map
                  </span>
                  <span className="flex items-center gap-1 bg-white/20 text-white px-2 py-1 rounded-lg">
                    <BarChart3 className="w-3 h-3" />Statistics
                  </span>
                  <span className="flex items-center gap-1 bg-white/20 text-white px-2 py-1 rounded-lg">
                    <Users className="w-3 h-3" />Community Reports
                  </span>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all hidden md:block" />
            </div>
          </button>
        </section>
      )}

      {/* Utility Services Section */}
      <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/80">
        <h3 className="text-base md:text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
          <Clipboard className="w-5 h-5 text-emerald-600" />{t.yourToolsServices}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Receipt, title: t.ticketTracker, page: 'tickets', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
            { icon: FileText, title: t.myDocuments, page: 'documents', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
            { icon: AlertCircle, title: t.accidentReport, page: 'accident', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' }
          ].map((item, i) => (
            <button key={i} onClick={() => setPage(item.page)}
              className="group p-4 rounded-2xl bg-white/80 backdrop-blur-sm text-gray-800 shadow-sm hover:shadow-lg border border-gray-100 transition-all hover:-translate-y-1 flex items-center gap-3">
              <div className={`w-12 h-12 ${item.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                <item.icon className={`w-6 h-6 ${item.iconColor} transition-transform duration-300 group-hover:scale-110`} />
              </div>
              <span className="font-semibold text-sm md:text-base leading-tight">{item.title}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Inline Ad Slot 2 */}
      <InlineBanner2 page="home" />

      {/* Location & Traffic Section */}
      <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/80">
        <h3 className="text-base md:text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
          <Navigation className="w-5 h-5 text-blue-600" />{t.trafficLocations}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: MapPin, title: t.hotspots, page: 'hotspots', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
            { icon: Radio, title: t.traffic, page: 'traffic', iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
            { icon: Camera, title: t.speedCameras, page: 'cameras', iconBg: 'bg-red-100', iconColor: 'text-red-600' }
          ].map((item, i) => (
            <button key={i} onClick={() => setPage(item.page)}
              className="group p-4 rounded-2xl bg-white/80 backdrop-blur-sm text-gray-800 shadow-sm hover:shadow-lg border border-gray-100 transition-all hover:-translate-y-1 flex items-center gap-3">
              <div className={`w-12 h-12 ${item.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                <item.icon className={`w-6 h-6 ${item.iconColor} transition-transform duration-300 group-hover:scale-110`} />
              </div>
              <span className="font-semibold text-sm md:text-base leading-tight">{item.title}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Service Providers Section */}
      <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/80">
        <h3 className="text-base md:text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-600" />{t.serviceProviders}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={() => setPage('services')}
            className="group p-5 rounded-2xl bg-gradient-to-br from-emerald-800 to-emerald-900 text-white shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Building2 className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-bold text-base md:text-lg block">{t.findServiceProviders}</span>
                <p className="text-emerald-200 text-xs md:text-sm mt-1">{t.serviceProvidersDesc}</p>
              </div>
            </div>
          </button>
          <button onClick={() => setPage('getlisted')}
            className="group p-5 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Send className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-bold text-base md:text-lg block">{t.getYourBusinessListed}</span>
                <p className="text-amber-100 text-xs md:text-sm mt-1">{t.getListedDesc}</p>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Inline Ad Slot 3 */}
      <InlineBanner3 page="home" />

      {/* Bottom Banner Ad */}
      <BottomBanner page="home" />

      {/* Data Correction Report */}
      <DataCorrectionReport />
    </div>
  );
};

// ==================== WALKTHROUGH PAGE ====================
const WalkthroughPage = ({ setPage, goBack, lang }) => {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [audioMode, setAudioMode] = useState(false);
  const t = T[lang];
  const isJA = lang === 'patois';
  const current = POLICE_STEPS[step];

  const playAudio = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9;
      u.onend = () => setPlaying(false);
      window.speechSynthesis.speak(u);
      setPlaying(true);
    }
  };

  const stopAudio = () => { window.speechSynthesis?.cancel(); setPlaying(false); };

  useEffect(() => { if (audioMode) playAudio(current.audio); }, [step, audioMode]);
  useEffect(() => () => stopAudio(), []);

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-red-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-black">{t.policeStopGuide}</h2>
              <p className="text-red-100">{isJA ? "Stay calm. Know yuh rights." : "Stay calm. Know your rights."}</p>
            </div>
          </div>
          <button onClick={() => { if (audioMode) stopAudio(); setAudioMode(!audioMode); }}
            className={`p-3 rounded-xl ${audioMode ? 'bg-white text-red-600' : 'bg-white/20'}`}>
            {audioMode ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
          </button>
        </div>
        {audioMode && (
          <div className="mt-4 p-3 bg-white/10 rounded-xl flex items-center gap-3">
            <Volume2 className="w-5 h-5 animate-pulse" />
            <span className="text-sm">Audio Guide Active - {playing ? 'Playing...' : 'Ready'}</span>
          </div>
        )}
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-md border border-white/60">
        <div className="flex items-center justify-between">
          {POLICE_STEPS.map((s, i) => (
            <button key={s.id} onClick={() => setStep(i)} className={`flex flex-col items-center ${i === step ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-transform ${
                i === step ? 'bg-emerald-600 text-white scale-110' : i < step ? 'bg-emerald-200 text-emerald-700' : 'bg-gray-100'
              }`}>{i < step ? <CheckCircle className="w-5 h-5" /> : s.id}</div>
            </button>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-3">Step {step + 1}: <span className="font-medium text-gray-700">{isJA ? current.titleJA : current.title}</span></p>
      </div>

      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl font-black">{current.id}</span>
            <div>
              <h3 className="text-2xl font-bold">{isJA ? current.titleJA : current.title}</h3>
              <p className="text-emerald-100">{current.inst}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
            <h4 className="font-bold text-emerald-900 mb-3 flex items-center gap-2"><Shield className="w-5 h-5" />{t.yourRights}</h4>
            <ul className="space-y-2">
              {current.rights.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-emerald-800">
                  <Star className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />{r}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex justify-between p-6 bg-gray-50 border-t">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold disabled:opacity-50 bg-gray-200 hover:bg-gray-300 text-gray-700">
            <ChevronLeft className="w-5 h-5" />{t.previous}
          </button>
          <button onClick={() => setStep(Math.min(POLICE_STEPS.length - 1, step + 1))} disabled={step === POLICE_STEPS.length - 1}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold disabled:opacity-50 bg-emerald-600 hover:bg-emerald-700 text-white">
            {t.next}<ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
        <h4 className="font-bold text-red-900 mb-4 flex items-center gap-2"><Phone className="w-5 h-5" />Emergency Contacts</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="tel:119" className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-red-100">
            <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">119</div>
            <div><div className="font-bold">Police Emergency</div><div className="text-sm text-gray-500">JCF</div></div>
          </a>
          <a href="tel:18769688875" className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-red-100">
            <div className="w-10 h-10 bg-amber-600 text-white rounded-full flex items-center justify-center"><Scale className="w-5 h-5" /></div>
            <div><div className="font-bold">INDECOM</div><div className="text-sm text-gray-500">Police Complaints</div></div>
          </a>
          <a href="tel:18769264081" className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-red-100">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center"><Gavel className="w-5 h-5" /></div>
            <div><div className="font-bold">Legal Aid</div><div className="text-sm text-gray-500">Free Help</div></div>
          </a>
        </div>
      </div>
    </div>
  );
};

// ==================== OFFENCES PAGE ====================
const OffencesPage = ({ setPage, goBack, lang }) => {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const detailsRef = useRef(null);
  const t = T[lang];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const filtered = Object.entries(OFFENCES).filter(([k, v]) => {
    const term = search.toLowerCase();
    return v.title.toLowerCase().includes(term) ||
           v.reg.toLowerCase().includes(term) ||
           k.toLowerCase().includes(term);
  });

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9;
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const speakOffence = () => {
    if (!selected || !OFFENCES[selected]) return;
    const off = OFFENCES[selected];
    const text = `${off.title}. Fine: ${off.fine}. Demerit points: ${off.points}. ${off.details}`;
    speak(text);
  };

  const handleSelect = (key) => {
    stopSpeaking();
    setSelected(key);
    // Delay and scroll to details
    setTimeout(() => {
      if (detailsRef.current) {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Scale className="w-8 h-8" />{t.offencesFines}</h2>
        <p className="text-blue-100">Road Traffic Regulations, 2022 (Fifth Schedule)</p>
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search offences..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 text-lg" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          {filtered.map(([key, off]) => (
            <button key={key} onClick={() => handleSelect(key)}
              className={`w-full p-5 rounded-xl text-left transition-all ${selected === key ? 'bg-blue-600 text-white shadow-lg' : 'bg-white hover:bg-blue-50 border-2 border-gray-100'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className={`font-bold text-lg ${selected === key ? 'text-white' : 'text-gray-900'}`}>{off.title}</h4>
                  <p className={`text-sm mt-1 ${selected === key ? 'text-blue-100' : 'text-gray-500'}`}>Reg. {off.reg}</p>
                </div>
                <span className={`px-3 py-1 rounded-lg text-sm font-bold ${selected === key ? 'bg-white/20' : 'bg-red-100 text-red-700'}`}>{off.fine}</span>
              </div>
            </button>
          ))}
        </div>
        <div ref={detailsRef} className="lg:sticky lg:top-24">
          {selected ? (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{OFFENCES[selected].title}</h3>
                  <p className="text-blue-100 mt-1">Regulation {OFFENCES[selected].reg}</p>
                </div>
                <button
                  onClick={() => speaking ? stopSpeaking() : speakOffence()}
                  className={`p-3 rounded-xl ${speaking ? 'bg-white text-blue-600' : 'bg-white/20 hover:bg-white/30'}`}>
                  {speaking ? <Volume2 className="w-6 h-6 animate-pulse" /> : <Volume2 className="w-6 h-6" />}
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="bg-gray-50 rounded-xl p-4 border"><p className="text-gray-700">{OFFENCES[selected].details}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-sm text-red-700 font-bold">{t.fineRange}</p>
                    <p className="text-xl font-black text-red-800">{OFFENCES[selected].fine}</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <p className="text-sm text-amber-700 font-bold">Demerit Points</p>
                    <p className="text-xl font-black text-amber-800">{OFFENCES[selected].points}</p>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-2">{t.yourRights}</h4>
                  <ul className="space-y-2">
                    {OFFENCES[selected].rights.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-blue-800"><CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
              <Scale className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-600">Select an Offence</h4>
            </div>
          )}
        </div>
      </div>

      {/* Floating Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-2xl transition-all transform hover:scale-110"
          aria-label="Back to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

// ==================== DOCUMENTS PAGE ====================
const DocumentsPage = ({ setPage, goBack, lang }) => {
  const [docs, setDocs] = useLocalStorage('ja-docs', { license: null, insurance: null, fitness: null, registration: null });
  const fileRef = useRef(null);
  const [target, setTarget] = useState(null);
  const t = T[lang];

  const handleUpload = (type) => { setTarget(type); fileRef.current.click(); };

  // Documents live in localStorage (device-only, never uploaded), which has a
  // ~5MB total quota. Downscale images before storing so a phone photo
  // doesn't blow the quota; cap PDFs since they can't be compressed here.
  const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 1600;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !target) return;

    try {
      let data;
      if (file.type.startsWith('image/')) {
        data = await compressImage(file);
      } else {
        if (file.size > 2 * 1024 * 1024) {
          alert('PDFs must be under 2MB to store on this device. Try a smaller file or a photo of the document instead.');
          return;
        }
        data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Could not read file'));
          reader.readAsDataURL(file);
        });
      }
      const result = setDocs({ ...docs, [target]: { name: file.name, data, date: new Date().toISOString() } });
      if (result && result.success === false) {
        alert('Not enough space on this device to store the document. Remove another document and try again.');
      }
    } catch (err) {
      alert('Could not store document: ' + err.message);
    }
  };

  // Chrome blocks window.open on data: URLs — convert to a blob URL first.
  const viewDoc = (dataUrl) => {
    try {
      const [meta, b64] = dataUrl.split(',');
      const mime = meta.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blobUrl = URL.createObjectURL(new Blob([arr], { type: mime }));
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      window.open(dataUrl, '_blank');
    }
  };

  const docTypes = [
    { key: 'license', title: "Driver's Licence", icon: User, color: 'from-blue-500 to-blue-600' },
    { key: 'insurance', title: "Insurance", icon: Shield, color: 'from-emerald-500 to-emerald-600' },
    { key: 'fitness', title: "Fitness", icon: CheckCircle, color: 'from-amber-500 to-amber-600' },
    { key: 'registration', title: "Registration", icon: FileText, color: 'from-purple-500 to-purple-600' }
  ];

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><FileText className="w-8 h-8" />{t.myDocuments}</h2>
      </div>
      <input type="file" ref={fileRef} onChange={handleFile} accept="image/*,.pdf" className="hidden" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {docTypes.map(d => (
          <div key={d.key} className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className={`bg-gradient-to-r ${d.color} text-white p-5`}>
              <div className="flex items-center gap-3"><d.icon className="w-8 h-8" /><h3 className="font-bold text-lg">{d.title}</h3></div>
            </div>
            <div className="p-5">
              {docs[d.key] ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4 border">
                    {docs[d.key].data.startsWith('data:image') ? (
                      <img src={docs[d.key].data} alt={d.title} className="w-full h-48 object-contain rounded-lg" />
                    ) : (
                      <div className="h-48 flex items-center justify-center"><FileText className="w-16 h-16 text-gray-400" /></div>
                    )}
                    <p className="text-sm text-gray-500 mt-3 text-center truncate">{docs[d.key].name}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => viewDoc(docs[d.key].data)} className="flex-1 flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-3 rounded-xl">
                      <Eye className="w-4 h-4" />View
                    </button>
                    <button onClick={() => setDocs({...docs, [d.key]: null})} className="bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-3 px-4 rounded-xl">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => handleUpload(d.key)} className="w-full p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 group">
                  <Upload className="w-12 h-12 text-gray-300 group-hover:text-emerald-500 mx-auto mb-3" />
                  <p className="font-semibold text-gray-600">Upload {d.title}</p>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== HOTSPOTS PAGE ====================
const HotspotsPage = ({ setPage, goBack, lang }) => {
  const { user, isAuthenticated } = useAuth();
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newSpot, setNewSpot] = useState({ loc: '', parish: 'Kingston', desc: '', freq: 'occasional', type: 'checkpoint', photos: [] });
  const [filter, setFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [sortBy, setSortBy] = useState('time'); // time, distance
  const [showFlagModal, setShowFlagModal] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const fileInputRef = useRef(null);

  // Fetch hotspots on mount
  useEffect(() => {
    fetchHotspots();
  }, []);

  // Try to get user location
  useEffect(() => {
    getCurrentLocation()
      .then(loc => setUserLocation(loc))
      .catch(() => {}); // Silently fail if no permission
  }, []);

  const fetchHotspots = async () => {
    setLoading(true);
    const { data, error } = await getHotspots();
    if (!error && data) {
      setHotspots(data);
    }
    setLoading(false);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (newSpot.photos.length >= 3) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewSpot(prev => ({...prev, photos: [...prev.photos, { id: Date.now() + Math.random(), data: ev.target.result }]}));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setNewSpot(prev => ({...prev, photos: prev.photos.filter(p => p.id !== photoId)}));
  };

  const handleAdd = async () => {
    if (!newSpot.loc) return;
    setSubmitting(true);

    // Get coordinates for the parish
    const parishCoords = PARISH_COORDS[newSpot.parish];

    const { data, error } = await createHotspot({
      type: newSpot.type,
      location: newSpot.loc,
      parish: newSpot.parish,
      description: newSpot.desc,
      frequency: newSpot.freq,
      latitude: userLocation?.lat || parishCoords?.lat,
      longitude: userLocation?.lng || parishCoords?.lng,
      photo_url: newSpot.photos[0]?.data || null
    }, user?.id);

    if (!error) {
      setShowForm(false);
      setNewSpot({ loc: '', parish: 'Kingston', desc: '', freq: 'occasional', type: 'checkpoint', photos: [] });
      fetchHotspots();
    }
    setSubmitting(false);
  };

  const handleConfirm = async (id) => {
    await confirmHotspot(id, user?.id);
    fetchHotspots();
  };

  const handleFlag = async (id) => {
    if (!isAuthenticated) {
      alert('Please sign in to flag reports');
      return;
    }
    await flagHotspot(id, user?.id, flagReason);
    setShowFlagModal(null);
    setFlagReason('');
    fetchHotspots();
  };

  const handleDelete = async (id) => {
    if (confirm('Remove this hotspot?')) {
      await deleteHotspot(id, user?.id);
      fetchHotspots();
    }
  };

  const useMyLocation = async () => {
    try {
      const loc = await getCurrentLocation();
      setUserLocation(loc);
      // Try to get nearest parish (simplified)
      alert('Location captured! Your report will include GPS coordinates.');
    } catch (err) {
      alert('Could not get location. Please enable location services.');
    }
  };

  // Filter and sort hotspots
  let displayHotspots = filter === 'all' ? hotspots : hotspots.filter(h => h.type === filter);

  if (sortBy === 'distance' && userLocation) {
    displayHotspots = sortByDistance(displayHotspots, userLocation.lat, userLocation.lng);
  }

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><MapPin className="w-8 h-8" />{T[lang].hotspots}</h2>
        <p className="text-amber-100 mt-2">Community-reported police checkpoints and traffic spots</p>
        {userLocation && (
          <p className="text-xs text-amber-200 mt-1 flex items-center gap-1">
            <Navigation className="w-3 h-3" /> Location enabled - showing nearby hotspots
          </p>
        )}
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="hotspots" />

      <button onClick={() => setShowForm(true)} className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-4 rounded-xl shadow-lg">
        <Flag className="w-5 h-5" />Report Hotspot
      </button>

      {/* Sort and Filter Controls */}
      <div className="flex gap-2 items-center justify-between">
        <div className="flex gap-2 overflow-x-auto pb-2 flex-1">
          {[
            { id: 'all', label: 'All', count: hotspots.length },
            { id: 'checkpoint', label: 'Checkpoints', count: hotspots.filter(h => h.type === 'checkpoint').length },
            { id: 'speed_trap', label: 'Speed Traps', count: hotspots.filter(h => h.type === 'speed_trap').length },
            { id: 'accident', label: 'Accidents', count: hotspots.filter(h => h.type === 'accident').length }
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${filter === f.id ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border'}`}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        {userLocation && (
          <button onClick={() => setSortBy(sortBy === 'time' ? 'distance' : 'time')}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${sortBy === 'distance' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
            <Navigation className="w-4 h-4" />
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
          <h3 className="text-xl font-bold mb-4">Report Hotspot</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Type *</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { id: 'checkpoint', label: 'Police Checkpoint', icon: Shield },
                  { id: 'speed_trap', label: 'Speed Trap', icon: Camera },
                  { id: 'accident', label: 'Accident/Hazard', icon: AlertTriangle }
                ].map(t => (
                  <button key={t.id} type="button" onClick={() => setNewSpot({...newSpot, type: t.id})}
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${newSpot.type === t.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                    <t.icon className="w-5 h-5" />
                    <span className="text-xs font-medium text-center">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <input type="text" placeholder="Location / Intersection *" value={newSpot.loc}
                onChange={e => setNewSpot({...newSpot, loc: e.target.value})}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500" />
              <button onClick={useMyLocation} type="button"
                className="px-4 py-3 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200" title="Use my location">
                <Navigation className="w-5 h-5" />
              </button>
            </div>

            <select value={newSpot.parish} onChange={e => setNewSpot({...newSpot, parish: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500">
              {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            <div>
              <label className="text-sm font-medium text-gray-700">Frequency</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['occasional', 'frequent', 'always'].map(f => (
                  <button key={f} type="button" onClick={() => setNewSpot({...newSpot, freq: f})}
                    className={`py-2 rounded-lg border-2 capitalize ${newSpot.freq === f ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <textarea placeholder="Additional details..." value={newSpot.desc}
              onChange={e => setNewSpot({...newSpot, desc: e.target.value})}
              rows={2} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500" />

            {/* Photo Upload */}
            <div>
              <label className="text-sm font-medium text-gray-700">Photo Evidence (optional, max 3)</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {newSpot.photos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img src={photo.data} alt="" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
                    <button onClick={() => removePhoto(photo.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {newSpot.photos.length < 3 && (
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-amber-400 hover:text-amber-500">
                    <Camera className="w-6 h-6" />
                    <span className="text-xs mt-1">Add</span>
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
            </div>

            {!isAuthenticated && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p>Sign in to track your reports and build reputation!</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleAdd} disabled={submitting || !newSpot.loc}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Flag className="w-5 h-5" />}
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
              <button onClick={() => { setShowForm(false); setNewSpot({ loc: '', parish: 'Kingston', desc: '', freq: 'occasional', type: 'checkpoint', photos: [] }); }}
                className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Flag as Incorrect</h3>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Why is this report incorrect? (optional)"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => handleFlag(showFlagModal)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl">
                Flag Report
              </button>
              <button onClick={() => { setShowFlagModal(null); setFlagReason(''); }}
                className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayHotspots.length === 0 ? (
            <div className="col-span-2 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-600">No Hotspots Reported</h4>
              <p className="text-gray-500 mt-2">Be the first to report a checkpoint in your area!</p>
            </div>
          ) : displayHotspots.map(h => (
            <div key={h.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className={`p-1 ${h.type === 'checkpoint' ? 'bg-amber-500' : h.type === 'speed_trap' ? 'bg-red-500' : 'bg-orange-500'}`} />
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-lg">{h.location || h.loc}</h4>
                      {(h.isVerified || h.confirmations >= 3) && <BadgeCheck className="w-5 h-5 text-emerald-500" title="Verified by community" />}
                    </div>
                    <p className="text-emerald-600 font-medium">{h.parish}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">{h.timeAgo || getTimeAgo(h.created_at || h.date)}</p>
                      {h.expiresIn && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          <Timer className="w-3 h-3 inline mr-1" />{h.expiresIn}
                        </span>
                      )}
                    </div>
                    {h.distance !== undefined && h.distance !== null && (
                      <p className="text-xs text-blue-600 mt-1">
                        <Navigation className="w-3 h-3 inline mr-1" />
                        {formatDistance(h.distance)} away
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs font-bold rounded-lg capitalize ${
                      h.type === 'checkpoint' ? 'bg-amber-100 text-amber-700' :
                      h.type === 'speed_trap' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    }`}>{h.type.replace('_', ' ')}</span>
                    <p className="text-xs text-gray-500 mt-1">{h.confirmations || h.reports || 1} report{(h.confirmations || h.reports || 1) > 1 ? 's' : ''}</p>
                  </div>
                </div>

                {(h.description || h.desc) && <p className="text-gray-600 text-sm mt-3">{h.description || h.desc}</p>}

                {(h.photo_url || h.photos?.length > 0) && (
                  <div className="flex gap-2 mt-3">
                    {h.photo_url ? (
                      <img src={h.photo_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    ) : h.photos?.map((p, i) => (
                      <img key={i} src={p.data} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    ))}
                  </div>
                )}

                {/* Reporter info */}
                {h.reporter && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                    <User className="w-3 h-3" />
                    <span>{h.reporter.full_name || 'Anonymous'}</span>
                    {h.reporter.reputation_score >= 20 && (
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium bg-${getReputationLevel(h.reporter.reputation_score).color}-100 text-${getReputationLevel(h.reporter.reputation_score).color}-700`}>
                        {getReputationLevel(h.reporter.reputation_score).badge} {getReputationLevel(h.reporter.reputation_score).level}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button onClick={() => handleConfirm(h.id)}
                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 py-2 rounded-lg text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />Still Here
                  </button>
                  <button onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((h.location || h.loc) + ', ' + h.parish + ', Jamaica')}`, '_blank')}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 py-2 rounded-lg text-sm font-medium">
                    <Navigation className="w-4 h-4" />Maps
                  </button>
                  <button onClick={() => setShowFlagModal(h.id)}
                    className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg" title="Flag as incorrect">
                    <Flag className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900 flex items-center gap-2"><Info className="w-5 h-5" />Community Guidelines</h4>
        <ul className="mt-3 space-y-2 text-blue-800 text-sm">
          <li>• Report what you see - help fellow motorists</li>
          <li>• Add photos when safe to do so</li>
          <li>• Confirm reports you pass to help verify and extend expiry</li>
          <li>• Reports with 3+ confirmations are marked verified</li>
          <li>• Hotspots auto-expire after a few hours unless confirmed</li>
          <li>• Do not use phone while driving - pull over safely first</li>
        </ul>
      </div>

      {/* Bottom Banner Ad */}
      <BottomBanner page="hotspots" />
    </div>
  );
};

// ==================== SPEED CAMERAS PAGE ====================
const SpeedCamerasPage = ({ setPage, goBack, lang }) => {
  const { user, isAuthenticated } = useAuth();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newCamera, setNewCamera] = useState({ loc: '', parish: 'Kingston', type: 'Fixed', limit: 50, desc: '', photos: [] });
  const [view, setView] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [sortBy, setSortBy] = useState('confirmations');
  const [showFlagModal, setShowFlagModal] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const fileInputRef = useRef(null);

  // Fetch cameras on mount
  useEffect(() => {
    fetchCameras();
  }, []);

  // Try to get user location
  useEffect(() => {
    getCurrentLocation()
      .then(loc => setUserLocation(loc))
      .catch(() => {});
  }, []);

  const fetchCameras = async () => {
    setLoading(true);
    const { data, error } = await getSpeedCameras();
    if (!error && data) {
      setCameras(data);
    }
    setLoading(false);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (newCamera.photos.length >= 3) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewCamera(prev => ({...prev, photos: [...prev.photos, { id: Date.now() + Math.random(), data: ev.target.result }]}));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setNewCamera(prev => ({...prev, photos: prev.photos.filter(p => p.id !== photoId)}));
  };

  const handleAdd = async () => {
    if (!newCamera.loc) return;
    setSubmitting(true);

    const parishCoords = PARISH_COORDS[newCamera.parish];

    const { data, error } = await createSpeedCamera({
      location: newCamera.loc,
      parish: newCamera.parish,
      type: newCamera.type.toLowerCase(),
      speed_limit: newCamera.limit,
      direction: newCamera.desc,
      latitude: userLocation?.lat || parishCoords?.lat,
      longitude: userLocation?.lng || parishCoords?.lng,
      photo_url: newCamera.photos[0]?.data || null
    }, user?.id);

    if (!error) {
      setShowForm(false);
      setNewCamera({ loc: '', parish: 'Kingston', type: 'Fixed', limit: 50, desc: '', photos: [] });
      fetchCameras();
    }
    setSubmitting(false);
  };

  const handleConfirm = async (id) => {
    await confirmSpeedCamera(id, user?.id);
    fetchCameras();
  };

  const handleFlag = async (id) => {
    if (!isAuthenticated) {
      alert('Please sign in to flag reports');
      return;
    }
    await flagSpeedCamera(id, user?.id, flagReason);
    setShowFlagModal(null);
    setFlagReason('');
    fetchCameras();
  };

  const handleDelete = async (id) => {
    if (confirm('Remove this speed camera report?')) {
      await deleteSpeedCamera(id);
      fetchCameras();
    }
  };

  const useMyLocation = async () => {
    try {
      const loc = await getCurrentLocation();
      setUserLocation(loc);
      alert('Location captured! Your report will include GPS coordinates.');
    } catch (err) {
      alert('Could not get location. Please enable location services.');
    }
  };

  // Filter cameras
  const officialCameras = cameras.filter(c => c.is_official || c.source === 'official');
  const communityCameras = cameras.filter(c => !c.is_official && c.source !== 'official');

  let displayCameras = view === 'community' ? communityCameras : cameras;

  // Search filter
  if (search) {
    displayCameras = displayCameras.filter(c =>
      (c.parish || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.location || c.loc || '').toLowerCase().includes(search.toLowerCase())
    );
  }

  // Sort by distance if location available
  if (sortBy === 'distance' && userLocation) {
    displayCameras = sortByDistance(displayCameras, userLocation.lat, userLocation.lng);
  }

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Camera className="w-8 h-8" />{T[lang].speedCameras}</h2>
        <p className="text-red-200 mt-2">Speed camera locations across Jamaica</p>
        {userLocation && (
          <p className="text-xs text-red-200 mt-1 flex items-center gap-1">
            <Navigation className="w-3 h-3" /> Location enabled - can sort by distance
          </p>
        )}
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="cameras" />

      <button onClick={() => setShowForm(true)} className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-4 rounded-xl shadow-lg">
        <Plus className="w-5 h-5" />Report New Speed Camera
      </button>

      {/* View tabs */}
      <div className="flex gap-2">
        <button onClick={() => setView('all')}
          className={`flex-1 py-2 rounded-xl font-semibold ${view === 'all' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}>
          All Cameras ({cameras.length})
        </button>
        <button onClick={() => setView('community')}
          className={`flex-1 py-2 rounded-xl font-semibold ${view === 'community' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}>
          Community ({communityCameras.length})
        </button>
        {userLocation && (
          <button onClick={() => setSortBy(sortBy === 'distance' ? 'confirmations' : 'distance')}
            className={`px-3 py-2 rounded-xl ${sortBy === 'distance' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
            title="Sort by distance">
            <Navigation className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search by parish or location..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-red-500" />
      </div>

      {showForm && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
          <h3 className="text-xl font-bold mb-4">Report Speed Camera Location</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input type="text" placeholder="Location / Road Name *" value={newCamera.loc}
                onChange={e => setNewCamera({...newCamera, loc: e.target.value})}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-500" />
              <button onClick={useMyLocation} type="button"
                className="px-4 py-3 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200" title="Use my location">
                <Navigation className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <select value={newCamera.parish} onChange={e => setNewCamera({...newCamera, parish: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-500">
                {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={newCamera.type} onChange={e => setNewCamera({...newCamera, type: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-500">
                <option value="Fixed">Fixed Camera</option>
                <option value="Mobile">Mobile Unit</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Speed Limit at Location</label>
              <div className="flex gap-2 mt-2">
                {[30, 50, 80, 110].map(limit => (
                  <button key={limit} type="button" onClick={() => setNewCamera({...newCamera, limit})}
                    className={`flex-1 py-2 rounded-lg border-2 font-bold ${newCamera.limit === limit ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200'}`}>
                    {limit}
                  </button>
                ))}
              </div>
            </div>

            <textarea placeholder="Additional details (direction, landmarks, etc.)" value={newCamera.desc}
              onChange={e => setNewCamera({...newCamera, desc: e.target.value})}
              rows={2} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-500" />

            {/* Photo Upload */}
            <div>
              <label className="text-sm font-medium text-gray-700">Photo Evidence (helps verify, max 3)</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {newCamera.photos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img src={photo.data} alt="" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
                    <button onClick={() => removePhoto(photo.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {newCamera.photos.length < 3 && (
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-red-400 hover:text-red-500">
                    <Camera className="w-6 h-6" />
                    <span className="text-xs mt-1">Add</span>
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
            </div>

            {!isAuthenticated && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <p>Sign in to track your reports and build reputation!</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleAdd} disabled={submitting || !newCamera.loc}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
              <button onClick={() => { setShowForm(false); setNewCamera({ loc: '', parish: 'Kingston', type: 'Fixed', limit: 50, desc: '', photos: [] }); }}
                className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Flag as Incorrect</h3>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Why is this camera report incorrect? (optional)"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => handleFlag(showFlagModal)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl">
                Flag Report
              </button>
              <button onClick={() => { setShowFlagModal(null); setFlagReason(''); }}
                className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-red-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayCameras.length === 0 ? (
            <div className="col-span-2 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
              <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-600">No Cameras Found</h4>
              <p className="text-gray-500 mt-2">Try a different search or report a new location</p>
            </div>
          ) : displayCameras.map(cam => (
            <div key={cam.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className={`p-1 ${(cam.type || '').toLowerCase() === 'fixed' ? 'bg-red-500' : 'bg-amber-500'}`} />
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-lg">{cam.location || cam.loc}</h4>
                      {(cam.is_official || cam.source === 'official') && <BadgeCheck className="w-5 h-5 text-blue-500" title="Official" />}
                      {!cam.is_official && cam.isVerified && <BadgeCheck className="w-5 h-5 text-emerald-500" title="Verified by community" />}
                    </div>
                    <p className="text-gray-500">{cam.parish}</p>
                    {cam.distance !== undefined && cam.distance !== null && (
                      <p className="text-xs text-blue-600 mt-1">
                        <Navigation className="w-3 h-3 inline mr-1" />
                        {formatDistance(cam.distance)} away
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold capitalize ${(cam.type || '').toLowerCase() === 'fixed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {cam.type || 'Fixed'}
                    </span>
                    {!cam.is_official && (
                      <p className="text-xs text-gray-500 mt-1">{cam.confirmations || 1} report{(cam.confirmations || 1) > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>

                {(cam.direction || cam.desc) && <p className="text-gray-600 text-sm mt-3">{cam.direction || cam.desc}</p>}

                {(cam.photo_url || cam.photos?.length > 0) && (
                  <div className="flex gap-2 mt-3">
                    {cam.photo_url ? (
                      <img src={cam.photo_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    ) : cam.photos?.map((p, i) => (
                      <img key={i} src={p.data} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    ))}
                  </div>
                )}

                {/* Reporter info */}
                {cam.reporter && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                    <User className="w-3 h-3" />
                    <span>{cam.reporter.full_name || 'Anonymous'}</span>
                    {cam.reporter.reputation_score >= 20 && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                        {getReputationLevel(cam.reporter.reputation_score).badge} {getReputationLevel(cam.reporter.reputation_score).level}
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-black text-red-600">{cam.speed_limit || cam.limit}</p>
                  <p className="text-xs text-gray-500">km/h limit</p>
                </div>

                <div className="flex gap-2 mt-4">
                  {!cam.is_official && (
                    <button onClick={() => handleConfirm(cam.id)}
                      className="flex-1 flex items-center justify-center gap-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 py-2 rounded-lg text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />Confirm
                    </button>
                  )}
                  <button onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((cam.location || cam.loc) + ', ' + cam.parish + ', Jamaica')}`, '_blank')}
                    className={`${!cam.is_official ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 py-2 rounded-lg text-sm font-medium`}>
                    <Navigation className="w-4 h-4" />View in Maps
                  </button>
                  {!cam.is_official && (
                    <button onClick={() => setShowFlagModal(cam.id)}
                      className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg" title="Flag as incorrect">
                      <Flag className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
        <h4 className="font-bold text-amber-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Speed Camera Facts</h4>
        <ul className="mt-3 space-y-2 text-amber-800 text-sm">
          <li>• Electronic Enforcement Devices (Reg 237-246) can issue tickets automatically</li>
          <li>• Vehicle owner is responsible unless driver is identified (Reg 241)</li>
          <li>• You can request device calibration records as a defense</li>
          <li>• Fixed penalty: $5,000-$10,000, Court: up to $30,000</li>
        </ul>
      </div>

      {/* Bottom Banner Ad */}
      <BottomBanner page="cameras" />
    </div>
  );
};

// ==================== ACCIDENT REPORT PAGE ====================
const PhotoUploader = ({ photos, setPhotos, label, maxPhotos = 5 }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (photos.length >= maxPhotos) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, { id: Date.now() + Math.random(), data: ev.target.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (id) => setPhotos(photos.filter(p => p.id !== id));

  return (
    <div className="space-y-3">
      <label className="font-medium text-gray-700">{label}</label>
      <div className="flex flex-wrap gap-3">
        {photos.map(photo => (
          <div key={photo.id} className="relative group">
            <img src={photo.data} alt="" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
            <button onClick={() => removePhoto(photo.id)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <button onClick={() => fileInputRef.current?.click()}
            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
            <Camera className="w-6 h-6" />
            <span className="text-xs mt-1">Add</span>
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
      <p className="text-xs text-gray-500">{photos.length}/{maxPhotos} photos</p>
    </div>
  );
};

// Vehicle Form Component - defined outside to prevent recreation on each render
const VehicleFormComponent = ({ vehicle, showRemove, onUpdate, onRemove, vehicleIndex }) => (
  <div className="space-y-6 bg-gray-50 rounded-xl p-5">
    <div className="flex items-center justify-between">
      <h4 className="font-bold text-lg flex items-center gap-2">
        <Car className="w-5 h-5" />
        {vehicle.isMyVehicle ? 'Your Vehicle & Info' : `Other Driver ${vehicleIndex + 1}`}
      </h4>
      {showRemove && (
        <button onClick={() => onRemove(vehicle.id)} className="text-red-500 hover:text-red-700">
          <Trash2 className="w-5 h-5" />
        </button>
      )}
    </div>

    {/* Driver Information */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><User className="w-4 h-4" />Driver Information</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="text" placeholder="Driver Full Name *" value={vehicle.driverName}
          onChange={e => onUpdate(vehicle.id, 'driverName', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="tel" placeholder="Phone Number *" value={vehicle.driverPhone}
          onChange={e => onUpdate(vehicle.id, 'driverPhone', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="email" placeholder="Email Address" value={vehicle.driverEmail}
          onChange={e => onUpdate(vehicle.id, 'driverEmail', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Address" value={vehicle.driverAddress}
          onChange={e => onUpdate(vehicle.id, 'driverAddress', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
      </div>
    </div>

    {/* License Information */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><FileText className="w-4 h-4" />Driver's Licence</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="text" placeholder="Licence Number *" value={vehicle.licenseNumber}
          onChange={e => onUpdate(vehicle.id, 'licenseNumber', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="date" placeholder="Licence Expiry" value={vehicle.licenseExpiry}
          onChange={e => onUpdate(vehicle.id, 'licenseExpiry', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
      </div>
      <PhotoUploader
        photos={vehicle.licensePhotos}
        setPhotos={(photos) => onUpdate(vehicle.id, 'licensePhotos', photos)}
        label="Photo of Driver's Licence (front & back)"
        maxPhotos={2}
      />
    </div>

    {/* Vehicle Information */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><Car className="w-4 h-4" />Vehicle Details</h5>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <input type="text" placeholder="Make (e.g., Toyota)" value={vehicle.vehicleMake}
          onChange={e => onUpdate(vehicle.id, 'vehicleMake', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Model (e.g., Corolla)" value={vehicle.vehicleModel}
          onChange={e => onUpdate(vehicle.id, 'vehicleModel', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Year" value={vehicle.vehicleYear}
          onChange={e => onUpdate(vehicle.id, 'vehicleYear', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Color" value={vehicle.vehicleColor}
          onChange={e => onUpdate(vehicle.id, 'vehicleColor', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Licence Plate *" value={vehicle.vehiclePlate}
          onChange={e => onUpdate(vehicle.id, 'vehiclePlate', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="VIN (optional)" value={vehicle.vehicleVin}
          onChange={e => onUpdate(vehicle.id, 'vehicleVin', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
      </div>
    </div>

    {/* Insurance Information */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><Shield className="w-4 h-4" />Insurance Information</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="text" placeholder="Insurance Company *" value={vehicle.insuranceCompany}
          onChange={e => onUpdate(vehicle.id, 'insuranceCompany', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="text" placeholder="Policy Number *" value={vehicle.policyNumber}
          onChange={e => onUpdate(vehicle.id, 'policyNumber', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="date" placeholder="Policy Expiry" value={vehicle.insuranceExpiry}
          onChange={e => onUpdate(vehicle.id, 'insuranceExpiry', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
        <input type="tel" placeholder="Insurance Phone" value={vehicle.insurancePhone}
          onChange={e => onUpdate(vehicle.id, 'insurancePhone', e.target.value)}
          className="px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
      </div>
      <PhotoUploader
        photos={vehicle.insurancePhotos}
        setPhotos={(photos) => onUpdate(vehicle.id, 'insurancePhotos', photos)}
        label="Photo of Insurance Certificate"
        maxPhotos={2}
      />
    </div>

    {/* Vehicle Damage */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Vehicle Damage</h5>
      <textarea placeholder="Describe the damage to this vehicle..." value={vehicle.damageDescription}
        onChange={e => onUpdate(vehicle.id, 'damageDescription', e.target.value)}
        rows={3} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
      <PhotoUploader
        photos={vehicle.damagePhotos}
        setPhotos={(photos) => onUpdate(vehicle.id, 'damagePhotos', photos)}
        label="Photos of Vehicle Damage"
        maxPhotos={8}
      />
    </div>

    {/* Passengers */}
    <div className="bg-white rounded-lg p-4 space-y-4">
      <h5 className="font-semibold text-gray-700 flex items-center gap-2"><User className="w-4 h-4" />Passengers in Vehicle</h5>
      <textarea placeholder="List any passengers (names and contact info if available)..." value={vehicle.passengers}
        onChange={e => onUpdate(vehicle.id, 'passengers', e.target.value)}
        rows={2} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-400 focus:outline-none" />
    </div>
  </div>
);

const emptyVehicle = () => ({
  id: Date.now(),
  isMyVehicle: false,
  // Driver info
  driverName: '', driverPhone: '', driverEmail: '', driverAddress: '',
  licenseNumber: '', licenseExpiry: '', licensePhotos: [],
  // Vehicle info
  vehicleMake: '', vehicleModel: '', vehicleYear: '', vehicleColor: '', vehiclePlate: '', vehicleVin: '',
  // Insurance info
  insuranceCompany: '', policyNumber: '', insuranceExpiry: '', insurancePhone: '',
  insurancePhotos: [],
  // Damage
  damageDescription: '', damagePhotos: [],
  // Witnesses from this vehicle
  passengers: ''
});

const AccidentReportPage = ({ setPage, goBack, lang }) => {
  const [reports, setReports] = useLocalStorage('ja-accidents', []);
  const [mode, setMode] = useState('list'); // list, new, view
  const [step, setStep] = useState(1);
  const [viewReport, setViewReport] = useState(null);

  // Form state
  const [accidentInfo, setAccidentInfo] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '', parish: 'Kingston', description: '',
    weatherConditions: '', roadConditions: '', trafficConditions: '',
    policeReportNumber: '', policeOfficerName: '', policeStation: '', policeCalled: false,
    injuries: '', ambulanceCalled: false, hospitalName: '',
    scenePhotos: [], witnesses: []
  });

  const [vehicleCount, setVehicleCount] = useState(2);
  const [vehicles, setVehicles] = useState([
    { ...emptyVehicle(), id: 1, isMyVehicle: true },
    { ...emptyVehicle(), id: 2, isMyVehicle: false }
  ]);

  const updateVehicle = (id, field, value) => {
    setVehicles(prevVehicles => prevVehicles.map(v => {
      if (v.id !== id) return v;
      // Handle functional updates for arrays (like photos)
      const newValue = typeof value === 'function' ? value(v[field]) : value;
      return { ...v, [field]: newValue };
    }));
  };

  const addVehicle = () => {
    setVehicles(prevVehicles => {
      if (prevVehicles.length < 6) {
        return [...prevVehicles, { ...emptyVehicle(), id: Date.now() }];
      }
      return prevVehicles;
    });
    setVehicleCount(prev => prev + 1);
  };

  const removeVehicle = (id) => {
    setVehicles(prevVehicles => {
      if (prevVehicles.length > 2 && !prevVehicles.find(v => v.id === id)?.isMyVehicle) {
        return prevVehicles.filter(v => v.id !== id);
      }
      return prevVehicles;
    });
    setVehicleCount(prev => prev - 1);
  };

  const [saveStatus, setSaveStatus] = useState({ saving: false, message: '', type: '' });

  const handleSave = () => {
    setSaveStatus({ saving: true, message: '', type: '' });

    // Compress photos before saving to avoid localStorage quota issues
    const compressedAccidentInfo = {
      ...accidentInfo,
      scenePhotos: accidentInfo.scenePhotos?.map(photo => ({
        ...photo,
        data: photo.data?.substring(0, 50000) // Limit photo data size
      })) || []
    };

    const compressedVehicles = vehicles.map(v => ({
      ...v,
      photos: v.photos?.map(photo => ({
        ...photo,
        data: photo.data?.substring(0, 50000)
      })) || []
    }));

    const report = {
      id: Date.now(),
      created: new Date().toISOString(),
      accidentInfo: compressedAccidentInfo,
      vehicles: compressedVehicles,
      vehicleCount
    };

    const result = setReports([report, ...reports]);

    if (result?.success === false) {
      setSaveStatus({ saving: false, message: result.error, type: 'error' });
      return;
    }

    setSaveStatus({ saving: false, message: 'Report saved successfully!', type: 'success' });
    setTimeout(() => {
      resetForm();
      setSaveStatus({ saving: false, message: '', type: '' });
    }, 1500);
  };

  const resetForm = () => {
    setMode('list');
    setStep(1);
    setAccidentInfo({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      location: '', parish: 'Kingston', description: '',
      weatherConditions: '', roadConditions: '', trafficConditions: '',
      policeReportNumber: '', policeOfficerName: '', policeStation: '', policeCalled: false,
      injuries: '', ambulanceCalled: false, hospitalName: '',
      scenePhotos: [], witnesses: []
    });
    setVehicleCount(2);
    setVehicles([
      { ...emptyVehicle(), id: 1, isMyVehicle: true },
      { ...emptyVehicle(), id: 2, isMyVehicle: false }
    ]);
  };

  const deleteReport = (id) => {
    if (confirm('Delete this report?')) {
      setReports(reports.filter(r => r.id !== id));
    }
  };

  // Step navigation
  const steps = [
    { num: 1, title: 'Accident Details' },
    { num: 2, title: 'Your Vehicle' },
    { num: 3, title: 'Other Vehicles' },
    { num: 4, title: 'Scene Photos' },
    { num: 5, title: 'Review & Save' }
  ];

  const myVehicle = vehicles.find(v => v.isMyVehicle);
  const otherVehicles = vehicles.filter(v => !v.isMyVehicle);

  // List view
  if (mode === 'list') {
    return (
      <div className="space-y-6">
        <BackButton setPage={setPage} goBack={goBack} />
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl p-6 shadow-xl">
          <h2 className="text-2xl font-black flex items-center gap-3"><AlertCircle className="w-8 h-8" />{T[lang].accidentReport}</h2>
          <p className="text-orange-100 mt-2">Document accidents with photos, driver info, and insurance details</p>
        </div>

        <button onClick={() => setMode('new')}
          className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-4 rounded-xl shadow-lg">
          <Plus className="w-5 h-5" />Start New Accident Report
        </button>

        {reports.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg">Previous Reports ({reports.length})</h3>
            {reports.map(r => (
              <div key={r.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-lg">{r.accidentInfo?.location || 'No location'}, {r.accidentInfo?.parish}</h4>
                      <p className="text-gray-500 text-sm">{r.accidentInfo?.date} at {r.accidentInfo?.time}</p>
                      <p className="text-sm text-orange-600 mt-1">{r.vehicleCount || 2} vehicles involved</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setViewReport(r); setMode('view'); }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200">
                        <Eye className="w-5 h-5" />
                      </button>
                      <button onClick={() => deleteReport(r.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  {r.accidentInfo?.description && (
                    <p className="text-gray-600 text-sm mt-3 line-clamp-2">{r.accidentInfo.description}</p>
                  )}
                  {r.accidentInfo?.scenePhotos?.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {r.accidentInfo.scenePhotos.slice(0, 4).map((p, i) => (
                        <img key={i} src={p.data} alt="" className="w-12 h-12 object-cover rounded-lg" />
                      ))}
                      {r.accidentInfo.scenePhotos.length > 4 && (
                        <span className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                          +{r.accidentInfo.scenePhotos.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
          <h4 className="font-bold text-amber-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Important Reminders</h4>
          <ul className="mt-3 space-y-2 text-amber-800 text-sm">
            <li>• Ensure everyone's safety first - move to a safe location if possible</li>
            <li>• Call 119 (Police) if there are injuries or major damage</li>
            <li>• Exchange information with all drivers involved</li>
            <li>• Take photos of all damage, licence plates, and documents</li>
            <li>• Report to police within 24 hours if there are injuries</li>
            <li>• Notify your insurance company promptly</li>
          </ul>
        </div>
      </div>
    );
  }

  // View report
  if (mode === 'view' && viewReport) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl p-6 shadow-xl">
          <button onClick={() => { setMode('list'); setViewReport(null); }} className="flex items-center gap-2 text-orange-100 hover:text-white mb-3">
            <ChevronLeft className="w-5 h-5" />Back to Reports
          </button>
          <h2 className="text-2xl font-black">Accident Report</h2>
          <p className="text-orange-100">{viewReport.accidentInfo?.date} at {viewReport.accidentInfo?.time}</p>
        </div>

        {/* Accident Details */}
        <div className="bg-white rounded-xl shadow-lg p-5 space-y-4">
          <h3 className="font-bold text-lg">Accident Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Location:</span><p className="font-medium break-words">{viewReport.accidentInfo?.location}, {viewReport.accidentInfo?.parish}</p></div>
            <div><span className="text-gray-500">Vehicles Involved:</span><p className="font-medium">{viewReport.vehicleCount}</p></div>
            <div><span className="text-gray-500">Police Report #:</span><p className="font-medium break-words">{viewReport.accidentInfo?.policeReportNumber || 'N/A'}</p></div>
            <div><span className="text-gray-500">Injuries:</span><p className="font-medium">{viewReport.accidentInfo?.injuries || 'None reported'}</p></div>
          </div>
          {viewReport.accidentInfo?.description && (
            <div><span className="text-gray-500 text-sm">Description:</span><p className="mt-1">{viewReport.accidentInfo.description}</p></div>
          )}
          {viewReport.accidentInfo?.scenePhotos?.length > 0 && (
            <div>
              <span className="text-gray-500 text-sm">Scene Photos:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {viewReport.accidentInfo.scenePhotos.map((p, i) => (
                  <img key={i} src={p.data} alt="" className="w-24 h-24 object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Vehicles */}
        {viewReport.vehicles?.map((v, i) => (
          <div key={i} className="bg-white rounded-xl shadow-lg p-5 space-y-4">
            <h3 className="font-bold text-lg">{v.isMyVehicle ? 'Your Vehicle' : `Other Vehicle ${i}`}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">Driver:</span><p className="font-medium break-words">{v.driverName || 'N/A'}</p></div>
              <div><span className="text-gray-500">Phone:</span><p className="font-medium">{v.driverPhone || 'N/A'}</p></div>
              <div><span className="text-gray-500">Licence #:</span><p className="font-medium break-words">{v.licenseNumber || 'N/A'}</p></div>
              <div><span className="text-gray-500">Vehicle:</span><p className="font-medium break-words">{v.vehicleYear} {v.vehicleMake} {v.vehicleModel}</p></div>
              <div><span className="text-gray-500">Plate:</span><p className="font-medium">{v.vehiclePlate || 'N/A'}</p></div>
              <div><span className="text-gray-500">Color:</span><p className="font-medium">{v.vehicleColor || 'N/A'}</p></div>
              <div><span className="text-gray-500">Insurance:</span><p className="font-medium break-words">{v.insuranceCompany || 'N/A'}</p></div>
              <div><span className="text-gray-500">Policy #:</span><p className="font-medium break-words">{v.policyNumber || 'N/A'}</p></div>
            </div>
            {v.damageDescription && <div><span className="text-gray-500 text-sm">Damage:</span><p className="mt-1">{v.damageDescription}</p></div>}
            {v.damagePhotos?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {v.damagePhotos.map((p, j) => <img key={j} src={p.data} alt="" className="w-20 h-20 object-cover rounded-lg" />)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // New report wizard
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl p-6 shadow-xl">
        <button onClick={resetForm} className="flex items-center gap-2 text-orange-100 hover:text-white mb-3">
          <ChevronLeft className="w-5 h-5" />Cancel
        </button>
        <h2 className="text-2xl font-black">New Accident Report</h2>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow p-4 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s.num} className="flex items-center">
            <button onClick={() => setStep(s.num)}
              className={`flex flex-col items-center min-w-[60px] ${step === s.num ? 'text-orange-600' : step > s.num ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === s.num ? 'bg-orange-600 text-white' : step > s.num ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className="text-xs mt-1 whitespace-nowrap">{s.title}</span>
            </button>
            {i < steps.length - 1 && <div className={`w-8 h-0.5 mx-1 ${step > s.num ? 'bg-emerald-600' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Accident Details */}
      {step === 1 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
          <h3 className="text-xl font-bold">Accident Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Date *</label>
              <input type="date" value={accidentInfo.date}
                onChange={e => setAccidentInfo({...accidentInfo, date: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Time *</label>
              <input type="time" value={accidentInfo.time}
                onChange={e => setAccidentInfo({...accidentInfo, time: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Location/Address *</label>
              <input type="text" placeholder="Street name, landmark, etc." value={accidentInfo.location}
                onChange={e => setAccidentInfo({...accidentInfo, location: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Parish *</label>
              <select value={accidentInfo.parish}
                onChange={e => setAccidentInfo({...accidentInfo, parish: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400">
                {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Number of Vehicles Involved *</label>
            <div className="flex gap-2 mt-2">
              {[2, 3, 4, 5, 6].map(n => (
                <button key={n} onClick={() => {
                  setVehicleCount(n);
                  const newVehicles = [vehicles[0]];
                  for (let i = 1; i < n; i++) {
                    newVehicles.push(vehicles[i] || { ...emptyVehicle(), id: Date.now() + i });
                  }
                  setVehicles(newVehicles);
                }}
                  className={`px-4 py-2 rounded-lg font-bold ${vehicleCount === n ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description of Accident *</label>
            <textarea placeholder="Describe what happened..." value={accidentInfo.description}
              onChange={e => setAccidentInfo({...accidentInfo, description: e.target.value})}
              rows={4} className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Weather Conditions</label>
              <select value={accidentInfo.weatherConditions}
                onChange={e => setAccidentInfo({...accidentInfo, weatherConditions: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400">
                <option value="">Select...</option>
                <option value="Clear">Clear/Sunny</option>
                <option value="Cloudy">Cloudy</option>
                <option value="Rain">Raining</option>
                <option value="Heavy Rain">Heavy Rain</option>
                <option value="Fog">Fog/Mist</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Road Conditions</label>
              <select value={accidentInfo.roadConditions}
                onChange={e => setAccidentInfo({...accidentInfo, roadConditions: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400">
                <option value="">Select...</option>
                <option value="Dry">Dry</option>
                <option value="Wet">Wet</option>
                <option value="Flooded">Flooded</option>
                <option value="Potholes">Potholes</option>
                <option value="Under Construction">Under Construction</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Traffic</label>
              <select value={accidentInfo.trafficConditions}
                onChange={e => setAccidentInfo({...accidentInfo, trafficConditions: e.target.value})}
                className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400">
                <option value="">Select...</option>
                <option value="Light">Light</option>
                <option value="Moderate">Moderate</option>
                <option value="Heavy">Heavy</option>
              </select>
            </div>
          </div>

          <div className="border-t pt-6">
            <h4 className="font-bold mb-4">Police & Medical</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                <input type="checkbox" checked={accidentInfo.policeCalled}
                  onChange={e => setAccidentInfo({...accidentInfo, policeCalled: e.target.checked})}
                  className="w-5 h-5 rounded text-orange-600" />
                <span>Police were called</span>
              </label>
              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                <input type="checkbox" checked={accidentInfo.ambulanceCalled}
                  onChange={e => setAccidentInfo({...accidentInfo, ambulanceCalled: e.target.checked})}
                  className="w-5 h-5 rounded text-orange-600" />
                <span>Ambulance was called</span>
              </label>
            </div>
            {accidentInfo.policeCalled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <input type="text" placeholder="Police Report #" value={accidentInfo.policeReportNumber}
                  onChange={e => setAccidentInfo({...accidentInfo, policeReportNumber: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
                <input type="text" placeholder="Officer Name" value={accidentInfo.policeOfficerName}
                  onChange={e => setAccidentInfo({...accidentInfo, policeOfficerName: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
                <input type="text" placeholder="Police Station" value={accidentInfo.policeStation}
                  onChange={e => setAccidentInfo({...accidentInfo, policeStation: e.target.value})}
                  className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
              </div>
            )}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700">Injuries (describe any injuries)</label>
              <textarea placeholder="Describe any injuries to any person..." value={accidentInfo.injuries}
                onChange={e => setAccidentInfo({...accidentInfo, injuries: e.target.value})}
                rows={2} className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400" />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Your Vehicle */}
      {step === 2 && myVehicle && <VehicleFormComponent vehicle={myVehicle} showRemove={false} onUpdate={updateVehicle} onRemove={removeVehicle} vehicleIndex={0} />}

      {/* Step 3: Other Vehicles */}
      {step === 3 && (
        <div className="space-y-6">
          {otherVehicles.map((v, i) => (
            <VehicleFormComponent key={v.id} vehicle={v} showRemove={otherVehicles.length > 1} onUpdate={updateVehicle} onRemove={removeVehicle} vehicleIndex={i} />
          ))}
          {vehicles.length < 6 && (
            <button onClick={addVehicle}
              className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-orange-400 hover:text-orange-500 flex items-center justify-center gap-2">
              <Plus className="w-5 h-5" />Add Another Vehicle
            </button>
          )}
        </div>
      )}

      {/* Step 4: Scene Photos */}
      {step === 4 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
          <h3 className="text-xl font-bold">Accident Scene Photos</h3>
          <p className="text-gray-600">Take photos of the overall accident scene, road conditions, traffic signs, and any other relevant details.</p>

          <PhotoUploader
            photos={accidentInfo.scenePhotos}
            setPhotos={(photos) => setAccidentInfo(prev => ({...prev, scenePhotos: typeof photos === 'function' ? photos(prev.scenePhotos) : photos}))}
            label="Scene Photos (overall view, road, signs, etc.)"
            maxPhotos={10}
          />

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <h4 className="font-bold text-blue-900">Photo Tips</h4>
            <ul className="mt-2 space-y-1 text-blue-800 text-sm">
              <li>• Take wide shots showing the overall scene</li>
              <li>• Capture all vehicles' positions</li>
              <li>• Photograph road signs and traffic signals</li>
              <li>• Document road conditions (potholes, wet road, etc.)</li>
              <li>• Get close-ups of any skid marks</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 5: Review */}
      {step === 5 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
          <h3 className="text-xl font-bold">Review Your Report</h3>

          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-bold mb-2">Accident Details</h4>
            <p><strong>Date/Time:</strong> {accidentInfo.date} at {accidentInfo.time}</p>
            <p><strong>Location:</strong> {accidentInfo.location}, {accidentInfo.parish}</p>
            <p><strong>Vehicles:</strong> {vehicleCount}</p>
            <p><strong>Description:</strong> {accidentInfo.description || 'None provided'}</p>
            <p><strong>Scene Photos:</strong> {accidentInfo.scenePhotos.length}</p>
          </div>

          {vehicles.map((v, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold mb-2">{v.isMyVehicle ? 'Your Vehicle' : `Other Vehicle ${i}`}</h4>
              <p><strong>Driver:</strong> {v.driverName || 'Not provided'}</p>
              <p><strong>Vehicle:</strong> {v.vehicleYear} {v.vehicleMake} {v.vehicleModel} ({v.vehicleColor})</p>
              <p><strong>Plate:</strong> {v.vehiclePlate || 'Not provided'}</p>
              <p><strong>Insurance:</strong> {v.insuranceCompany} - {v.policyNumber}</p>
              <p><strong>Photos:</strong> Licence: {v.licensePhotos.length}, Insurance: {v.insurancePhotos.length}, Damage: {v.damagePhotos.length}</p>
            </div>
          ))}

          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
            <p className="text-amber-800 text-sm">Please review all information before saving. This report will be stored locally on your device.</p>
          </div>
        </div>
      )}

      {/* Save Status Message */}
      {saveStatus.message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}`}>
          {saveStatus.type === 'error' ? <AlertCircle className="w-5 h-5 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 flex-shrink-0" />}
          <span className="font-medium">{saveStatus.message}</span>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} disabled={saveStatus.saving}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
            <ChevronLeft className="w-5 h-5" />Previous
          </button>
        )}
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2">
            Next<ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button onClick={handleSave} disabled={saveStatus.saving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {saveStatus.saving ? (
              <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
            ) : (
              <><CheckCircle className="w-5 h-5" />Save Report</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== LAWYERS PAGE ====================
const LawyersPage = ({ setPage, goBack, lang }) => {
  const [search, setSearch] = useState('');
  const filtered = search ? LAWYERS.filter(l => l.parish.toLowerCase().includes(search.toLowerCase())) : LAWYERS;

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Gavel className="w-8 h-8" />{T[lang].lawyers}</h2>
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search by parish..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-indigo-500" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(law => (
          <div key={law.id} className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-start justify-between">
              <div><h4 className="font-bold text-lg">{law.name}</h4><p className="text-indigo-600 font-medium">{law.parish}</p></div>
              <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg">
                <Star className="w-4 h-4 fill-current" /><span className="font-bold text-sm">{law.rating}</span>
              </div>
            </div>
            <a href={`tel:${law.phone.replace(/[^0-9]/g, '')}`}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg">
              <Phone className="w-4 h-4" />{law.phone}
            </a>
          </div>
        ))}
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900">Free Legal Help</h4>
        <p className="text-blue-800 mt-2">Legal Aid Council: 876-926-4081 for free assistance if you qualify.</p>
      </div>
      <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6">
        <h4 className="font-bold text-indigo-900">Are you a traffic lawyer?</h4>
        <p className="text-indigo-700 mt-2">Get your practice listed in our directory and reach motorists who need legal help.</p>
        <button onClick={() => setPage('getlisted')} className="mt-4 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700">
          Get Listed
        </button>
      </div>
    </div>
  );
};

// ==================== INSURANCE COMPANIES PAGE ====================
const InsuranceCompaniesPage = ({ setPage, goBack, lang }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const filtered = search
    ? INSURANCE_COMPANIES.filter(c => c.parish.toLowerCase().includes(search.toLowerCase()) || c.name.toLowerCase().includes(search.toLowerCase()))
    : INSURANCE_COMPANIES;

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Shield className="w-8 h-8" />Insurance Companies</h2>
        <p className="text-emerald-200 mt-2">Motor insurance providers across Jamaica</p>
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search by parish or company name..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-emerald-500" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(company => (
          <div key={company.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <div className="p-1 bg-emerald-500" />
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold text-lg">{company.name}</h4>
                  <p className="text-emerald-600 font-medium">{company.parish}</p>
                </div>
                <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg">
                  <Star className="w-4 h-4 fill-current" /><span className="font-bold text-sm">{company.rating}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{company.services}</p>
              <div className="space-y-2">
                <a href={`tel:${company.phone.replace(/[^0-9]/g, '')}`}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 rounded-lg">
                  <Phone className="w-4 h-4" />{company.phone}
                </a>
                {company.email && (
                  <a href={`mailto:${company.email}`}
                    className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 rounded-lg text-sm">
                    {company.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900 flex items-center gap-2"><Info className="w-5 h-5" />Insurance Requirements</h4>
        <ul className="mt-3 space-y-2 text-blue-800 text-sm">
          <li>• All vehicles must have at least Third Party insurance (mandatory)</li>
          <li>• Comprehensive insurance covers your own vehicle damage</li>
          <li>• Insurance must be valid - carry certificate or cover note</li>
          <li>• You have 7 days to produce insurance at a police station if stopped</li>
        </ul>
      </div>
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
        <h4 className="font-bold text-emerald-900">Are you an insurance provider?</h4>
        <p className="text-emerald-700 mt-2">Get your company listed in our directory and reach more motorists.</p>
        <button onClick={() => setPage('getlisted')} className="mt-4 px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700">
          Get Listed
        </button>
      </div>
    </div>
  );
};

// ==================== VEHICLE EVALUATORS PAGE ====================
const VehicleEvaluatorsPage = ({ setPage, goBack, lang }) => {
  const [search, setSearch] = useState('');
  const [showITA, setShowITA] = useState(true);
  const itaCenters = VEHICLE_EVALUATORS.filter(v => v.name.includes('Island Traffic Authority'));
  const privateCenters = VEHICLE_EVALUATORS.filter(v => !v.name.includes('Island Traffic Authority'));

  const displayList = showITA ? itaCenters : privateCenters;
  const filtered = search
    ? displayList.filter(c => c.parish.toLowerCase().includes(search.toLowerCase()) || c.name.toLowerCase().includes(search.toLowerCase()))
    : displayList;

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Wrench className="w-8 h-8" />Vehicle Inspection Centers</h2>
        <p className="text-blue-200 mt-2">Fitness testing and pre-inspection services</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setShowITA(true)}
          className={`flex-1 py-3 rounded-xl font-bold ${showITA ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
          Island Traffic Authority (Official)
        </button>
        <button onClick={() => setShowITA(false)}
          className={`flex-1 py-3 rounded-xl font-bold ${!showITA ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
          Private Inspection Centers
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search by parish or name..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(center => (
          <div key={center.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className={`p-1 ${showITA ? 'bg-blue-500' : 'bg-purple-500'}`} />
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-bold text-lg">{center.name}</h4>
                  <p className="text-blue-600 font-medium">{center.parish}</p>
                </div>
                {center.rating && (
                  <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg">
                    <Star className="w-4 h-4 fill-current" /><span className="font-bold text-sm">{center.rating}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600"><MapPin className="w-4 h-4 inline mr-1" />{center.address}</p>
              <p className="text-sm text-gray-500 mt-1"><Clock className="w-4 h-4 inline mr-1" />{center.hours}</p>
              <p className="text-sm text-emerald-600 mt-2">{center.services}</p>
              <div className="flex gap-2 mt-4">
                <a href={`tel:${center.phone.replace(/[^0-9]/g, '')}`}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg">
                  <Phone className="w-4 h-4" />{center.phone}
                </a>
                <button onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(center.address + ', ' + center.parish + ', Jamaica')}`, '_blank')}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">
                  <Navigation className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
        <h4 className="font-bold text-amber-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Fitness Certificate Info</h4>
        <ul className="mt-3 space-y-2 text-amber-800 text-sm">
          <li>• Fitness certificate is required for all vehicles</li>
          <li>• New vehicles exempt for first year, then annual inspection</li>
          <li>• Private pre-inspection can help identify issues before official test</li>
          <li>• You have 7 days to produce certificate at a police station if stopped</li>
        </ul>
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900">Do you operate a vehicle inspection center?</h4>
        <p className="text-blue-700 mt-2">Get your business listed in our directory and help motorists find your services.</p>
        <button onClick={() => setPage('getlisted')} className="mt-4 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700">
          Get Listed
        </button>
      </div>
    </div>
  );
};

// ==================== GET LISTED PAGE ====================
const GetListedPage = ({ setPage, goBack, lang }) => {
  const [submissions, setSubmissions] = useLocalStorage('ja-submissions', []);
  const [form, setForm] = useState({
    businessType: 'lawyer', businessName: '', contactName: '', email: '', phone: '',
    parish: 'Kingston', address: '', website: '', services: '', description: '', submitted: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.businessName || !form.email || !form.phone) {
      alert('Please fill in all required fields');
      return;
    }
    const submission = { ...form, id: Date.now(), submittedAt: new Date().toISOString(), status: 'pending' };
    setSubmissions([submission, ...submissions]);
    setSubmitted(true);
    setForm({ businessType: 'lawyer', businessName: '', contactName: '', email: '', phone: '',
      parish: 'Kingston', address: '', website: '', services: '', description: '', submitted: false });
  };

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Send className="w-8 h-8" />Get Listed in Our Directory</h2>
        <p className="text-violet-200 mt-2">Submit your business to be featured in Jamaica Driver's Guide</p>
      </div>

      {submitted ? (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-emerald-900">Submission Received!</h3>
          <p className="text-emerald-700 mt-2">Thank you for your interest. We'll review your submission and get back to you.</p>
          <button onClick={() => setSubmitted(false)} className="mt-6 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl">
            Submit Another Business
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
          <div>
            <label className="block font-medium text-gray-700 mb-2">Business Type *</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { id: 'lawyer', label: 'Traffic Lawyer', icon: Gavel },
                { id: 'insurance', label: 'Insurance Company', icon: Shield },
                { id: 'evaluator', label: 'Vehicle Inspector', icon: Wrench },
                { id: 'mechanic', label: 'Mechanic / Garage', icon: Wrench },
                { id: 'parts', label: 'Auto Parts Store', icon: Car },
                { id: 'dealership', label: 'Car Dealership', icon: Car },
                { id: 'other', label: 'Other', icon: Building2 }
              ].map(type => (
                <button key={type.id} type="button" onClick={() => setForm({...form, businessType: type.id})}
                  className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${form.businessType === type.id ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 hover:border-violet-300'}`}>
                  <type.icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-gray-700 mb-2">Business Name *</label>
              <input type="text" value={form.businessName} onChange={e => setForm({...form, businessName: e.target.value})}
                placeholder="Your business name" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" required />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-2">Contact Name</label>
              <input type="text" value={form.contactName} onChange={e => setForm({...form, contactName: e.target.value})}
                placeholder="Contact person" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-2">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                placeholder="email@example.com" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" required />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-2">Phone *</label>
              <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                placeholder="876-XXX-XXXX" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" required />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-2">Parish *</label>
              <select value={form.parish} onChange={e => setForm({...form, parish: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500">
                {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-2">Website</label>
              <input type="url" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
                placeholder="https://..." className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" />
            </div>
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-2">Address</label>
            <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})}
              placeholder="Street address" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" />
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-2">Services Offered</label>
            <input type="text" value={form.services} onChange={e => setForm({...form, services: e.target.value})}
              placeholder="e.g., Traffic law, DUI defense, Motor insurance" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" />
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-2">Additional Information</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Tell us more about your business..." rows={4}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-500" />
          </div>

          <button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2">
            <Send className="w-5 h-5" />Submit for Review
          </button>
        </form>
      )}

      <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6">
        <h4 className="font-bold text-gray-900 flex items-center gap-2"><Info className="w-5 h-5" />Listing Guidelines</h4>
        <ul className="mt-3 space-y-2 text-gray-700 text-sm">
          <li>• All submissions are reviewed before being added to the directory</li>
          <li>• Businesses must be legally registered in Jamaica</li>
          <li>• Services must be related to motoring (legal, insurance, vehicle services)</li>
          <li>• We may contact you for verification</li>
          <li>• Listings are free - this is a community resource</li>
        </ul>
      </div>
    </div>
  );
};

// ==================== LEARNER GUIDE PAGE ====================
const LearnerGuidePage = ({ setPage, goBack, lang }) => {
  const [section, setSection] = useState('process');
  const [expandedSkill, setExpandedSkill] = useState(null);

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><GraduationCap className="w-8 h-8" />Learner Driver Guide</h2>
        <p className="text-green-200 mt-2">Complete guide to getting your Jamaican Driver's Licence</p>
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="learner" />

      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'process', label: 'Getting Started', icon: Target },
          { id: 'restrictions', label: 'Rules', icon: AlertTriangle },
          { id: 'practice', label: 'Practice', icon: Clipboard },
          { id: 'test', label: 'Tests', icon: Award },
          { id: 'classes', label: 'Licence Classes', icon: Car }
        ].map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${section === s.id ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border'}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {section === 'process' && (
        <div className="space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><User className="w-6 h-6 text-green-600" />Age Requirement</h3>
            <p className="text-2xl font-black text-green-600">{LEARNER_GUIDE.requirements.age.minimum} years old</p>
            <p className="text-gray-600 mt-1">{LEARNER_GUIDE.requirements.age.description}</p>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><FileText className="w-6 h-6 text-green-600" />Required Documents</h3>
            <ul className="space-y-2">
              {LEARNER_GUIDE.requirements.documents.map((doc, i) => (
                <li key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{doc}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-green-600 text-white p-5">
              <h3 className="font-bold text-xl">Step-by-Step Process</h3>
            </div>
            <div className="divide-y">
              {LEARNER_GUIDE.requirements.process.map((step, i) => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="font-bold">{step.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {section === 'restrictions' && (
        <div className="space-y-4">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-red-900 flex items-center gap-2"><AlertTriangle className="w-6 h-6" />Important Restrictions</h3>
            <p className="text-red-800 mt-2">Learner drivers must follow these rules - violations can result in fines and points!</p>
          </div>
          {LEARNER_GUIDE.restrictions.map((r, i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-bold text-lg">{r.rule}</h4>
                  <p className="text-gray-600 mt-1">{r.description}</p>
                </div>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg">{r.regulation}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {section === 'practice' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-blue-900">Practice Checklist</h3>
            <p className="text-blue-800 mt-2">Make sure you're comfortable with all these skills before your practical test</p>
          </div>
          {LEARNER_GUIDE.practiceChecklist.map((category, i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <button onClick={() => setExpandedSkill(expandedSkill === i ? null : i)}
                className="w-full p-5 flex items-center justify-between hover:bg-gray-50">
                <h4 className="font-bold text-lg">{category.item}</h4>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded-lg">{category.skills.length} skills</span>
                  {expandedSkill === i ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </button>
              {expandedSkill === i && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {category.skills.map((skill, j) => (
                      <div key={j} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500" />{skill}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {section === 'test' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-amber-500 text-white p-5">
              <h3 className="font-bold text-xl flex items-center gap-2"><FileText className="w-6 h-6" />Written Test</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-3xl font-black text-amber-600">{LEARNER_GUIDE.testComponents.written.questions}</p>
                  <p className="text-sm text-gray-500">Questions</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-3xl font-black text-green-600">{LEARNER_GUIDE.testComponents.written.passScore}</p>
                  <p className="text-sm text-gray-500">To Pass (80%)</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-3xl font-black text-blue-600">{LEARNER_GUIDE.testComponents.written.time}</p>
                  <p className="text-sm text-gray-500">Time Limit</p>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-2">Topics Covered:</h4>
                <div className="flex flex-wrap gap-2">
                  {LEARNER_GUIDE.testComponents.written.topics.map((t, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-lg text-sm">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-green-600 text-white p-5">
              <h3 className="font-bold text-xl flex items-center gap-2"><Car className="w-6 h-6" />Practical Driving Test</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-2xl font-black text-green-600">{LEARNER_GUIDE.testComponents.practical.duration}</p>
                  <p className="text-sm text-gray-500">Duration</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-lg font-black text-red-600">{LEARNER_GUIDE.testComponents.practical.passCriteria}</p>
                  <p className="text-sm text-gray-500">Pass Criteria</p>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-2">Test Components:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {LEARNER_GUIDE.testComponents.practical.components.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />{c}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {section === 'classes' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-purple-900">Licence Classes in Jamaica</h3>
            <p className="text-purple-800 mt-2">Different classes authorize you to drive different types of vehicles</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {LEARNER_GUIDE.licenceClasses.map((lc, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-2xl font-black">
                    {lc.class}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">{lc.vehicles}</h4>
                    <p className="text-gray-500">Minimum age: {lc.minAge} years</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Banner Ad */}
      <BottomBanner page="learner" />
    </div>
  );
};

// ==================== HOW-TO GUIDES PAGE ====================
const HowToGuidesPage = ({ setPage, goBack, lang }) => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [filter, setFilter] = useState('all');

  // Scroll to top when a guide is selected or deselected
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [selectedGuide]);

  const categories = [
    { id: 'all', label: 'All Guides' },
    { id: 'Registration', label: 'Registration' },
    { id: 'Driver\'s Licence', label: 'Licence' },
    { id: 'Fitness', label: 'Fitness' },
    { id: 'Replacement', label: 'Replacement' },
    { id: 'De-registration', label: 'De-registration' }
  ];

  const filteredGuides = filter === 'all'
    ? HOW_TO_GUIDES.guides
    : HOW_TO_GUIDES.guides.filter(g => g.category === filter);

  const getIcon = (iconName) => {
    const icons = { Car, RefreshCw, AlertTriangle, GraduationCap, Award, CheckCircle, Clipboard, FileText, Lock, Trash2 };
    return icons[iconName] || FileText;
  };

  if (selectedGuide) {
    const guide = HOW_TO_GUIDES.guides.find(g => g.id === selectedGuide);
    const IconComponent = getIcon(guide.icon);

    return (
      <div className="space-y-6">
        <button onClick={() => setSelectedGuide(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium">
          <ChevronLeft className="w-5 h-5" />Back to All Guides
        </button>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-2xl p-6 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <IconComponent className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-black">{guide.title}</h2>
              <p className="text-teal-100 mt-1">{guide.subtitle}</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <span className="px-3 py-1 bg-white/20 rounded-lg text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />{guide.estimatedTime}
                </span>
                <span className="px-3 py-1 bg-white/20 rounded-lg text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" />{guide.where}
                </span>
                {guide.canDoOnline && (
                  <span className="px-3 py-1 bg-green-500 rounded-lg text-sm flex items-center gap-1">
                    <Globe className="w-4 h-4" />Available Online
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Online Services Callout */}
        {guide.canDoOnline && (
          <button
            onClick={() => setPage('online-services')}
            className="w-full group bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 hover:border-cyan-300 rounded-2xl p-5 text-left transition-all hover:shadow-md"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Globe className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-cyan-900">Complete This Online</h3>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">ONLINE</span>
                </div>
                <p className="text-cyan-700 text-sm">
                  Skip the queue! Renew your {guide.category === "Driver's Licence" ? 'licence' : guide.category === 'Fitness' ? 'fitness certificate' : 'registration'} online via the TAJ MyTAXES portal.
                </p>
                <p className="text-cyan-600 text-xs mt-2 flex items-center gap-1 group-hover:text-cyan-700">
                  Go to Registration & Fitness Renewal <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Steps */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gray-100 p-4 border-b">
            <h3 className="font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-teal-600" />Step-by-Step Process</h3>
          </div>
          <div className="divide-y">
            {guide.steps.map((step, i) => (
              <div key={i} className="p-5">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg">{step.title}</h4>
                    <p className="text-gray-600 mt-1">{step.description}</p>
                    {step.documents && (
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-gray-500 mb-2">Documents needed:</p>
                        <div className="flex flex-wrap gap-2">
                          {step.documents.map((doc, j) => (
                            <span key={j} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm flex items-center gap-1">
                              <FileText className="w-3 h-3" />{doc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {step.tip && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                        <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />{step.tip}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fees */}
        {guide.fees && guide.fees.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-emerald-50 p-4 border-b">
              <h3 className="font-bold text-lg flex items-center gap-2 text-emerald-800"><DollarSign className="w-5 h-5" />Fees</h3>
              <p className="text-sm text-emerald-600">*Fees as at {HOW_TO_GUIDES.asAtDate}. Verify at jamaicatax.gov.jm</p>
            </div>
            <div className="p-4">
              <table className="w-full">
                <tbody className="divide-y">
                  {guide.fees.map((fee, i) => (
                    <tr key={i}>
                      <td className="py-3 font-medium">{fee.item}</td>
                      <td className="py-3 text-right font-bold text-emerald-700">{fee.amount}</td>
                      <td className="py-3 text-right text-gray-500 text-sm pl-4">{fee.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tips */}
        {guide.tips && guide.tips.length > 0 && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
            <h3 className="font-bold text-lg text-blue-900 flex items-center gap-2 mb-4"><Zap className="w-5 h-5" />Pro Tips</h3>
            <ul className="space-y-2">
              {guide.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-blue-800">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />{tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Common Mistakes */}
        {guide.commonMistakes && guide.commonMistakes.length > 0 && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
            <h3 className="font-bold text-lg text-red-900 flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5" />Common Mistakes to Avoid</h3>
            <ul className="space-y-2">
              {guide.commonMistakes.map((mistake, i) => (
                <li key={i} className="flex items-start gap-2 text-red-800">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />{mistake}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Inspection Checklist for Fitness */}
        {guide.inspectionChecklist && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-amber-50 p-4 border-b">
              <h3 className="font-bold text-lg flex items-center gap-2 text-amber-800"><Clipboard className="w-5 h-5" />Pre-Inspection Checklist</h3>
            </div>
            <div className="p-4 space-y-4">
              {guide.inspectionChecklist.map((item, i) => (
                <div key={i} className="border rounded-xl p-4">
                  <h4 className="font-bold mb-2">{item.item}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {item.checks.map((check, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 border-2 border-gray-300 rounded" />{check}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validity Periods for Fitness */}
        {guide.validityPeriods && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-purple-50 p-4 border-b">
              <h3 className="font-bold text-lg flex items-center gap-2 text-purple-800"><Calendar className="w-5 h-5" />Fitness Validity Periods</h3>
            </div>
            <div className="divide-y">
              {guide.validityPeriods.map((v, i) => (
                <div key={i} className="p-4 flex items-center justify-between">
                  <span className="text-gray-700">{v.condition}</span>
                  <span className="font-bold text-purple-700 bg-purple-100 px-3 py-1 rounded-lg">{v.period}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seller/Buyer Checklists for Transfer */}
        {guide.sellerChecklist && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-red-100 p-4 border-b">
                <h3 className="font-bold text-lg text-red-800">Seller's Checklist</h3>
              </div>
              <div className="p-4 space-y-2">
                {guide.sellerChecklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-green-100 p-4 border-b">
                <h3 className="font-bold text-lg text-green-800">Buyer's Checklist</h3>
              </div>
              <div className="p-4 space-y-2">
                {guide.buyerChecklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Restrictions for Learner */}
        {guide.restrictions && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
            <h3 className="font-bold text-lg text-red-900 flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5" />Restrictions</h3>
            <ul className="space-y-2">
              {guide.restrictions.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-red-800">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />{r}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Test Components */}
        {guide.testComponents && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-green-100 p-4 border-b">
              <h3 className="font-bold text-lg text-green-800">Practical Test Components</h3>
            </div>
            <div className="p-4 space-y-3">
              {guide.testComponents.map((tc, i) => (
                <div key={i} className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-bold">{tc.component}</h4>
                  <p className="text-sm text-gray-600 mt-1">{tc.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Clipboard className="w-8 h-8" />How-To Guides</h2>
        <p className="text-teal-100 mt-2">Step-by-step procedures for vehicle registration, licensing, and more</p>
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="howto" />

      {/* Important Update Banner */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5">
        <div className="flex gap-4 items-start">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-blue-900">{HOW_TO_GUIDES.importantUpdate.title}</h4>
            <p className="text-blue-800 mt-1 text-sm">{HOW_TO_GUIDES.importantUpdate.description}</p>
            <p className="text-blue-600 mt-2 text-xs">Effective: {HOW_TO_GUIDES.importantUpdate.effectiveDate}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {HOW_TO_GUIDES.importantUpdate.keyChanges.slice(0, 3).map((change, i) => (
                <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{change}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Online Services Promo */}
      <button
        onClick={() => setPage('online-services')}
        className="w-full group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-2xl p-6 text-left transition-all hover:shadow-lg"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <Globe className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-xl">Skip the Queue — Go Online!</h3>
              <span className="px-2 py-0.5 bg-green-400 text-green-900 text-xs font-bold rounded-full">NEW</span>
            </div>
            <p className="text-cyan-100 text-sm">
              Renew your driver's licence, vehicle registration, and fitness certificate online via the TAJ MyTAXES portal.
              No more waiting in lines!
            </p>
          </div>
          <ChevronRight className="w-6 h-6 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
        </div>
      </button>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setFilter(cat.id)}
            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${filter === cat.id ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 border'}`}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Guides Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredGuides.map(guide => {
          const IconComponent = getIcon(guide.icon);
          return (
            <button key={guide.id} onClick={() => setSelectedGuide(guide.id)}
              className="bg-white rounded-xl shadow-lg p-5 text-left hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{guide.title}</h3>
                  <p className="text-gray-500 text-sm mt-1">{guide.subtitle}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{guide.category}</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />{guide.estimatedTime}
                    </span>
                    {guide.canDoOnline && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Online</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Links */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h3 className="font-bold text-lg mb-4">Quick Links</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button onClick={() => setPage('online-services')} className="flex items-center gap-2 p-3 bg-white rounded-xl hover:bg-cyan-50 border-2 border-cyan-200">
            <RefreshCw className="w-5 h-5 text-cyan-600" />
            <span className="text-sm font-medium">Renew Online</span>
          </button>
          <a href="https://cgds.gov.jm" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-white rounded-xl hover:bg-gray-100">
            <ExternalLink className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium">CGDS Portal</span>
          </a>
          <button onClick={() => setPage('fees')} className="flex items-center gap-2 p-3 bg-white rounded-xl hover:bg-gray-100">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium">View All Fees</span>
          </button>
          <button onClick={() => setPage('evaluators')} className="flex items-center gap-2 p-3 bg-white rounded-xl hover:bg-gray-100">
            <MapPin className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium">ITA Locations</span>
          </button>
        </div>
      </div>

      {/* Bottom Banner Ad */}
      <BottomBanner page="howto" />
    </div>
  );
};

// ==================== ONLINE SERVICES PAGE ====================
const OnlineServicesPage = ({ setPage, goBack }) => {
  const [section, setSection] = useState('renew');

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Car className="w-8 h-8" />Registration and Fitness Renewal</h2>
        <p className="text-cyan-100 mt-2">Renew and retrieve your motor vehicle documents online via TAJ</p>
      </div>

      {/* Quick Action Buttons - Prominent Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <a href="https://mytaxes.ads.taj.gov.jm/_/" target="_blank" rel="noopener noreferrer"
          className="group flex items-center gap-4 p-6 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl shadow-lg transition-all hover:shadow-xl hover:-translate-y-1">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <RefreshCw className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold">Renew Documents</h3>
            <p className="text-emerald-100 text-sm mt-1">TAJ MyTAXES Portal</p>
            <p className="text-emerald-200 text-xs mt-1">Licence, Registration, Fitness</p>
          </div>
          <ExternalLink className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
        </a>

        <a href="https://www.cgds.gov.jm/home" target="_blank" rel="noopener noreferrer"
          className="group flex items-center gap-4 p-6 bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-2xl shadow-lg transition-all hover:shadow-xl hover:-translate-y-1">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <FileText className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold">Retrieve Documents</h3>
            <p className="text-indigo-100 text-sm mt-1">CGDS Portal</p>
            <p className="text-indigo-200 text-xs mt-1">eMVRC, Licence Info, Fitness History</p>
          </div>
          <ExternalLink className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
        </a>
      </div>

      {/* Document Storage Tip */}
      <button
        onClick={() => setPage('documents')}
        className="w-full group bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 hover:border-amber-300 rounded-2xl p-5 text-left transition-all hover:shadow-md"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <FileText className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-amber-900">Store Your Documents Locally</h3>
              <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-bold rounded-full">TIP</span>
            </div>
            <p className="text-amber-800 text-sm">
              Use our <span className="font-semibold">My Documents</span> feature to securely store photos of your licence,
              registration, fitness certificate, and insurance. Have them at your fingertips whenever you need them —
              even offline!
            </p>
            <p className="text-amber-600 text-xs mt-2 flex items-center gap-1 group-hover:text-amber-700">
              Go to My Documents <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </p>
          </div>
        </div>
      </button>

      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'renew', label: 'Renew Documents', icon: RefreshCw },
          { id: 'retrieve', label: 'Retrieve Documents', icon: FileText },
          { id: 'tips', label: 'Tips & FAQ', icon: HelpCircle }
        ].map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${section === s.id ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 border'}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {/* Renew Documents Section */}
      {section === 'renew' && (
        <div className="space-y-6">
          {/* Overview */}
          <div className="bg-cyan-50 border-2 border-cyan-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-cyan-900 flex items-center gap-2">
              <RefreshCw className="w-6 h-6" />Renew Your Documents Online
            </h3>
            <p className="text-cyan-800 mt-2">
              The Tax Administration Jamaica (TAJ) MyTAXES portal allows you to renew your driver's licence,
              motor vehicle registration, and certificate of fitness from anywhere without visiting an office.
            </p>
          </div>

          {/* What You Can Renew */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-cyan-600 text-white p-5">
              <h3 className="font-bold text-xl">What You Can Renew Online</h3>
            </div>
            <div className="divide-y">
              {[
                { title: "Driver's Licence", desc: "Renew your driver's licence before it expires. You'll receive a digital receipt while your new licence is processed.", icon: Car, fee: "$4,500*" },
                { title: "Motor Vehicle Registration", desc: "Renew your vehicle registration online. Your new registration sticker will be mailed or available for pickup.", icon: FileText, fee: "Varies by vehicle type" },
                { title: "Certificate of Fitness", desc: "After passing your fitness inspection at an authorized depot, you can pay for the certificate online.", icon: CheckCircle, fee: "$1,000*" }
              ].map((item, i) => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold">{item.title}</h4>
                      <span className="text-sm font-semibold text-cyan-600">{item.fee}</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step by Step */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-emerald-600 text-white p-5">
              <h3 className="font-bold text-xl">How to Renew Online</h3>
            </div>
            <div className="divide-y">
              {[
                { step: 1, title: "Create a MyTAXES Account", desc: "Visit the TAJ MyTAXES portal and register with your TRN number, email, and personal details." },
                { step: 2, title: "Log In & Select Service", desc: "Sign in to your account and select the document you want to renew from the available services." },
                { step: 3, title: "Verify Your Information", desc: "Confirm your personal details and document information. Update any outdated information if needed." },
                { step: 4, title: "Pay Online", desc: "Make payment using a credit/debit card or other available payment methods." },
                { step: 5, title: "Get Your Receipt", desc: "Download or print your payment receipt. This serves as temporary proof while your document is processed." },
                { step: 6, title: "Collect Your Document", desc: "Pick up your renewed document at your selected TAJ office, or wait for postal delivery if available." }
              ].map((step, i) => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="font-bold">{step.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <a href="https://mytaxes.ads.taj.gov.jm/_/" target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full p-5 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-lg rounded-2xl transition-colors shadow-lg">
            <Globe className="w-6 h-6" />
            Go to TAJ MyTAXES Portal
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      )}

      {/* Retrieve Documents Section */}
      {section === 'retrieve' && (
        <div className="space-y-6">
          {/* Overview */}
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-indigo-900 flex items-center gap-2">
              <FileText className="w-6 h-6" />Retrieve Your Documents Online
            </h3>
            <p className="text-indigo-800 mt-2">
              The Central Government Document Services (CGDS) portal allows you to download digital copies of your
              motor vehicle registration certificate (MVRC), driver's licence details, and other official documents.
            </p>
          </div>

          {/* What You Can Retrieve */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-indigo-600 text-white p-5">
              <h3 className="font-bold text-xl">What You Can Retrieve</h3>
            </div>
            <div className="divide-y">
              {[
                { title: "Electronic MVRC (eMVRC)", desc: "Download a verified digital copy of your Motor Vehicle Registration Certificate. Accepted by police and insurance companies.", icon: Car },
                { title: "Driver's Licence Information", desc: "View and download your driver's licence details including expiry date, licence class, and endorsements.", icon: CreditCard },
                { title: "Fitness Certificate History", desc: "Access your vehicle's fitness certificate history and inspection records.", icon: CheckCircle }
              ].map((item, i) => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold">{item.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step by Step */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-purple-600 text-white p-5">
              <h3 className="font-bold text-xl">How to Retrieve Documents</h3>
            </div>
            <div className="divide-y">
              {[
                { step: 1, title: "Visit the CGDS Portal", desc: "Go to the Central Government Document Services website." },
                { step: 2, title: "Select Document Type", desc: "Choose whether you want to retrieve vehicle documents (MVRC) or driver's licence information." },
                { step: 3, title: "Enter Your Details", desc: "Provide your TRN, vehicle registration number, or licence number as required." },
                { step: 4, title: "Verify Your Identity", desc: "Answer security questions or enter verification codes sent to your registered contact." },
                { step: 5, title: "Download Your Document", desc: "Save or print the verified digital document for your records." }
              ].map((step, i) => (
                <div key={i} className="p-4 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="font-bold">{step.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
            <h3 className="font-bold text-lg mb-4">Benefits of Digital Documents</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Smartphone, text: "Always accessible on your phone" },
                { icon: Shield, text: "Verified and tamper-proof" },
                { icon: Clock, text: "No waiting in lines" },
                { icon: CheckCircle, text: "Accepted by authorities" }
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <b.icon className="w-5 h-5 text-indigo-600" />
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <a href="https://www.cgds.gov.jm/home" target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full p-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-lg rounded-2xl transition-colors shadow-lg">
            <FileText className="w-6 h-6" />
            Go to CGDS Portal
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      )}

      {/* Tips & FAQ Section */}
      {section === 'tips' && (
        <div className="space-y-6">
          {/* Important Notice */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />Before You Start
            </h3>
            <p className="text-amber-800 mt-2">
              Make sure you have your Tax Registration Number (TRN), a valid email address, and access to your
              registered phone number for verification purposes.
            </p>
          </div>

          {/* Requirements */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-amber-500 text-white p-5">
              <h3 className="font-bold text-xl">What You'll Need</h3>
            </div>
            <div className="p-6 space-y-3">
              {[
                "Tax Registration Number (TRN)",
                "Valid email address",
                "Phone number for verification",
                "Credit/Debit card for online payments",
                "Vehicle registration number (for vehicle services)"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gray-800 text-white p-5">
              <h3 className="font-bold text-xl">Frequently Asked Questions</h3>
            </div>
            <div className="divide-y">
              {[
                { q: "Is the digital receipt valid if stopped by police?", a: "Yes, the digital payment receipt from TAJ is accepted by police as proof of payment while your physical document is being processed." },
                { q: "How long does it take to get my renewed document?", a: "Most documents are processed within 5-10 business days. You'll be notified when it's ready for pickup or mailing." },
                { q: "Can I renew an expired licence online?", a: "Licences expired for less than 5 years can be renewed online. If expired longer, you may need to visit a TAJ office." },
                { q: "Is the eMVRC accepted everywhere?", a: "Yes, the electronic MVRC is legally valid and accepted by police, insurance companies, and other authorities in Jamaica." },
                { q: "What if I don't have a TRN?", a: "You can apply for a TRN at any TAJ office. Bring valid ID (passport, voter ID, or national ID) and proof of address." }
              ].map((faq, i) => (
                <div key={i} className="p-4">
                  <h4 className="font-bold text-gray-900 flex items-start gap-2">
                    <HelpCircle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-600 text-sm mt-2 ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4">Need Help?</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-600" />
                <span>TAJ Customer Service: 1-888-TAX-HELP (829-4357)</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <span>Email: customerservice@taj.gov.jm</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-600" />
                <span>Hours: Mon-Fri 8:30am - 4:00pm</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fee Disclaimer */}
      <p className="text-xs text-gray-500 text-center">
        *Fees shown are approximate and subject to change. Visit the official TAJ website for current rates.
      </p>
    </div>
  );
};

// ==================== INSURANCE LOOKUP PAGE ====================
const InsuranceLookupPage = ({ setPage, goBack }) => {
  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Shield className="w-8 h-8" />Insurance Lookup
        </h2>
        <p className="text-blue-100 mt-2">Verify motor vehicle insurance coverage in Jamaica</p>
      </div>

      {/* About This Service */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-50 p-5 border-b">
          <h3 className="font-bold text-lg flex items-center gap-2 text-blue-900">
            <Info className="w-5 h-5" />About This Service
          </h3>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-gray-700">
            This free public service allows you to verify whether a motor vehicle registered in Jamaica
            has valid insurance coverage. Simply enter the vehicle's Cover ID, Chassis No, Engine No or
            Registration No to check its insurance status.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Brought to you by</p>
                <p className="text-blue-700 text-sm mt-1">
                  <strong>The Insurance Association of Jamaica (IAJ)</strong> — the representative body
                  for general insurance companies operating in Jamaica.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main CTA */}
      <a
        href="https://epicvault.net/covers/iaj/public"
        target="_blank"
        rel="noopener noreferrer"
        className="block group bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <Search className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold">Check Insurance Status</h3>
            <p className="text-emerald-100 text-sm mt-1">Look up any vehicle's insurance coverage</p>
            <p className="text-emerald-200 text-xs mt-1">Search by Cover ID, Chassis No, Engine No or Reg No</p>
          </div>
          <ExternalLink className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
        </div>
      </a>

      {/* Important Notice */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900">Important Notice</h3>
            <ul className="mt-2 space-y-2 text-amber-800 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-1">•</span>
                <span>
                  <strong>Data may be incomplete:</strong> In some cases, a vehicle may not appear in the system
                  if the insurance company has not yet submitted or updated the information. There can be delays
                  or gaps in reporting.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-1">•</span>
                <span>
                  <strong>Not a final determination:</strong> This lookup tool is intended for informational
                  purposes only and should not be used as the sole basis for determining a vehicle's insurance status.
                  Always request official documentation when needed.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-1">•</span>
                <span>
                  <strong>Contact your insurer:</strong> If you believe the information shown is incorrect,
                  please contact your insurance provider directly to verify your coverage and ensure your
                  details are up to date in the system.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Related Services */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gray-50 p-5 border-b">
          <h3 className="font-bold text-lg flex items-center gap-2 text-gray-800">
            <Clipboard className="w-5 h-5 text-emerald-600" />Related Services
          </h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setPage('online-services')}
              className="group p-4 rounded-xl bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-200 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Car className="w-5 h-5 text-cyan-700" />
                </div>
                <div>
                  <h4 className="font-bold text-cyan-900">Registration & Fitness</h4>
                  <p className="text-cyan-700 text-xs">Renew your documents online</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setPage('insurance')}
              className="group p-4 rounded-xl bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <h4 className="font-bold text-blue-900">Insurance Companies</h4>
                  <p className="text-blue-700 text-xs">Find & compare insurers</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setPage('accident')}
              className="group p-4 rounded-xl bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <AlertCircle className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <h4 className="font-bold text-orange-900">Accident Report</h4>
                  <p className="text-orange-700 text-xs">Document incidents properly</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setPage('lawyers')}
              className="group p-4 rounded-xl bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-200 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Gavel className="w-5 h-5 text-violet-700" />
                </div>
                <div>
                  <h4 className="font-bold text-violet-900">Traffic Lawyers</h4>
                  <p className="text-violet-700 text-xs">Get legal assistance</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <p className="text-xs text-gray-500 text-center">
        Insurance data is provided by participating insurance companies through the Insurance Association of Jamaica.
      </p>
    </div>
  );
};

// ==================== FEES PAGE ====================
const FeesPage = ({ setPage, goBack, lang }) => {
  const [section, setSection] = useState('vehicle');

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><DollarSign className="w-8 h-8" />Fees Schedule</h2>
        <p className="text-emerald-100 mt-2">Motor vehicle and driver's licence fees</p>
        <p className="text-emerald-200 text-sm mt-2">*Fees as at {FEES_SCHEDULE.asAtDate}. Always verify current fees at jamaicatax.gov.jm</p>
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="fees" />

      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'vehicle', label: 'Vehicle Licence', icon: Car },
          { id: 'driver', label: "Driver's Licence", icon: User },
          { id: 'fitness', label: 'Fitness', icon: CheckCircle },
          { id: 'other', label: 'Other Fees', icon: FileText },
          { id: 'payfines', label: 'Pay Traffic Fines', icon: Receipt }
        ].map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${section === s.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 border'}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {/* Vehicle Licence Fees */}
      {section === 'vehicle' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 text-sm"><strong>Note:</strong> {FEES_SCHEDULE.vehicleLicence.note}</p>
          </div>

          {FEES_SCHEDULE.vehicleLicence.categories.map((cat, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gray-100 p-4 border-b">
                <h3 className="font-bold text-lg">{cat.type}</h3>
              </div>
              <div className="divide-y">
                {cat.ranges.map((r, j) => (
                  <div key={j} className="p-4 flex items-center justify-between">
                    <span className="text-gray-700">{r.range}</span>
                    <span className="font-bold text-emerald-700 text-lg">{r.fee}*</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Driver's Licence Fees */}
      {section === 'driver' && (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gray-100 p-4 border-b">
            <h3 className="font-bold text-lg">{FEES_SCHEDULE.driversLicence.title}</h3>
          </div>
          <div className="divide-y">
            {FEES_SCHEDULE.driversLicence.items.map((item, i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">{item.item}</span>
                  {item.validity && <span className="ml-2 text-sm text-gray-500">({item.validity})</span>}
                  {item.note && <p className="text-sm text-gray-500 mt-1">{item.note}</p>}
                </div>
                <span className="font-bold text-emerald-700 text-lg">{item.fee}*</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fitness Fees */}
      {section === 'fitness' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 text-sm"><strong>Note:</strong> {FEES_SCHEDULE.fitness.note}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gray-100 p-4 border-b">
              <h3 className="font-bold text-lg">Inspection Fees</h3>
            </div>
            <div className="divide-y">
              {FEES_SCHEDULE.fitness.items.map((item, i) => (
                <div key={i} className="p-4 flex items-center justify-between">
                  <span className="text-gray-700">{item.type}</span>
                  <span className="font-bold text-emerald-700 text-lg">{item.fee}*</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-purple-100 p-4 border-b">
              <h3 className="font-bold text-lg text-purple-800">Validity Periods (July 2025 onwards)</h3>
            </div>
            <div className="divide-y">
              {FEES_SCHEDULE.fitness.validityPeriods.map((v, i) => (
                <div key={i} className="p-4 flex items-center justify-between">
                  <span className="text-gray-700">{v.condition}</span>
                  <span className="font-bold text-purple-700 bg-purple-100 px-3 py-1 rounded-lg">{v.period}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Other Fees */}
      {section === 'other' && (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gray-100 p-4 border-b">
            <h3 className="font-bold text-lg">{FEES_SCHEDULE.otherFees.title}</h3>
          </div>
          <div className="divide-y">
            {FEES_SCHEDULE.otherFees.items.map((item, i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">{item.item}</span>
                  {item.note && <p className="text-sm text-gray-500 mt-1">{item.note}</p>}
                </div>
                <span className="font-bold text-emerald-700 text-lg">{item.fee}*</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pay Traffic Fines */}
      {section === 'payfines' && (
        <div className="space-y-6">
          {/* Important Info */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
            <p className="text-amber-800 font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              You have 28 days from the date of the ticket to pay a fixed penalty or contest it in court.
            </p>
          </div>

          {/* Online Payment */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-emerald-600 text-white p-4">
              <h3 className="font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5" />Pay Online via TAJ</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">Pay your traffic ticket online using the Tax Administration Jamaica (TAJ) portal.</p>
              <div className="grid gap-3">
                <a href="https://mytaxes.ads.taj.gov.jm/_/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center">
                      <ExternalLink className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-emerald-800">TAJ Online Services</p>
                      <p className="text-sm text-emerald-600">Pay traffic fines and renew documents online</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-emerald-600" />
                </a>
                <a href="https://jamaicatax.gov.jm" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center">
                      <Globe className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-blue-800">TAJ Main Website</p>
                      <p className="text-sm text-blue-600">jamaicatax.gov.jm</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-blue-600" />
                </a>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mt-4">
                <h4 className="font-bold text-gray-800 mb-2">What you'll need:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" />Your ticket number</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" />Credit/Debit card for payment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" />TRN (Tax Registration Number) - optional</li>
                </ul>
              </div>
            </div>
          </div>

          {/* In-Person Payment */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-blue-600 text-white p-4">
              <h3 className="font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5" />Pay In Person</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">Visit any of these locations to pay your traffic fine:</p>

              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2"><Building2 className="w-5 h-5" />TAJ Offices Islandwide</h4>
                  <p className="text-sm text-gray-600 mt-1">Any Tax Administration Jamaica office accepts traffic fine payments</p>
                  <p className="text-sm text-gray-500 mt-2">Hours: Mon-Fri 8:30am - 4:00pm</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2"><Building2 className="w-5 h-5" />Tax Collectorate Offices</h4>
                  <p className="text-sm text-gray-600 mt-1">Parish tax collectorate offices throughout Jamaica</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2"><CreditCard className="w-5 h-5" />Bill Payment Services</h4>
                  <p className="text-sm text-gray-600 mt-1">Select bill payment locations (fees may apply)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Traffic Courts */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-purple-600 text-white p-4">
              <h3 className="font-bold text-lg flex items-center gap-2"><Scale className="w-5 h-5" />Traffic Courts</h3>
              <p className="text-purple-200 text-sm mt-1">For contesting tickets or court appearances</p>
            </div>
            <div className="divide-y">
              {RESOURCES.legal.filter(l => l.name.includes('Traffic Court')).map((court, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-gray-900">{court.name}</h4>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />{court.address}
                      </p>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Phone className="w-4 h-4" />{court.phone}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{court.hours}</p>
                    </div>
                    <a href={`tel:${court.phone}`} className="p-3 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200">
                      <Phone className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 bg-purple-50 border-t">
              <p className="text-sm text-purple-800">
                <strong>Note:</strong> If you wish to contest a ticket, you must appear at the Traffic Court on or before the court date specified on your ticket.
              </p>
            </div>
          </div>

          {/* Payment Tips */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2"><Info className="w-5 h-5" />Payment Tips</h3>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600">1.</span>
                Always keep a copy of your payment receipt
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600">2.</span>
                Pay within 28 days to avoid additional penalties
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600">3.</span>
                Note your ticket number before paying - you'll need it
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600">4.</span>
                Online payments may take 24-48 hours to process
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Source Info */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h3 className="font-bold text-lg mb-4">Official Sources</h3>
        <div className="space-y-3">
          <a href={FEES_SCHEDULE.sourceUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-white rounded-xl hover:bg-gray-100">
            <ExternalLink className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium">{FEES_SCHEDULE.source}</p>
              <p className="text-sm text-gray-500">Official rates and fees page</p>
            </div>
          </a>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800 text-sm flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              Fees are subject to change. Always verify current fees at the TAJ office or website before any transaction.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Banner Ad */}
      <BottomBanner page="fees" />
    </div>
  );
};

// ==================== ROAD CODE PAGE ====================
const RoadCodePage = ({ setPage, goBack, lang }) => {
  const [category, setCategory] = useState('regulatory');
  const [search, setSearch] = useState('');

  const categories = [
    { id: 'regulatory', label: 'Regulatory', color: 'red', description: 'Must obey - these are orders' },
    { id: 'warning', label: 'Warning', color: 'amber', description: 'Danger ahead - be alert' },
    { id: 'informational', label: 'Information', color: 'blue', description: 'Helpful directions and info' },
    { id: 'pavement', label: 'Road Markings', color: 'gray', description: 'Lines and markings on the road' }
  ];

  const currentCategory = categories.find(c => c.id === category);
  const signs = ROAD_SIGNS[category] || [];
  const filtered = search
    ? signs.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.meaning.toLowerCase().includes(search.toLowerCase()))
    : signs;

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><BookOpen className="w-8 h-8" />Jamaica Road Code</h2>
        <p className="text-gray-300 mt-2">Traffic signs, road markings, and rules of the road</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setCategory(cat.id)}
            className={`p-4 rounded-xl border-2 transition-all ${category === cat.id
              ? `bg-${cat.color}-50 border-${cat.color}-500 text-${cat.color}-700`
              : 'bg-white border-gray-200 hover:border-gray-400'}`}>
            <h4 className="font-bold">{cat.label}</h4>
            <p className="text-xs text-gray-500 mt-1">{cat.description}</p>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" placeholder="Search signs..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-gray-500" />
      </div>

      {category === 'pavement' ? (
        <div className="space-y-3">
          {filtered.map(marking => (
            <div key={marking.id} className="bg-white rounded-xl shadow-lg p-5">
              <h4 className="font-bold text-lg">{marking.name}</h4>
              <p className="text-gray-600 mt-2">{marking.meaning}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(sign => (
            <div key={sign.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className={`p-4 ${
                category === 'regulatory' ? 'bg-red-500' :
                category === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
              } text-white`}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{sign.icon}</span>
                  <div>
                    <h4 className="font-bold">{sign.name}</h4>
                    <p className="text-sm opacity-80">{sign.shape} • {sign.color}</p>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-700">{sign.meaning}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
        <h4 className="font-bold text-emerald-900 flex items-center gap-2"><Info className="w-5 h-5" />Sign Shape Guide</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500 mx-auto mb-2" style={{clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'}} />
            <p className="text-sm font-medium">Octagon</p>
            <p className="text-xs text-gray-500">STOP only</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500 rounded-full mx-auto mb-2" />
            <p className="text-sm font-medium">Circle</p>
            <p className="text-xs text-gray-500">Regulatory orders</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-amber-500 mx-auto mb-2" style={{clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)', transform: 'rotate(180deg)'}} />
            <p className="text-sm font-medium">Triangle</p>
            <p className="text-xs text-gray-500">Give Way / Yield</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-amber-500 mx-auto mb-2" style={{transform: 'rotate(45deg)'}} />
            <p className="text-sm font-medium">Diamond</p>
            <p className="text-xs text-gray-500">Warning signs</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900">Sign Color Guide</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-500 rounded" />
            <div><p className="font-medium">Red</p><p className="text-xs text-gray-500">Prohibition / Stop</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-500 rounded" />
            <div><p className="font-medium">Yellow/Amber</p><p className="text-xs text-gray-500">Warning / Caution</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded" />
            <div><p className="font-medium">Blue</p><p className="text-xs text-gray-500">Information / Direction</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500 rounded" />
            <div><p className="font-medium">Green</p><p className="text-xs text-gray-500">Highway / Direction</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-500 rounded" />
            <div><p className="font-medium">Orange</p><p className="text-xs text-gray-500">Construction / Roadwork</p></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-800 rounded" />
            <div><p className="font-medium">Brown</p><p className="text-xs text-gray-500">Tourist / Recreation</p></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MECHANICS PAGE ====================
const MechanicsPage = ({ setPage, goBack, lang }) => {
  const [search, setSearch] = useState('');
  const [selectedParish, setSelectedParish] = useState('');
  const parishes = [...new Set(MECHANICS.map(m => m.parish))].sort();

  const filtered = MECHANICS.filter(m => {
    const matchesSearch = !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.services.toLowerCase().includes(search.toLowerCase()) ||
      m.specialty.toLowerCase().includes(search.toLowerCase());
    const matchesParish = !selectedParish || m.parish === selectedParish;
    return matchesSearch && matchesParish;
  });

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Wrench className="w-8 h-8" />Mechanics & Garages</h2>
        <p className="text-slate-200 mt-2">Find trusted mechanics and auto repair shops across Jamaica</p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input type="text" placeholder="Search by name, service, or specialty..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-slate-500" />
        </div>
        <select value={selectedParish} onChange={e => setSelectedParish(e.target.value)}
          className="px-4 py-4 rounded-xl border-2 border-gray-200 focus:border-slate-500 bg-white">
          <option value="">All Parishes</option>
          {parishes.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Results Count */}
      <p className="text-gray-600">{filtered.length} mechanic{filtered.length !== 1 ? 's' : ''} found</p>

      {/* Mechanics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(mech => (
          <div key={mech.id} className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-bold text-lg text-gray-900">{mech.name}</h4>
                <p className="text-slate-600 font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4" />{mech.parish}
                </p>
                <p className="text-sm text-gray-500 mt-1">{mech.address}</p>
              </div>
              <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg">
                <Star className="w-4 h-4 fill-current" /><span className="font-bold text-sm">{mech.rating}</span>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2">
                <Settings className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600">{mech.services}</p>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{mech.specialty}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <p className="text-sm text-gray-500">{mech.hours}</p>
              </div>
            </div>

            <a href={`tel:${mech.phone.replace(/[^0-9]/g, '')}`}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-medium py-3 rounded-lg transition-colors">
              <Phone className="w-4 h-4" />{mech.phone}
            </a>
          </div>
        ))}
      </div>

      {/* Get Listed CTA */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900">Are you a mechanic or garage owner?</h4>
        <p className="text-blue-700 mt-2">Get your business listed in our directory and reach more customers.</p>
        <button onClick={() => setPage('getlisted')} className="mt-4 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700">
          Get Listed
        </button>
      </div>
    </div>
  );
};

// ==================== PARTS STORES PAGE ====================
const PartsStoresPage = ({ setPage, goBack, lang }) => {
  const [search, setSearch] = useState('');
  const [selectedParish, setSelectedParish] = useState('');
  const parishes = [...new Set(PARTS_STORES.map(p => p.parish))].sort();

  const filtered = PARTS_STORES.filter(p => {
    const matchesSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.services.toLowerCase().includes(search.toLowerCase()) ||
      p.brands.toLowerCase().includes(search.toLowerCase());
    const matchesParish = !selectedParish || p.parish === selectedParish;
    return matchesSearch && matchesParish;
  });

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Car className="w-8 h-8" />Auto Parts Stores</h2>
        <p className="text-indigo-200 mt-2">Find auto parts stores and suppliers across Jamaica</p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input type="text" placeholder="Search by name, parts type, or brand..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-indigo-500" />
        </div>
        <select value={selectedParish} onChange={e => setSelectedParish(e.target.value)}
          className="px-4 py-4 rounded-xl border-2 border-gray-200 focus:border-indigo-500 bg-white">
          <option value="">All Parishes</option>
          {parishes.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Results Count */}
      <p className="text-gray-600">{filtered.length} store{filtered.length !== 1 ? 's' : ''} found</p>

      {/* Parts Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(store => (
          <div key={store.id} className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-bold text-lg text-gray-900">{store.name}</h4>
                <p className="text-indigo-600 font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4" />{store.parish}
                </p>
                <p className="text-sm text-gray-500 mt-1">{store.address}</p>
              </div>
              <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg">
                <Star className="w-4 h-4 fill-current" /><span className="font-bold text-sm">{store.rating}</span>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2">
                <Box className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600">{store.services}</p>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{store.brands}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <p className="text-sm text-gray-500">{store.hours}</p>
              </div>
            </div>

            <a href={`tel:${store.phone.replace(/[^0-9]/g, '')}`}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg transition-colors">
              <Phone className="w-4 h-4" />{store.phone}
            </a>
          </div>
        ))}
      </div>

      {/* Get Listed CTA */}
      <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6">
        <h4 className="font-bold text-indigo-900">Do you own an auto parts store?</h4>
        <p className="text-indigo-700 mt-2">Get your business listed in our directory and reach more customers.</p>
        <button onClick={() => setPage('getlisted')} className="mt-4 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700">
          Get Listed
        </button>
      </div>
    </div>
  );
};

// ==================== RESOURCES PAGE ====================
const ResourcesPage = ({ setPage, goBack, lang }) => {
  const [cat, setCat] = useState('taj');
  const cats = [
    { id: 'taj', title: 'Tax Offices (TAJ)', icon: FileText },
    { id: 'ita', title: 'Fitness Depots (ITA)', icon: CheckCircle },
    { id: 'transport', title: 'Transport Authority', icon: Car },
    { id: 'police', title: 'Police & Traffic', icon: Shield },
    { id: 'legal', title: 'Legal & Courts', icon: Scale },
    { id: 'customs', title: 'Customs & Import', icon: Clipboard }
  ];

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Book className="w-8 h-8" />{T[lang].resources}</h2>
        <p className="text-purple-200 text-sm mt-2">Government agencies and essential contacts for motorists</p>
      </div>

      {/* Top Banner Ad */}
      <TopBanner page="resources" />

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-amber-800 text-sm">
          <strong>Disclaimer:</strong> This information is compiled from publicly available sources and may change without notice.
          Please verify details directly with the relevant agencies before visiting. Last updated: January 2025.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {cats.map(c => (
          <button key={c.id} onClick={() => setCat(c.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all ${cat === c.id ? 'bg-purple-600 text-white shadow-lg' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
            <c.icon className="w-4 h-4" />{c.title}
          </button>
        ))}
      </div>

      {/* Resource cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {RESOURCES[cat]?.map((r, i) => (
          <div key={i} className={`bg-white rounded-xl shadow-lg overflow-hidden ${r.isEmergency ? 'border-2 border-red-500' : ''}`}>
            <div className={`p-5 ${r.isEmergency ? 'bg-red-50' : ''}`}>
              <h4 className="font-bold text-lg text-gray-900">{r.name}</h4>

              {r.address && (
                <p className="text-gray-600 text-sm mt-2 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  {r.address}
                </p>
              )}

              {r.phone && (
                <a href={`tel:${r.phone.replace(/[^0-9]/g, '')}`}
                   className={`mt-2 flex items-center gap-2 font-medium ${r.isEmergency ? 'text-red-600 text-xl' : 'text-blue-600'}`}>
                  <Phone className={`${r.isEmergency ? 'w-5 h-5' : 'w-4 h-4'}`} />{r.phone}
                </a>
              )}

              {r.website && (
                <a href={`https://${r.website}`} target="_blank" rel="noopener noreferrer"
                   className="text-purple-600 hover:text-purple-700 mt-2 flex items-center gap-2 text-sm">
                  <ExternalLink className="w-4 h-4" />{r.website}
                </a>
              )}

              {r.hours && (
                <p className="text-gray-500 text-sm mt-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />{r.hours}
                </p>
              )}

              {r.services && (
                <p className="text-gray-600 text-sm mt-3 pt-3 border-t border-gray-100">{r.services}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Helpful links at bottom */}
      <div className="bg-gray-50 rounded-xl p-5 mt-6">
        <h4 className="font-bold text-gray-900 mb-3">Useful Online Services</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <a href="https://www.jamaicatax.gov.jm" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
            <ExternalLink className="w-4 h-4" />TAJ Online Services
          </a>
          <a href="https://www.ita.gov.jm" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
            <ExternalLink className="w-4 h-4" />ITA Online Booking
          </a>
          <a href="https://www.jcf.gov.jm" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
            <ExternalLink className="w-4 h-4" />JCF Services
          </a>
          <a href="https://www.ta.org.jm" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
            <ExternalLink className="w-4 h-4" />Transport Authority
          </a>
        </div>
      </div>

      {/* Bottom Banner Ad */}
      <BottomBanner page="resources" />
    </div>
  );
};

// ==================== DEMERIT PAGE ====================
const DemeritPage = ({ setPage, goBack, lang }) => (
  <div className="space-y-6">
    <BackButton setPage={setPage} goBack={goBack} />
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-6 shadow-xl">
      <h2 className="text-2xl font-black flex items-center gap-3"><AlertTriangle className="w-8 h-8" />{T[lang].demeritPoints}</h2>
    </div>
    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
      <h4 className="font-bold text-emerald-900">Point Expiration</h4>
      <p className="text-emerald-800 mt-2">{DEMERIT.expiry}</p>
    </div>
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-gray-900 text-white p-5"><h3 className="font-bold text-xl">Points by Offence</h3></div>
      <div className="divide-y">
        {DEMERIT.points.map((p, i) => (
          <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50">
            <span className="font-medium">{p.off}</span>
            <span className="px-4 py-2 bg-red-100 text-red-700 font-bold rounded-lg">{p.pts} pts</span>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-amber-500 text-white p-5"><h3 className="font-bold text-xl">Consequences</h3></div>
      <div className="p-6 space-y-4">
        {DEMERIT.consequences.map((c, i) => (
          <div key={i} className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="font-bold text-amber-900 mb-2">{c.range}</div>
            <p className="text-amber-800">{c.action}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ==================== TRAFFIC PAGE ====================
const TrafficPage = ({ lang, setPage, goBack }) => {
  const [updated, setUpdated] = useState(new Date());
  const [view, setView] = useState('map');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [selectedParish, setSelectedParish] = useState('Kingston');
  const [hotspotCount, setHotspotCount] = useState(0);
  const [cameraCount, setCameraCount] = useState(0);

  // Fetch weather on mount and parish change
  useEffect(() => {
    const fetchWeather = async () => {
      setWeatherLoading(true);
      const { data, error, fallback } = await getWeather(selectedParish);
      setWeather(data || fallback);
      setWeatherLoading(false);
    };
    fetchWeather();
  }, [selectedParish]);

  // Fetch counts for quick links
  useEffect(() => {
    const fetchCounts = async () => {
      const { data: hotspots } = await getHotspots();
      const { data: cameras } = await getSpeedCameras();
      setHotspotCount(hotspots?.length || 0);
      setCameraCount(cameras?.length || 0);
    };
    fetchCounts();
  }, []);

  const handleRefresh = async () => {
    setUpdated(new Date());
    setWeatherLoading(true);
    const { data, fallback } = await getWeather(selectedParish);
    setWeather(data || fallback);
    setWeatherLoading(false);
  };

  const openGoogleMapsTraffic = () => {
    const coords = PARISH_COORDS[selectedParish] || PARISH_COORDS['Kingston'];
    window.open(`https://www.google.com/maps/@${coords.lat},${coords.lng},12z/data=!5m1!1e1`, '_blank');
  };

  const openWaze = () => {
    window.open('https://www.waze.com/live-map', '_blank');
  };

  const getWeatherIcon = (iconName) => {
    switch(iconName) {
      case 'sun': return <Sun className="w-12 h-12 text-amber-500" />;
      case 'cloud-sun': return <Sun className="w-12 h-12 text-amber-400" />;
      case 'cloud': return <CloudRain className="w-12 h-12 text-gray-400" />;
      case 'cloud-rain': return <CloudRain className="w-12 h-12 text-blue-500" />;
      case 'cloud-drizzle': return <CloudRain className="w-12 h-12 text-blue-400" />;
      case 'cloud-lightning': return <Zap className="w-12 h-12 text-purple-500" />;
      default: return <Sun className="w-12 h-12 text-amber-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black flex items-center gap-3"><Radio className="w-8 h-8" />{T[lang].traffic}</h2>
          <button onClick={handleRefresh} className={`p-2 bg-white/20 rounded-lg hover:bg-white/30 ${weatherLoading ? 'animate-spin' : ''}`}>
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-cyan-200 mt-2">Updated: {updated.toLocaleTimeString()}</p>
      </div>

      {/* View Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'map', label: 'Live Traffic Map', icon: Navigation },
          { id: 'weather', label: 'Weather', icon: Sun }
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold whitespace-nowrap ${view === v.id ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 border'}`}>
            <v.icon className="w-4 h-4" />{v.label}
          </button>
        ))}
      </div>

      {view === 'map' && (
        <div className="space-y-4">
          {/* Google Maps Traffic Embed */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gray-900 text-white p-4">
              <h3 className="font-bold flex items-center gap-2"><Navigation className="w-5 h-5" />Live Traffic Map - Jamaica</h3>
            </div>
            <div className="relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d121575.50983654075!2d-76.8099!3d18.0179!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sjm!4v1704672000000!5m2!1sen!2sjm"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
                onLoad={() => setMapLoaded(true)}
              />
              {!mapLoaded && (
                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
                    <p className="text-gray-500 mt-2">Loading map...</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 border-t">
              <p className="text-sm text-gray-600 mb-3">Open in your preferred traffic app for real-time updates:</p>
              <div className="flex gap-3">
                <button onClick={openGoogleMapsTraffic}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl">
                  <Navigation className="w-5 h-5" />Google Maps Traffic
                </button>
                <button onClick={openWaze}
                  className="flex-1 flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 rounded-xl">
                  <Radio className="w-5 h-5" />Waze Live Map
                </button>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => setPage('hotspots')}
              className="bg-amber-500 hover:bg-amber-600 text-white p-5 rounded-xl shadow-lg relative">
              <MapPin className="w-8 h-8 mb-2" />
              <h4 className="font-bold">Police Hotspots</h4>
              <p className="text-sm opacity-80">View checkpoints</p>
              {hotspotCount > 0 && (
                <span className="absolute top-2 right-2 bg-white text-amber-600 text-xs font-bold px-2 py-1 rounded-full">{hotspotCount}</span>
              )}
            </button>
            <button onClick={() => setPage('cameras')}
              className="bg-red-500 hover:bg-red-600 text-white p-5 rounded-xl shadow-lg relative">
              <Camera className="w-8 h-8 mb-2" />
              <h4 className="font-bold">Speed Cameras</h4>
              <p className="text-sm opacity-80">Known locations</p>
              {cameraCount > 0 && (
                <span className="absolute top-2 right-2 bg-white text-red-600 text-xs font-bold px-2 py-1 rounded-full">{cameraCount}</span>
              )}
            </button>
          </div>
        </div>
      )}

      {view === 'weather' && (
        <div className="space-y-4">
          {/* Parish Selector */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">Select Parish</label>
            <select
              value={selectedParish}
              onChange={(e) => setSelectedParish(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-cyan-500"
            >
              {Object.keys(PARISH_COORDS).map(parish => (
                <option key={parish} value={parish}>{parish}</option>
              ))}
            </select>
          </div>

          {/* Weather Display */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              Current Weather - {selectedParish}
              {weather?.updatedAt && (
                <span className="text-xs text-gray-400 ml-auto">
                  {new Date(weather.updatedAt).toLocaleTimeString()}
                </span>
              )}
            </h3>

            {weatherLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
              </div>
            ) : weather ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  {getWeatherIcon(weather.icon)}
                  <p className="font-bold mt-2">{weather.condition}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-4xl font-black text-red-600">{weather.temperature}°C</p>
                  <p className="text-sm text-gray-500">Feels like {weather.feelsLike}°C</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <CloudRain className="w-8 h-8 text-blue-500 mx-auto" />
                  <p className="text-sm mt-1">{weather.rainProbability}% chance rain</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <WifiOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Unable to fetch weather data</p>
              </div>
            )}

            {weather?.humidity && (
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Humidity:</span>
                  <span className="font-bold ml-2">{weather.humidity}%</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Wind:</span>
                  <span className="font-bold ml-2">{weather.windSpeed} km/h</span>
                </div>
              </div>
            )}
          </div>

          {/* Driving Tips */}
          {weather?.drivingTips && weather.drivingTips.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
              <h4 className="font-bold text-blue-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />Driving Tips
              </h4>
              <ul className="mt-3 space-y-2 text-blue-800 text-sm">
                {weather.drivingTips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Weather Info Note */}
          <div className="bg-gray-50 rounded-xl p-4 text-center text-sm text-gray-500">
            <p>Weather data provided by Open-Meteo</p>
            <p className="text-xs mt-1">Free, open-source weather API</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== QUICK ANSWER BOT PAGE ====================
const QuickAnswerBotPage = ({ lang, setPage, goBack, navigateToOffence }) => {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [speaking, setSpeaking] = useState(false);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.9;
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const processQuery = (q) => {
    let normalized = q.toLowerCase().trim();
    if (!normalized) return null;

    // ========== COMMON MISSPELLINGS HANDLER ==========
    const COMMON_MISSPELLINGS = {
      'lisence': 'licence', 'lisense': 'licence', 'liscence': 'licence', 'liscense': 'licence',
      'licene': 'licence', 'licnse': 'licence', 'licens': 'licence', 'lience': 'licence',
      'insurnace': 'insurance', 'insurace': 'insurance', 'insurence': 'insurance', 'insrance': 'insurance',
      'breathalizer': 'breathalyzer', 'breathaliser': 'breathalyser', 'breathalzer': 'breathalyzer',
      'registation': 'registration', 'regitration': 'registration', 'registraion': 'registration',
      'vehcile': 'vehicle', 'vehical': 'vehicle', 'vehicel': 'vehicle', 'vechicle': 'vehicle',
      'speedin': 'speeding', 'speding': 'speeding', 'speeed': 'speed',
      'drving': 'driving', 'drivng': 'driving', 'dirving': 'driving', 'driveing': 'driving',
      'acident': 'accident', 'accidnet': 'accident', 'accedent': 'accident', 'accidant': 'accident',
      'policce': 'police', 'plice': 'police', 'polic': 'police', 'polce': 'police',
      'trafic': 'traffic', 'traffc': 'traffic', 'trafffic': 'traffic',
      'offence': 'offence', 'offense': 'offence', 'offenses': 'offences', 'offenses': 'offences',
      'penality': 'penalty', 'penaly': 'penalty', 'penlty': 'penalty',
      'motorcyle': 'motorcycle', 'motocycle': 'motorcycle', 'motorbik': 'motorbike',
      'helment': 'helmet', 'hemlet': 'helmet', 'healmet': 'helmet',
      'seatblet': 'seatbelt', 'setbelt': 'seatbelt', 'seatbet': 'seatbelt',
      'overtake': 'overtaking', 'overtak': 'overtaking',
      'alchol': 'alcohol', 'alchohol': 'alcohol', 'alcohal': 'alcohol', 'alcahol': 'alcohol',
      'intoxacated': 'intoxicated', 'intoxacation': 'intoxication',
      'parkng': 'parking', 'pakring': 'parking',
      'regsitration': 'registration', 'registraition': 'registration',
      'fitnes': 'fitness', 'fittness': 'fitness',
      'documets': 'documents', 'documants': 'documents', 'documnts': 'documents'
    };

    // Apply misspelling corrections
    Object.entries(COMMON_MISSPELLINGS).forEach(([wrong, correct]) => {
      normalized = normalized.replace(new RegExp(wrong, 'gi'), correct);
    });

    // Natural language question patterns - COMPREHENSIVE DATABASE
    const naturalPatterns = [
      // ==================== POLICE STOP SCENARIOS ====================
      { pattern: /what (?:happens|if|do i do) (?:if|when) (?:i )?(?:get |am )?(?:stopped|pulled over)/i,
        response: { type: 'info', title: 'If You Get Pulled Over',
          answer: 'Stay calm, pull over safely, turn off engine, and keep hands visible. You must produce your driver\'s licence, registration (eMVRC), and insurance on request.',
          tips: ['Don\'t exit vehicle unless asked', 'Be polite but know your rights', 'You can remain silent beyond basic ID', 'You have 7 days to produce documents at a station if not carrying them', 'Ask why you were stopped'],
          regulation: 'Regulation 11, Road Traffic Act Section 44' }},
      { pattern: /what (?:happens|if) (?:i )?(?:refuse|don'?t|won'?t|will not) (?:to )?(?:show|produce|give|hand over) (?:my )?(?:licen[cs]e|documents?|id|papers)/i,
        response: { type: 'warning', title: 'Refusing to Produce Documents',
          answer: 'Refusing to produce documents when lawfully requested by police is an offence. You MUST identify yourself and show your licence, registration, and insurance when stopped.',
          tips: ['You can be arrested for obstruction', 'If you genuinely don\'t have them, you get 7 days to produce at a station', 'Refusing can escalate the situation', 'Cooperate but note the officer\'s badge number', 'You can file a complaint later if treated unfairly'],
          regulation: 'Road Traffic Act Section 44, Constabulary Force Act' }},
      { pattern: /what (?:happens|if) (?:i )?(?:refuse|don'?t|won'?t) (?:to )?(?:show|produce|give) (?:my )?insurance/i,
        response: () => {
          const off = OFFENCES.no_insurance;
          return { type: 'fine', title: 'Refusing to Show Insurance',
            answer: `If you cannot produce valid insurance, you may be charged with driving without insurance. Fine: ${off?.fine || '$50,000-$100,000'}. Your vehicle can be seized.`,
            tips: ['Keep digital copy on phone as backup', 'You have 7 days to produce at a station', 'No insurance = vehicle seizure', 'Third-party insurance is mandatory'],
            points: off?.points, regulation: 'Motor Vehicles Insurance (Third Party Risks) Act', offenceKey: 'no_insurance' };
        }},
      { pattern: /(?:can )?(?:police|officer|cop) (?:search|check|look in|inspect) (?:my )?(?:car|vehicle|trunk|boot)/i,
        response: { type: 'rights', title: 'Vehicle Search Rights',
          answer: 'Police can search your vehicle without a warrant if they have reasonable suspicion of illegal activity, or with your consent. You can refuse consent for a search.',
          tips: ['You can ask "Am I being detained?"', 'You can refuse consent to search', 'If they search anyway, don\'t resist - note details for complaint', 'They need reasonable suspicion or warrant for forced search', 'Document everything'],
          regulation: 'Constabulary Force Act, Constitution of Jamaica' }},
      { pattern: /(?:can )?(?:police|officer) (?:take|seize|impound|tow) (?:my )?(?:car|vehicle)/i,
        response: { type: 'info', title: 'Vehicle Seizure/Impound',
          answer: 'Police can seize your vehicle for: no insurance, suspected stolen, involved in serious crime, dangerous condition, or unpaid traffic warrants.',
          tips: ['No insurance = automatic seizure', 'You\'ll get a receipt for seized vehicle', 'Storage fees accumulate daily', 'Contact Police Traffic HQ to retrieve', 'May need to show valid documents to get vehicle back'],
          regulation: 'Road Traffic Act Section 27, Motor Vehicles Insurance Act' }},

      // ==================== DUI / DRUNK DRIVING ====================
      { pattern: /(?:what (?:happens|if)|fine for|penalty for) (?:i )?(?:drive|driving|drove|caught) (?:while )?(?:drunk|intoxicated|under the influence|dui|dwi)/i,
        response: () => {
          const off = OFFENCES.dui;
          return { type: 'fine', title: 'Drunk Driving (DUI)',
            answer: `Driving under the influence is a SERIOUS offence. Fine: ${off?.fine || 'Up to $500,000'} and/or imprisonment up to 12 months. Repeat offenders face harsher penalties.`,
            tips: ['Blood alcohol limit: 80mg/100ml blood or 35μg/100ml breath', 'Can result in immediate arrest', 'Licence suspension up to 12 months', 'Vehicle may be seized', 'Second offence: up to 2 years imprisonment'],
            points: off?.points, regulation: 'Road Traffic Act Section 44, 44A', offenceKey: 'dui' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:drink|have a drink|have alcohol) (?:and|then|before) driv/i,
        response: () => {
          const off = OFFENCES.dui;
          return { type: 'fine', title: 'Drinking and Driving',
            answer: `NO. It is illegal to drive with blood alcohol above 80mg/100ml or breath alcohol above 35μg/100ml. Even small amounts impair driving.`,
            tips: ['One drink can put you over the limit', 'Alcohol affects everyone differently', 'Wait at least 1 hour per unit of alcohol', 'Best policy: Don\'t drink if you\'re driving', 'Use a taxi or designated driver'],
            points: off?.points, regulation: 'Road Traffic Act Section 44', offenceKey: 'dui' };
        }},
      { pattern: /(?:can i )?(?:refuse|say no to) (?:a )?(?:breath|breathaly[sz]er|alcohol|sobriety) (?:test)?/i,
        response: () => {
          const off = OFFENCES.refusing_breath_test;
          return { type: 'fine', title: 'Refusing a Breath Test',
            answer: `Refusing a breath test is ITSELF an offence, treated as seriously as DUI. Fine: ${off?.fine || 'Up to $500,000'}. You may be arrested immediately.`,
            tips: ['Refusal = automatic assumption of guilt', 'Police can arrest you on the spot', 'Same penalties as actual DUI', 'Blood test can be requested at hospital', 'Your refusal will be used against you in court'],
            points: off?.points, regulation: 'Road Traffic Act Section 44A', offenceKey: 'refusing_breath_test' };
        }},
      { pattern: /what (?:is|are) (?:the )?(?:blood )?alcohol (?:limit|level|legal)/i,
        response: { type: 'info', title: 'Legal Alcohol Limits',
          answer: 'The legal limit in Jamaica is 80mg of alcohol per 100ml of blood, OR 35 micrograms per 100ml of breath.',
          tips: ['This is roughly 1-2 standard drinks for most people', 'Limits are LOWER for commercial drivers', 'Factors: weight, gender, food, metabolism', 'When in doubt, don\'t drive', 'Penalties increase with higher readings'],
          regulation: 'Road Traffic Act Section 44' }},

      // ==================== NO LICENCE SCENARIOS ====================
      { pattern: /(?:what (?:happens|if)|fine for|penalty for|caught) (?:i )?(?:drive|driving|drove) (?:with)?(?:out|no) (?:a )?(?:licen[cs]e|licence)/i,
        response: () => {
          const off = OFFENCES.no_licence;
          return { type: 'fine', title: 'Driving Without a Licence',
            answer: `Driving without a valid licence is a serious offence. Fixed penalty: ${off?.fine || '$15,000'}. You may be arrested and vehicle impounded.`,
            tips: ['Includes expired licence', 'Includes driving wrong vehicle class', 'Can result in arrest', 'Vehicle may be impounded', 'Court appearance may be required'],
            points: off?.points, regulation: 'Road Traffic Act Section 17, Regulation 8-10', offenceKey: 'no_licence' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?driv(?:e|ing) (?:with)?(?:out|no) (?:a )?(?:licen[cs]e|licence)/i,
        response: () => {
          const off = OFFENCES.no_licence;
          return { type: 'fine', title: 'Driving Without a Licence',
            answer: `NO. You MUST have a valid driver\'s licence to operate a motor vehicle in Jamaica. Fixed penalty: ${off?.fine || '$15,000'}.`,
            tips: ['Learner\'s permit requires supervision', 'International licences valid for 3 months', 'Must carry licence while driving', 'Licence must match vehicle class', 'Expired licence = no licence'],
            points: off?.points, regulation: 'Road Traffic Act Section 17', offenceKey: 'no_licence' };
        }},
      { pattern: /(?:what (?:happens|if)|can i) (?:i )?(?:forgot|left|don'?t have|lost) (?:my )?(?:licen[cs]e|licence)/i,
        response: { type: 'info', title: 'Forgot Your Licence',
          answer: 'If stopped without your licence, you have 7 days to produce it at any police station. Get a receipt when you do.',
          tips: ['Carry a photo on your phone as backup', 'Ask officer which station to report to', 'Keep digital copy in cloud storage', 'Get receipt when you produce it', 'Failure to produce within 7 days = offence'],
          regulation: 'Road Traffic Act Section 44(3)' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:my )?licen[cs]e (?:is )?expired/i,
        response: () => {
          const off = OFFENCES.no_licence;
          return { type: 'fine', title: 'Driving with Expired Licence',
            answer: `Driving with an expired licence is the same as driving without one. Fixed penalty: ${off?.fine || '$15,000'}.`,
            tips: ['Renew before expiry date', 'Grace period is NOT guaranteed', 'Can renew up to 6 months before expiry', 'Online renewal available', 'Don\'t wait until it expires'],
            points: off?.points, regulation: 'Regulation 8-10', offenceKey: 'no_licence' };
        }},

      // ==================== NO INSURANCE SCENARIOS ====================
      { pattern: /(?:what (?:happens|if)|fine for|penalty for|caught) (?:i )?(?:drive|driving|drove) (?:with)?(?:out|no) insurance/i,
        response: () => {
          const off = OFFENCES.no_insurance;
          return { type: 'fine', title: 'Driving Without Insurance',
            answer: `Driving without insurance is one of the most serious traffic offences. Fine: ${off?.fine || '$50,000 - $100,000'}. Your vehicle WILL be seized.`,
            tips: ['Third-party insurance is MANDATORY', 'Vehicle will be impounded immediately', 'Must show proof of insurance to retrieve vehicle', 'Can face court prosecution', 'Second offence carries imprisonment'],
            points: off?.points, regulation: 'Motor Vehicles Insurance (Third Party Risks) Act', offenceKey: 'no_insurance' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?driv(?:e|ing) (?:with)?(?:out|no) insurance/i,
        response: () => {
          const off = OFFENCES.no_insurance;
          return { type: 'fine', title: 'Driving Without Insurance',
            answer: `ABSOLUTELY NOT. Third-party motor insurance is mandatory in Jamaica. Fine: ${off?.fine || '$50,000-$100,000'} and vehicle seizure.`,
            tips: ['Minimum required: Third-party liability', 'Comprehensive is optional but recommended', 'Must carry certificate while driving', 'Cover note valid for 30 days', 'No excuses accepted'],
            points: off?.points, regulation: 'Motor Vehicles Insurance (Third Party Risks) Act', offenceKey: 'no_insurance' };
        }},
      { pattern: /(?:what (?:happens|if)|can i) (?:my )?insurance (?:is )?expired/i,
        response: () => {
          const off = OFFENCES.no_insurance;
          return { type: 'fine', title: 'Expired Insurance',
            answer: `Driving with expired insurance is the same as driving without insurance. Fine: ${off?.fine || '$50,000-$100,000'}. Vehicle can be seized.`,
            tips: ['Set calendar reminder for renewal', 'No grace period for insurance', 'Accidents with expired insurance = you pay everything', 'Can renew online with most insurers', 'Coverage must be continuous'],
            points: off?.points, regulation: 'Motor Vehicles Insurance (Third Party Risks) Act', offenceKey: 'no_insurance' };
        }},

      // ==================== VEHICLE LIGHTS & EQUIPMENT ====================
      { pattern: /(?:can i|is it legal|allowed|when) (?:to )?(?:use|drive with|turn on) (?:my )?fog (?:light|lamp)s?/i,
        response: () => {
          const off = OFFENCES.improper_lights;
          return { type: 'fine', title: 'Fog Light Usage',
            answer: `Fog lights should ONLY be used in fog, heavy rain, or severely reduced visibility. Using them in clear conditions can dazzle other drivers. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Only use in fog, heavy rain, or mist', 'Must turn off when visibility improves', 'Front fog lights: white or yellow only', 'Rear fog lights: red only', 'Don\'t use as decoration'],
            points: off?.points, regulation: 'Regulation 124-126', offenceKey: 'improper_lights' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:driving )?(?:with)?(?:out|no) (?:head)?lights?/i,
        response: () => {
          const off = OFFENCES.no_lights;
          return { type: 'fine', title: 'Driving Without Lights',
            answer: `Driving without proper lights at night or in poor visibility is dangerous and illegal. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Headlights required from sunset to sunrise', 'Also required in rain, fog, or poor visibility', 'Both headlights must work', 'Tail lights must also work', 'Check lights regularly'],
            points: off?.points, regulation: 'Regulation 120-123', offenceKey: 'no_lights' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:use|have|install) (?:led|hid|xenon|blue|colored?) (?:head)?lights?/i,
        response: { type: 'info', title: 'LED/HID/Colored Headlights',
          answer: 'Headlights must be WHITE or YELLOW only. Blue, red, or other colored lights on the front are illegal. Aftermarket HID/LED must be properly aligned.',
          tips: ['White or yellow headlights only', 'Blue lights are for emergency vehicles', 'Aftermarket lights must be aimed correctly', 'Improperly aimed lights = offence', 'Can fail fitness inspection'],
          regulation: 'Regulation 120-126' }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:use|have|install) (?:under(?:body|glow)|neon|decorative) lights?/i,
        response: { type: 'info', title: 'Decorative/Underglow Lights',
          answer: 'Decorative lights (underglow, neon) are generally not permitted while driving on public roads. They can distract other drivers and are illegal.',
          tips: ['Blue and red reserved for emergency vehicles', 'Can be used at car shows (off road)', 'May cause you to be stopped', 'Can fail fitness inspection', 'Interior lights okay if not distracting'],
          regulation: 'Regulation 120-126, 137' }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:driving )?(?:with )?(?:broken|faulty|not working|one) (?:head)?light/i,
        response: () => {
          const off = OFFENCES.defective_lights;
          return { type: 'fine', title: 'Defective Lights',
            answer: `Driving with defective lights (headlight out, brake lights not working, etc.) is an offence. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Fix lights immediately', 'Check all lights before driving', 'Includes headlights, tail lights, brake lights, indicators', 'Police may stop you and issue ticket', 'Can cause accidents'],
            points: off?.points, regulation: 'Regulation 120-126', offenceKey: 'defective_lights' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:driving )?(?:with)?(?:out|no) (?:indicator|signal|turn signal)s?/i,
        response: () => {
          const off = OFFENCES.no_indicators;
          return { type: 'fine', title: 'Indicator/Signal Lights',
            answer: `Your vehicle must have working indicators. Failing to use indicators when turning is also an offence. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Must indicate before turning or changing lanes', 'All indicators must work', 'Includes front and rear', 'Hand signals allowed as backup', 'Give adequate warning'],
            points: off?.points, regulation: 'Regulation 124, 216', offenceKey: 'no_indicators' };
        }},

      // ==================== TINTED WINDOWS ====================
      { pattern: /(?:can i|is it legal|allowed|what|how much) (?:to )?(?:have|use|put|tint|get) (?:dark )?(?:window )?tint/i,
        response: () => {
          const off = OFFENCES.tinted_windows;
          return { type: 'fine', title: 'Window Tint Regulations',
            answer: `Front windshield and front side windows must allow at least 35% light transmission. Darker tint is only permitted on rear windows. Fine: ${off?.fine || '$8,000'}.`,
            tips: ['Windshield: top 6 inches can be tinted', 'Front side windows: 35% minimum light', 'Rear windows: any darkness allowed', 'Medical exemptions available', 'Factory tint usually compliant'],
            points: off?.points, regulation: 'Regulation 140', offenceKey: 'tinted_windows' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:having )?(?:too much|illegal|dark) (?:window )?tint/i,
        response: () => {
          const off = OFFENCES.tinted_windows;
          return { type: 'fine', title: 'Illegal Window Tint',
            answer: `Excessive window tint is illegal. Fixed penalty: ${off?.fine || '$8,000'}. You may be ordered to remove it.`,
            tips: ['Police can test tint with meter', 'May fail fitness inspection', 'Can be ordered to remove on the spot', 'Apply for exemption if medical reason', 'Aftermarket tint often non-compliant'],
            points: off?.points, regulation: 'Regulation 140', offenceKey: 'tinted_windows' };
        }},

      // ==================== SEATBELT & SAFETY ====================
      { pattern: /(?:do i|must i|have to) wear (?:a )?seat\s?belt/i,
        response: () => {
          const off = OFFENCES.no_seatbelt;
          return { type: 'fine', title: 'Seatbelt Requirements',
            answer: `YES. All front seat occupants MUST wear seatbelts. Driver is responsible for passengers under 16. Fine: ${off?.fine || '$3,000'}.`,
            tips: ['Driver and front passenger MUST wear belt', 'Driver responsible for child passengers', 'Children under 5 need child seat', 'Rear passengers should also wear belts', 'Pregnancy is NOT an exemption'],
            points: off?.points, regulation: 'Regulation 207', offenceKey: 'no_seatbelt' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:not wearing|without) (?:a )?seat\s?belt/i,
        response: () => {
          const off = OFFENCES.no_seatbelt;
          return { type: 'fine', title: 'No Seatbelt Fine',
            answer: `Not wearing a seatbelt is an offence. Fixed penalty: ${off?.fine || '$3,000'}. Driver can also be fined for unbelted passengers.`,
            tips: ['Fine applies to driver AND each unbelted passenger', 'Driver responsible for passengers under 16', 'Seatbelt exemptions are rare', 'Taxi passengers should also wear belts', 'Saves lives in accidents'],
            points: off?.points, regulation: 'Regulation 207', offenceKey: 'no_seatbelt' };
        }},
      { pattern: /(?:do i|must|need) (?:a )?child (?:car )?seat/i,
        response: { type: 'info', title: 'Child Car Seat Requirements',
          answer: 'Children under 5 years must be secured in an appropriate child restraint (car seat). Children under 12 should not sit in the front seat.',
          tips: ['Infants: Rear-facing seat', 'Toddlers: Forward-facing with harness', 'Older children: Booster seat until adult belt fits', 'Never put child seat in front with active airbag', 'Secure seat properly according to instructions'],
          regulation: 'Regulation 207, Road Safety Guidelines' }},

      // ==================== SPEEDING ====================
      { pattern: /(?:what (?:is|are)|tell me) (?:the )?speed limit/i,
        response: { type: 'info', title: 'Jamaica Speed Limits',
          answer: 'Speed limits in Jamaica: 50 km/h in towns/built-up areas, 80 km/h on open roads, 110 km/h on Highway 2000 (50 km/h minimum), 30 km/h in school zones.',
          tips: ['Speed cameras are active islandwide', 'School zone limits apply during school hours', 'Look for posted speed signs', 'Fines increase with severity', 'Double penalties in construction zones'],
          regulation: 'Regulation 229-232' }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:i )?(?:am |was |get |got )?(?:speeding|going too fast|over the limit)/i,
        response: () => {
          const off = OFFENCES.speeding;
          return { type: 'fine', title: 'Speeding Penalties',
            answer: `Speeding fines start at ${off?.fine || '$6,000'} and increase based on how much over the limit. Serious speeding can result in court.`,
            tips: ['1-20 km/h over: Fixed penalty', '20-40 km/h over: Higher fine, possible court', '40+ km/h over: Mandatory court, possible licence suspension', 'School zones: Double penalties', 'Speed cameras capture your plate'],
            points: off?.points, regulation: 'Regulation 229-232', offenceKey: 'speeding' };
        }},
      { pattern: /(?:what (?:is|are)|fine for) (?:speeding in )?school zone/i,
        response: () => {
          const off = OFFENCES.school_zone_speeding;
          return { type: 'fine', title: 'School Zone Speeding',
            answer: `Speed limit in school zones is 30 km/h during school hours. Fine: ${off?.fine || '$10,000'} with enhanced penalties.`,
            tips: ['Active during school hours (roughly 7am-4pm)', 'Watch for children crossing', 'Higher fines than regular speeding', 'Speed cameras in many school zones', 'Applies on school days only'],
            points: off?.points, regulation: 'Regulation 231', offenceKey: 'school_zone_speeding' };
        }},

      // ==================== PARKING & STOPPING ====================
      { pattern: /(?:can i|is it legal|allowed) (?:to )?park (?:on |in )?(?:the )?(?:sidewalk|pavement|yellow line|no parking)/i,
        response: () => {
          const off = OFFENCES.illegal_parking;
          return { type: 'fine', title: 'Illegal Parking',
            answer: `Parking on sidewalks, yellow lines, or no-parking zones is illegal. Fine: ${off?.fine || '$5,000'}. Vehicle may be clamped or towed.`,
            tips: ['Yellow line = no stopping', 'Single yellow = no parking at certain times', 'Double yellow = no parking anytime', 'Handicap spots require permit', 'Can be clamped or towed'],
            points: off?.points, regulation: 'Regulation 244-250', offenceKey: 'illegal_parking' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:illegal|bad|wrong) parking/i,
        response: () => {
          const off = OFFENCES.illegal_parking;
          return { type: 'fine', title: 'Parking Violations',
            answer: `Illegal parking carries fines from ${off?.fine || '$5,000'}. Your vehicle can be clamped, towed, or both.`,
            tips: ['Clamp removal fee: Additional cost', 'Tow fee + storage = expensive', 'Pay promptly to avoid extra charges', 'KSAC enforces in Kingston', 'Private lots can also tow'],
            points: off?.points, regulation: 'Regulation 244-250', offenceKey: 'illegal_parking' };
        }},
      { pattern: /(?:can i|is it legal) (?:to )?(?:stop|park) (?:on |at )?(?:a )?(?:bus stop|corner|intersection|crosswalk)/i,
        response: { type: 'info', title: 'No Stopping Zones',
          answer: 'You cannot stop or park: at bus stops, within 6m of intersections, on crosswalks, on bridges, in tunnels, or blocking driveways.',
          tips: ['Bus stops are for buses only', 'Don\'t block intersections', 'Keep crosswalks clear', 'Don\'t block fire hydrants', 'Emergency stopping allowed briefly'],
          regulation: 'Regulation 244-250' }},
      { pattern: /(?:what (?:happens|if)|my car (?:was|got)) (?:clamped|wheel.?lock|booted|towed)/i,
        response: { type: 'procedure', title: 'Vehicle Clamped or Towed',
          answer: 'If your vehicle is clamped or towed, you must pay the parking fine plus removal/storage fees to retrieve it.',
          steps: ['Note the clamp/tow notice details', 'Contact the number on the notice', 'Pay parking fine + clamp/tow fee', 'Get release authorization', 'Retrieve vehicle promptly (storage fees accumulate)'],
          regulation: 'KSAC Parking Regulations' }},

      // ==================== ACCIDENTS & COLLISIONS ====================
      { pattern: /what (?:do i do|should i do|happens) (?:if|after|when) (?:i )?(?:have |had |get in |got in )?(?:an )?accident/i,
        response: { type: 'procedure', title: 'What To Do After an Accident',
          answer: 'Stop immediately, check for injuries, call police (119) if injuries or major damage, exchange information, and document everything.',
          steps: ['STOP - Do not leave the scene', 'Check for injuries, call ambulance if needed (110)', 'Call police (119) for injuries or major damage', 'Move vehicles if blocking traffic (take photos first)', 'Exchange: Names, licence numbers, insurance, plates', 'Document: Photos of damage, scene, witnesses', 'Report to insurance within 24-48 hours'],
          regulation: 'Road Traffic Act Section 39-41' }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:i )?(?:hit and run|leave|left|fleeing) (?:the )?(?:scene|accident)/i,
        response: { type: 'warning', title: 'Hit and Run / Leaving Scene',
          answer: 'Leaving the scene of an accident is a SERIOUS criminal offence. Fine up to $500,000 and/or imprisonment. Licence will be suspended.',
          tips: ['You MUST stop after any accident', 'Applies even for minor damage', 'Applies even if other driver\'s fault', 'Call police if you feel unsafe', 'Leaving makes it worse - stay and cooperate'],
          regulation: 'Road Traffic Act Section 39-40' }},
      { pattern: /(?:do i|must i|should i|when to) (?:call|report to|contact) (?:the )?police (?:after |for )?(?:an )?accident/i,
        response: { type: 'info', title: 'When to Report Accident to Police',
          answer: 'You MUST report to police if: anyone is injured, there is significant damage, other driver leaves scene, or you suspect DUI. Report within 24 hours.',
          tips: ['Any injury = must report', 'Major damage = must report', 'Other driver flees = must report', 'Minor fender-bender = can exchange info only', 'When in doubt, report it'],
          regulation: 'Road Traffic Act Section 39-41' }},
      { pattern: /(?:do i|must i|should i) report (?:accident )?to insurance/i,
        response: { type: 'info', title: 'Reporting Accidents to Insurance',
          answer: 'Yes, report any accident to your insurance company within 24-48 hours, even if you don\'t plan to claim. Failure to report can void coverage.',
          tips: ['Report even minor incidents', 'Report even if not your fault', 'Keep copy of police report', 'Don\'t admit fault at scene', 'Get claim number from insurer'],
          regulation: 'Insurance Policy Terms' }},

      // ==================== LICENCE PLATES & REGISTRATION ====================
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:driving )?(?:with)?(?:out|no) (?:number |licence )?plates?/i,
        response: () => {
          const off = OFFENCES.no_number_plates;
          return { type: 'fine', title: 'No Number Plates',
            answer: `Driving without proper number plates is illegal. Fine: ${off?.fine || '$10,000'}. Vehicle may be impounded.`,
            tips: ['Both front and rear plates required', 'Plates must be clearly visible', 'Cannot be obscured or altered', 'Temporary plates must be valid', 'Dealer plates have restrictions'],
            points: off?.points, regulation: 'Regulation 149-154', offenceKey: 'no_number_plates' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:cover|hide|obscure|tint|alter) (?:my )?(?:number |licence )?plates?/i,
        response: () => {
          const off = OFFENCES.obscured_plates;
          return { type: 'fine', title: 'Obscured Number Plates',
            answer: `Covering, tinting, or altering number plates to avoid identification is illegal. Fine: ${off?.fine || '$10,000'}. Often indicates criminal activity.`,
            tips: ['Plates must be clearly readable', 'No tinted covers allowed', 'No reflective sprays', 'Dirty plates = still an offence', 'Speed cameras read plates'],
            points: off?.points, regulation: 'Regulation 149-154', offenceKey: 'obscured_plates' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:driving )?(?:with)?(?:out|no) (?:registration|fitness)/i,
        response: () => {
          const off = OFFENCES.no_registration || OFFENCES.no_fitness;
          return { type: 'fine', title: 'No Registration/Fitness',
            answer: `Driving without valid registration or fitness certificate is illegal. Fine: ${off?.fine || '$10,000'}. Vehicle may be impounded.`,
            tips: ['Registration must be current', 'Fitness inspection required annually', 'eMVRC replaces paper certificate', 'Can check status at TAJ', 'Grace period is limited'],
            points: off?.points, regulation: 'Road Traffic Act Section 6, 24', offenceKey: 'no_registration' };
        }},

      // ==================== MOTORCYCLE SPECIFIC ====================
      { pattern: /(?:do i|must i|have to) wear (?:a )?helmet/i,
        response: () => {
          const off = OFFENCES.no_helmet;
          return { type: 'fine', title: 'Motorcycle Helmet Requirements',
            answer: `YES. All motorcycle riders AND passengers MUST wear approved helmets. Fine: ${off?.fine || '$5,000'}.`,
            tips: ['Helmet must meet safety standards', 'Must be properly fastened', 'Passenger also needs helmet', 'Construction hard hats don\'t count', 'Protects against head injury'],
            points: off?.points, regulation: 'Regulation 186', offenceKey: 'no_helmet' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:riding )?(?:motor ?bike|motor ?cycle) (?:with)?(?:out|no) helmet/i,
        response: () => {
          const off = OFFENCES.no_helmet;
          return { type: 'fine', title: 'No Helmet Fine',
            answer: `Riding without a helmet is dangerous and illegal. Fine: ${off?.fine || '$5,000'} for rider AND any passenger without helmet.`,
            tips: ['Both rider and passenger fined separately', 'Helmet saves lives', 'Must be proper motorcycle helmet', 'Properly secured under chin', 'Don\'t risk it'],
            points: off?.points, regulation: 'Regulation 186', offenceKey: 'no_helmet' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:carry|have) (?:a )?passenger (?:on )?(?:motor ?bike|motor ?cycle)/i,
        response: { type: 'info', title: 'Motorcycle Passengers',
          answer: 'You can carry ONE passenger on a motorcycle if it has proper pillion seat and footrests. Passenger must also wear a helmet.',
          tips: ['Must have proper pillion seat', 'Must have footrests for passenger', 'Passenger must wear helmet', 'Only one passenger allowed', 'Children not recommended'],
          regulation: 'Regulation 186-188' }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:ride|drive) (?:motor ?bike|motor ?cycle) between (?:cars|traffic|lanes)|lane split/i,
        response: { type: 'info', title: 'Lane Splitting / Filtering',
          answer: 'Lane splitting (riding between cars) is not explicitly legal in Jamaica and is dangerous. You can filter to the front at red lights with caution.',
          tips: ['Not recommended in moving traffic', 'Filtering at lights is common', 'Use extreme caution', 'Drivers may not see you', 'You\'re liable if accident occurs'],
          regulation: 'Road Traffic Act - General Road Rules' }},

      // ==================== PHONE & DISTRACTIONS ====================
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:use|talk on|text|check) (?:my )?(?:phone|cell|mobile)/i,
        response: () => {
          const off = OFFENCES.mobile_phone;
          return { type: 'fine', title: 'Mobile Phone While Driving',
            answer: `Using a hand-held phone while driving is ILLEGAL. Fine: ${off?.fine || '$12,000'}. Hands-free devices ARE permitted.`,
            tips: ['No texting, calls, or browsing', 'Pull over to use phone', 'Bluetooth/hands-free allowed', 'GPS use should be hands-free', 'High accident cause'],
            points: off?.points, regulation: 'Regulation 236', offenceKey: 'mobile_phone' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:to )?(?:use|have) (?:hands.?free|bluetooth|earpiece)/i,
        response: { type: 'info', title: 'Hands-Free Device Use',
          answer: 'Yes, hands-free devices (Bluetooth, speaker, single earpiece) are permitted while driving. You should still minimize distraction.',
          tips: ['Bluetooth headset/speaker allowed', 'Single earpiece allowed (keep one ear free)', 'Set up before driving', 'Don\'t fumble with device while driving', 'Pull over for complex calls'],
          regulation: 'Regulation 236' }},

      // ==================== RECKLESS/DANGEROUS DRIVING ====================
      { pattern: /(?:what (?:is|happens)|fine for|caught) (?:reckless|dangerous|careless) driv(?:e|ing)/i,
        response: () => {
          const off = OFFENCES.reckless_driving || OFFENCES.dangerous_driving;
          return { type: 'fine', title: 'Reckless/Dangerous Driving',
            answer: `Reckless or dangerous driving is a serious offence. Fine: ${off?.fine || 'Up to $200,000'} and/or imprisonment. Licence suspension likely.`,
            tips: ['Includes excessive speeding', 'Includes street racing', 'Includes weaving through traffic', 'Can be charged criminally', 'Causing death = manslaughter'],
            points: off?.points, regulation: 'Road Traffic Act Section 31-32', offenceKey: 'reckless_driving' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|caught) (?:street )?racing|drag racing|speeding excessively/i,
        response: { type: 'warning', title: 'Street Racing',
          answer: 'Street racing is extremely dangerous and illegal. You can be arrested, face heavy fines, have your vehicle seized, and licence suspended.',
          tips: ['Criminal offence', 'Vehicle can be impounded', 'Licence suspension', 'Imprisonment possible', 'If death results = manslaughter charges'],
          regulation: 'Road Traffic Act Section 31-32, Criminal Justice Act' }},

      // ==================== TICKETS & FINES ====================
      { pattern: /what (?:happens|if) (?:i )?(?:get |receive )?(?:a )?ticket/i,
        response: { type: 'info', title: 'If You Receive a Ticket',
          answer: 'You typically have 14 days to pay a fixed penalty ticket at Tax Administration Jamaica (TAJ). Failure to pay may result in court summons and higher fines.',
          tips: ['Keep the ticket safe', 'Pay online at jamaicatax.gov.jm', 'Pay in person at TAJ or collectorate', 'You can contest in court if you believe it\'s unjust', 'Late payment = additional penalties'],
          regulation: 'Road Traffic Act, Fifth Schedule' }},
      { pattern: /what (?:happens|if) (?:i )?(?:don'?t|do not|can'?t|cannot) (?:pay|afford) (?:my )?(?:ticket|fine)/i,
        response: { type: 'info', title: 'If You Can\'t Pay a Fine',
          answer: 'If you cannot pay a fixed penalty, the matter may proceed to court where a magistrate can set payment terms or alternative penalties.',
          tips: ['Request payment arrangements', 'Court fines may be higher', 'Non-payment can lead to arrest warrants', 'Seek legal advice: Legal Aid Council (876-926-4081)', 'Don\'t ignore it - it won\'t go away'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:how (?:do i|to|can i)|where (?:can i|do i)) (?:pay|settle) (?:a |my )?(?:ticket|fine)/i,
        response: { type: 'procedure', title: 'How to Pay a Traffic Ticket',
          answer: 'You can pay traffic tickets online at jamaicatax.gov.jm, at any Tax Administration Jamaica (TAJ) office, or at any collectorate.',
          steps: ['Go to jamaicatax.gov.jm or visit TAJ office', 'Have your ticket number ready', 'Pay by cash, card, or online banking', 'Keep your receipt as proof of payment', 'Payment clears your record'],
          locations: ['Online: jamaicatax.gov.jm', 'TAJ offices islandwide', 'Any tax collectorate', 'Select bill payment services'],
          regulation: 'Tax Administration Jamaica' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:contest|fight|challenge|appeal) (?:a |my )?(?:ticket|fine)/i,
        response: { type: 'procedure', title: 'Contesting a Traffic Ticket',
          answer: 'You can contest any ticket in Parish Court. You must appear on the court date listed or request one.',
          steps: ['Do NOT pay the ticket if you want to contest', 'Note the court date on your ticket', 'Gather evidence (photos, witnesses)', 'Appear in court on the date', 'Present your case to the magistrate', 'Consider hiring a traffic lawyer'],
          tips: ['Payment = admission of guilt', 'You can represent yourself', 'Lawyer recommended for serious offences', 'Bring all documentation', 'Be respectful in court'],
          regulation: 'Road Traffic Act, Parish Court Rules' }},

      // ==================== LICENCE PROCEDURES ====================
      { pattern: /how (?:do i|to|can i) (?:get|obtain|apply for) (?:a |my )?(?:driver'?s? )?licen[cs]e/i,
        response: { type: 'procedure', title: 'Getting a Driver\'s Licence',
          answer: 'Visit Island Traffic Authority with required documents. New applicants must pass written and practical tests.',
          steps: ['Gather: Birth certificate, TRN, NIS, 2 passport photos', 'Visit ITA for eye test and application', 'Study the Road Code', 'Pass the written test', 'Get learner\'s permit and practice', 'Pass the practical driving test', 'Pay fee: $3,500'],
          regulation: 'Road Traffic Act, Regulation 6-10' }},
      { pattern: /how (?:do i|to|can i) renew (?:my )?(?:driver'?s? )?licen[cs]e/i,
        response: { type: 'procedure', title: 'Renewing Your Driver\'s Licence',
          answer: 'Renew at any Island Traffic Authority (ITA) office or apply online through their portal.',
          steps: ['Check expiry date on current licence', 'Visit ITA or apply online', 'Bring: Current licence, TRN, passport photo', 'Complete eye test if required', 'Pay renewal fee: $2,500', 'Receive new licence'],
          regulation: 'Road Traffic Act, Regulation 8' }},

      // ==================== VEHICLE REGISTRATION ====================
      { pattern: /how (?:do i|to|can i) (?:register|title) (?:a |my )?(?:new )?(?:car|vehicle)/i,
        response: { type: 'procedure', title: 'Registering a Vehicle',
          answer: 'Vehicle registration is done through Tax Administration Jamaica (TAJ) offices or online at cgds.gov.jm.',
          steps: ['Get fitness certificate (inspection)', 'Obtain valid insurance', 'Get bill of sale or import docs', 'Submit registration form at TAJ', 'Pay registration fee', 'Receive eMVRC (electronic certificate)'],
          regulation: 'Motor Vehicles Registration Act' }},
      { pattern: /how (?:do i|to|can i) (?:transfer|sell|buy) (?:a |my )?(?:car|vehicle)/i,
        response: { type: 'procedure', title: 'Transferring Vehicle Ownership',
          answer: 'Both seller and buyer must visit TAJ together, or seller can authorize via notarized documents.',
          steps: ['Seller: Clear any liens, prepare documents', 'Both parties visit TAJ with valid IDs', 'Bring current eMVRC/title', 'Complete transfer form (RTAD-5)', 'Pay transfer fee ($6,000)', 'Buyer receives new eMVRC within 14 days'],
          regulation: 'Motor Vehicles Registration Act' }},

      // ==================== LEARNER DRIVERS ====================
      { pattern: /(?:learner|l.?plate|provisional|learning to driv)/i,
        response: { type: 'info', title: 'Learner Driver Requirements',
          answer: 'Learner drivers must: display L plates front and rear, be accompanied by a licensed driver (3+ years experience), not drive on highways, and not drive between 7pm-5am.',
          tips: ['L plates: Red L on white background', 'Must be visible front and rear', 'Supervisor must sit in front passenger seat', 'No driving on Highway 2000', 'No night driving (7pm-5am)', 'Practice in low-traffic areas first'],
          regulation: 'Regulation 15-17' }},
      { pattern: /(?:can )?(?:a )?learner (?:drive on |use )?(?:the )?highway/i,
        response: { type: 'info', title: 'Learners on Highways',
          answer: 'NO. Learner drivers are NOT permitted to drive on Highway 2000 or other highways. Stick to regular roads.',
          tips: ['Highway 2000 prohibited for learners', 'Toll roads generally prohibited', 'High-speed roads not suitable', 'Build experience on regular roads first', 'Supervisor can be fined too'],
          regulation: 'Regulation 15-17' }},
      { pattern: /(?:when )?can (?:a )?learner drive (?:at night|after dark)/i,
        response: { type: 'info', title: 'Learner Night Driving',
          answer: 'Learner drivers are NOT permitted to drive between 7:00 PM and 5:00 AM. Daylight driving only.',
          tips: ['Driving hours: 5am to 7pm only', 'No exceptions for emergencies', 'Get your full licence first', 'Night driving requires more skill', 'Build experience during day'],
          regulation: 'Regulation 15-17' }},

      // ==================== BATCH 1: OVERTAKING & TRAFFIC RULES ====================
      { pattern: /(?:can i|is it legal|allowed|how) (?:to )?(?:overtake|pass)/i,
        response: { type: 'info', title: 'Overtaking Rules',
          answer: 'Overtake only on the right. Never overtake on solid white lines, hills, curves, intersections, or when visibility is limited.',
          tips: ['Only overtake when safe', 'Check mirrors and blind spots', 'Signal before and after', 'Never on double white lines', 'Return when you see overtaken vehicle in mirror'],
          regulation: 'Regulation 213-215' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:illegal|dangerous) (?:overtaking|passing)/i,
        response: () => {
          const off = OFFENCES.dangerous_overtaking;
          return { type: 'fine', title: 'Dangerous Overtaking', answer: `Dangerous overtaking is serious. Fine: ${off?.fine || '$10,000'}.`,
            tips: ['Includes overtaking on solid lines', 'At junctions', 'With insufficient visibility', 'Cutting in too soon'], points: off?.points, regulation: 'Regulation 213-215' };
        }},
      { pattern: /(?:how (?:do i|to)|rules for) roundabout/i,
        response: { type: 'info', title: 'Roundabout Rules',
          answer: 'Give way to traffic on the roundabout (from your right). Signal left when exiting.',
          tips: ['Yield to vehicles on your right', 'Signal left when exiting', 'Don\'t change lanes on roundabout', 'Keep moving steadily'],
          regulation: 'Regulation 220-222' }},
      { pattern: /(?:who has )?right of way (?:at|on) roundabout/i,
        response: { type: 'info', title: 'Roundabout Right of Way',
          answer: 'Traffic ALREADY on the roundabout has right of way. Yield to vehicles from your RIGHT.',
          tips: ['Look right before entering', 'Once on roundabout, you have priority', 'Keep moving steadily'],
          regulation: 'Regulation 220-222' }},
      { pattern: /(?:what (?:does|do)|meaning of) (?:red|amber|yellow|green) (?:light|signal)/i,
        response: { type: 'info', title: 'Traffic Light Meanings',
          answer: 'RED = Stop. AMBER = Stop if safe. GREEN = Go if clear. Flashing red = stop sign. Flashing amber = proceed with caution.',
          tips: ['Red means STOP completely', 'Amber means stop if safe', 'Green means go if clear'],
          regulation: 'Regulation 201-205' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:running|jumping) (?:a )?red (?:light|signal)/i,
        response: () => {
          const off = OFFENCES.red_light;
          return { type: 'fine', title: 'Running a Red Light', answer: `Running red lights is dangerous. Fine: ${off?.fine || '$8,000'}. Cameras capture violations.`,
            tips: ['Cameras capture your plate', 'Ticket sent by mail', 'Can cause serious accidents'], points: off?.points, regulation: 'Regulation 201-205' };
        }},
      { pattern: /(?:can i|allowed) (?:turn|go) (?:on )?red/i,
        response: { type: 'info', title: 'Turning on Red',
          answer: 'Generally NO turning on red unless indicated by sign or arrow. Wait for green.',
          tips: ['Wait for green signal', 'Some intersections allow left on red - look for signs', 'When in doubt, wait'],
          regulation: 'Regulation 201-205' }},
      { pattern: /(?:who has )?right of way (?:at|in) intersection/i,
        response: { type: 'info', title: 'Intersection Right of Way',
          answer: 'Unmarked: vehicle on right has priority. Stop signs: first to stop goes first. Yield: give way to crossing traffic.',
          tips: ['Main road usually has priority', 'Stop signs mean full stop', 'When in doubt, yield'],
          regulation: 'Regulation 217-219' }},
      { pattern: /(?:who has )?right of way (?:pedestrian|crossing)/i,
        response: { type: 'info', title: 'Pedestrian Right of Way',
          answer: 'Pedestrians have right of way at marked crossings. You MUST stop for pedestrians on or approaching a crossing.',
          tips: ['Always stop for pedestrians at crossings', 'Look for people waiting to cross', 'Never overtake at crossings'],
          regulation: 'Regulation 223-226' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:not stopping|failing) (?:for|at) pedestrian/i,
        response: () => {
          const off = OFFENCES.pedestrian_crossing;
          return { type: 'fine', title: 'Pedestrian Crossing Violation', answer: `Failing to stop for pedestrians is dangerous. Fine: ${off?.fine || '$8,000'}.`,
            tips: ['Always watch for pedestrians', 'Stop well before crossing line', 'Wait until fully crosses'], points: off?.points, regulation: 'Regulation 223-226' };
        }},
      { pattern: /(?:what (?:does|do)|meaning of) (?:white|yellow) lines?/i,
        response: { type: 'info', title: 'Road Line Markings',
          answer: 'Solid white: Do not cross. Broken white: May cross if safe. Double yellow: No parking. Double white: No crossing.',
          tips: ['Solid lines = no crossing', 'Broken lines = can cross with care', 'Yellow = parking restrictions'],
          regulation: 'Regulation 197-200' }},
      { pattern: /(?:can i|allowed) cross (?:a )?solid (?:white )?line/i,
        response: { type: 'info', title: 'Crossing Solid Lines',
          answer: 'Generally NO. Solid white lines mean no crossing except to avoid obstruction or emergency.',
          tips: ['Solid line = no overtaking', 'Exception for obstructions', 'Broken line = okay to cross'],
          regulation: 'Regulation 197-200' }},
      { pattern: /(?:what (?:do i do|should i)|how) (?:when|if) (?:emergency|ambulance|fire|police) vehicle/i,
        response: { type: 'info', title: 'Emergency Vehicle Response',
          answer: 'Pull over to the LEFT and STOP. Wait until the emergency vehicle passes.',
          tips: ['Pull to the left side', 'Stop if safe', 'Don\'t block intersections', 'Never follow closely behind'],
          regulation: 'Regulation 235' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:not moving|blocking) (?:for )?(?:ambulance|emergency)/i,
        response: () => {
          const off = OFFENCES.obstruct_emergency;
          return { type: 'fine', title: 'Obstructing Emergency Vehicle', answer: `Failing to give way to emergency vehicles is serious. Fine: ${off?.fine || '$15,000'}.`,
            tips: ['Lives depend on your response', 'Pull over immediately', 'Criminal charges possible'], points: off?.points, regulation: 'Regulation 235' };
        }},
      { pattern: /(?:rules for|how to|near) school bus/i,
        response: { type: 'info', title: 'School Bus Rules',
          answer: 'When a school bus stops with flashing lights, stop and wait until children have safely crossed.',
          tips: ['Stop when bus lights flash', 'Watch for children crossing', 'Slow down in school zones', 'Be extra careful 7-9am and 2-4pm'],
          regulation: 'Regulation 231, 235' }},
      { pattern: /(?:can i|is it legal|allowed) (?:make )?(?:a )?u.?turn/i,
        response: { type: 'info', title: 'U-Turn Rules',
          answer: 'U-turns NOT permitted where prohibited by signs, at traffic lights, on highways, near crossings, or limited visibility.',
          tips: ['Look for No U-turn signs', 'Never on highways', 'Not at traffic signals', 'Use a side road instead'],
          regulation: 'Regulation 210' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:illegal )?u.?turn/i,
        response: () => {
          const off = OFFENCES.illegal_u_turn;
          return { type: 'fine', title: 'Illegal U-Turn', answer: `Illegal U-turns are dangerous. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Use side roads to turn around', 'Find a proper turning point'], points: off?.points, regulation: 'Regulation 210' };
        }},
      { pattern: /(?:can i|what (?:happens|if)) (?:go|drive) wrong way|one.?way/i,
        response: () => {
          const off = OFFENCES.wrong_way;
          return { type: 'fine', title: 'Wrong Way Driving', answer: `Driving wrong way is extremely dangerous. Fine: ${off?.fine || '$10,000'}. Can be dangerous driving charge.`,
            tips: ['Look for one-way signs', 'Check arrow markings on road', 'If you enter wrong, carefully reverse'], points: off?.points, regulation: 'Regulation 209' };
        }},
      { pattern: /(?:rules for|when can i) revers(?:e|ing)/i,
        response: { type: 'info', title: 'Reversing Rules',
          answer: 'Only reverse when necessary and safe. Never reverse onto a main road from a side road.',
          tips: ['Check all around before reversing', 'Go slowly', 'Never reverse on highways', 'Get help for poor visibility'],
          regulation: 'Regulation 211' }},
      { pattern: /(?:when can i|rules for|allowed) (?:use|sound) (?:my )?horn/i,
        response: { type: 'info', title: 'Horn Usage',
          answer: 'Use horn ONLY as warning of danger. Not aggressively, not in residential areas at night, not in stationary traffic.',
          tips: ['Warning of danger only', 'Not for expressing anger', 'No horn 11pm-7am in built-up areas', 'Short taps, not long blasts'],
          regulation: 'Regulation 134' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:excessive|unnecessary) horn/i,
        response: () => {
          const off = OFFENCES.unnecessary_horn;
          return { type: 'fine', title: 'Unnecessary Horn Use', answer: `Excessive horn use is an offence. Fine: ${off?.fine || '$3,000'}.`,
            tips: ['Only use for warning', 'Not to greet friends', 'Not in traffic jams'], points: off?.points, regulation: 'Regulation 134' };
        }},
      { pattern: /(?:can i|is it legal) (?:modify|tune|change) (?:my )?(?:car|vehicle|exhaust)/i,
        response: { type: 'info', title: 'Vehicle Modifications',
          answer: 'Modifications must not make vehicle unroadworthy or excessively noisy. Some mods need certification.',
          tips: ['Must pass fitness inspection', 'Exhaust noise limits apply', 'Some mods void insurance'],
          regulation: 'Regulation 137-145' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:loud|modified|illegal) exhaust/i,
        response: () => {
          const off = OFFENCES.noisy_exhaust;
          return { type: 'fine', title: 'Loud Exhaust', answer: `Excessively loud exhaust is illegal. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Factory exhaust usually okay', 'Straight pipes illegal', 'Can fail fitness test'], points: off?.points, regulation: 'Regulation 137' };
        }},
      { pattern: /(?:can i|is it legal|how many) (?:passengers|people|overload)/i,
        response: { type: 'info', title: 'Passenger Limits',
          answer: 'You cannot carry more passengers than seatbelts. Overloading is dangerous and illegal.',
          tips: ['One person per seatbelt', 'No passengers in cargo area', 'Overloading affects braking'],
          regulation: 'Regulation 185' }},
      { pattern: /(?:what (?:happens|if)|fine for) overload/i,
        response: () => {
          const off = OFFENCES.overloading;
          return { type: 'fine', title: 'Overloading', answer: `Overloading is dangerous. Fine: ${off?.fine || '$10,000'}.`,
            tips: ['Braking distance increases', 'Tires can blow out', 'Insurance may not cover'], points: off?.points, regulation: 'Regulation 185' };
        }},
      { pattern: /(?:rules for|tips for) (?:driving at )?night/i,
        response: { type: 'info', title: 'Night Driving',
          answer: 'Use headlights sunset to sunrise. Dip for oncoming traffic. Increase following distance.',
          tips: ['Check all lights before driving', 'Dip lights for oncoming cars', 'Reduce speed', 'Take breaks on long trips'],
          regulation: 'Regulation 120-126' }},
      { pattern: /(?:when must i|should i) (?:use|turn on) (?:head)?lights/i,
        response: { type: 'info', title: 'When to Use Headlights',
          answer: 'Headlights required: sunset to sunrise, rain, fog, reduced visibility. Use dipped beam when meeting traffic.',
          tips: ['Sunset to sunrise = mandatory', 'In rain, even daytime', 'In fog or mist', 'In tunnels'],
          regulation: 'Regulation 120-123' }},
      { pattern: /(?:what is|how to use) (?:high beam|full beam|bright)/i,
        response: { type: 'info', title: 'High Beam Usage',
          answer: 'Use high beams on dark roads with no traffic. ALWAYS dip for oncoming or following vehicles.',
          tips: ['Dip within 200m of oncoming vehicle', 'Dip when following', 'Don\'t use in fog'],
          regulation: 'Regulation 122' }},
      { pattern: /(?:how to|tips for) driv(?:e|ing) in (?:the )?rain/i,
        response: { type: 'info', title: 'Driving in Rain',
          answer: 'Reduce speed, increase following distance, use headlights, avoid sudden braking.',
          tips: ['Slow down significantly', 'Double following distance', 'Turn on headlights', 'Avoid standing water', 'Don\'t use cruise control'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:what is|how to handle) (?:aquaplaning|hydroplaning|skid)/i,
        response: { type: 'info', title: 'Aquaplaning/Skidding',
          answer: 'If aquaplaning: Don\'t brake suddenly, ease off accelerator, hold steering steady, wait for grip.',
          tips: ['Don\'t panic', 'Ease off gas', 'Don\'t slam brakes', 'Steer straight'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:can i|should i) driv(?:e|ing) (?:through|in) flood/i,
        response: { type: 'warning', title: 'Driving Through Floods',
          answer: 'Do NOT drive through flooded roads. Just 6 inches can sweep a car away. Turn around.',
          tips: ['Never drive through floodwater', 'You can\'t judge depth', 'Car can be swept away', 'Find alternate route'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:rules for|how to become) (?:a )?taxi/i,
        response: { type: 'procedure', title: 'Taxi Driver Requirements',
          answer: 'Need: Valid licence (3+ years), taxi badge, PPV licence, insurance.',
          steps: ['Hold full licence 3+ years', 'Apply to Transport Authority', 'Get taxi badge', 'Get PPV licence'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:can i|is it legal) (?:operate|carry passengers) (?:as )?(?:taxi|uber|robot)/i,
        response: { type: 'warning', title: 'Illegal Taxi Operation',
          answer: 'Operating as taxi without proper licensing is illegal. Robot taxis are illegal.',
          tips: ['Need taxi badge', 'Need PPV licence', 'Illegal taxis get seized', 'Heavy fines apply'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:what age|how old) (?:to )?(?:learn|get|drive|licence)/i,
        response: { type: 'info', title: 'Driving Age Requirements',
          answer: 'Learner permit: 17 years. Full licence: 17 after tests. Commercial may require 21+.',
          tips: ['17 for learner permit', '17 for full licence', 'Commercial: 21-25 depending on class'],
          regulation: 'Regulation 6-7' }},
      { pattern: /(?:can i|is it valid) (?:use|drive with) (?:foreign|international|overseas) licen[cs]e/i,
        response: { type: 'info', title: 'Foreign Licence',
          answer: 'Foreign licences valid for 3 months from entry. International Permit extends this.',
          tips: ['Valid for 3 months as visitor', 'International Permit recommended', 'Apply for local licence if staying'],
          regulation: 'Road Traffic Act Section 21' }},
      { pattern: /(?:how to|can i) (?:convert|exchange) (?:foreign )?licen[cs]e/i,
        response: { type: 'procedure', title: 'Converting Foreign Licence',
          answer: 'Apply at ITA. Requirements vary by country.',
          steps: ['Visit ITA', 'Bring foreign licence', 'Bring passport', 'May need tests'],
          regulation: 'Road Traffic Act Section 21-22' }},
      { pattern: /(?:what is|what (?:happens|if)) road rage/i,
        response: { type: 'warning', title: 'Road Rage',
          answer: 'Road rage can result in criminal charges. Stay calm, don\'t engage.',
          tips: ['Stay calm, don\'t react', 'Lock your doors', 'Drive to police if followed', 'Report: 119'],
          regulation: 'Offences Against the Person Act' }},
      { pattern: /(?:can i|is it legal) (?:use|have) (?:a )?dash\s?cam/i,
        response: { type: 'info', title: 'Dashcam Usage',
          answer: 'Dashcams are legal. Useful for evidence. Don\'t obstruct your view.',
          tips: ['Legal and recommended', 'Position not to block view', 'Useful for insurance claims'],
          regulation: 'Generally permitted' }},
      { pattern: /(?:can i) driv(?:e|ing) (?:with|if) (?:medical|epilepsy|diabetes|vision)/i,
        response: { type: 'info', title: 'Medical Conditions & Driving',
          answer: 'Some conditions require disclosure. May need medical clearance.',
          tips: ['Disclose to licensing authority', 'May need doctor\'s letter', 'Vision test required'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:what are) (?:the )?vision (?:requirement|test)/i,
        response: { type: 'info', title: 'Vision Requirements',
          answer: 'Must read number plate from 20 meters. Glasses/contacts allowed.',
          tips: ['Eye test at ITA', '20m number plate test', 'Glasses permitted'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:what is|how to get) (?:vehicle )?fitness (?:test|certificate)/i,
        response: { type: 'procedure', title: 'Fitness Inspection',
          answer: 'Annual inspection at authorized depots. Check brakes, lights, tires, emissions.',
          steps: ['Find authorized depot', 'Book appointment', 'Ensure basics work', 'Pay fee', 'Get certificate'],
          regulation: 'Road Traffic Act Section 24' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:no|expired|without) fitness/i,
        response: () => {
          const off = OFFENCES.no_fitness;
          return { type: 'fine', title: 'No Fitness Certificate', answer: `No valid fitness is illegal. Fine: ${off?.fine || '$10,000'}.`,
            tips: ['Annual renewal required', 'Keep certificate in vehicle', 'Insurance may be void'], points: off?.points, regulation: 'Road Traffic Act Section 24' };
        }},
      { pattern: /(?:what do i need|requirements) (?:to )?rent (?:a )?car/i,
        response: { type: 'info', title: 'Car Rental Requirements',
          answer: 'Need: Valid licence (1-2 years), age 21-25+, credit card, ID/passport.',
          tips: ['Usually 21+', 'Licence held 1+ years', 'Credit card for deposit', 'Inspect car before driving'],
          regulation: 'Rental company policies' }},
      { pattern: /(?:what is|tips for) (?:the )?driving test/i,
        response: { type: 'procedure', title: 'Driving Test Tips',
          answer: 'Test assesses: vehicle control, positioning, observation, signals, maneuvers.',
          steps: ['Practice with licensed driver', 'Learn all maneuvers', 'Know Road Code', 'Check mirrors constantly', 'Signal for every maneuver'],
          regulation: 'Regulation 9-10' }},
      { pattern: /(?:what is|how to pass) (?:the )?written (?:test|exam)/i,
        response: { type: 'procedure', title: 'Written Test',
          answer: 'Covers: Road signs, traffic laws, right of way, safe driving. Study the Road Code.',
          steps: ['Get Jamaica Road Code', 'Study all road signs', 'Learn speed limits', 'Practice sample questions'],
          regulation: 'Regulation 9' }},
      { pattern: /(?:what are) (?:road )?(?:warning|regulatory) signs/i,
        response: { type: 'info', title: 'Road Sign Types',
          answer: 'REGULATORY (must obey - red/black). WARNING (caution - yellow). INFORMATION (guidance - blue/green).',
          tips: ['Red/white = must obey', 'Yellow = warning', 'Blue = information', 'Green = direction'],
          regulation: 'Regulation 195-200' }},
      { pattern: /(?:what does) stop sign (?:mean|require)/i,
        response: { type: 'info', title: 'Stop Sign',
          answer: 'COMPLETE stop required. Wheels must stop moving. Proceed only when safe.',
          tips: ['Full stop required', 'Look both ways', 'Rolling stops are illegal'],
          regulation: 'Regulation 203' }},
      { pattern: /(?:what does) yield sign (?:mean|require)/i,
        response: { type: 'info', title: 'Yield Sign',
          answer: 'Slow down and give way. Stop if necessary, but not always required.',
          tips: ['Give way to crossing traffic', 'Stop if traffic approaching', 'Different from stop sign'],
          regulation: 'Regulation 203' }},

      // ==================== BATCH 2: MORE SCENARIOS ====================
      { pattern: /(?:rules for|what are) construction zone/i,
        response: { type: 'info', title: 'Construction Zone Rules',
          answer: 'Reduced speed limits, lane changes, workers present. Fines may be doubled.',
          tips: ['Speed limits strictly enforced', 'Fines doubled', 'Watch for workers', 'Follow temporary signs'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:rules for|tips for|how to) highway (?:driving|2000)/i,
        response: { type: 'info', title: 'Highway Driving',
          answer: 'Highway 2000: 110 km/h max, 50 km/h minimum. Use left lane for overtaking. No stopping.',
          tips: ['Max: 110 km/h', 'Min: 50 km/h', 'Keep left except to overtake', 'Maintain 2+ second gap'],
          regulation: 'Regulation 229-232' }},
      { pattern: /(?:what is|fine for) (?:going )?(?:under|below|too slow)/i,
        response: () => {
          const off = OFFENCES.minimum_speed_violation;
          return { type: 'fine', title: 'Minimum Speed Violation', answer: `Driving below minimum on highways is dangerous. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Highway 2000 minimum is 50 km/h', 'Stay in left lane if slower'], points: off?.points, regulation: 'Regulation 230' };
        }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:breakdown|car broke)/i,
        response: { type: 'procedure', title: 'Vehicle Breakdown',
          answer: 'Pull off road safely, hazard lights, warning triangle, call for help.',
          steps: ['Pull off the road', 'Turn on hazards', 'Place warning triangle 45m behind', 'Stay safe', 'Call for assistance'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:do i|must i|should i) (?:have|carry) (?:warning )?triangle/i,
        response: { type: 'info', title: 'Warning Triangle',
          answer: 'Yes, carrying a warning triangle is recommended. Place 45-50m behind when stopped.',
          tips: ['Should carry one always', 'Place 45-50m behind vehicle', 'Reflective for visibility'],
          regulation: 'Regulation 166' }},
      { pattern: /(?:can) children (?:sit )?(?:in )?front/i,
        response: { type: 'info', title: 'Children in Front Seat',
          answer: 'Children under 12 should not sit in front, especially with airbags. Rear is safest.',
          tips: ['Under 12: rear seat recommended', 'Never rear-facing seat in front with airbag'],
          regulation: 'Regulation 207' }},
      { pattern: /(?:can i|allowed) leave (?:children|kids|child) (?:in )?car/i,
        response: { type: 'warning', title: 'Leaving Children in Vehicle',
          answer: 'NEVER leave children unattended. Cars heat up rapidly. This is illegal and dangerous.',
          tips: ['Never leave children alone', 'Cars heat up in minutes', 'Criminal charges possible'],
          regulation: 'Child Care and Protection Act' }},
      { pattern: /(?:can i|rules for) (?:drive with|have) (?:pets?|dogs?|animals?)/i,
        response: { type: 'info', title: 'Driving with Pets',
          answer: 'Pets should be restrained. Unrestrained animals can distract drivers.',
          tips: ['Use pet carriers or harnesses', 'Never leave pets in hot cars', 'Don\'t let dogs ride in truck beds'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:what (?:do i do|if)|hit) (?:a )?(?:dog|cat|animal|goat|cow)/i,
        response: { type: 'info', title: 'Hitting an Animal',
          answer: 'Stop if safe, report livestock to police. You may be liable for farm animals.',
          tips: ['Stop safely if possible', 'Report livestock to police', 'Try to find owner'],
          regulation: 'Road Traffic Act Section 39-41' }},
      { pattern: /(?:rules for|how to) (?:pass|near) bus/i,
        response: { type: 'info', title: 'Bus Safety',
          answer: 'Give buses room. Don\'t overtake at stops. Watch for passengers.',
          tips: ['Let buses pull out when signaling', 'Watch for passengers at bus stops', 'Keep safe distance'],
          regulation: 'Regulation 235' }},
      { pattern: /(?:rules for|how to) (?:pass|near) (?:cyclist|bicycle)/i,
        response: { type: 'info', title: 'Passing Cyclists',
          answer: 'Give at least 1 meter clearance. Wait if road too narrow. Cyclists have same rights.',
          tips: ['At least 1m clearance', 'Wait if can\'t pass safely', 'Check before opening car door'],
          regulation: 'Regulation 213' }},
      { pattern: /(?:can i|is it legal) (?:ride|cycle) (?:on )?(?:road|sidewalk)/i,
        response: { type: 'info', title: 'Bicycle Rules',
          answer: 'Bicycles use the road, not sidewalk. Must obey all traffic rules. Lights required at night.',
          tips: ['Use roads, not sidewalks', 'Obey traffic signals', 'Lights required at night', 'Helmet recommended'],
          regulation: 'Regulation 189-193' }},
      { pattern: /(?:tips for|how to) (?:walk|cross|pedestrian) (?:safely)?/i,
        response: { type: 'info', title: 'Pedestrian Safety',
          answer: 'Use crossings. Look both ways. Walk facing traffic on roads without sidewalks.',
          tips: ['Use crossings when available', 'Look right first', 'Don\'t use phone while crossing', 'Be visible at night'],
          regulation: 'Regulation 223-226' }},
      { pattern: /(?:what is|meaning of) (?:flashing) (?:lights|high beams)/i,
        response: { type: 'info', title: 'Flashing Headlights',
          answer: 'Can mean: "Go ahead", warning of hazard, or your lights are too bright.',
          tips: ['Usually means proceed', 'Could be warning', 'Thank with wave', 'Don\'t flash aggressively'],
          regulation: 'Custom/courtesy' }},
      { pattern: /(?:how (?:do|does)|explain) demerit (?:points?|system)/i,
        response: { type: 'info', title: 'Demerit Points System',
          answer: 'Points added for offences. 20 points = suspension. Points expire after 15 months.',
          tips: ['Minor: 2-4 points', 'Major: 4-6 points', '20 points = suspension', 'Points expire 15 months'],
          regulation: 'Road Traffic Regulations Part IV' }},
      { pattern: /(?:how (?:do i|to)|can i) (?:check|see) (?:my )?(?:demerit )?points/i,
        response: { type: 'info', title: 'Checking Demerit Points',
          answer: 'Check at any Island Traffic Authority office. Bring driver\'s licence.',
          tips: ['Visit ITA office', 'Bring driver\'s licence', 'Plan before reaching 20'],
          regulation: 'Road Traffic Regulations Part IV' }},
      { pattern: /(?:what (?:happens|if)|can i lose) (?:reach )?20 (?:demerit )?points/i,
        response: { type: 'warning', title: 'Reaching 20 Points',
          answer: 'At 20 points, licence automatically suspended. Must surrender licence.',
          tips: ['Automatic suspension', 'Must surrender licence', 'May need to retake tests'],
          regulation: 'Road Traffic Regulations Part IV' }},
      { pattern: /(?:what are|explain) licen[cs]e class/i,
        response: { type: 'info', title: 'Licence Classes',
          answer: 'Class 1-2: Motorcycles. Class 3-4: Cars. Class 5-6: Light commercial. Class 7-8: Heavy.',
          tips: ['Class 3: Private car', 'Class 4: Hired car', 'Can\'t drive above your class'],
          regulation: 'Regulation 6' }},
      { pattern: /(?:what (?:happens|if)|fine for) driv(?:e|ing) wrong (?:vehicle )?class/i,
        response: () => {
          const off = OFFENCES.driving_wrong_class;
          return { type: 'fine', title: 'Wrong Vehicle Class', answer: `Driving vehicle your licence doesn\'t cover is illegal. Fine: ${off?.fine || '$15,000'}.`,
            tips: ['Class 3 can\'t drive trucks', 'Insurance invalid if wrong class'], points: off?.points, regulation: 'Regulation 6' };
        }},
      { pattern: /(?:do i|must i) (?:always )?carry (?:my )?(?:licence|documents)/i,
        response: { type: 'info', title: 'Documents to Carry',
          answer: 'Always carry: Driver\'s licence, eMVRC (registration), insurance certificate.',
          tips: ['Licence required', 'eMVRC registration', 'Insurance certificate', '7 days to produce if stopped without'],
          regulation: 'Road Traffic Act Section 44' }},
      { pattern: /(?:where|how) (?:do i|can i) report (?:dangerous driver|bad driving)/i,
        response: { type: 'info', title: 'Reporting Dangerous Drivers',
          answer: 'Report to police: 119 (emergency). Provide vehicle details, plate, location.',
          tips: ['Call 119 for immediate danger', 'Note plate number', 'Describe behavior', 'Don\'t engage'],
          regulation: 'Police procedures' }},
      { pattern: /(?:what is|tell me about) (?:the )?road code/i,
        response: { type: 'info', title: 'Jamaica Road Code',
          answer: 'Official guide to road rules, signs, safe driving. Based on Road Traffic Act.',
          tips: ['Available at ITA', 'Study for written test', 'Covers all road signs'],
          regulation: 'Road Traffic Act 2022' }},
      { pattern: /(?:what (?:happens|if)|fine for) driv(?:e|ing) (?:with )?suspended licen[cs]e/i,
        response: { type: 'warning', title: 'Driving While Suspended',
          answer: 'CRIMINAL offence. Heavy fines, possible imprisonment, extended suspension.',
          tips: ['Criminal offence', 'Imprisonment possible', 'Vehicle seizure', 'Insurance void'],
          regulation: 'Road Traffic Act Section 19' }},
      { pattern: /(?:can i|allowed) eat (?:while|and) driv/i,
        response: { type: 'info', title: 'Eating While Driving',
          answer: 'Not specifically illegal but distracting. Can be careless driving if affects control.',
          tips: ['Distraction = danger', 'Pull over to eat', 'Both hands on wheel'],
          regulation: 'Road Traffic Act - Careless Driving' }},
      { pattern: /(?:can i|allowed) (?:smoke|vape) (?:while|and) driv/i,
        response: { type: 'info', title: 'Smoking While Driving',
          answer: 'Not specifically illegal but can distract. Littering butts IS illegal.',
          tips: ['Can be distraction', 'Don\'t throw butts out window'],
          regulation: 'Litter Act, Road Traffic Act' }},
      { pattern: /(?:can i|allowed) (?:wear|use) (?:headphones|earbuds)/i,
        response: { type: 'info', title: 'Headphones While Driving',
          answer: 'Both ears covered not recommended. Must hear traffic and emergencies.',
          tips: ['One earpiece only', 'Must hear sirens', 'Use car speakers instead'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:barefoot|without shoes)/i,
        response: { type: 'info', title: 'Driving Barefoot',
          answer: 'Not specifically illegal but discouraged. Proper footwear recommended.',
          tips: ['Proper shoes recommended', 'Flip-flops can slip', 'Keep spare shoes in car'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:how (?:do i|to)|where) pay (?:highway|toll)/i,
        response: { type: 'info', title: 'Toll Payment',
          answer: 'Pay at toll booths (cash/card), via T-tag, or pre-paid passes.',
          tips: ['T-tag fastest', 'Cash lanes accept JMD', 'Keep receipt'],
          regulation: 'Highway 2000 Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:not paying|evade) toll/i,
        response: { type: 'warning', title: 'Toll Evasion',
          answer: 'Evading toll is illegal. Plate recorded. Bill with penalties sent.',
          tips: ['Cameras record plate', 'Penalty charges added', 'Can affect registration'],
          regulation: 'Highway 2000 Act' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?brakes fail/i,
        response: { type: 'procedure', title: 'Brake Failure',
          answer: 'Pump brake, use handbrake gradually, downshift, use friction to stop.',
          steps: ['Don\'t panic', 'Pump brake rapidly', 'Apply handbrake slowly', 'Downshift gears', 'Aim for safe stop'],
          regulation: 'Emergency procedures' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:tire|tyre) (?:blows|bursts)/i,
        response: { type: 'procedure', title: 'Tire Blowout',
          answer: 'Don\'t brake suddenly, hold steering firmly, ease off gas, let car slow naturally.',
          steps: ['Don\'t slam brakes', 'Grip steering', 'Ease off accelerator', 'Pull off road safely'],
          regulation: 'Emergency procedures' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?stuck (?:in )?(?:mud|sand)/i,
        response: { type: 'procedure', title: 'Stuck in Mud/Sand',
          answer: 'Don\'t spin wheels. Try gentle rocking. Place mats under tires.',
          steps: ['Don\'t spin wheels', 'Try gentle rocking', 'Put mats under tires', 'Call for tow if needed'],
          regulation: 'Emergency procedures' }},
      { pattern: /(?:how often|when) (?:should i) (?:change|check) oil/i,
        response: { type: 'info', title: 'Engine Oil Change',
          answer: 'Every 3,000-5,000 km or 3-6 months. Check level weekly.',
          tips: ['Check with dipstick', 'Dark black = needs change', 'Low oil = engine damage'],
          regulation: 'Maintenance best practices' }},
      { pattern: /(?:how often|when) (?:should i) (?:check|change) (?:tyres|tires)/i,
        response: { type: 'info', title: 'Tire Maintenance',
          answer: 'Check pressure monthly. Check tread regularly (min 1.6mm). Rotate every 10,000 km.',
          tips: ['Check pressure when cold', 'Minimum tread: 1.6mm', 'Replace when worn'],
          regulation: 'Regulation 158-162' }},
      { pattern: /(?:what is|meaning of) (?:check engine|warning) light/i,
        response: { type: 'info', title: 'Warning Lights',
          answer: 'Check engine: get checked soon. Red lights (oil, temp, brakes): stop immediately.',
          tips: ['Yellow = check soon', 'Red = stop immediately', 'Get diagnostic scan'],
          regulation: 'Vehicle safety standards' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:tailgating|following too close)/i,
        response: () => {
          const off = OFFENCES.tailgating;
          return { type: 'fine', title: 'Tailgating', answer: `Following too closely is dangerous. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['2-second rule minimum', 'Increase in rain', 'Causes rear-end crashes'], points: off?.points, regulation: 'Regulation 212' };
        }},
      { pattern: /(?:what is|explain) (?:the )?(?:2|two).?second rule/i,
        response: { type: 'info', title: 'Two-Second Rule',
          answer: 'Maintain at least 2 seconds behind the vehicle ahead. Double in rain.',
          tips: ['Pick a fixed point', 'Count seconds as car passes', 'If less than 2, drop back', 'Increase in bad conditions'],
          regulation: 'Regulation 212' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:driving )?(?:with)?(?:out|no) mirrors/i,
        response: () => {
          const off = OFFENCES.no_mirrors;
          return { type: 'fine', title: 'No/Broken Mirrors', answer: `Driving without proper mirrors is dangerous. Fine: ${off?.fine || '$6,000'}.`,
            tips: ['Must have rearview mirror', 'Side mirrors required', 'Fix broken mirrors immediately'], points: off?.points, regulation: 'Regulation 142' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:driving )?(?:with)?(?:out|no) (?:wind)?(?:screen )?wipers/i,
        response: () => {
          const off = OFFENCES.no_wipers;
          return { type: 'fine', title: 'No/Broken Wipers', answer: `Wipers must work for visibility. Fine: ${off?.fine || '$4,000'}.`,
            tips: ['Wipers must work', 'Replace worn blades', 'Essential in rain'], points: off?.points, regulation: 'Regulation 144' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:bald|worn|smooth) (?:tyres?|tires?)/i,
        response: () => {
          const off = OFFENCES.bald_tires;
          return { type: 'fine', title: 'Bald/Worn Tires', answer: `Bald tires are dangerous. Fine: ${off?.fine || '$6,000'}. Minimum tread: 1.6mm.`,
            tips: ['Minimum tread 1.6mm', 'Poor grip in rain', 'Can blow out', 'Check regularly'], points: off?.points, regulation: 'Regulation 158-162' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:defective|broken|faulty) brakes/i,
        response: () => {
          const off = OFFENCES.defective_brakes;
          return { type: 'fine', title: 'Defective Brakes', answer: `Faulty brakes are extremely dangerous. Fine: ${off?.fine || '$10,000'}. Don\'t drive.`,
            tips: ['Don\'t drive with bad brakes', 'Listen for squealing', 'Check brake fluid', 'Service regularly'], points: off?.points, regulation: 'Regulation 163-165' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:dangerous|unroadworthy) vehicle/i,
        response: () => {
          const off = OFFENCES.unroadworthy_vehicle;
          return { type: 'fine', title: 'Unroadworthy Vehicle', answer: `Driving unroadworthy vehicle is illegal. Fine: ${off?.fine || '$10,000'}. Vehicle can be taken off road.`,
            tips: ['Must pass fitness', 'Regular maintenance', 'Fix defects immediately'], points: off?.points, regulation: 'Road Traffic Act Section 24' };
        }},
      { pattern: /(?:can i|is it legal) (?:use|have) (?:roof )?rack/i,
        response: { type: 'info', title: 'Roof Rack Usage',
          answer: 'Roof racks legal if properly secured. Load must not exceed vehicle limits.',
          tips: ['Secure all items', 'Don\'t exceed weight limit', 'Check regularly while driving', 'Items can fly off'],
          regulation: 'Regulation 185' }},
      { pattern: /(?:can i|is it legal) (?:tow|pull) (?:a )?(?:trailer|caravan)/i,
        response: { type: 'info', title: 'Towing Trailers',
          answer: 'Need proper tow bar, lights connected, weight within limits. May need trailer licence.',
          tips: ['Proper tow bar required', 'Trailer lights must work', 'Don\'t exceed tow weight', 'Speed limits lower when towing'],
          regulation: 'Regulation 168-170' }},
      { pattern: /(?:what is|fine for) (?:driving )?(?:without )?(?:road )?tax/i,
        response: { type: 'info', title: 'Road Tax',
          answer: 'Road tax (motor vehicle licence) must be current. Displayed on eMVRC.',
          tips: ['Part of registration fee', 'Renew annually', 'Included in fitness renewal'],
          regulation: 'Motor Vehicles Registration Act' }},
      { pattern: /(?:how (?:do i|to)|what is) (?:emvrc|electronic registration)/i,
        response: { type: 'info', title: 'eMVRC Explained',
          answer: 'Electronic Motor Vehicle Registration Certificate. Replaces old paper certificate. Available online.',
          tips: ['Download from cgds.gov.jm', 'Keep copy in vehicle', 'Shows registration status'],
          regulation: 'Motor Vehicles Registration Act' }},

      // ==================== BATCH 3: ADDITIONAL SCENARIOS ====================
      { pattern: /smok(?:e|ing) (?:and |while )?driv/i,
        response: () => {
          const off = OFFENCES.drug_driving;
          return { type: 'fine', title: 'Smoking and Driving', answer: `Smoking while driving can impair your ability and is dangerous. If smoking drugs like ganja, it's treated as DUI. Fine: ${off?.fine || 'Up to $500,000'}.`,
            tips: ['Ganja impairs driving', 'Same penalties as alcohol DUI', 'Cigarette smoke can also distract'], points: off?.points, regulation: 'Road Traffic Act Section 44' };
        }},
      { pattern: /driv(?:e|ing) (?:while )?(?:high|stoned|blazed)/i,
        response: () => {
          const off = OFFENCES.drug_driving;
          return { type: 'fine', title: 'Driving While High', answer: `Driving while high/impaired by drugs (including ganja) is as serious as DUI. Fine: ${off?.fine || 'Up to $500,000'}. Imprisonment possible.`,
            tips: ['Ganja impairs driving ability', 'Blood/urine test can be required', 'Same penalties as alcohol DUI', 'Licence suspension'], points: off?.points, regulation: 'Road Traffic Act Section 44' };
        }},
      { pattern: /(?:smoke|ganja|weed|marijuana|high|drugs?) (?:and |while )?driv/i,
        response: () => {
          const off = OFFENCES.drug_driving;
          return { type: 'fine', title: 'Driving Under Influence of Drugs', answer: `Driving while impaired by drugs (including ganja/marijuana) is as serious as DUI. Fine: ${off?.fine || 'Up to $500,000'}. Imprisonment possible.`,
            tips: ['Includes ganja/marijuana', 'Includes prescription drugs that impair', 'Blood/urine test can be required', 'Same penalties as alcohol DUI', 'Licence suspension'], points: off?.points, regulation: 'Road Traffic Act Section 44' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for|can i).*(?:drug|drugs|ganja|marijuana|weed|high).*driv/i,
        response: () => {
          const off = OFFENCES.drug_driving;
          return { type: 'fine', title: 'Driving Under Influence of Drugs', answer: `Driving while impaired by drugs (including ganja/marijuana) is as serious as DUI. Fine: ${off?.fine || 'Up to $500,000'}. Imprisonment possible.`,
            tips: ['Includes ganja/marijuana', 'Includes prescription drugs that impair', 'Blood/urine test can be required', 'Same penalties as alcohol DUI', 'Licence suspension'], points: off?.points, regulation: 'Road Traffic Act Section 44' };
        }},
      { pattern: /(?:can )?police (?:do|perform|conduct) (?:drug|saliva) test/i,
        response: { type: 'info', title: 'Roadside Drug Testing',
          answer: 'Police can request a drug test if they suspect impairment. Refusing is an offence.',
          tips: ['Observation tests for impairment', 'Can request blood test', 'Refusal = same as positive test'],
          regulation: 'Road Traffic Act Section 44A' }},
      { pattern: /(?:can i|allowed) (?:have|carry|transport) (?:alcohol|liquor|beer|rum) in (?:car|vehicle)/i,
        response: { type: 'info', title: 'Transporting Alcohol',
          answer: 'You can transport sealed alcohol. Open containers in driver reach area are problematic.',
          tips: ['Keep in trunk/boot', 'Must be sealed', 'Don\'t drink while driving', 'Passengers drinking is risky'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:no|without) motorcycle licen[cs]e/i,
        response: () => {
          const off = OFFENCES.no_licence;
          return { type: 'fine', title: 'Riding Without Motorcycle Licence', answer: `Riding without proper Class 1 or 2 licence is illegal. Fine: ${off?.fine || '$15,000'}.`,
            tips: ['Need Class 1 (under 125cc) or Class 2', 'Car licence doesn\'t cover bikes', 'Must take motorcycle test'], points: off?.points, regulation: 'Regulation 6' };
        }},
      { pattern: /(?:can i|is it legal|allowed) (?:ride|carry) (?:on )?(?:back|pillion) (?:of )?motorcycle/i,
        response: { type: 'info', title: 'Motorcycle Pillion Riding',
          answer: 'Passengers allowed on motorcycles with proper pillion seat and footrests. Must wear helmet.',
          tips: ['Proper seat required', 'Footrests for passenger', 'Helmet mandatory', 'One passenger only'],
          regulation: 'Regulation 186-188' }},
      { pattern: /(?:can i|allowed) (?:carry|transport) (?:goods|cargo|load) (?:on )?motorcycle/i,
        response: { type: 'info', title: 'Carrying Goods on Motorcycle',
          answer: 'Can carry goods if properly secured and doesn\'t affect control or stability.',
          tips: ['Must be secured', 'Not affect balance', 'Don\'t obstruct vision', 'Weight limits apply'],
          regulation: 'Regulation 185' }},
      { pattern: /(?:what is|need) (?:protective )?gear (?:for )?motorcycle/i,
        response: { type: 'info', title: 'Motorcycle Protective Gear',
          answer: 'Helmet is mandatory. Gloves, jacket, boots, and pants recommended for protection.',
          tips: ['Helmet: MANDATORY', 'Gloves protect hands', 'Boots cover ankles', 'Jacket and pants prevent road rash'],
          regulation: 'Regulation 186' }},
      { pattern: /(?:rules for|can i drive) (?:commercial|truck|lorry|heavy) vehicle/i,
        response: { type: 'info', title: 'Commercial Vehicle Requirements',
          answer: 'Commercial vehicles need appropriate licence class (5-8), may need special permits, log books.',
          tips: ['Class 5-6: Light commercial', 'Class 7-8: Heavy vehicles', 'May need operator\'s licence', 'Weight limits enforced'],
          regulation: 'Regulation 6, Transport Authority Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:overweight|overloaded) (?:truck|lorry|vehicle)/i,
        response: () => {
          const off = OFFENCES.overloading_goods;
          return { type: 'fine', title: 'Overweight Vehicle', answer: `Overloading commercial vehicles is serious. Fine: ${off?.fine || '$20,000'}. Can damage roads.`,
            tips: ['Weigh stations check loads', 'Damages roads', 'Can be ordered off road', 'Insurance may not cover'], points: off?.points, regulation: 'Regulation 185' };
        }},
      { pattern: /(?:rules for|can i) (?:tow|towing) (?:another )?(?:car|vehicle)/i,
        response: { type: 'info', title: 'Towing Another Vehicle',
          answer: 'Towing with rope/chain: Max 4.5m length, attach warning flag, limit speed.',
          tips: ['Use proper tow bar if possible', 'Max 4.5m between vehicles', 'Red flag/light on rope', 'Driver needed in towed vehicle usually'],
          regulation: 'Regulation 168-170' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:illegal|improper) towing/i,
        response: { type: 'fine', title: 'Improper Towing',
          answer: 'Improper towing is dangerous and illegal. Fine: $6,000+.',
          tips: ['Use proper equipment', 'Signal towing', 'Keep safe speed'],
          regulation: 'Regulation 168-170' }},
      { pattern: /(?:rules for|what (?:happens|if)) (?:tourist|visitor) driv(?:e|ing)/i,
        response: { type: 'info', title: 'Tourist/Visitor Driving',
          answer: 'Tourists can drive with foreign licence for 3 months. International permit extends this.',
          tips: ['Valid foreign licence okay for 3 months', 'International Driving Permit recommended', 'Drive on LEFT in Jamaica', 'Know local speed limits'],
          regulation: 'Road Traffic Act Section 21' }},
      { pattern: /(?:do )?(?:tourist|visitor|foreigner) (?:need|require) (?:international|local) (?:permit|licence)/i,
        response: { type: 'info', title: 'International Driving Permit',
          answer: 'International Driving Permit recommended for visitors. Valid with original licence.',
          tips: ['Get IDP before arriving', 'Must carry original licence too', 'Valid for 12 months', 'Helps with car rental'],
          regulation: 'Road Traffic Act Section 21' }},
      { pattern: /(?:rules for|how to become) driving (?:instructor|school)/i,
        response: { type: 'procedure', title: 'Driving Instructor Requirements',
          answer: 'Must be 25+, held full licence 5+ years, pass instructor test, registered with ITA.',
          steps: ['Age 25 minimum', 'Hold full licence 5+ years', 'Pass driving instructor test', 'Register with ITA'],
          regulation: 'Regulation 11-14' }},
      { pattern: /(?:can i|allowed) (?:teach|instruct) (?:someone to )?driv/i,
        response: { type: 'info', title: 'Teaching Someone to Drive',
          answer: 'To supervise a learner: Hold full licence 3+ years, sit in front passenger seat.',
          tips: ['Full licence 3+ years', 'Sit in front passenger seat', 'Learner must display L plates', 'You\'re responsible for learner'],
          regulation: 'Regulation 15-17' }},
      { pattern: /(?:fine for|what (?:happens|if)) (?:taxi|ppv) overcharg/i,
        response: () => {
          const off = OFFENCES.taxi_overcharging;
          return { type: 'fine', title: 'Taxi Overcharging', answer: `Overcharging passengers is illegal. Fine: ${off?.fine || '$10,000'}.`,
            tips: ['Agree fare before journey', 'Report to Transport Authority', 'Use metered taxis where available'], points: off?.points, regulation: 'Transport Authority Act' };
        }},
      { pattern: /(?:fine for|what (?:happens|if)) (?:taxi|ppv) (?:refus|reject) (?:fare|passenger)/i,
        response: () => {
          const off = OFFENCES.refusing_fare;
          return { type: 'fine', title: 'Refusing a Fare', answer: `Licensed taxis cannot refuse reasonable fares. Fine: ${off?.fine || '$8,000'}.`,
            tips: ['Must accept reasonable fares', 'Cannot discriminate', 'Report violations'], points: off?.points, regulation: 'Transport Authority Act' };
        }},
      { pattern: /(?:what is|fine for) (?:pirating|robot|illegal taxi)/i,
        response: () => {
          const off = OFFENCES.pirating;
          return { type: 'fine', title: 'Pirating/Robot Taxi', answer: `Operating as taxi without licence is serious. Fine: ${off?.fine || '$100,000'}. Vehicle seized.`,
            tips: ['Need taxi badge and PPV licence', 'Vehicle can be seized', 'Criminal charges possible', 'Insurance void'], points: off?.points, regulation: 'Transport Authority Act' };
        }},
      { pattern: /(?:can i|allowed) (?:use|operate) ride.?(?:share|hail)|uber|lyft/i,
        response: { type: 'info', title: 'Ride-Sharing Services',
          answer: 'Ride-sharing operates in legal grey area. Proper licensing is recommended.',
          tips: ['May need PPV licence', 'Insurance requirements apply', 'Check Transport Authority rules'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:car )?(?:catch|on) fire/i,
        response: { type: 'procedure', title: 'Vehicle Fire',
          answer: 'Stop immediately, turn off engine, evacuate, call fire service (110), don\'t open hood.',
          steps: ['Stop and turn off engine', 'Evacuate immediately', 'Move away from vehicle', 'Call 110', 'Don\'t try to put out large fires'],
          regulation: 'Emergency procedures' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:hit|accident) (?:with )?(?:parked )?(?:car|vehicle)/i,
        response: { type: 'procedure', title: 'Hitting a Parked Vehicle',
          answer: 'You MUST stop and leave your details. Leaving is hit and run.',
          steps: ['Stop immediately', 'Leave a note with your details', 'Take photos of damage', 'Try to find owner', 'Report to police if significant'],
          regulation: 'Road Traffic Act Section 39-41' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:hit|accident) (?:property|fence|wall|gate)/i,
        response: { type: 'procedure', title: 'Hitting Property',
          answer: 'You must stop, try to find owner, leave details, and report to police.',
          steps: ['Stop immediately', 'Locate property owner', 'Leave contact details', 'Take photos', 'Report to police'],
          regulation: 'Road Traffic Act Section 39-41' }},
      { pattern: /(?:what (?:do i do|should i)|how to) (?:if )?(?:witness|see) (?:an )?accident/i,
        response: { type: 'procedure', title: 'Witnessing an Accident',
          answer: 'Stop if safe, call emergency services if needed, offer to be witness, don\'t obstruct.',
          steps: ['Stop safely if you can help', 'Call 119 for police, 110 for ambulance', 'Give first aid if trained', 'Provide witness statement', 'Don\'t block the scene'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:what is|how does) (?:insurance )?(?:claim|claims) (?:process|work)/i,
        response: { type: 'procedure', title: 'Insurance Claim Process',
          answer: 'Report accident within 24-48 hours, provide documentation, get claim number.',
          steps: ['Report to insurer immediately', 'Get police report if applicable', 'Provide photos and details', 'Get claim number', 'Follow adjuster instructions'],
          regulation: 'Insurance policy terms' }},
      { pattern: /(?:do i|should i) (?:admit|say) (?:fault|guilty|sorry)/i,
        response: { type: 'warning', title: 'Admitting Fault at Scene',
          answer: 'Do NOT admit fault at the accident scene. Let insurance and authorities determine liability.',
          tips: ['Never say "it was my fault"', 'Exchange information only', 'Let insurance determine fault', 'Be factual, not apologetic'],
          regulation: 'Insurance best practices' }},
      { pattern: /(?:what (?:is|are)) (?:third.?party|comprehensive|full) (?:insurance|coverage)/i,
        response: { type: 'info', title: 'Types of Motor Insurance',
          answer: 'Third-party: Covers damage to others (mandatory). Comprehensive: Also covers your vehicle, theft, fire.',
          tips: ['Third-party is minimum legal requirement', 'Comprehensive costs more but covers more', 'Consider comprehensive for newer cars'],
          regulation: 'Motor Vehicles Insurance Act' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:without|no) (?:windscreen|windshield)/i,
        response: { type: 'warning', title: 'Driving Without Windscreen',
          answer: 'A windscreen is required. Driving without one is dangerous and illegal.',
          tips: ['Windscreen must be intact', 'Cracks may fail fitness', 'Replace immediately if broken'],
          regulation: 'Regulation 144' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:cracked|broken|damaged) windscreen/i,
        response: { type: 'info', title: 'Damaged Windscreen',
          answer: 'Driving with severely cracked windscreen can be offence if it obstructs view. Fine possible.',
          tips: ['Small chips can be repaired', 'Large cracks need replacement', 'May fail fitness inspection'],
          regulation: 'Regulation 144' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:obstructed|blocked) (?:view|vision|windscreen)/i,
        response: { type: 'fine', title: 'Obstructed View',
          answer: 'Items obstructing driver\'s view are illegal. Fine: $5,000+.',
          tips: ['No items hanging from mirror', 'Dashboard clear', 'No stickers blocking view'],
          regulation: 'Regulation 144, 207' }},
      { pattern: /(?:can i|allowed) (?:hang|put) (?:items?|things?) (?:on|from) (?:mirror|dash)/i,
        response: { type: 'info', title: 'Items on Mirror/Dashboard',
          answer: 'Items should not obstruct driver\'s view. Large decorations are problematic.',
          tips: ['Small items generally okay', 'Nothing blocking view', 'Air fresheners usually fine'],
          regulation: 'Regulation 144' }},
      { pattern: /(?:what is|fine for) (?:emission|exhaust|pollution) (?:test|fail|standards)/i,
        response: { type: 'info', title: 'Emission Standards',
          answer: 'Vehicles must pass emission tests during fitness inspection. Excessive smoke is an offence.',
          tips: ['Checked during fitness', 'Tune engine regularly', 'Smoke = fail inspection'],
          regulation: 'Road Traffic Act, Environmental regulations' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:excessive|black|too much) smoke/i,
        response: { type: 'fine', title: 'Excessive Vehicle Smoke',
          answer: 'Excessive exhaust smoke is an offence. Fine: $6,000. Will fail fitness.',
          tips: ['Get engine serviced', 'Check for burning oil', 'May need repairs'],
          regulation: 'Environmental regulations' }},
      { pattern: /(?:can i|allowed) (?:drive|use) (?:atv|quad|off.?road) (?:on )?(?:road|street)/i,
        response: { type: 'warning', title: 'ATV/Quad on Public Roads',
          answer: 'ATVs and off-road vehicles generally cannot be driven on public roads unless registered.',
          tips: ['Not designed for road use', 'Need registration if road legal', 'Insurance required'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:can i|allowed) (?:drive|use) (?:golf cart|buggy) (?:on )?(?:road|street)/i,
        response: { type: 'info', title: 'Golf Carts on Roads',
          answer: 'Golf carts are not generally permitted on public roads unless in designated areas.',
          tips: ['Usually for private property only', 'Some resort areas allow', 'Check local regulations'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:can i|allowed) (?:drive|use) (?:electric )?scooter/i,
        response: { type: 'info', title: 'Electric Scooters',
          answer: 'Electric scooters may need to comply with vehicle regulations depending on power and speed.',
          tips: ['Check classification', 'May need registration', 'Safety gear recommended'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:what is|need) (?:to )?(?:drive|operate) (?:bus|minibus|coaster)/i,
        response: { type: 'info', title: 'Bus/Minibus Requirements',
          answer: 'Need Class 6-8 licence, PPV licence for passengers, and operator permit.',
          tips: ['Higher licence class needed', 'PPV licence for passengers', 'Medical fitness required'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:can i|allowed) (?:drive|operate) (?:with|having) (?:glasses|spectacles|contacts)/i,
        response: { type: 'info', title: 'Driving with Glasses',
          answer: 'Yes, if you need glasses to meet vision requirements, you must wear them while driving.',
          tips: ['Licence may be endorsed', 'Must wear while driving', 'Keep spare pair available'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:if|with) (?:colorblind|colour blind)/i,
        response: { type: 'info', title: 'Colour Blindness and Driving',
          answer: 'Most colour blind people can drive. Traffic lights have position indicators too.',
          tips: ['Usually not disqualifying', 'Top/middle/bottom for lights', 'Disclose at licence application'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:if|with) (?:one eye|monocular|blind in one)/i,
        response: { type: 'info', title: 'Driving with One Eye',
          answer: 'May be allowed with medical clearance. Vision in remaining eye must meet standards.',
          tips: ['Need medical clearance', 'Good vision in other eye', 'Adaptation period needed'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:if|after|with) (?:stroke|heart attack|surgery)/i,
        response: { type: 'info', title: 'Driving After Medical Event',
          answer: 'Waiting periods may apply after major medical events. Need doctor clearance.',
          tips: ['Consult your doctor', 'May need waiting period', 'Report to licensing authority if required'],
          regulation: 'Regulation 7' }},
      { pattern: /(?:how (?:do i|to)|where) (?:get|find) (?:police|accident) report/i,
        response: { type: 'procedure', title: 'Getting an Accident Report',
          answer: 'Request from the police station where the report was filed. Pay nominal fee.',
          steps: ['Go to the police station', 'Request accident report', 'Provide date and details', 'Pay fee', 'Wait for processing'],
          regulation: 'Police procedures' }},
      { pattern: /(?:what is|how long) (?:to )?(?:wait|processing) (?:for )?(?:licence|registration)/i,
        response: { type: 'info', title: 'Processing Times',
          answer: 'Licence: Same day to 2 weeks. Registration: 1-2 weeks. eMVRC: Immediate.',
          tips: ['Bring all documents first time', 'Check online for status', 'Processing times vary'],
          regulation: 'ITA/TAJ procedures' }},
      { pattern: /(?:can i|how (?:do i|to)) replace (?:lost|stolen|damaged) licen[cs]e/i,
        response: { type: 'procedure', title: 'Replacing Lost Licence',
          answer: 'Visit ITA office with ID, report loss, pay replacement fee.',
          steps: ['Report to police if stolen', 'Visit ITA office', 'Bring valid ID and TRN', 'Pay replacement fee (~$3,500)', 'Get new licence'],
          regulation: 'Regulation 8' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:appeal|challenge) (?:licence )?suspension/i,
        response: { type: 'procedure', title: 'Appealing Licence Suspension',
          answer: 'Apply to the court that issued suspension or the Transport Authority depending on reason.',
          steps: ['Understand reason for suspension', 'Consult lawyer', 'File appeal with court/authority', 'Present your case', 'Await decision'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:how (?:do i|to)|can i) (?:reduce|remove) demerit points/i,
        response: { type: 'info', title: 'Reducing Demerit Points',
          answer: 'Points automatically expire after 15 months from offence date. No early removal.',
          tips: ['Drive carefully to avoid new points', 'Points expire automatically at 15 months', 'No training courses to reduce points currently'],
          regulation: 'Road Traffic Regulations Part IV' }},
      { pattern: /(?:what (?:happens|if)) (?:court|magistrate) (?:date|summons|trial)/i,
        response: { type: 'procedure', title: 'Traffic Court Appearance',
          answer: 'Attend on date specified. Dress respectfully, bring all documents, be on time.',
          steps: ['Appear on scheduled date', 'Dress formally', 'Bring all relevant documents', 'Be respectful to court', 'Present your case or accept plea'],
          regulation: 'Parish Court procedures' }},
      { pattern: /(?:what is|meaning of) (?:parish|traffic|resident magistrate) court/i,
        response: { type: 'info', title: 'Traffic Courts',
          answer: 'Traffic matters are heard in Parish Courts (formerly Resident Magistrate Courts).',
          tips: ['Located in each parish', 'Minor matters: fixed penalty or court', 'Serious matters: mandatory court'],
          regulation: 'Judicature (Parish Courts) Act' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:hire|get|find) (?:a )?(?:traffic )?lawyer/i,
        response: { type: 'info', title: 'Finding a Traffic Lawyer',
          answer: 'Contact Jamaica Bar Association for referrals or search legal directories.',
          tips: ['Bar Association has lawyer list', 'Many lawyers specialize in traffic', 'Legal Aid for those who can\'t afford'],
          regulation: 'General legal practice' }},
      { pattern: /(?:what is) legal aid|(?:can i get) free lawyer/i,
        response: { type: 'info', title: 'Legal Aid',
          answer: 'Legal Aid Council provides free/low-cost legal help for those who qualify financially.',
          tips: ['Based on income', 'Call: 876-926-4081', 'Apply at Legal Aid Council offices'],
          regulation: 'Legal Aid Act' }},

      // ==================== BATCH 4: FINAL SCENARIOS ====================
      { pattern: /(?:what is|how (?:do i|to)) (?:pay|settle) (?:parking )?(?:ticket|fine) online/i,
        response: { type: 'procedure', title: 'Paying Fines Online',
          answer: 'Pay traffic fines online at jamaicatax.gov.jm using your ticket number.',
          steps: ['Go to jamaicatax.gov.jm', 'Select traffic fines', 'Enter ticket number', 'Pay via card or online banking', 'Save receipt'],
          regulation: 'TAJ procedures' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:fake|counterfeit|false) (?:licence|documents?|insurance)/i,
        response: { type: 'warning', title: 'Fake Documents',
          answer: 'Using fake documents is a CRIMINAL offence. Imprisonment possible. Vehicle seized.',
          tips: ['Forgery is a crime', 'Immediate arrest likely', 'Vehicle impounded', 'Serious penalties'],
          regulation: 'Forgery Act, Road Traffic Act' }},
      { pattern: /(?:can )?police (?:take|confiscate|seize) (?:my )?(?:licence|documents)/i,
        response: { type: 'info', title: 'Police Confiscating Documents',
          answer: 'Police can seize documents if they suspect they are fake, invalid, or for evidence.',
          tips: ['Get receipt for seized items', 'Note officer details', 'May be returned if valid'],
          regulation: 'Road Traffic Act, Constabulary Force Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:driving|drove) (?:while )?(?:disqualified|banned|suspended)/i,
        response: () => {
          const off = OFFENCES.driving_while_disqualified;
          return { type: 'fine', title: 'Driving While Disqualified', answer: `Driving while disqualified is CRIMINAL. Fine: ${off?.fine || '$100,000+'}. Imprisonment likely.`,
            tips: ['Can be arrested immediately', 'Vehicle seized', 'Imprisonment up to 12 months', 'Extended ban'], points: off?.points, regulation: 'Road Traffic Act Section 19' };
        }},
      { pattern: /(?:what is|how does) (?:fixed penalty|ticket) (?:system )?work/i,
        response: { type: 'info', title: 'Fixed Penalty System',
          answer: 'Fixed penalties allow payment without court appearance. Pay within 14 days or face court.',
          tips: ['Listed offences have fixed amounts', 'Pay to avoid court', 'Not paying means court summons', 'Right to contest in court always exists'],
          regulation: 'Road Traffic Act Fifth Schedule' }},
      { pattern: /(?:what is) (?:a )?court summons/i,
        response: { type: 'info', title: 'Court Summons',
          answer: 'A legal document requiring you to appear in court. Failure to appear can lead to arrest warrant.',
          tips: ['Must attend on date shown', 'Can hire lawyer', 'Bring all relevant documents', 'Dress appropriately'],
          regulation: 'Parish Court procedures' }},
      { pattern: /(?:what (?:happens|if)|can i) (?:miss|skip|ignore) court (?:date|summons)/i,
        response: { type: 'warning', title: 'Missing Court Date',
          answer: 'Missing a court date can result in an arrest warrant. Always attend or apply for postponement.',
          tips: ['Warrant can be issued', 'Apply for adjournment if needed', 'Lawyer can appear for minor matters', 'Don\'t ignore it'],
          regulation: 'Parish Court procedures' }},
      { pattern: /(?:what is|meaning of) (?:warrant|arrest warrant)/i,
        response: { type: 'warning', title: 'Arrest Warrant',
          answer: 'An arrest warrant authorizes police to arrest you. Often issued for unpaid fines or missed court dates.',
          tips: ['Clear outstanding warrants', 'Surrender voluntarily', 'Can be arrested at any time', 'Affects travel'],
          regulation: 'Justice system' }},
      { pattern: /(?:can i|am i allowed) (?:travel|leave (?:the )?country) (?:with )?(?:pending|unpaid) (?:tickets?|fines?|cases?)/i,
        response: { type: 'info', title: 'Travel with Pending Matters',
          answer: 'Unpaid fines may not stop travel, but warrants can. Clear matters before traveling.',
          tips: ['Warrants can stop you at airport', 'Pay fines before traveling', 'Check for any holds'],
          regulation: 'Immigration procedures' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:fail|failing) (?:to )?stop (?:for )?police/i,
        response: { type: 'warning', title: 'Failing to Stop for Police',
          answer: 'Failing to stop when signaled by police is serious. Can result in pursuit and additional charges.',
          tips: ['Always stop when signaled', 'Pull over safely', 'Evasion = more charges', 'Dangerous pursuit can occur'],
          regulation: 'Road Traffic Act, Constabulary Force Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:run|running|evad|fled) (?:from )?police/i,
        response: { type: 'warning', title: 'Fleeing from Police',
          answer: 'Evading police is a serious criminal offence. You will be pursued and face additional charges.',
          tips: ['NEVER run from police', 'Additional criminal charges', 'Dangerous to you and public', 'Will make situation worse'],
          regulation: 'Road Traffic Act, Criminal Law' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:report|complain about) (?:a )?police (?:officer|stop|misconduct)/i,
        response: { type: 'procedure', title: 'Reporting Police Misconduct',
          answer: 'Report to INDECOM (Independent Commission of Investigations) or Police Civilian Oversight Authority.',
          steps: ['Note officer\'s name and badge number', 'Document everything', 'Contact INDECOM: 876-968-8871', 'Or file with PCOA', 'Keep copies of complaint'],
          regulation: 'INDECOM Act' }},
      { pattern: /(?:what is) (?:indecom|independent commission)/i,
        response: { type: 'info', title: 'INDECOM',
          answer: 'Independent Commission of Investigations - investigates actions by security forces including police.',
          tips: ['Reports police misconduct here', 'Independent body', 'Call: 876-968-8871'],
          regulation: 'INDECOM Act' }},
      { pattern: /(?:can )?police (?:detain|hold|arrest) (?:me )?(?:for )?(?:traffic|driving)/i,
        response: { type: 'info', title: 'Arrest for Traffic Offences',
          answer: 'For minor traffic offences, usually ticket only. Serious offences (DUI, dangerous driving) can lead to arrest.',
          tips: ['Minor: Ticket issued', 'Serious: Can be arrested', 'Warrants: Immediate arrest', 'Stay calm and cooperate'],
          regulation: 'Road Traffic Act, Constabulary Force Act' }},
      { pattern: /(?:what (?:is|are)) (?:my )?rights (?:if|when) arrested/i,
        response: { type: 'rights', title: 'Rights When Arrested',
          answer: 'Right to know why, right to remain silent, right to a lawyer, right to inform someone.',
          tips: ['Know the reason for arrest', 'Right to remain silent', 'Request a lawyer', 'Can inform family member', 'Don\'t resist'],
          regulation: 'Constitution of Jamaica' }},
      { pattern: /(?:how long) (?:can )?police (?:hold|detain|keep) (?:me|you)/i,
        response: { type: 'info', title: 'Police Detention Time',
          answer: 'For minor matters, usually processed quickly. For serious matters, up to 24-48 hours before court.',
          tips: ['Minor offences: quick release on bail', 'Serious: 24-48 hours', 'Must be brought before court promptly', 'Right to lawyer'],
          regulation: 'Constitution, Criminal Procedure' }},
      { pattern: /(?:what is|how does) (?:bail|station bail) work/i,
        response: { type: 'info', title: 'Bail Procedures',
          answer: 'Bail allows release from custody pending court appearance. May require money or surety.',
          tips: ['Station bail for minor offences', 'Court bail for serious', 'Cash or surety may be needed', 'Conditions may apply'],
          regulation: 'Bail Act' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:refuse|decline) (?:breath|sobriety|field) test/i,
        response: () => {
          const off = OFFENCES.refusing_breath_test;
          return { type: 'fine', title: 'Refusing Sobriety Test', answer: `Refusing roadside tests is itself an offence equal to DUI. Fine: ${off?.fine || 'Up to $500,000'}.`,
            tips: ['Refusal = treated as positive', 'Same penalties as DUI', 'Can request blood test instead', 'Better to comply'], points: off?.points, regulation: 'Road Traffic Act Section 44A' };
        }},
      { pattern: /(?:what is|difference between) (?:blood|breath) (?:test|alcohol)/i,
        response: { type: 'info', title: 'Blood vs Breath Test',
          answer: 'Breath test is roadside using breathalyzer. Blood test is at hospital, more accurate.',
          tips: ['Breath: Immediate roadside', 'Blood: At hospital', 'Can request blood if disagree with breath', 'Results used in court'],
          regulation: 'Road Traffic Act Section 44A' }},
      { pattern: /(?:can i|allowed) (?:record|video|film) (?:police|traffic stop)/i,
        response: { type: 'info', title: 'Recording Police',
          answer: 'Recording police interactions is generally allowed. Be respectful and don\'t obstruct.',
          tips: ['Keep phone visible', 'Don\'t obstruct officers', 'Stay calm', 'Announce you are recording', 'Recording is your right'],
          regulation: 'Constitution of Jamaica' }},
      { pattern: /(?:can )?(?:a )?(?:police|officer) (?:stop|pull over) (?:me )?(?:for )?(?:no reason|any reason)/i,
        response: { type: 'info', title: 'Police Stop Authority',
          answer: 'Police can conduct routine traffic stops but should have a reason. You can ask why you were stopped.',
          tips: ['Random checks are allowed', 'You can ask the reason', 'Document if no valid reason given', 'Cooperate but know your rights'],
          regulation: 'Road Traffic Act, Constabulary Force Act' }},
      { pattern: /(?:what is) (?:a )?roadblock|(?:rules for) checkpoint/i,
        response: { type: 'info', title: 'Police Checkpoints',
          answer: 'Police can set up checkpoints to check documents, sobriety, and vehicle condition.',
          tips: ['Must stop at checkpoints', 'Have documents ready', 'Sobriety may be checked', 'Routine procedure'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:can i|allowed) (?:have|carry|transport) (?:gun|firearm|weapon) (?:in )?(?:car|vehicle)/i,
        response: { type: 'warning', title: 'Firearms in Vehicles',
          answer: 'Only with valid firearm licence. Must be properly secured and stored. Declare if asked.',
          tips: ['Need valid firearm licence', 'Must be secured', 'Declare to police if asked', 'Know the rules'],
          regulation: 'Firearms Act' }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:using|on) phone (?:at )?(?:red )?light/i,
        response: () => {
          const off = OFFENCES.mobile_phone;
          return { type: 'fine', title: 'Phone Use at Traffic Lights', answer: `Using your phone even at red lights is still an offence. Fine: ${off?.fine || '$12,000'}.`,
            tips: ['Applies even when stationary', 'Still in control of vehicle', 'Put phone away completely'], points: off?.points, regulation: 'Regulation 236' };
        }},
      { pattern: /(?:what is|meaning of) (?:careless|inconsiderate) driving/i,
        response: () => {
          const off = OFFENCES.careless_driving;
          return { type: 'fine', title: 'Careless Driving', answer: `Driving without due care and attention. Fine: ${off?.fine || '$15,000'}. Less serious than dangerous driving.`,
            tips: ['Includes minor negligence', 'Not paying attention', 'Distracted driving'], points: off?.points, regulation: 'Road Traffic Act Section 32' };
        }},
      { pattern: /(?:what is|meaning of|difference) dangerous (?:vs )?(?:reckless )?driving/i,
        response: () => {
          const off = OFFENCES.dangerous_driving;
          return { type: 'fine', title: 'Dangerous Driving', answer: `Driving far below acceptable standard with obvious danger. Fine: ${off?.fine || 'Up to $200,000'}. Imprisonment possible.`,
            tips: ['Very serious offence', 'Can lead to imprisonment', 'Includes: racing, excessive speed, deliberately dangerous acts'], points: off?.points, regulation: 'Road Traffic Act Section 31' };
        }},
      { pattern: /(?:what (?:happens|if)|fine for) (?:cause|causing) (?:death|injury) (?:by )?driving/i,
        response: { type: 'warning', title: 'Causing Death by Driving',
          answer: 'Causing death by dangerous or careless driving is a serious criminal offence. Imprisonment up to 10 years.',
          tips: ['Criminal prosecution', 'Up to 10 years imprisonment', 'Permanent driving ban possible', 'Manslaughter charges possible'],
          regulation: 'Road Traffic Act Section 31-32' }},
      { pattern: /(?:can i|allowed) (?:use|drive with) (?:gps|satnav|navigation)/i,
        response: { type: 'info', title: 'GPS/Navigation Use',
          answer: 'GPS use is allowed if mounted properly and doesn\'t distract. Don\'t program while driving.',
          tips: ['Use proper mount', 'Set destination before driving', 'Voice guidance preferred', 'Don\'t look at screen while moving'],
          regulation: 'Regulation 236' }},
      { pattern: /(?:can i|allowed) (?:use|wear) (?:smartwatch|apple watch) (?:while )?driv/i,
        response: { type: 'info', title: 'Smartwatch While Driving',
          answer: 'Avoid interacting with smartwatch while driving. Glancing at notifications is risky.',
          tips: ['Minimize distractions', 'Don\'t read texts on watch', 'Pull over to interact', 'Safety first'],
          regulation: 'Regulation 236' }},
      { pattern: /(?:can i|allowed) (?:adjust|change) (?:music|radio|ac) (?:while )?driv/i,
        response: { type: 'info', title: 'Adjusting Controls While Driving',
          answer: 'Brief adjustments to car controls are allowed. Prolonged distraction is dangerous.',
          tips: ['Keep eyes on road', 'Use steering wheel controls if available', 'Don\'t search for music while moving', 'Set up before driving'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:rules for|tips for) driv(?:e|ing) (?:in )?(?:the )?(?:dark|night time)/i,
        response: { type: 'info', title: 'Night Driving Tips',
          answer: 'Reduce speed, use headlights properly, increase following distance, stay alert.',
          tips: ['Use low beam in traffic', 'High beam on dark roads only', 'Watch for pedestrians', 'Take breaks if tired', 'Don\'t drive if drowsy'],
          regulation: 'Regulation 120-126' }},
      { pattern: /(?:rules for|tips for) driv(?:e|ing) (?:in )?(?:fog|mist)/i,
        response: { type: 'info', title: 'Driving in Fog',
          answer: 'Use fog lights, reduce speed significantly, increase distance, don\'t use high beams.',
          tips: ['Fog lights on', 'Low beam only', 'Reduce speed', 'Increase following distance', 'Don\'t overtake'],
          regulation: 'Regulation 120-126' }},
      { pattern: /(?:rules for|tips for) driv(?:e|ing) (?:on )?(?:wet|slippery) (?:road|surface)/i,
        response: { type: 'info', title: 'Driving on Wet Roads',
          answer: 'Reduce speed, brake gently, increase following distance, avoid sudden movements.',
          tips: ['Double stopping distance', 'Brake early and gently', 'Avoid puddles if possible', 'Watch for aquaplaning', 'Use headlights'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:rules for|tips for) driv(?:e|ing) (?:in )?(?:hurricane|storm|heavy rain)/i,
        response: { type: 'warning', title: 'Driving in Storms',
          answer: 'Avoid driving in hurricanes and severe weather. If caught, find safe shelter.',
          tips: ['Don\'t drive in hurricanes', 'Pull over if storm is severe', 'Avoid flooded roads', 'Watch for debris', 'Stay informed about weather'],
          regulation: 'Road Traffic Act - Due Care' }},
      { pattern: /(?:can i|allowed) driv(?:e|ing) (?:during|in) (?:curfew|lockdown)/i,
        response: { type: 'info', title: 'Driving During Curfew',
          answer: 'During declared curfew/lockdown, only essential travel is permitted. Check government announcements.',
          tips: ['Essential workers may travel', 'Carry work ID if essential', 'Check specific regulations', 'Fines for violations'],
          regulation: 'Disaster Risk Management Act' }},
      { pattern: /(?:can i|allowed) (?:drive|park) (?:on )?(?:beach|sand)/i,
        response: { type: 'info', title: 'Driving on Beaches',
          answer: 'Driving on beaches is generally not allowed and can damage the environment.',
          tips: ['Most beaches prohibit vehicles', 'Can get stuck in sand', 'Environmental damage', 'Check local regulations'],
          regulation: 'Beach Control Act' }},
      { pattern: /(?:can i|allowed) (?:drive|use) (?:on )?private (?:road|property)/i,
        response: { type: 'info', title: 'Private Roads',
          answer: 'Private roads may have their own rules. Owners can restrict access.',
          tips: ['Check for posted rules', 'May not need licence on private land', 'Insurance still advisable', 'Respect owner\'s rules'],
          regulation: 'Property law' }},
      { pattern: /(?:what (?:is|are)) (?:the )?(?:speed|alcohol) limit (?:for )?(?:commercial|truck|bus|taxi)/i,
        response: { type: 'info', title: 'Commercial Vehicle Limits',
          answer: 'Commercial vehicles often have lower speed limits. Zero alcohol tolerance for commercial drivers.',
          tips: ['Lower speed limits apply', 'Zero alcohol for PPV drivers', 'More frequent checks', 'Log books may be required'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:what is|need for) (?:commercial|truck|lorry) (?:operator )?licen[cs]e/i,
        response: { type: 'info', title: 'Commercial Operator Licence',
          answer: 'Operators of commercial vehicles may need additional licensing from Transport Authority.',
          tips: ['Apply to Transport Authority', 'Additional requirements', 'Vehicle specifications', 'Regular inspections'],
          regulation: 'Transport Authority Act' }},
      { pattern: /(?:can i|allowed) (?:use|put) (?:blue|red|green|amber) lights?/i,
        response: { type: 'warning', title: 'Emergency Light Colors',
          answer: 'Blue lights are for police only. Red for fire/ambulance. Only authorized vehicles can use.',
          tips: ['Blue = Police only', 'Red = Emergency services', 'Amber = Caution (construction, tow)', 'Using unauthorized = offence'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:what is) (?:a )?(?:construction|work) zone/i,
        response: { type: 'info', title: 'Construction Zone Rules',
          answer: 'Areas with road work have reduced speed limits, lane changes, and workers present.',
          tips: ['Obey reduced limits', 'Watch for workers', 'Fines may be doubled', 'Follow temporary signs'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:fine for|what (?:happens|if)) speeding (?:in )?(?:construction|work) zone/i,
        response: { type: 'fine', title: 'Construction Zone Speeding',
          answer: 'Speeding in construction zones carries enhanced penalties. Fines may be doubled.',
          tips: ['Fines doubled', 'Strictly enforced', 'Workers present', 'Watch for signs'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:can i|allowed) (?:stop|park) (?:on )?(?:highway|motorway|highway 2000)/i,
        response: { type: 'warning', title: 'Stopping on Highway',
          answer: 'Stopping on highways only for emergencies. Use emergency lanes. Move to nearest exit.',
          tips: ['Emergency only', 'Use emergency lane', 'Turn on hazards', 'Get off at next exit', 'Call for assistance'],
          regulation: 'Highway 2000 regulations' }},
      { pattern: /(?:what (?:is|are)) (?:the )?emergency (?:number|contact|service)/i,
        response: { type: 'info', title: 'Emergency Numbers',
          answer: 'Police: 119. Ambulance: 110. Fire: 110. Highway 2000 assistance: 888-469-4929.',
          tips: ['Police: 119', 'Ambulance/Fire: 110', 'Highway assistance: 888-469-4929', 'Save these in your phone'],
          regulation: 'Emergency services' }},
      { pattern: /(?:how (?:do i|to)|where) (?:report|call) (?:road|pothole|dangerous)/i,
        response: { type: 'info', title: 'Reporting Road Problems',
          answer: 'Report potholes and road hazards to NWA (National Works Agency) or local authorities.',
          tips: ['NWA hotline', 'Note exact location', 'Take photos', 'Report dangerous conditions'],
          regulation: 'NWA/Municipal procedures' }},
      { pattern: /(?:can i|how (?:do i|to)) (?:claim|sue) (?:for )?(?:pothole|road) damage/i,
        response: { type: 'info', title: 'Claiming for Road Damage',
          answer: 'You may be able to claim from NWA or municipality if vehicle damaged by road defect.',
          tips: ['Document the damage', 'Report the defect', 'Get repair estimates', 'May need legal help'],
          regulation: 'Civil claims' }},
      { pattern: /(?:what is|how (?:do i|to)) (?:third party|third.party) claim/i,
        response: { type: 'info', title: 'Third Party Claims',
          answer: 'Claim against another driver\'s insurance for damage they caused to you.',
          tips: ['Get other driver\'s insurance details', 'Report to their insurer', 'Document everything', 'May need lawyer for disputes'],
          regulation: 'Motor Vehicles Insurance Act' }},
      { pattern: /(?:what (?:happens|if)|can i) (?:no|without) claim (?:bonus|discount)/i,
        response: { type: 'info', title: 'No-Claims Bonus',
          answer: 'Discount on premium for claim-free years. Making claims can reduce or remove it.',
          tips: ['Reward for safe driving', 'Can be up to 60% discount', 'Lost if you claim', 'Some policies protect it'],
          regulation: 'Insurance terms' }},
      { pattern: /(?:how (?:do i|to)|can i) (?:compare|choose) insurance/i,
        response: { type: 'info', title: 'Choosing Insurance',
          answer: 'Compare quotes from multiple insurers. Consider coverage, price, and claims service.',
          tips: ['Get multiple quotes', 'Check coverage limits', 'Read reviews', 'Consider claims service'],
          regulation: 'Motor Vehicles Insurance Act' }},

      // ==================== ADDITIONAL COMMON QUERIES ====================
      { pattern: /what (?:are|is) (?:the )?documents? (?:needed|required|necessary)/i,
        response: { type: 'info', title: 'Required Documents When Driving',
          answer: 'You must carry: Driver\'s licence, vehicle registration (eMVRC), and valid insurance certificate.',
          tips: ['Keep digital copies as backup', 'eMVRC available at cgds.gov.jm', '7 days to produce if not carrying', 'All must be current and valid'],
          regulation: 'Road Traffic Act Section 44' }},
      { pattern: /how (?:long|much time) (?:do i have )?(?:to )?pay (?:a |my )?(?:ticket|fine)/i,
        response: { type: 'info', title: 'Time to Pay Traffic Ticket',
          answer: 'You have 28 days to pay a fixed penalty traffic ticket. After that, the matter may proceed to court with higher penalties.',
          tips: ['Pay within 28 days to avoid court', 'Pay online at jamaicatax.gov.jm', 'Keep receipt as proof', 'Late payment incurs additional penalties'],
          regulation: 'Road Traffic Act Fifth Schedule' }},
      { pattern: /(?:where|how) (?:do i|can i) (?:check|verify|see) (?:my )?(?:ticket|fine|outstanding)/i,
        response: { type: 'procedure', title: 'Checking Outstanding Tickets',
          answer: 'Check your outstanding tickets online at jamaicatax.gov.jm or visit any TAJ office.',
          steps: ['Go to jamaicatax.gov.jm', 'Select traffic fines section', 'Enter your TRN or ticket number', 'View outstanding amounts'],
          tips: ['Keep ticket number handy', 'TAJ offices can check by name/TRN', 'Clear outstanding fines before licence renewal'],
          regulation: 'TAJ procedures' }},
      { pattern: /(?:what|which) offences? (?:have|carry|get) (?:the )?(?:most|highest|maximum) (?:points?|fines?)/i,
        response: { type: 'info', title: 'Highest Penalty Offences',
          answer: 'Highest fines: DUI (up to $500,000), Dangerous driving ($200,000), No insurance ($100,000). Most points: Dangerous driving (6), DUI (6), Speeding in school zone (4).',
          tips: ['DUI can include imprisonment', 'Multiple offences = points add up', '20 points = licence suspension', 'Causing death = criminal charges'],
          regulation: 'Road Traffic Act Fifth Schedule' }},
      { pattern: /(?:do|must) (?:i|police) (?:need to )?sign (?:the )?ticket/i,
        response: { type: 'info', title: 'Signing Traffic Tickets',
          answer: 'You are NOT required to sign a traffic ticket. Your signature is just acknowledgment of receipt, not admission of guilt.',
          tips: ['Signing is optional', 'Not signing doesn\'t void the ticket', 'Refusing to sign is not an offence', 'You can still contest in court'],
          regulation: 'Road Traffic Act' }},
      { pattern: /(?:what is|explain) (?:the )?(?:28|twenty.?eight) day/i,
        response: { type: 'info', title: '28-Day Payment Window',
          answer: 'You have 28 days from receiving a traffic ticket to pay the fixed penalty. After 28 days, the matter proceeds to court where fines may be higher.',
          tips: ['Pay within 28 days', 'Court fines are usually higher', 'Can contest instead of paying', 'Online payment available'],
          regulation: 'Road Traffic Act Fifth Schedule' }},
      { pattern: /(?:what is|explain) (?:the )?(?:7|seven) day/i,
        response: { type: 'info', title: '7-Day Document Production',
          answer: 'If stopped without your documents, you have 7 days to produce them at any police station of your choice.',
          tips: ['Ask for a form to take to the station', 'Bring all required documents within 7 days', 'Failure to produce = offence', 'Note the station you choose'],
          regulation: 'Road Traffic Act Section 44' }},
      { pattern: /(?:list|show|what are) (?:all )?(?:the )?(?:speed|speeding) (?:limits?|fines?)/i,
        response: { type: 'speed', title: 'Jamaica Speed Limits Summary',
          answer: 'Speed limits: 50 km/h in towns, 80 km/h on open roads, 110 km/h on Highway 2000, 30 km/h in school zones. Minimum on Highway 2000: 50 km/h.',
          tips: ['Speeding fines start at $6,000', 'School zone violations carry extra penalties', 'Speed cameras islandwide', 'Highway 2000 has minimum speed too'],
          regulation: 'Regulations 229-232' }},
      { pattern: /(?:how|where) (?:do i|can i|to) (?:report|file|make) (?:a )?complaint (?:about )?(?:police|officer|bad driving|dangerous)/i,
        response: { type: 'procedure', title: 'Filing Complaints',
          answer: 'Report police misconduct to INDECOM (876-968-8871). Report dangerous drivers to 119. Report road hazards to NWA.',
          steps: ['For police misconduct: Contact INDECOM', 'For dangerous drivers: Call 119', 'For road problems: Contact NWA', 'Document all details: time, place, badge number, plate number'],
          tips: ['INDECOM handles all police complaints', 'Note officer badge numbers', 'Take photos/video if safe'],
          regulation: 'INDECOM Act, Police procedures' }},
      { pattern: /(?:offence|offense|code) (?:number )?([dD]\d{3})/i,
        response: (match) => {
          const code = normalized.match(/d\d{3}/i)?.[0]?.toUpperCase();
          if (code && ENHANCED_OFFENCES[code]) {
            const off = ENHANCED_OFFENCES[code];
            return { type: 'fine', title: `${code}: ${off.offence}`,
              answer: off.offence,
              tips: ['View full details in Offences section', `Category: ${off.category}`, `Fine: ${off.fine}`],
              points: off.points, regulation: `${off.schedule || 'Fifth Schedule'}` };
          }
          return { type: 'help', title: 'Offence Code Not Found',
            answer: `Could not find offence code "${code}". Codes range from D001 to D147.`,
            tips: ['Browse the Offences section for all codes', 'Try searching by keyword instead'] };
        }},

      // ==================== CATEGORY SEARCH ====================
      { pattern: /(?:show|list|what are|all) (?:the )?(.+?) (?:offences?|violations?|fines?)/i,
        response: () => {
          // Extract category keyword from query
          const categoryKeywords = {
            'speed': 'Speed', 'speeding': 'Speed',
            'document': 'Documents', 'documents': 'Documents', 'licence': 'Documents', 'license': 'Documents', 'insurance': 'Documents',
            'vehicle': 'Vehicle', 'equipment': 'Vehicle', 'lights': 'Vehicle', 'tyre': 'Vehicle', 'tire': 'Vehicle',
            'driving': 'Driving', 'behavior': 'Driving', 'behaviour': 'Driving', 'dangerous': 'Driving', 'reckless': 'Driving',
            'alcohol': 'Impaired', 'dui': 'Impaired', 'drunk': 'Impaired', 'drug': 'Impaired', 'impaired': 'Impaired',
            'parking': 'Parking', 'park': 'Parking',
            'motorcycle': 'Motorcycle', 'motorbike': 'Motorcycle', 'helmet': 'Motorcycle',
            'taxi': 'PPV/Taxi', 'ppv': 'PPV/Taxi', 'bus': 'PPV/Taxi',
            'learner': 'Learner', 'l-plate': 'Learner', 'provisional': 'Learner',
            'child': 'Child Safety', 'children': 'Child Safety', 'seatbelt': 'Seatbelt'
          };

          let matchedCategory = null;
          for (const [keyword, category] of Object.entries(categoryKeywords)) {
            if (normalized.includes(keyword)) {
              matchedCategory = category;
              break;
            }
          }

          if (matchedCategory) {
            const categoryMatches = Object.values(ENHANCED_OFFENCES)
              .filter(off => off.category && off.category.toLowerCase().includes(matchedCategory.toLowerCase()))
              .slice(0, 10);

            if (categoryMatches.length > 0) {
              return { type: 'offences', title: `${matchedCategory} Offences`,
                answer: `Found ${categoryMatches.length} offence(s) in the ${matchedCategory} category.`,
                matches: categoryMatches.map(off => ({
                  key: off.code,
                  title: off.shortTitle || off.offence,
                  fine: off.fine,
                  points: String(off.points),
                  reg: off.reg,
                  category: off.category,
                  enhanced: true
                })),
                regulation: 'Road Traffic Regulations' };
            }
          }

          return { type: 'info', title: 'Offence Categories',
            answer: 'You can search for offences by category. Available categories include:',
            tips: ['Speed/Speeding offences', 'Document offences (licence, insurance)', 'Vehicle/Equipment offences', 'Driving behaviour offences', 'Impaired driving (DUI/drugs)', 'Parking offences', 'Motorcycle offences', 'Taxi/PPV offences', 'Learner driver offences'],
            regulation: 'Fifth Schedule' };
        }}
    ];

    // Check natural language patterns first
    for (const { pattern, response } of naturalPatterns) {
      if (pattern.test(normalized)) {
        return typeof response === 'function' ? response() : response;
      }
    }

    // ========== INTELLIGENT INTENT DETECTION (knowledge base) ==========
    // Classify what the user is really asking (news, definition, contact,
    // fee, how-to) and answer from the matching knowledge domain in natural
    // language, instead of forcing every question through the offence data.
    const kbTokens = normalized.split(/\s+/).filter((w) => w.length > 2 && !BOT_STOP_WORDS.has(w));

    // News/updates intent: amnesty, deadlines, system start dates, headlines
    if (/\b(amnesty|ticket day|news|latest|update|announce|deadline|outstanding|arrears|october 1|september 30|new (?:rule|law|system))/.test(normalized)) {
      const news = fuzzyMatchKnowledge(kbTokens.length ? kbTokens : ['news'], ['news']);
      if (news.length > 0) return news[0].item.answer;
    }

    // Contact intent: phone numbers for police, ambulance, INDECOM, ITA, TAJ
    if (/\b(number|phone|call|contact|hotline|reach)\b/.test(normalized) &&
        /\b(police|ambulance|fire|emergency|indecom|legal aid|ita|island traffic|taj|tax)/.test(normalized)) {
      const contacts = fuzzyMatchKnowledge(kbTokens, ['contact']);
      if (contacts.length > 0) return contacts[0].item.answer;
    }

    // Licence class questions: exact lookup, because single letters like "b"
    // are too short for fuzzy token matching ("class b" vs "class c6").
    const classMatch = normalized.match(/class\s+([a-e][0-9]?)\b/i);
    if (classMatch) {
      const classEntry = KNOWLEDGE_INDEX.find(
        (e) => e.domain === 'document' && e.title.toLowerCase() === `class ${classMatch[1].toLowerCase()} licence`
      );
      if (classEntry) return classEntry.answer;
    }

    // Definition intent: "what is a fixed penalty", "what does eMVRC mean"
    if (/\b(what (?:is|are)|define|meaning of|what does .+ mean)\b/.test(normalized)) {
      const defs = fuzzyMatchKnowledge(kbTokens, ['glossary', 'document']);
      if (defs.length > 0 && defs[0].score >= 0.7) return defs[0].item.answer;
    }

    // Fee intent: cost/price questions that are NOT about fines. Runs before
    // the offence-fine branch so "how much is a learner's permit" returns the
    // $1,800 TAJ fee rather than the learner-driver offence fine.
    const asksFee = /\b(fee|fees|cost|costs|price|prices|how much|pay(?:ing)? for|charge)\b/.test(normalized);
    const asksOffence = /\b(fine|penalt|caught|offence|offense|ticket|demerit|court)\b/.test(normalized);
    if (asksFee && !asksOffence) {
      const fees = fuzzyMatchKnowledge(kbTokens, ['fees', 'guide']);
      if (fees.length > 0 && fees[0].score >= 0.7) return fees[0].item.answer;
    }

    // How-to intent: procedures and step-by-step processes
    if (/\b(how (?:do|can|to)|what (?:to|should i) do|steps?|process|procedure|renew|apply|register|transfer|obtain)\b/.test(normalized)) {
      const guides = fuzzyMatchKnowledge(kbTokens, ['guide', 'situation']);
      if (guides.length > 0 && guides[0].score >= 0.6) return guides[0].item.answer;
    }

    // Check for regulation number searches (e.g., "229", "reg 229", "regulation 229")
    const regMatch = normalized.match(/(?:reg(?:ulation)?[\s.]*)?\b(\d{1,3})\b/);
    if (regMatch && !normalized.includes('$') && !normalized.includes('fine')) {
      const regNum = regMatch[1];
      const regMatches = Object.entries(OFFENCES)
        .filter(([k, v]) => v.reg && v.reg.includes(regNum))
        .map(([k, v]) => ({ key: k, ...v }));

      if (regMatches.length > 0) {
        return { type: 'offences', title: `Offences under Regulation ${regNum}`,
          answer: `Found ${regMatches.length} offence(s) related to Regulation ${regNum}`,
          matches: regMatches, regulation: `Regulation ${regNum}` };
      }
    }

    // Check speed limit questions
    if (normalized.includes('speed') || normalized.includes('fast') || normalized.includes('km')) {
      if (normalized.includes('highway') || normalized.includes('toll') || normalized.includes('2000')) {
        return { type: 'speed', title: 'Highway/Toll Road Speed Limit',
          answer: '110 km/h maximum on Highway 2000, 50 km/h minimum. Going below minimum is also an offence.',
          details: regulationData.speedLimits?.toll_roads || {}, regulation: 'Regulation 229-232' };
      }
      if (normalized.includes('school')) {
        return { type: 'speed', title: 'School Zone Speed Limit',
          answer: '30 km/h during school hours (typically 7am-4pm on school days). Speeding in school zones carries higher fines.',
          details: regulationData.speedLimits?.general?.school_zones || {}, regulation: 'Regulation 231' };
      }
      if (normalized.includes('town') || normalized.includes('city') || normalized.includes('urban')) {
        return { type: 'speed', title: 'Town/City Speed Limit',
          answer: '50 km/h maximum in built-up/urban areas unless otherwise posted.',
          details: regulationData.speedLimits?.general || {}, regulation: 'Regulation 229' };
      }
      return { type: 'speed', title: 'Jamaica Speed Limits',
        answer: 'General limits: 50 km/h in towns, 80 km/h on open roads, 110 km/h on Highway 2000, 30 km/h in school zones.',
        tips: ['Speed cameras are active island-wide', 'Speeding fines start at $6,000', 'School zone violations carry extra penalties'],
        regulation: 'Regulations 229-232' };
    }

    // Check for fine/cost questions
    if (normalized.includes('fine') || normalized.includes('how much') || normalized.includes('cost') || normalized.includes('penalty')) {
      for (const [key, mapping] of Object.entries(QUERY_MAPPINGS)) {
        if (normalized.includes(key) && mapping.length > 0 && OFFENCES[mapping[0]]) {
          const off = OFFENCES[mapping[0]];
          const ptsPhrase = off.points && off.points !== 'No Points'
            ? ` and it carries ${off.points} demerit point${off.points === '1' ? '' : 's'}`
            : '';
          return { type: 'fine', title: `Fine for ${off.title}`,
            answer: `The penalty for ${off.title.toLowerCase()} is ${off.fine}${ptsPhrase}. ${off.details || ''}`,
            points: off.points, regulation: `Regulation ${off.reg}`, offenceKey: mapping[0] };
        }
      }
    }

    // Check for rights questions
    if (normalized.includes('right') || normalized.includes('can i refuse') || normalized.includes('allowed to')) {
      const rights = regulationData.situations?.police_stop?.steps?.flatMap(s => s.rights) || [];
      return { type: 'rights', title: 'Your Rights During a Traffic Stop',
        answer: 'You have several important rights when stopped by police:',
        tips: ['You can remain silent beyond providing ID/documents', 'You can refuse vehicle search without warrant', 'You can ask why you were stopped', 'You have 7 days to produce documents at a station', 'You can request officer\'s badge number'],
        regulation: 'Road Traffic Act, Constitution of Jamaica' };
    }

    // Check for document questions
    if (normalized.includes('document') || normalized.includes('produce') || normalized.includes('7 day') || normalized.includes('carry')) {
      return { type: 'document', title: 'Document Requirements',
        answer: 'You must carry: Driver\'s licence, vehicle registration (eMVRC), and insurance. If stopped without them, you have 7 days to produce at a police station.',
        tips: ['Keep digital copies on your phone as backup', 'eMVRC can be downloaded from cgds.gov.jm', 'Failure to produce within 7 days = offence', 'Original documents are best'],
        regulation: 'Regulation 11, Section 44' };
    }

    // Check for demerit points questions
    if (normalized.includes('point') || normalized.includes('demerit') || normalized.includes('suspend') || normalized.includes('revoke')) {
      return { type: 'demerit', title: 'Demerit Points System',
        answer: 'Demerit points are added to your licence for traffic violations, and the system takes full effect on October 1, 2026 with zero-tolerance enforcement. At 10–13 points (speeding-related) you must complete training; at 14–19 you also face written and driving re-tests; at 20 or more points your licence is suspended. Points expire 15 months after the offence if your licence was not suspended.',
        tips: [
          'Settle outstanding tickets by September 30, 2026 to start the new system with a clean slate',
          'Minor offences: 2-4 points; major offences: 4-6+; DUI conviction triggers suspension plus re-tests',
          'Points that caused a suspension are removed once the suspension ends',
          'Appeal a suspension notice to the Road Traffic Appeal Tribunal within 14 days',
          'Check your points at ITA offices'
        ],
        regulation: 'Road Traffic Regulations 2022, Regulation 164; effective 1 Oct 2026 per Ministry of Transport' };
    }

    // Check for insurance questions
    if (normalized.includes('insurance') && !normalized.includes('fine')) {
      return { type: 'info', title: 'Motor Vehicle Insurance',
        answer: 'Third-party insurance is mandatory for all vehicles on Jamaican roads. Comprehensive insurance is optional but recommended.',
        tips: ['Minimum: Third-party coverage', 'Must have valid insurance certificate', 'Can purchase from licensed insurers', 'Cover note valid for 30 days while awaiting certificate'],
        regulation: 'Motor Vehicles Insurance (Third Party Risks) Act' };
    }

    // Check for fitness/inspection questions
    if (normalized.includes('fitness') || normalized.includes('inspection') || normalized.includes('roadworthy')) {
      return { type: 'info', title: 'Vehicle Fitness Certificate',
        answer: 'All vehicles require annual fitness inspection at an authorized depot. New vehicles are exempt for the first year.',
        tips: ['Inspection covers brakes, lights, tires, emissions, etc.', 'Cost varies by vehicle type', 'Private vehicles: Annual renewal', 'Commercial: May need more frequent inspection'],
        regulation: 'Road Traffic Act Section 24' };
    }

    // Search through query mappings for matching offences
    for (const [key, offenceKeys] of Object.entries(QUERY_MAPPINGS)) {
      if (normalized.includes(key)) {
        const matches = offenceKeys.filter(k => OFFENCES[k]).map(k => ({ key: k, ...OFFENCES[k] }));
        if (matches.length > 0) {
          return { type: 'offences', title: `Offences related to "${key}"`,
            answer: `Found ${matches.length} related offence(s). Tap any to see details.`, matches, regulation: 'Road Traffic Regulations, Fifth Schedule' };
        }
      }
    }

    // Default search through all offences (including regulation numbers)
    const directMatches = Object.entries(OFFENCES)
      .filter(([k, v]) => {
        const term = normalized.replace(/\s/g, '_');
        return v.title.toLowerCase().includes(normalized) ||
               k.includes(term) ||
               (v.reg && v.reg.toLowerCase().includes(normalized));
      })
      .map(([k, v]) => ({ key: k, ...v }));

    if (directMatches.length > 0) {
      return { type: 'offences', title: `Search results for "${q}"`,
        answer: `Found ${directMatches.length} matching offence(s)`, matches: directMatches, regulation: 'Fifth Schedule' };
    }

    // FLEXIBLE KEYWORD MATCHING - More relaxed interpretation
    // Extract keywords from query and try to match patterns based on keywords
    const keywords = normalized.split(/\s+/).filter(w => w.length > 2);
    const keywordMappings = {
      // ========== IMPAIRED DRIVING - DRUGS ==========
      'smoke': 'drug_driving', 'smoking': 'drug_driving', 'ganja': 'drug_driving', 'weed': 'drug_driving',
      'high': 'drug_driving', 'stoned': 'drug_driving', 'marijuana': 'drug_driving', 'drugs': 'drug_driving',
      'cannabis': 'drug_driving', 'spliff': 'drug_driving', 'joint': 'drug_driving', 'blaze': 'drug_driving',
      'blazed': 'drug_driving', 'impaired': 'drug_driving', 'prescription': 'drug_driving',
      // ========== IMPAIRED DRIVING - ALCOHOL ==========
      'drunk': 'dui', 'alcohol': 'dui', 'drinking': 'dui', 'intoxicated': 'dui', 'beer': 'dui', 'rum': 'dui',
      'wine': 'dui', 'liquor': 'dui', 'tipsy': 'dui', 'sober': 'dui', 'breathalyzer': 'dui', 'breathalyser': 'dui',
      'influence': 'dui', 'dwi': 'dui', 'blood': 'dui', 'limit': 'dui',
      'refusing': 'refusing_breath_test', 'refuse': 'refusing_breath_test', 'breath': 'refusing_breath_test',
      // ========== DOCUMENTS - LICENCE ==========
      'licence': 'no_licence', 'license': 'no_licence', 'permit': 'no_licence', 'expired': 'no_licence',
      'learner': 'learner_unaccompanied', 'provisional': 'no_licence', 'unlicensed': 'no_licence', 'unlicenced': 'no_licence',
      'l-plates': 'learner_without_l_plates', 'lplates': 'learner_without_l_plates',
      // ========== DOCUMENTS - INSURANCE ==========
      'insurance': 'no_insurance', 'insured': 'no_insurance', 'coverage': 'no_insurance', 'uninsured': 'no_insurance',
      'policy': 'no_insurance', 'third-party': 'no_insurance', 'comprehensive': 'no_insurance', 'thirdparty': 'no_insurance',
      // ========== DOCUMENTS - FITNESS/REGISTRATION ==========
      'fitness': 'no_fitness', 'inspection': 'no_fitness', 'roadworthy': 'no_fitness', 'mot': 'no_fitness',
      'registration': 'no_registration', 'emvrc': 'no_registration', 'registered': 'no_registration', 'unregistered': 'no_registration',
      // ========== VEHICLE EQUIPMENT ==========
      'tint': 'tinted_windows', 'tinted': 'tinted_windows', 'window': 'tinted_windows', 'dark': 'tinted_windows',
      'seatbelt': 'seatbelt', 'belt': 'seatbelt', 'buckle': 'seatbelt', 'strap': 'seatbelt',
      'phone': 'mobile_phone', 'cell': 'mobile_phone', 'texting': 'mobile_phone', 'calling': 'mobile_phone',
      'mobile': 'mobile_phone', 'cellphone': 'mobile_phone', 'smartphone': 'mobile_phone', 'handheld': 'mobile_phone',
      'helmet': 'helmet_violation', 'headgear': 'helmet_violation',
      'plate': 'no_number_plates', 'plates': 'no_number_plates', 'numberplate': 'no_number_plates',
      'lights': 'no_sidelights', 'headlight': 'no_sidelights', 'taillight': 'no_sidelights', 'headlights': 'no_sidelights',
      'brake': 'defective_brakes', 'brakes': 'defective_brakes', 'braking': 'defective_brakes',
      'tyre': 'defective_tyres', 'tire': 'defective_tyres', 'tyres': 'defective_tyres', 'tires': 'defective_tyres', 'bald': 'defective_tyres',
      'steering': 'defective_steering', 'steer': 'defective_steering',
      'horn': 'no_horn', 'honk': 'improper_horn_use', 'honking': 'improper_horn_use',
      'mirror': 'no_mirrors', 'mirrors': 'no_mirrors', 'rearview': 'no_mirrors',
      'wiper': 'no_windscreen_wipers', 'wipers': 'no_windscreen_wipers', 'windscreen': 'no_windscreen_wipers',
      'muffler': 'excessive_noise', 'exhaust': 'excessive_noise', 'loud': 'excessive_noise', 'noisy': 'excessive_noise', 'noise': 'excessive_noise',
      'unfit': 'driving_unfit_vehicle', 'defective': 'driving_unfit_vehicle',
      // ========== SPEED ==========
      'speeding': 'speeding', 'speed': 'speeding', 'fast': 'speeding', 'racing': 'speeding',
      'excessive': 'speeding', 'km/h': 'speeding', 'kmh': 'speeding', 'mph': 'speeding',
      'school': 'school_zone_speeding', 'zone': 'school_zone_speeding',
      'minimum': 'minimum_speed_violation', 'slow': 'minimum_speed_violation',
      // ========== TRAFFIC SIGNALS AND SIGNS ==========
      'red': 'traffic_light', 'light': 'traffic_light', 'signal': 'traffic_light', 'traffic': 'traffic_light',
      'amber': 'traffic_light', 'yellow': 'traffic_light', 'green': 'traffic_light',
      'stop': 'failure_to_stop', 'stopsign': 'failure_to_stop',
      'yield': 'failure_to_yield', 'right-of-way': 'failure_to_yield', 'rightofway': 'failure_to_yield',
      // ========== DRIVING BEHAVIOUR ==========
      'overtaking': 'improper_overtaking', 'overtake': 'improper_overtaking', 'passing': 'improper_overtaking',
      'tailgating': 'following_too_close', 'following': 'following_too_close', 'close': 'following_too_close',
      'dangerous': 'dangerous_driving', 'reckless': 'dangerous_driving',
      'careless': 'careless_driving', 'negligent': 'careless_driving', 'inattention': 'careless_driving',
      'aggressive': 'dangerous_driving', 'rage': 'dangerous_driving', 'road-rage': 'dangerous_driving',
      'lane': 'improper_lane_change', 'weaving': 'improper_lane_change', 'cutting': 'improper_lane_change',
      'wrong-way': 'wrong_way_driving', 'wrongway': 'wrong_way_driving', 'oncoming': 'wrong_way_driving',
      'pedestrian': 'pedestrian_crossing_violation', 'crosswalk': 'pedestrian_crossing_violation', 'crossing': 'pedestrian_crossing_violation',
      // ========== PARKING ==========
      'parking': 'parking_violations', 'parked': 'parking_violations', 'park': 'parking_violations',
      'double': 'parking_violations', 'sidewalk': 'parking_violations', 'pavement': 'parking_violations',
      // ========== BUS SPECIFIC ==========
      'bus': 'bus_lane_violation', 'buslane': 'bus_lane_violation', 'busstop': 'bus_stop_parking',
      'coaster': 'no_ppv_licence', 'minibus': 'no_ppv_licence', 'conductor': 'no_ppv_licence',
      // ========== TAXI/PPV SPECIFIC ==========
      'taxi': 'no_ppv_licence', 'taximan': 'no_ppv_licence', 'cabbie': 'no_ppv_licence', 'cab': 'no_ppv_licence',
      'uber': 'pirating', 'robot': 'pirating', 'pirate': 'pirating', 'pirating': 'pirating', 'illegal-taxi': 'pirating',
      'ppv': 'no_ppv_licence', 'badge': 'no_ppv_licence', 'public-passenger': 'no_ppv_licence',
      'fare': 'taxi_overcharging', 'overcharge': 'taxi_overcharging', 'overcharging': 'taxi_overcharging', 'charge': 'taxi_overcharging',
      'refuse': 'refusing_fare', 'refusing': 'refusing_fare', 'passenger': 'refusing_fare',
      'transport-authority': 'no_ppv_licence', 'transportauthority': 'no_ppv_licence',
      // ========== COMMERCIAL/GOODS VEHICLES ==========
      'commercial': 'no_ppv_licence', 'truck': 'overloading_goods', 'lorry': 'overloading_goods',
      'goods': 'dangerous_goods_violation', 'hazardous': 'dangerous_goods_violation', 'hazmat': 'dangerous_goods_violation',
      'overloading': 'overloading_passengers', 'overloaded': 'overloading_passengers', 'crowded': 'overloading_passengers',
      'cargo': 'overloading_goods', 'load': 'insecure_load', 'weight': 'overloading_goods', 'heavy': 'overloading_goods',
      'insecure': 'insecure_load', 'unsecured': 'insecure_load', 'projecting': 'projecting_load',
      // ========== MOTORCYCLE ==========
      'motorcycle': 'helmet_violation', 'motorbike': 'helmet_violation', 'bike': 'helmet_violation',
      'pillion': 'motorcycle_pillion_violation', 'passenger-bike': 'motorcycle_pillion_violation',
      'lane-splitting': 'motorcycle_lane_splitting', 'lanesplitting': 'motorcycle_lane_splitting',
      // ========== LEARNER DRIVER ==========
      'learner': 'learner_unaccompanied', 'accompanied': 'learner_unaccompanied', 'supervisor': 'learner_unaccompanied',
      'l-plate': 'learner_without_l_plates', 'lplate': 'learner_without_l_plates',
      'night-driving': 'learner_night_driving', 'highway': 'learner_on_highway', 'tollroad': 'learner_on_highway',
      // ========== CHILD SAFETY ==========
      'child': 'child_restraint_violation', 'children': 'child_restraint_violation', 'baby': 'child_restraint_violation',
      'carseat': 'child_restraint_violation', 'car-seat': 'child_restraint_violation', 'booster': 'child_restraint_violation',
      'restraint': 'child_restraint_violation', 'minor': 'smoking_in_vehicle_with_child',
      // ========== EMERGENCY VEHICLES ==========
      'emergency': 'emergency_vehicle_obstruction', 'ambulance': 'emergency_vehicle_obstruction',
      'fire-truck': 'emergency_vehicle_obstruction', 'firetruck': 'emergency_vehicle_obstruction',
      'siren': 'emergency_vehicle_obstruction', 'blue-light': 'unauthorized_blue_lights', 'bluelight': 'unauthorized_blue_lights',
      'impersonate': 'impersonating_emergency', 'impersonating': 'impersonating_emergency',
      // ========== ACCIDENTS ==========
      'accident': 'careless_driving', 'crash': 'careless_driving', 'collision': 'careless_driving',
      'hit': 'careless_driving', 'scene': 'careless_driving',
      // ========== U-TURN/REVERSING ==========
      'uturn': 'u_turn_violation', 'u-turn': 'u_turn_violation',
      'reverse': 'reversing_unsafely', 'reversing': 'reversing_unsafely', 'backwards': 'reversing_unsafely',
      // ========== OTHER ==========
      'littering': 'littering_from_vehicle', 'litter': 'littering_from_vehicle', 'garbage': 'littering_from_vehicle', 'trash': 'littering_from_vehicle',
      'stolen': 'using_vehicle_unauthorized', 'unauthorized': 'using_vehicle_unauthorized', 'consent': 'using_vehicle_unauthorized',
      // ========== GENERAL/FINES ==========
      'police': 'failure_to_stop', 'stopped': 'failure_to_stop', 'pulled': 'failure_to_stop',
      'officer': 'failure_to_stop', 'checkpoint': 'failure_to_stop', 'roadblock': 'failure_to_stop',
      'points': 'speeding', 'demerit': 'speeding', 'suspension': 'no_licence',
      'fine': 'speeding', 'fines': 'speeding', 'penalty': 'speeding', 'ticket': 'speeding', 'court': 'speeding',
      'driving': 'no_licence', 'drive': 'no_licence', 'drove': 'no_licence', 'driver': 'no_licence',
      // ========== JAMAICAN PATOIS/SLANG ==========
      'pikni': 'child_restraint_violation', 'pickney': 'child_restraint_violation', 'likkle': 'child_restraint_violation',
      'babylon': 'failure_to_stop', 'beast': 'failure_to_stop', 'jakes': 'failure_to_stop', 'popo': 'failure_to_stop',
      'dutty': 'parking_violations', 'duttyness': 'littering_from_vehicle',
      'ded': 'careless_driving', 'mashup': 'careless_driving', 'mash-up': 'careless_driving', 'brukdung': 'careless_driving',
      'bruk-dung': 'careless_driving', 'lick': 'careless_driving', 'lickdung': 'careless_driving', 'lick-dung': 'careless_driving',
      'tekweh': 'using_vehicle_unauthorized', 'tek-weh': 'using_vehicle_unauthorized', 'tief': 'using_vehicle_unauthorized', 'teef': 'using_vehicle_unauthorized',
      'ketch': 'speeding', 'kotch': 'parking_violations', 'jam': 'obstructing_traffic',
      'hol': 'failure_to_stop', 'holdup': 'failure_to_stop', 'carrygo': 'no_insurance', 'carry-go': 'no_insurance',
      'lockup': 'dui', 'lock-up': 'dui', 'lockoff': 'no_licence',
      'galang': 'speeding', 'gwaan': 'speeding', 'speedy': 'speeding', 'fast-fast': 'speeding',
      'smaddy': 'pedestrian_crossing_violation', 'smadi': 'pedestrian_crossing_violation',
      'inna': 'dui', 'likka': 'dui', 'rum-head': 'dui', 'rumhead': 'dui',
      'renk': 'excessive_noise', 'nuff': 'overloading_passengers', 'whole-heap': 'overloading_passengers',
      'facety': 'dangerous_driving', 'rude': 'dangerous_driving', 'badmind': 'dangerous_driving',
      // ========== ADDITIONAL MISSING KEYWORDS ==========
      'obstruct': 'obstructing_traffic', 'block': 'obstructing_traffic', 'blocking': 'obstructing_traffic',
      'indicator': 'failure_to_signal', 'blinker': 'failure_to_signal', 'blinkers': 'failure_to_signal',
      'foglight': 'improper_lights', 'foglights': 'improper_lights',
      'number': 'no_number_plates', 'visible': 'obscured_plates', 'obscure': 'obscured_plates', 'covered': 'obscured_plates',
      'disqualified': 'driving_while_disqualified', 'banned': 'driving_while_disqualified',
      'produce': 'failure_to_produce_documents', 'show': 'failure_to_produce_documents',
      'seize': 'vehicle_seizure', 'impound': 'vehicle_seizure', 'impounded': 'vehicle_seizure', 'tow': 'vehicle_seizure',
      'manslaughter': 'causing_death', 'death': 'causing_death', 'kill': 'causing_death', 'killed': 'causing_death',
      'injury': 'careless_driving', 'injure': 'careless_driving', 'injured': 'careless_driving', 'hurt': 'careless_driving',
      'flee': 'hit_and_run', 'fleeing': 'hit_and_run', 'hitandrun': 'hit_and_run', 'hit-and-run': 'hit_and_run', 'runaway': 'hit_and_run',
      'unattended': 'leaving_vehicle_unattended', 'left': 'leaving_vehicle_unattended',
      'warrant': 'failure_to_appear', 'summons': 'failure_to_appear', 'appear': 'failure_to_appear'
    };

    // Find best matching offence based on keywords
    for (const keyword of keywords) {
      if (keywordMappings[keyword] && OFFENCES[keywordMappings[keyword]]) {
        const off = OFFENCES[keywordMappings[keyword]];
        return { type: 'fine', title: off.title,
          answer: `Based on your question about "${keyword}": ${off.details || off.title}. Fine: ${off.fine}`,
          tips: ['This is a common traffic offence', 'Check the Offences section for more details'],
          points: off.points, regulation: `Regulation ${off.reg}`, offenceKey: keywordMappings[keyword] };
      }
    }

    // Last resort: fuzzy match on offence titles
    const fuzzyMatches = Object.entries(OFFENCES)
      .filter(([k, v]) => {
        return keywords.some(kw =>
          v.title.toLowerCase().includes(kw) ||
          k.toLowerCase().includes(kw) ||
          (v.details && v.details.toLowerCase().includes(kw))
        );
      })
      .map(([k, v]) => ({ key: k, ...v }));

    if (fuzzyMatches.length > 0) {
      return { type: 'offences', title: `Related results for "${q}"`,
        answer: `Found ${fuzzyMatches.length} possibly related offence(s). Tap any to see details.`,
        matches: fuzzyMatches.slice(0, 5), regulation: 'Fifth Schedule' };
    }

    // ========== ENHANCED OFFENCES SEARCH ==========
    // Search through enhanced offences data (147 offences with rich details)

    // 1. Check for offence code (D001, D002, etc.)
    const codeMatch = normalized.match(/d\d{3}/i);
    if (codeMatch) {
      const code = codeMatch[0].toUpperCase();
      if (ENHANCED_OFFENCES[code]) {
        const off = ENHANCED_OFFENCES[code];
        return { type: 'fine', title: off.shortTitle,
          answer: `${off.details} Fine: ${off.fine}. ${off.imprisonment ? `Imprisonment: up to ${off.imprisonment}.` : ''}`,
          tips: off.tip ? [off.tip, ...off.rights.slice(0, 2)] : off.rights.slice(0, 3),
          points: String(off.points), regulation: off.reg, offenceKey: code, enhanced: true };
      }
    }

    // 2. Search enhanced offences by keywords
    const enhancedMatches = [];
    for (const keyword of keywords) {
      if (ENHANCED_KEYWORD_MAPPINGS[keyword]) {
        ENHANCED_KEYWORD_MAPPINGS[keyword].forEach(code => {
          if (!enhancedMatches.find(m => m.code === code)) {
            enhancedMatches.push(ENHANCED_OFFENCES[code]);
          }
        });
      }
    }

    // 3. Also search by title/description
    Object.values(ENHANCED_OFFENCES).forEach(off => {
      if (!enhancedMatches.find(m => m.code === off.code)) {
        if (off.title.toLowerCase().includes(normalized) ||
            off.shortTitle.toLowerCase().includes(normalized) ||
            off.details.toLowerCase().includes(normalized)) {
          enhancedMatches.push(off);
        }
      }
    });

    if (enhancedMatches.length > 0) {
      // If single match, return detailed result
      if (enhancedMatches.length === 1) {
        const off = enhancedMatches[0];
        return { type: 'fine', title: off.shortTitle,
          answer: `${off.details.slice(0, 200)}... Fine: ${off.fine}. ${off.courtRequired ? 'Court appearance required.' : ''}`,
          tips: off.tip ? [off.tip, ...off.rights.slice(0, 2)] : ['Check Offences & Fines for full details'],
          points: String(off.points), regulation: off.reg, offenceKey: off.code, enhanced: true };
      }
      // Multiple matches - return list
      return { type: 'offences', title: `Offences matching "${q}"`,
        answer: `Found ${enhancedMatches.length} related offence(s) from our enhanced database.`,
        matches: enhancedMatches.slice(0, 8).map(off => ({
          key: off.code,
          title: off.shortTitle,
          fine: off.fine,
          points: String(off.points),
          reg: off.reg,
          details: off.details.slice(0, 100) + '...',
          category: off.category,
          enhanced: true
        })),
        regulation: 'Road Traffic Regulations, 2022' };
    }

    // ========== QUICK FACTS - Check if asking about common facts ==========
    const QUICK_FACTS = {
      '28': { title: '28-Day Payment Rule', answer: 'You have 28 days to pay a traffic ticket. After that, it goes to court.', regulation: 'Fifth Schedule' },
      'twenty-eight': { title: '28-Day Payment Rule', answer: 'You have 28 days to pay a traffic ticket. After that, it goes to court.', regulation: 'Fifth Schedule' },
      '7': { title: '7-Day Document Production', answer: 'You have 7 days to produce documents at a police station if stopped without them.', regulation: 'Section 44' },
      'seven': { title: '7-Day Document Production', answer: 'You have 7 days to produce documents at a police station if stopped without them.', regulation: 'Section 44' },
      '20': { title: '20-Point Suspension', answer: 'If you accumulate 20 demerit points, your licence is automatically suspended.', regulation: 'Part IV' },
      'twenty': { title: '20-Point Suspension', answer: 'If you accumulate 20 demerit points, your licence is automatically suspended.', regulation: 'Part IV' },
      '15': { title: '15-Month Point Expiry', answer: 'Demerit points expire 15 months after the date of the offence.', regulation: 'Part IV' },
      'fifteen': { title: '15-Month Point Expiry', answer: 'Demerit points expire 15 months after the date of the offence.', regulation: 'Part IV' },
      '50': { title: '50 km/h Town Limit', answer: 'Speed limit in towns and built-up areas is 50 km/h unless otherwise posted.', regulation: 'Regulation 229' },
      '80': { title: '80 km/h Open Road', answer: 'Speed limit on open roads is 80 km/h unless otherwise posted.', regulation: 'Regulation 229' },
      '110': { title: '110 km/h Highway Max', answer: 'Maximum speed on Highway 2000 is 110 km/h. Minimum is 50 km/h.', regulation: 'Regulation 230' },
      '30': { title: '30 km/h School Zone', answer: 'Speed limit in school zones is 30 km/h during school hours.', regulation: 'Regulation 231' },
      '119': { title: 'Police Emergency', answer: 'Call 119 for police emergencies or to report crimes/dangerous drivers. Call 110 for fire and ambulance emergencies.', regulation: 'Emergency Services' }
    };

    // Match numbers/keywords as whole words only so "50" doesn't match inside
    // "150" and "7" doesn't match inside "2007".
    for (const [fact, info] of Object.entries(QUICK_FACTS)) {
      const wordBoundary = new RegExp(`\\b${fact}\\b`, 'i');
      if (wordBoundary.test(normalized)) {
        return { type: 'info', title: info.title, answer: info.answer,
          tips: ['This is a commonly asked question', 'Check the full regulations for more details'],
          regulation: info.regulation };
      }
    }

    // ========== FUZZY FALLBACK - Fuse.js over all enhanced offences ==========
    // Strip stop-words, then fuzzy-match the remaining terms so misspelled or
    // loosely-phrased questions still surface the most relevant offences
    // before giving up with the generic help card.
    const fuzzyTokens = normalized
      .split(/\s+/)
      .filter((w) => w.length > 2 && !BOT_STOP_WORDS.has(w));

    if (fuzzyTokens.length > 0) {
      // Search offences AND the full knowledge base, then answer from
      // whichever domain fits the question best.
      const offenceResults = fuzzyMatchOffences(fuzzyTokens);
      const kbResults = fuzzyMatchKnowledge(fuzzyTokens);
      const offenceTop = offenceResults.length > 0 ? offenceResults[0].score : 0;
      const kbTop = kbResults.length > 0 ? kbResults[0].score : 0;

      // Knowledge answers are complete responses (a fee, a guide, a contact),
      // so prefer them when they clearly outscore the offence match.
      if (kbTop >= 0.6 && kbTop > offenceTop) {
        return kbResults[0].item.answer;
      }

      if (offenceTop >= 0.6) {
        const matches = offenceResults.slice(0, 5).map((r) => ({
          key: r.item.code,
          title: r.item.title,
          reg: r.item.reg,
          fine: r.item.fine,
          category: r.item.category,
          enhanced: true,
        }));
        return { type: 'offences',
          title: `Closest matches for "${q}"`,
          answer: `I didn't find an exact answer, but these ${matches.length} offence(s) look related. Tap any for full details.`,
          matches,
          regulation: 'Road Traffic Regulations, 2022 (fuzzy match)' };
      }

      if (kbTop >= 0.6) {
        return kbResults[0].item.answer;
      }
    }

    // ========== IMPROVED NO RESULTS - With helpful suggestions ==========
    return { type: 'help', title: 'No Exact Match Found',
      answer: `I couldn't find a specific answer for "${q}". Here are some things you can ask about:`,
      tips: [
        '💡 "What is the fine for speeding?"',
        '💡 "What are my rights if stopped by police?"',
        '💡 "How long to pay a ticket?"',
        '💡 "What documents must I carry?"',
        '💡 "Offence code D001" (for specific codes)'
      ],
      suggestions: [
        { label: 'Speed Limits', query: 'What are the speed limits in Jamaica?' },
        { label: 'DUI/Drunk Driving', query: 'What happens if caught drunk driving?' },
        { label: 'Licence Requirements', query: 'What documents must I carry?' },
        { label: 'Police Stop Rights', query: 'What are my rights if pulled over?' },
        { label: 'Browse Offences', action: 'offences' }
      ],
      regulation: 'Try a more specific question or browse the Offences section' };
  };

  const handleSearch = () => {
    const result = processQuery(query);
    if (result) {
      setAnswer(result);
      setHistory([{ query, result, time: new Date().toLocaleTimeString() }, ...history.slice(0, 9)]);
    }
  };

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><MessageCircle className="w-8 h-8" />Quick Answer Bot</h2>
        <p className="text-violet-200 mt-2">Ask questions about traffic offences, fines, rights, and regulations</p>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <HelpCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Ask a question... e.g., 'What's the fine for speeding?'"
              value={query} onChange={e => setQuery(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-violet-500 text-lg" />
          </div>
          <button onClick={handleSearch} className="px-6 py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl flex items-center gap-2">
            <Zap className="w-5 h-5" />Ask
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {['speed limit', 'fine for speeding', 'my rights', 'demerit points', 'DUI penalty', 'phone while driving'].map(q => (
            <button key={q} onClick={() => { setQuery(q); }}
              className="px-3 py-1 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg text-sm">{q}</button>
          ))}
        </div>
      </div>

      {answer && (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className={`p-5 ${answer.type === 'unknown' ? 'bg-gray-500' : 'bg-violet-600'} text-white flex items-center justify-between`}>
            <div>
              <h3 className="text-xl font-bold">{answer.title}</h3>
              {answer.regulation && <p className="text-violet-200 text-sm mt-1">{answer.regulation}</p>}
            </div>
            <button
              onClick={() => speaking ? stopSpeaking() : speak(`${answer.title}. ${answer.answer}`)}
              className={`p-3 rounded-xl ${speaking ? 'bg-white text-violet-600' : 'bg-white/20 hover:bg-white/30'}`}>
              {speaking ? <Volume2 className="w-6 h-6 animate-pulse" /> : <Volume2 className="w-6 h-6" />}
            </button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-lg font-medium">{answer.answer}</p>

            {answer.points && <div className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-lg font-bold">Demerit Points: {answer.points}</div>}

            {answer.tips && answer.tips.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2"><AlertCircle className="w-5 h-5" />Tips</h4>
                <ul className="space-y-2">{answer.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-blue-700 text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />{tip}
                  </li>
                ))}</ul>
              </div>
            )}

            {answer.steps && answer.steps.length > 0 && (
              <div className="bg-emerald-50 rounded-xl p-4">
                <h4 className="font-bold text-emerald-800 mb-2 flex items-center gap-2"><Clipboard className="w-5 h-5" />Steps</h4>
                <ol className="space-y-2">{answer.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-emerald-700 text-sm">
                    <span className="w-6 h-6 bg-emerald-200 text-emerald-800 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}</ol>
              </div>
            )}

            {answer.locations && answer.locations.length > 0 && (
              <div className="bg-orange-50 rounded-xl p-4">
                <h4 className="font-bold text-orange-800 mb-2 flex items-center gap-2"><MapPin className="w-5 h-5" />Locations</h4>
                <ul className="space-y-2">{answer.locations.map((loc, i) => (
                  <li key={i} className="flex items-start gap-2 text-orange-700 text-sm">
                    <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />{loc}
                  </li>
                ))}</ul>
              </div>
            )}

            {answer.type === 'rights' && answer.details && (
              <ul className="space-y-2">{answer.details.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />{r}</li>
              ))}</ul>
            )}

            {answer.type === 'offences' && answer.matches && (
              <div className="space-y-3">{answer.matches.slice(0, 5).map(off => (
                <button key={off.key} onClick={() => navigateToOffence ? navigateToOffence(off.key, off.category) : setPage('offences')}
                  className="w-full p-4 bg-gray-50 rounded-xl border hover:bg-violet-50 hover:border-violet-300 transition-all text-left">
                  <div className="flex justify-between items-start">
                    <div><h4 className="font-bold">{off.title}</h4><p className="text-sm text-gray-500">Reg. {off.reg}</p></div>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-bold">{off.fine}</span>
                  </div>
                  <p className="text-xs text-violet-600 mt-2 font-medium">Click to view full details →</p>
                </button>
              ))}</div>
            )}

            {answer.offenceKey && (
              <button onClick={() => navigateToOffence ? navigateToOffence(answer.offenceKey, answer.category) : setPage('offences')} className="mt-4 px-4 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg font-medium">
                View Full Details →
              </button>
            )}

            {answer.suggestion && (
              <button onClick={() => setPage(answer.suggestion)} className="mt-4 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium">
                Browse Offences Database →
              </button>
            )}

            {answer.suggestions && answer.suggestions.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-3">Try one of these:</p>
                <div className="flex flex-wrap gap-2">
                  {answer.suggestions.map((s, i) => (
                    <button key={i}
                      onClick={() => {
                        if (s.action) {
                          setPage(s.action);
                        } else if (s.query) {
                          setQuery(s.query);
                          const result = processQuery(s.query);
                          if (result) setAnswer(result);
                        }
                      }}
                      className="px-4 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg text-sm font-medium">
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
          <h3 className="font-bold text-lg mb-4">Recent Questions</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <button key={i} onClick={() => { setQuery(h.query); setAnswer(h.result); }}
                className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left">
                <p className="font-medium">{h.query}</p>
                <p className="text-sm text-gray-500">{h.time}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== FINE CALCULATOR PAGE ====================
const FineCalculatorPage = ({ setPage, goBack, lang }) => {
  const [selectedOffences, setSelectedOffences] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const parseFinAmount = (fineStr) => {
    const match = fineStr.match(/\$?([\d,]+)/);
    return match ? parseInt(match[1].replace(/,/g, '')) : 0;
  };

  const calculateTotal = () => {
    return selectedOffences.reduce((sum, key) => {
      const off = OFFENCES[key];
      return sum + parseFinAmount(off?.fine || '0');
    }, 0);
  };

  const calculatePoints = () => {
    return selectedOffences.reduce((sum, key) => {
      const off = OFFENCES[key];
      const pts = off?.points;
      if (pts === 'No Points' || !pts) return sum;
      const num = parseInt(pts.split('-')[0]);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  };

  const filtered = Object.entries(OFFENCES).filter(([k, v]) => {
    const matchesSearch = !search || v.title.toLowerCase().includes(search.toLowerCase()) || k.includes(search.toLowerCase());
    const matchesCategory = !category || v.category === category;
    return matchesSearch && matchesCategory;
  });

  const totalFine = calculateTotal();
  const totalPoints = calculatePoints();

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Calculator className="w-8 h-8" />Fine Calculator</h2>
        <p className="text-pink-200 mt-2">Estimate total fines and demerit points for multiple offences</p>
      </div>

      {selectedOffences.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 sticky top-20 z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Estimated Minimum Fine</p>
              <p className="text-4xl font-black text-red-600">${totalFine.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Estimated Points</p>
              <p className="text-4xl font-black text-amber-600">{totalPoints}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedOffences.map(key => (
              <span key={key} className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-700 rounded-lg text-sm">
                {OFFENCES[key]?.title}
                <button onClick={() => setSelectedOffences(selectedOffences.filter(k => k !== key))} className="hover:text-pink-900"><X className="w-4 h-4" /></button>
              </span>
            ))}
          </div>
          <button onClick={() => setSelectedOffences([])} className="mt-3 text-sm text-gray-500 hover:text-gray-700">Clear all</button>

          {totalPoints >= 10 && (
            <div className={`mt-4 p-4 rounded-xl ${totalPoints >= 20 ? 'bg-red-100 border-2 border-red-300' : 'bg-amber-100 border-2 border-amber-300'}`}>
              <p className={`font-bold ${totalPoints >= 20 ? 'text-red-800' : 'text-amber-800'}`}>
                {totalPoints >= 20 ? '⚠️ LICENCE SUSPENSION WARNING' : '⚠️ Training Required'}
              </p>
              <p className={`text-sm mt-1 ${totalPoints >= 20 ? 'text-red-700' : 'text-amber-700'}`}>
                {totalPoints >= 20 ? 'You would face licence suspension at 20+ points.' : 'At 10-19 points, you would need to complete training.'}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Search offences..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500" />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full md:w-48 pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 appearance-none">
              <option value="">All Categories</option>
              {OFFENCE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(([key, off]) => {
          const isSelected = selectedOffences.includes(key);
          return (
            <button key={key} onClick={() => setSelectedOffences(isSelected ? selectedOffences.filter(k => k !== key) : [...selectedOffences, key])}
              className={`p-4 rounded-xl text-left transition-all ${isSelected ? 'bg-pink-600 text-white shadow-lg ring-2 ring-pink-400' : 'bg-white hover:bg-pink-50 border-2 border-gray-100 hover:border-pink-200'}`}>
              <div className="flex justify-between items-start">
                <h4 className={`font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>{off.title}</h4>
                {isSelected && <CheckCircle className="w-5 h-5" />}
              </div>
              <p className={`text-xs mt-1 ${isSelected ? 'text-pink-200' : 'text-gray-500'}`}>{off.category}</p>
              <div className="flex gap-2 mt-3">
                <span className={`px-2 py-1 rounded text-xs font-bold ${isSelected ? 'bg-white/20' : 'bg-red-100 text-red-700'}`}>{off.fine.split('/')[0]}</span>
                <span className={`px-2 py-1 rounded text-xs font-bold ${isSelected ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`}>{off.points} pts</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <h4 className="font-bold text-blue-900">Important Notes</h4>
        <ul className="mt-3 space-y-2 text-blue-800 text-sm">
          <li>• Fines shown are minimum fixed penalties. Court fines can be higher.</li>
          <li>• Points expire after 15 months if licence is not suspended.</li>
          <li>• 10-13 points (speeding): Training required</li>
          <li>• 14-19 points (speeding): Training + Road Code test + Driving test</li>
          <li>• 20+ points: Licence suspension</li>
          <li>• DUI conviction requires rehabilitation program regardless of points.</li>
        </ul>
      </div>
    </div>
  );
};

// ==================== SETTINGS PAGE ====================
const SettingsPage = ({ setPage, goBack, lang, setLang }) => {
  const handleClear = () => { if (confirm('Clear all app data?')) { localStorage.clear(); window.location.reload(); }};

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Settings className="w-8 h-8" />{T[lang].settings}</h2>
      </div>
      <div className="bg-white rounded-xl shadow-lg p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Globe className="w-5 h-5" />Language</h3>
        <div className="flex gap-3">
          <button onClick={() => setLang('en')} className={`flex-1 py-3 rounded-xl font-bold ${lang === 'en' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>English</button>
          <button onClick={() => setLang('patois')} className={`flex-1 py-3 rounded-xl font-bold ${lang === 'patois' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Patois</button>
        </div>
      </div>
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <WifiOff className="w-6 h-6 text-emerald-600" />
          <div><h4 className="font-bold text-emerald-900">Offline Mode</h4><p className="text-emerald-800 text-sm mt-1">This app works offline! All data stored locally.</p></div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-lg p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Trash2 className="w-5 h-5" />Data</h3>
        <button onClick={handleClear} className="w-full py-3 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl">Clear All App Data</button>
      </div>
      <div className="bg-gray-50 rounded-xl p-5 text-center">
        <Shield className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
        <h4 className="font-bold">Jamaica Driver's Guide</h4>
        <p className="text-sm text-gray-500">Version 2.0</p>
        <p className="text-xs text-gray-400 mt-1">Road Traffic Regulations, 2022</p>
      </div>
    </div>
  );
};

// ==================== DATA PROTECTION CONSENT MODAL ====================
const DataProtectionConsent = ({ onAccept }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 text-white p-6 rounded-t-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="relative">
              <Shield className="w-12 h-12 text-yellow-400" />
              <Car className="w-6 h-6 absolute -bottom-1 -right-1 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Jamaica Driver's Guide</h2>
              <p className="text-emerald-200 text-sm">Regulations, Rights & Resources</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Data Protection Notice
            </h3>
            <p className="text-blue-800 text-sm mt-2">
              In accordance with the <strong>Jamaica Data Protection Act, 2020</strong>, we inform you of how we handle your data.
            </p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>What we collect:</strong> Tickets you add, documents you store, accident reports, and preferences. All data is stored locally on your device by default.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Optional account:</strong> If you create an account, your data may be synced to our secure servers to enable features like service provider listings and cross-device access.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Your rights:</strong> You have the right to access, correct, or delete your data at any time. You can delete your account and all associated data from Settings.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>No selling:</strong> We do not sell your personal data to third parties.
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I have read and understand the data protection notice. I consent to the collection and processing of my data as described above and in the <a href="#" onClick={(e) => { e.preventDefault(); window.open('/privacy-policy', '_blank'); }} className="text-emerald-600 underline hover:text-emerald-700">Privacy Policy</a>.
              </span>
            </label>
          </div>

          <button
            onClick={onAccept}
            disabled={!checked}
            className={`w-full py-3 rounded-xl font-bold text-white transition-all ${
              checked
                ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Continue to App
          </button>

          <p className="text-xs text-gray-500 text-center">
            By continuing, you agree to our Terms of Use and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== PRIVACY POLICY PAGE ====================
const PrivacyPolicyPage = ({ setPage, goBack }) => {
  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Shield className="w-8 h-8" />
          Privacy Policy
        </h2>
        <p className="text-slate-300 mt-1">Jamaica Driver's Guide - Data Protection</p>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
        <div>
          <p className="text-sm text-gray-500">Last Updated: January 2026</p>
          <p className="mt-3 text-gray-700">
            Jamaica Driver's Guide ("we", "our", "us") is committed to protecting your privacy in accordance with the
            <strong> Jamaica Data Protection Act, 2020</strong>. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our application.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            1. Information We Collect
          </h3>
          <div className="pl-7 space-y-2 text-gray-700">
            <p><strong>Local Data (stored on your device only):</strong></p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Traffic tickets you record</li>
              <li>Vehicle document information you store</li>
              <li>Accident reports you create</li>
              <li>App preferences and settings</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> This data is stored only in your browser's local storage and is NOT transmitted to any server.
                Clearing your browser data, cache, or using private/incognito mode will permanently delete this information.
              </p>
            </div>
            <p className="mt-3"><strong>Account Data (if you register):</strong></p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Email address</li>
              <li>Name (optional)</li>
              <li>Phone number (optional, for service providers)</li>
              <li>Business information (for service providers)</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Info className="w-5 h-5 text-emerald-600" />
            2. How We Use Your Information
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide app functionality (ticket tracking, document storage)</li>
              <li>Enable account features and cross-device synchronization</li>
              <li>Display service provider listings to users</li>
              <li>Improve our services and user experience</li>
              <li>Comply with legal obligations under Jamaican law</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-600" />
            3. Data Security
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We implement appropriate technical and organizational measures to protect your personal data against
              unauthorized access, alteration, disclosure, or destruction. This includes:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure database storage with access controls</li>
              <li>Local-first data storage when possible</li>
              <li>Regular security assessments</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            4. Your Rights Under the Jamaica Data Protection Act
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>Under the Jamaica Data Protection Act, 2020, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Object:</strong> Object to processing of your data for marketing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw your consent at any time</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, go to Settings in the app or contact us at the address below.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            5. Data Retention
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected,
              or as required by law. Local data remains on your device until you delete it. Account data is retained until
              you delete your account.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            6. Third-Party Services
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>Our app may use the following third-party services:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Supabase:</strong> For account authentication and data storage</li>
              <li><strong>Leaflet/OpenStreetMap:</strong> For map functionality</li>
              <li><strong>Google Fonts:</strong> For typography</li>
            </ul>
            <p className="mt-2">
              These services have their own privacy policies. We do not sell your personal data to any third party.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            7. Children's Privacy
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              This app is not intended for children under 16. We do not knowingly collect personal data from children.
              If you believe we have collected data from a child, please contact us immediately.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-emerald-600" />
            8. Changes to This Policy
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" />
            9. Contact Us
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data protection rights,
              please contact us:
            </p>
            <div className="mt-4">
              <button
                onClick={() => setPage('contact')}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                <Send className="w-4 h-4" />
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== HOW THIS GUIDE WORKS PAGE ====================
const HowThisGuideWorksPage = ({ setPage, goBack }) => {
  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <HelpCircle className="w-8 h-8" />
          How This Guide Works
        </h2>
        <p className="text-emerald-200 mt-1">Understanding the Jamaica Driver's Guide</p>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-8">
        <div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The <strong>Jamaica Driver's Guide</strong> is a free, comprehensive resource designed to help Jamaican motorists
            understand traffic regulations, know their rights, and access essential driving-related services — all in one place.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-600" />
            Our Mission
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We aim to make Jamaica's road traffic regulations accessible and easy to understand for every motorist.
              Whether you're a new learner, a seasoned driver, or a commercial vehicle operator, this guide provides the
              information you need to stay informed, compliant, and safe on Jamaica's roads.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            Where Our Information Comes From
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              The information in this guide is sourced from the following official and authoritative references:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>The Road Traffic Act</strong> — Jamaica's primary road traffic legislation</li>
              <li><strong>The Road Traffic Regulations, 2022</strong> — The latest regulations governing traffic offences, fines, and penalties</li>
              <li><strong>Tax Administration Jamaica (TAJ)</strong> — For licensing, registration, and fitness certificate procedures and fees</li>
              <li><strong>The Jamaica Constabulary Force (JCF)</strong> — For information on police procedures and your rights when stopped</li>
              <li><strong>The Road Safety Unit, Ministry of Transport</strong> — For road safety statistics and fatality data</li>
              <li><strong>Island Traffic Authority (ITA)</strong> — For vehicle fitness and road code information</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Disclaimer:</strong> While we strive for accuracy, this guide provides general information and should not
                be treated as legal advice. Laws and regulations may change. Always verify with official sources for the most
                current information.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-600" />
            Key Features
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <Scale className="w-4 h-4 text-emerald-600" />
                  Traffic Offences & Fines
                </div>
                <p>Browse 147+ traffic offences with fine amounts, demerit points, and related regulations from the Road Traffic Regulations, 2022.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <Calculator className="w-4 h-4 text-emerald-600" />
                  Fine Calculator
                </div>
                <p>Calculate the total cost of multiple traffic offences and understand the financial impact of violations.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  Police Stop Guide
                </div>
                <p>Know your rights and responsibilities when stopped by the police, with an audio walkthrough feature.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  How-To Guides
                </div>
                <p>Step-by-step instructions for vehicle registration, licence renewal, fitness certificates, and more.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  Road Safety Data
                </div>
                <p>Interactive maps showing road fatality statistics, accident hotspots, and speed camera locations across Jamaica.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <Briefcase className="w-4 h-4 text-emerald-600" />
                  Service Providers
                </div>
                <p>Find verified traffic lawyers, insurance companies, mechanics, and vehicle evaluators across all parishes.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <Receipt className="w-4 h-4 text-emerald-600" />
                  Personal Tools
                </div>
                <p>Track your tickets, store vehicle documents digitally, and generate accident reports — all from your account.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  Ask Bot
                </div>
                <p>Get instant answers to traffic-related questions using our natural language Q&A assistant.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            Who Is This Guide For?
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>New Learners</strong> — Preparing for your learner's permit or driving test</li>
              <li><strong>Licensed Drivers</strong> — Stay updated on current regulations, fines, and your rights</li>
              <li><strong>Vehicle Owners</strong> — Manage registration, fitness, and licensing requirements</li>
              <li><strong>Commercial Operators</strong> — Understand regulations specific to commercial vehicles</li>
              <li><strong>Legal Professionals</strong> — Quick reference for traffic offences and penalties</li>
              <li><strong>Anyone on Jamaica's Roads</strong> — Road safety information for pedestrians and passengers too</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Is This Guide Free?
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              <strong>Yes!</strong> The Jamaica Driver's Guide is completely free to use. All traffic offence information,
              how-to guides, road safety data, and educational content are available at no cost.
            </p>
            <p>
              Some features — such as the Ticket Tracker, Document Wallet, and Accident Report — require a free account
              so your data can be saved securely.
            </p>
            <p>
              Service providers (lawyers, mechanics, insurance companies, evaluators) can opt into paid listings for
              enhanced visibility in our directory.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-600" />
            How We Stay Updated
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We regularly review and update our content to reflect the latest changes in Jamaica's road traffic laws,
              fees, and procedures. Our road fatality and accident data is updated as new information becomes available
              from official sources.
            </p>
            <p>
              If you notice any outdated or incorrect information, we encourage you to
              <button onClick={() => setPage('contact')} className="text-emerald-600 underline hover:text-emerald-700 mx-1">contact us</button>
              so we can correct it promptly.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-600" />
            Your Privacy
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We take your privacy seriously. Personal data you enter (tickets, documents, reports) is stored securely
              and is never shared with third parties. For full details, please read our
              <button onClick={() => setPage('privacy-policy')} className="text-emerald-600 underline hover:text-emerald-700 mx-1">Privacy Policy</button>.
            </p>
          </div>
        </div>

        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
            <Send className="w-5 h-5 text-emerald-600" />
            Questions or Feedback?
          </h3>
          <p className="text-gray-700 text-sm mb-3">
            We'd love to hear from you! Whether you have a question, suggestion, or found something that needs updating,
            don't hesitate to reach out.
          </p>
          <button
            onClick={() => setPage('contact')}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            <Send className="w-4 h-4" />
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACT US PAGE ====================
const ContactUsPage = ({ setPage, goBack }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'general',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'general', label: 'General Inquiry' },
    { id: 'feedback', label: 'Feedback & Suggestions' },
    { id: 'bug', label: 'Report a Bug' },
    { id: 'business', label: 'Business Listing Inquiry' },
    { id: 'data', label: 'Data Protection Request' },
    { id: 'partnership', label: 'Partnership Opportunity' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Import dynamically to avoid circular deps
      const { contactSubmissions } = await import('./lib/supabase');
      const { error: submitError } = await contactSubmissions.submit(form);

      if (submitError) throw submitError;
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-6">
        <BackButton setPage={setPage} goBack={goBack} />
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us. We'll review your message and get back to you as soon as possible.
          </p>
          <button
            onClick={() => setPage('home')}
            className="px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Send className="w-8 h-8" />
          Contact Us
        </h2>
        <p className="text-blue-200 mt-1">We'd love to hear from you</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Name & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Phone & Category Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="876-xxx-xxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of your inquiry"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Please provide as much detail as possible..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>

        {/* Contact Info Sidebar */}
        <div className="space-y-4">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Get in Touch
            </h3>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Response Time</p>
                  <p>We typically respond within 24-48 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Location</p>
                  <p>Kingston, Jamaica</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Data Protection
            </h4>
            <p className="text-blue-800 text-sm">
              For data protection requests (access, correction, deletion), please select "Data Protection Request" as the category and include your registered email.
            </p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
            <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Inquiries
            </h4>
            <p className="text-emerald-800 text-sm">
              Interested in listing your business or partnership opportunities? We'd love to hear from you!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== URL ROUTING UTILITIES ====================
// Valid pages for URL routing
const VALID_PAGES = new Set([
  'home', 'offences', 'walkthrough', 'lawyers', 'insurance', 'evaluators', 'services',
  'cameras', 'hotspots', 'learner', 'fees', 'demerit', 'calculator', 'getlisted',
  'askbot', 'howto', 'online-services', 'insurance-lookup', 'roadcode', 'tickets',
  'documents', 'traffic', 'accident', 'mechanics', 'parts', 'resources', 'settings',
  'privacy-policy', 'contact', 'how-guide-works', 'login', 'dashboard', 'my-analytics', 'my-business',
  'vendor-portal', 'fatality-tracker', 'admin'
]);

// Get page from URL - supports both clean paths (/offences) and hash (#offences) for compatibility
const getPageFromUrl = () => {
  // First check hash (for backwards compatibility and direct hash links)
  const hash = window.location.hash.slice(1);
  if (hash) {
    const hashPage = hash.split('?')[0];
    if (VALID_PAGES.has(hashPage)) return hashPage;
  }

  // Then check pathname for clean URLs
  const pathname = window.location.pathname.slice(1); // Remove leading /
  if (!pathname || pathname === '') return 'home';
  const pathPage = pathname.split('/')[0].split('?')[0];
  return VALID_PAGES.has(pathPage) ? pathPage : 'home';
};

// ==================== MAIN APP CONTENT ====================
function AppContent() {
  // Initialize page from URL for SEO and direct linking
  const [page, setPage] = useState(() => getPageFromUrl());
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useLocalStorage('ja-lang', 'en');
  const [online, setOnline] = useState(navigator.onLine);
  const [selectedOffenceCode, setSelectedOffenceCode] = useState(null); // For deep-linking from chatbot
  const [selectedVendor, setSelectedVendor] = useState(null); // For My Business direct access
  const [fatalityTrackerPublic, setFatalityTrackerPublic] = useState(false); // Visibility toggle for fatality tracker
  const [hasConsented, setHasConsented] = useLocalStorage('ja-dpa-consent', false); // Data Protection Act consent
  const { isAdmin } = useAuth();

  // Track scroll positions and navigation history for back button
  const scrollPositions = useRef({});
  const pageHistory = useRef(['home']);
  const isGoingBack = useRef(false);
  const isNavigating = useRef(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const newPage = getPageFromUrl();
      if (newPage !== page) {
        isGoingBack.current = true;
        setPage(newPage);
        // Update history tracking
        if (pageHistory.current[pageHistory.current.length - 1] !== newPage) {
          pageHistory.current.push(newPage);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [page]);

  // Handle URL parameters on initial load (for vendor portal, etc.)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam === 'vendor-portal' && urlParams.get('token')) {
      setPage('vendor-portal');
    }
  }, []);

  // Load fatality tracker visibility setting
  useEffect(() => {
    const loadVisibility = async () => {
      const { data } = await appSettings.get('fatality_tracker_public');
      setFatalityTrackerPublic(data === 'true' || data === true);
    };
    loadVisibility();
  }, []);

  // Handle scroll position on page changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (isGoingBack.current) {
        // Restore scroll position when going back
        const savedPosition = scrollPositions.current[page] || 0;
        window.scrollTo({ top: savedPosition, behavior: 'instant' });
        isGoingBack.current = false;
      } else {
        // Scroll to top for new pages
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      // Navigation complete
      isNavigating.current = false;
    });
  }, [page]);

  // Initialize GA4 on app mount (if configured and enabled)
  useEffect(() => {
    const initAnalytics = async () => {
      try {
        // Check if GA4 is enabled first
        const [measurementIdResult, enabledResult] = await Promise.all([
          appSettings.get('ga4_measurement_id'),
          appSettings.get('ga4_enabled')
        ]);

        // Strip quotes from values (they may be stored as JSON strings with extra quotes)
        const enabledValue = String(enabledResult?.data || '').replace(/^"|"$/g, '');
        const isEnabled = enabledValue === 'true';
        const measurementId = String(measurementIdResult?.data || '').replace(/^"|"$/g, '');

        console.log('GA4 Config:', { isEnabled, measurementId, rawEnabled: enabledResult?.data });

        if (!isEnabled) {
          console.log('GA4 tracking is disabled in settings');
          return;
        }

        if (measurementId && measurementId.startsWith('G-')) {
          const initialized = await initGA4(measurementId);
          if (initialized) {
            console.log('GA4 successfully initialized');
          }
        }
      } catch (err) {
        console.warn('GA4 initialization skipped:', err);
      }
    };
    initAnalytics();
  }, []);

  // Track page views when page changes
  useEffect(() => {
    if (page) {
      const seoConfig = PAGE_SEO[page] || PAGE_SEO.home;
      const pageTitle = seoConfig?.title || page;
      trackPageView(page, pageTitle);
    }
  }, [page]);

  // Enhanced setPage that tracks history, scroll positions, and updates URL
  const navigateTo = (newPage) => {
    // Prevent rapid navigation issues
    if (isNavigating.current || newPage === page) return;
    isNavigating.current = true;
    // Save current scroll position before leaving
    scrollPositions.current[page] = window.scrollY;
    // Add to history
    pageHistory.current.push(newPage);
    // Update URL with clean path for SEO (e.g., /offences instead of /#offences)
    const newPath = newPage === 'home' ? '/' : `/${newPage}`;
    window.history.pushState({ page: newPage }, '', newPath);
    setPage(newPage);
  };

  // Go back to previous page with scroll restoration
  const goBack = () => {
    // Prevent rapid navigation issues
    if (isNavigating.current) return;

    // Save current scroll position before going back
    scrollPositions.current[page] = window.scrollY;
    isGoingBack.current = true;

    // Use browser's back to trigger popstate and stay in sync with browser history
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // Fallback to home if no browser history
      navigateTo('home');
    }
  };

  // Helper to navigate to offences with a specific offence selected
  const navigateToOffence = (offenceCode, category) => {
    setSelectedOffenceCode({ code: offenceCode, category });
    navigateTo('offences');
  };

  const renderPage = () => {
    // Use navigateTo for setPage to track history, and provide goBack for back buttons
    const props = { lang, setLang, setPage: navigateTo, goBack };
    const seoConfig = PAGE_SEO[page] || PAGE_SEO.home;

    return (
      <>
        <SEO page={page} title={seoConfig.title} description={seoConfig.description} />
        {(() => {
          switch(page) {
            case 'home': return <HomePage setPage={setPage} {...props} />;
            case 'askbot': return <QuickAnswerBotPage setPage={setPage} navigateToOffence={navigateToOffence} {...props} />;
            case 'calculator': return <FineCalculatorPage {...props} />;
            case 'walkthrough': return <WalkthroughPage {...props} />;
            case 'offences': return <OffencesPageEnhanced selectedOffenceCode={selectedOffenceCode} clearSelectedOffence={() => setSelectedOffenceCode(null)} {...props} />;
            case 'learner': return <LearnerGuidePage {...props} />;
            case 'howto': return <HowToGuidesPage setPage={setPage} {...props} />;
            case 'fees': return <FeesPage {...props} />;
            case 'online-services': return <OnlineServicesPage {...props} />;
            case 'insurance-lookup': return <InsuranceLookupPage {...props} />;
            case 'roadcode': return <RoadCodePage {...props} />;
            case 'tickets': return <AuthGate setPage={setPage} pageName="Ticket Tracker" pageIcon={Receipt}><TicketTrackerPage {...props} /></AuthGate>;
            case 'documents': return <AuthGate setPage={setPage} pageName="My Documents" pageIcon={FileText}><DocumentsPage {...props} /></AuthGate>;
            case 'hotspots': return <HotspotsPage setPage={setPage} {...props} />;
            case 'cameras': return <SpeedCamerasPage setPage={setPage} {...props} />;
            case 'traffic': return <TrafficPage setPage={setPage} {...props} />;
            case 'accident': return <AuthGate setPage={setPage} pageName="Accident Report" pageIcon={AlertCircle}><AccidentReportPage {...props} /></AuthGate>;
            // Service Providers - consolidated
            case 'services': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'getlisted': return <GetListedPageEnhanced setPage={setPage} {...props} />;
            // Legacy routes - redirect to services
            case 'lawyers': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'insurance': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'evaluators': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'mechanics': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'parts': return <ServiceProvidersPage setPage={setPage} {...props} />;
            // Other pages
            case 'resources': return <ResourcesPage {...props} />;
            case 'demerit': return <DemeritPage {...props} />;
            case 'settings': return <SettingsPage {...props} />;
            case 'privacy-policy': return <PrivacyPolicyPage {...props} />;
            case 'contact': return <ContactUsPage {...props} />;
            case 'how-guide-works': return <HowThisGuideWorksPage {...props} />;
            // Auth & User Pages
            case 'login': return <LoginPage setPage={setPage} />;
            case 'dashboard': return <AuthGate setPage={setPage} pageName="My Dashboard" pageIcon={User}><UserDashboard setPage={setPage} {...props} /></AuthGate>;
            case 'my-analytics': return <AdvertiserPortal setPage={setPage} />;
            // Admin Pages
            case 'admin': return <AdminDashboard setPage={setPage} />;
            case 'admin-submissions': return <AdminSubmissionsPageEnhanced setPage={setPage} />;
            case 'admin-users': return <AdminUsersPage setPage={setPage} />;
            case 'admin-listings': return <AdminAllListingsPage setPage={setPage} />;
            case 'admin-lawyers': return <AdminDirectoryPage setPage={setPage} type="lawyers" />;
            case 'admin-insurance': return <AdminDirectoryPage setPage={setPage} type="insurance" />;
            case 'admin-evaluators': return <AdminDirectoryPage setPage={setPage} type="evaluators" />;
            case 'admin-hotspots': return <AdminHotspotsPage setPage={setPage} />;
            case 'admin-cameras': return <AdminCamerasPage setPage={setPage} />;
            case 'admin-analytics': return <AdminAnalyticsPage setPage={setPage} />;
            case 'admin-ads': return <AdminBannerAdsPage setPage={setPage} />;
            case 'admin-hero': return <AdminHeroCarouselPage setPage={setPage} />;
            case 'admin-corrections': return <AdminDataCorrectionsPage setPage={setPage} />;
            case 'admin-notifications': return <AdminNotificationsPage setPage={setPage} />;
            case 'admin-contact': return <AdminContactSubmissionsPage setPage={setPage} />;
            case 'admin-settings': return <AdminSettingsPageEnhanced setPage={setPage} />;
            // Service Provider Management
            case 'admin-service-providers': return <AdminServiceProvidersPage setPage={setPage} />;
            case 'admin-subscriptions': return <AdminSubscriptionsPage setPage={setPage} />;
            case 'admin-payments': return <AdminPaymentsPage setPage={setPage} />;
            case 'admin-email-templates': return <AdminEmailTemplatesPage setPage={setPage} />;
            case 'admin-payment-requests': return <AdminPaymentRequestsPage setPage={setPage} />;
            case 'admin-fatalities': return <AdminRoadFatalities onBack={() => navigateTo('admin')} setPage={setPage} isPublic={fatalityTrackerPublic} setIsPublic={setFatalityTrackerPublic} />;
            // Road Fatality Map (visible to admins always, public when enabled)
            case 'fatality-tracker': {
              if (!isAdmin && !fatalityTrackerPublic) {
                // Redirect to home if not admin and feature is not public
                navigateTo('home');
                return null;
              }
              return <RoadFatalityTracker onBack={() => navigateTo('home')} setPage={navigateTo} />;
            }
            // Vendor Portal (token-based access)
            case 'vendor-portal': {
              const urlParams = new URLSearchParams(window.location.search);
              const token = urlParams.get('token');
              return <VendorPortalPage setPage={setPage} token={token} />;
            }
            // My Business Portal (direct access for logged-in owners)
            case 'my-business': {
              if (!selectedVendor) {
                return <HomePage setPage={setPage} {...props} />;
              }
              return <VendorPortalPage
                setPage={setPage}
                vendorId={selectedVendor.id}
                providerTable={selectedVendor.providerTable}
              />;
            }
            default: return <HomePage setPage={setPage} {...props} />;
          }
        })()}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-200 via-slate-100 to-blue-50">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:bg-emerald-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus:shadow-lg">
        Skip to main content
      </a>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');* { font-family: 'Plus Jakarta Sans', sans-serif; }`}</style>

      {/* Data Protection Consent Modal - shows on first visit */}
      {!hasConsented && (
        <DataProtectionConsent onAccept={() => setHasConsented(true)} />
      )}

      {!online && (
        <div className="bg-amber-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />{T[lang]?.youreOffline || T.en.youreOffline}
        </div>
      )}
      <Header
        page={page}
        setPage={navigateTo}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        lang={lang}
        setLang={setLang}
        setSelectedVendor={setSelectedVendor}
        fatalityTrackerPublic={fatalityTrackerPublic}
        navigateToOffence={navigateToOffence}
        offences={ENHANCED_OFFENCES}
        howToGuides={HOW_TO_GUIDES.guides}
        serviceProviders={{
          lawyers: LAWYERS,
          insurance: INSURANCE_COMPANIES,
          mechanics: MECHANICS,
          evaluators: VEHICLE_EVALUATORS
        }}
        speedCameras={SPEED_CAMERAS}
      />
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 py-8">{renderPage()}</main>
      <footer role="contentinfo" className="bg-gray-900 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Shield className="w-8 h-8 text-yellow-400" />
                <Car className="w-4 h-4 absolute -bottom-1 -right-1 text-white" />
              </div>
              <div><div className="font-bold">Jamaica Driver's Guide</div><div className="text-sm text-gray-400">Regulations, Rights & Resources</div></div>
            </div>
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>This app provides general information, not legal advice.</p>
              <p className="mt-1">Version 2.1</p>
              <div className="mt-2 flex items-center justify-center md:justify-end gap-3">
                <button onClick={() => navigateTo('privacy-policy')} className="text-blue-400 hover:text-blue-300 underline">
                  Privacy Policy
                </button>
                <span className="text-gray-600">•</span>
                <button onClick={() => navigateTo('contact')} className="text-blue-400 hover:text-blue-300 underline">
                  Contact Us
                </button>
                <span className="text-gray-600">•</span>
                <button onClick={() => navigateTo('how-guide-works')} className="text-emerald-400 hover:text-emerald-300 underline">
                  How This Guide Works
                </button>
              </div>
              <p className="mt-2 text-emerald-400 font-medium">Developed by Johnathon Kelly</p>
            </div>
          </div>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
}

// ==================== APP WITH AUTH PROVIDER ====================
export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <WebsiteSchema />
        <OrganizationSchema />
        <AppContent />
      </AuthProvider>
    </HelmetProvider>
  );
}
